import { useAircraftLookup } from '../../hooks/useAircraftLookup';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { fileUnfilledComponent } from '../../services/emailAddressService';
import { attachmentText, hasParsedAttachment, attachmentLabel } from '../../utils/attachmentPreview';
import { Dialog as DateDialog, DialogContent as DateDialogContent } from '../ui/dialog';
import { Calendar } from '../ui/calendar';
import { motion } from 'framer-motion';
import {
  Plane,
  Building,
  UtensilsCrossed,
  Handshake,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Car,
  Users,
  Plus,
  Minus,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Upload,
  Edit,
  Trash2,
  CheckCircle,
  Eye,
  X,
  User,
  Moon,
  Search,
  FileText,
  Image,
  Download,
  ChefHat,
  Timer,
  Navigation,
  Info,
  Crown,
  Hash,
  Settings,
  Hotel,
  PlaneLanding,
  PlaneTakeoff,
  Building2,
  Route,
  Plane as PlaneIcon,
  Briefcase,
  Phone,
  StickyNote,
  BadgeCheck,
  Mic
} from 'lucide-react';
import DatePickerButton from './DatePickerButton';
import FareTermsFormSection from './FareTermsFormSection';
import { parseTimeInput, parseLocalDate } from '../../utils/dateTimeUtils';

// UI Components
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';

// Custom Components (assuming they exist or will be created)
import CityAutocomplete from './CityAutocomplete';
import RestaurantAutocomplete from './RestaurantAutocomplete';
import AddressAutocomplete from './AddressAutocomplete';
import AirportAutocomplete from './AirportAutocomplete';
import AirlineAutocomplete from './AirlineAutocomplete';
import { PortalContainerProvider } from './PortalContainerContext';
import CustomDateTimePicker from './CustomDateTimePicker';
import FBOAutocomplete from './FBOAutocomplete';
import ControlledDateInput from './ControlledDateInput';
import VenueAutocomplete from './VenueAutocomplete';
import SpeakerLinkField from './SpeakerLinkField';
import { useParadigm } from '../../contexts/ParadigmContext';
import HotelAutocomplete from './HotelAutocomplete';
import VipServiceProviderAutocomplete from './VipServiceProviderAutocomplete';
import EditVipProviderModal from './EditVipProviderModal';
import { validateFlight, initializeUrbanrideApi } from '../../services/urbanrideService';

// SECURITY: Helper function to escape HTML to prevent XSS attacks
const escapeHtml = (text) => {
  if (typeof text !== 'string') return text;
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
};

// Helper function to normalize date format from various formats to ISO YYYY-MM-DD
// Handles DD/MM/YYYY (European), MM/DD/YYYY (US), and YYYY-MM-DD (ISO)
const normalizeDateFormat = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;

  const str = dateStr.trim();

  // If already contains 'T', extract just the date part
  const datePart = str.includes('T') ? str.split('T')[0] : str;

  // Try ISO format first (YYYY-MM-DD)
  if (datePart.length === 10 && datePart[4] === '-' && datePart[7] === '-') {
    const year = parseInt(datePart.substring(0, 4), 10);
    const month = parseInt(datePart.substring(5, 7), 10);
    const day = parseInt(datePart.substring(8, 10), 10);
    if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return datePart;
    }
  }

  // Try DD/MM/YYYY format (European - common for travel confirmations)
  if (datePart.includes('/')) {
    const parts = datePart.split('/');
    if (parts.length === 3) {
      let day, month, year;

      // Check if first part could be a year (YYYY/MM/DD)
      if (parts[0].length === 4 && parseInt(parts[0], 10) > 1900) {
        year = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        day = parseInt(parts[2], 10);
      } else if (parts[2].length === 4) {
        // European format DD/MM/YYYY
        day = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);

        // If first number > 12, it must be the day
        // If first number <= 12 and second > 12, swap (was US format)
        if (day <= 12 && month > 12) {
          [day, month] = [month, day];
        }
      } else {
        // Two-digit year, assume DD/MM/YY
        day = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);
        year = year < 50 ? 2000 + year : 1900 + year;
      }

      if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      }
    }
  }

  // Try DD-MM-YYYY or DD.MM.YYYY format
  for (const sep of ['-', '.']) {
    if (datePart.includes(sep) && datePart.split(sep).length === 3) {
      const parts = datePart.split(sep);
      if (parts[2].length === 4) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        }
      }
    }
  }

  return null; // Could not parse
};

// Helper function to parse a date string that might be in various formats
const parseFlexibleDate = (dateStr, timeStr = null) => {
  if (!dateStr) return null;

  // If the input already contains 'T' (ISO format with time), try parsing directly first
  if (typeof dateStr === 'string' && dateStr.includes('T')) {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date; // Preserve the original time
    }
  }

  // Normalize the date format (this extracts just the date part)
  const normalizedDate = normalizeDateFormat(dateStr);

  if (normalizedDate) {
    // If we have a separate time string, use it
    if (timeStr && typeof timeStr === 'string') {
      // Handle case where timeStr is also a full datetime (like from AwardWallet)
      let timePart = timeStr.trim();
      if (timePart.includes('T')) {
        // Extract just the time from a datetime string
        const timeMatch = timePart.match(/T(\d{2}:\d{2})/);
        timePart = timeMatch ? timeMatch[1] : '12:00';
      } else {
        timePart = timePart.substring(0, 5); // Get HH:MM
      }
      const combined = `${normalizedDate}T${timePart}`;
      const date = new Date(combined);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // If original dateStr had time info (T separator), extract and use that time
    if (typeof dateStr === 'string' && dateStr.includes('T')) {
      const timeMatch = dateStr.match(/T(\d{2}:\d{2})/);
      if (timeMatch) {
        const combined = `${normalizedDate}T${timeMatch[1]}`;
        const date = new Date(combined);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }

    // Last resort: use noon as default
    const date = new Date(`${normalizedDate}T12:00`);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  // Fallback: try parsing as-is
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

// Helper function to safely format dates - prevents "Invalid time value" errors
const safeFormatDate = (dateValue, formatString = 'MMM d, h:mm a', fallback = 'TBD') => {
  if (!dateValue || dateValue === 'null' || dateValue === 'undefined') {
    return fallback;
  }

  try {
    const date = new Date(dateValue);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return fallback;
    }
    return format(date, formatString);
  } catch (error) {
    return fallback;
  }
};

// Helper function to format flight times WITHOUT timezone conversion
// Flight times are stored as local times at the airport, not UTC
// Using new Date() would convert them to browser's local timezone which is wrong
const formatFlightTime = (dateValue, formatString = 'MMM d, h:mm a', fallback = 'TBD') => {
  if (!dateValue || dateValue === 'null' || dateValue === 'undefined') {
    return fallback;
  }

  try {
    // Parse ISO string directly without timezone conversion
    // Example: "2026-03-12T14:55:00" -> extract date and time parts
    const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})T?(\d{2})?:?(\d{2})?/);
    if (!match) {
      return fallback;
    }

    const [, year, month, day, hour = '00', minute = '00'] = match;

    // Create date object treating the time as-is (not converting from UTC)
    // We construct it with UTC methods to avoid any timezone shifts
    const date = new Date(Date.UTC(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute)
    ));

    if (isNaN(date.getTime())) {
      return fallback;
    }

    // Format in UTC to preserve the original time values
    // Map common format strings to manual formatting
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const h24 = date.getUTCHours();
    const h12 = h24 % 12 || 12;
    const ampm = h24 >= 12 ? 'PM' : 'AM';
    const min = String(date.getUTCMinutes()).padStart(2, '0');
    const dayNum = date.getUTCDate();
    const monthShort = monthNames[date.getUTCMonth()];

    // Handle common format strings
    if (formatString === 'MMM d, h:mm a') {
      return `${monthShort} ${dayNum}, ${h12}:${min} ${ampm}`;
    } else if (formatString === 'h:mm a') {
      return `${h12}:${min} ${ampm}`;
    } else if (formatString === 'MMM d') {
      return `${monthShort} ${dayNum}`;
    } else if (formatString === 'MMM d, yyyy') {
      return `${monthShort} ${dayNum}, ${year}`;
    }

    // Default format
    return `${monthShort} ${dayNum}, ${h12}:${min} ${ampm}`;
  } catch (error) {
    return fallback;
  }
};

// Attendee Form Modal Component - Defined outside main component to prevent re-creation
const AttendeeFormModal = React.memo(({ attendee, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: attendee?.name || '',
    email: attendee?.email || '',
    title: attendee?.title || '',
    company: attendee?.company || '',
    phone: attendee?.phone || '',
    ea_name: attendee?.ea_name || '',
    ea_email: attendee?.ea_email || '',
    ea_phone: attendee?.ea_phone || '',
  });

  const [showEAFields, setShowEAFields] = useState(
    !!(formData.ea_name || formData.ea_email || formData.ea_phone)
  );

  const handleAttendeeSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Attendee name is required.");
      return;
    }
    onSubmit(formData);
  };

  // Stable input handlers to prevent focus loss
  const handleNameChange = (e) => setFormData(prev => ({ ...prev, name: e.target.value }));
  const handleEmailChange = (e) => setFormData(prev => ({ ...prev, email: e.target.value }));
  const handlePhoneChange = (e) => setFormData(prev => ({ ...prev, phone: e.target.value }));
  const handleTitleChange = (e) => setFormData(prev => ({ ...prev, title: e.target.value }));
  const handleCompanyChange = (e) => setFormData(prev => ({ ...prev, company: e.target.value }));
  const handleEANameChange = (e) => setFormData(prev => ({ ...prev, ea_name: e.target.value }));
  const handleEAEmailChange = (e) => setFormData(prev => ({ ...prev, ea_email: e.target.value }));
  const handleEAPhoneChange = (e) => setFormData(prev => ({ ...prev, ea_phone: e.target.value }));

  const ExecutiveAssistantSection = () => (
    <div className="border-t border-ui-line pt-4">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-base font-semibold text-ui-ink">
          Executive Assistant (Optional)
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowEAFields(!showEAFields)}
          className="text-ui-teal-fg hover:text-ui-teal-fg hover:bg-ui-teal-bg"
        >
          {showEAFields ? (
            <>
              <Minus className="w-4 h-4 mr-1" />
              Hide EA Info
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-1" />
              Add EA Info
            </>
          )}
        </Button>
      </div>
      
      {showEAFields && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="space-y-4"
        >
          <div>
            <Label htmlFor="ea-name" className="text-sm font-medium text-ui-ink-2 mb-2 block">
              EA Full Name
            </Label>
            <Input
              id="ea-name"
              value={formData.ea_name}
              onChange={handleEANameChange}
              placeholder="Assistant's full name"
              className="h-11"
            />
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ea-email" className="text-sm font-medium text-ui-ink-2 mb-2 block">
                EA Email
              </Label>
              <Input
                id="ea-email"
                type="email"
                value={formData.ea_email}
                onChange={handleEAEmailChange}
                placeholder="assistant@company.com"
                className="h-11"
              />
            </div>
            <div>
              <Label htmlFor="ea-phone" className="text-sm font-medium text-ui-ink-2 mb-2 block">
                EA Phone
              </Label>
              <Input
                id="ea-phone"
                value={formData.ea_phone}
                onChange={handleEAPhoneChange}
                placeholder="(555) 123-4567"
                className="h-11"
              />
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-[600px]" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-ui-ink">
            {attendee ? 'Edit Attendee' : 'Add Meeting Attendee'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleAttendeeSubmit} className="space-y-4">
          {/* Primary Contact Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="attendee-name" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Full Name *
              </Label>
              <Input
                id="attendee-name"
                value={formData.name}
                onChange={handleNameChange}
                placeholder="Enter attendee's full name"
                required
                className="h-11"
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="attendee-email" className="text-sm font-medium text-ui-ink-2 mb-2 block">
                  Email Address
                </Label>
                <Input
                  id="attendee-email"
                  type="email"
                  value={formData.email}
                  onChange={handleEmailChange}
                  placeholder="attendee@company.com"
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="attendee-phone" className="text-sm font-medium text-ui-ink-2 mb-2 block">
                  Phone Number
                </Label>
                <Input
                  id="attendee-phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  className="h-11"
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="attendee-title" className="text-sm font-medium text-ui-ink-2 mb-2 block">
                  Job Title
                </Label>
                <Input
                  id="attendee-title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder="e.g., CEO, Manager, Director"
                  className="h-11"
                />
              </div>
              <div>
                <Label htmlFor="attendee-company" className="text-sm font-medium text-ui-ink-2 mb-2 block">
                  Company
                </Label>
                <Input
                  id="attendee-company"
                  value={formData.company}
                  onChange={handleCompanyChange}
                  placeholder="e.g., Acme Corporation"
                  className="h-11"
                />
              </div>
            </div>
          </div>
          
          {/* Executive Assistant Information */}
          <ExecutiveAssistantSection />
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" className="bg-gradient-to-r from-ui-teal-strong to-ui-teal-strong hover:from-ui-teal-strong hover:to-ui-teal-strong text-ui-on-primary">
              {attendee ? 'Save Changes' : 'Add Attendee'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

// Attendee Card Component - Extracted to prevent re-creation
const AttendeeCard = React.memo(({ attendee, onEdit, onDelete, isSpeakerBureau, speakerLabel, onLinkSpeaker }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="flex flex-col gap-2 p-3 bg-ui-surface rounded-lg border border-ui-line hover:border-ui-control transition-all group"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 bg-gradient-to-br from-ui-teal-bg to-ui-teal-line rounded-xl flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-ui-teal-fg" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-ui-ink truncate">
            {attendee.name}
          </div>
          <div className="text-sm text-ui-muted truncate">
            {attendee.company && `${attendee.company} • `}
            {attendee.email || 'No email provided'}
          </div>
          {attendee.title && (
            <div className="text-xs text-ui-muted truncate">
              {attendee.title}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 hover:bg-ui-teal-bg p-0"
          onClick={() => onEdit(attendee)}
        >
          <Edit className="w-4 h-4 text-ui-teal-fg" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 text-ui-red-fg hover:text-ui-red-fg hover:bg-ui-red-bg p-0"
          onClick={() => onDelete(attendee.tempId)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>

    {/* speaker_bureau: optional link to a saved Speaker */}
    {isSpeakerBureau && onLinkSpeaker && (
      <div className="pl-12">
        <SpeakerLinkField
          speakerId={attendee.speaker_id || null}
          speakerName={attendee.speaker_name || attendee.name || ''}
          onPick={(sp) => onLinkSpeaker(attendee.tempId, sp)}
        />
      </div>
    )}
  </motion.div>
));

// Current Attendees Section - Extracted to prevent re-creation
const CurrentAttendeesSection = React.memo(({
  attendees,
  showAllAttendees,
  setShowAllAttendees,
  onEditAttendee,
  onDeleteAttendee,
  isSpeakerBureau,
  speakerLabel,
  onLinkSpeaker
}) => {
  if (attendees.length === 0) return null;
  
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <Label className="text-base font-semibold text-ui-ink">
          Meeting Attendees ({attendees.length})
        </Label>
        {attendees.length > 5 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowAllAttendees(!showAllAttendees)}
            className="text-ui-teal-fg hover:text-ui-teal-fg"
          >
            {showAllAttendees ? 'Show Less' : `Show All (${attendees.length})`}
          </Button>
        )}
      </div>
      
      <div className="space-y-2">
        {(showAllAttendees ? attendees : attendees.slice(0, 5)).map((attendee) => (
          <AttendeeCard
            key={attendee.tempId}
            attendee={attendee}
            onEdit={onEditAttendee}
            onDelete={onDeleteAttendee}
            isSpeakerBureau={isSpeakerBureau}
            speakerLabel={speakerLabel}
            onLinkSpeaker={onLinkSpeaker}
          />
        ))}
      </div>
    </div>
  );
});

// Empty Attendees State Component - Extracted to prevent re-creation
const EmptyAttendeesState = React.memo(({ onAddNewContact }) => (
  <div className="text-center py-8 text-ui-muted bg-ui-surface rounded-lg border-2 border-dashed border-ui-control">
    <Users className="w-12 h-12 text-ui-faint mx-auto mb-3" />
    <p className="font-medium">No attendees added yet.</p>
    <p className="text-sm mt-1">A meeting requires at least one attendee.</p>
    <div className="flex justify-center gap-3 mt-4">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onAddNewContact}
        className="text-ui-teal-fg border-ui-teal-line hover:bg-ui-teal-bg"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add New Contact
      </Button>
    </div>
  </div>
));

// Meeting Duration Display Component - Extracted to prevent re-creation
const MeetingDurationDisplay = React.memo(({ startTime, endTime }) => {
  // Handle TBD cases
  const isTBD = startTime?.includes('TBD') || endTime === 'TBD';
  
  const calculateDuration = () => {
    if (isTBD) {
      return "To Be Determined";
    }
    
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffMinutes = Math.round((end - start) / (1000 * 60));
      
      if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
      }
      
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      
      if (minutes === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      }
      
      return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } catch (error) {
      return "Invalid time format";
    }
  };

  const formatTimeDisplay = () => {
    if (isTBD) {
      return "📅 Meeting schedule to be determined";
    }

    try {
      const startDisplay = format(new Date(startTime), 'MMM d, yyyy h:mm a');
      const endDisplay = format(new Date(endTime), 'h:mm a');
      return `${startDisplay} → ${endDisplay}`;
    } catch (error) {
      return "Invalid time format";
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`p-3 border rounded-lg ${isTBD ? 'bg-ui-amber-bg border-ui-amber-line' : 'bg-ui-teal-bg border-ui-teal-line'}`}
    >
      <div className={`flex items-center gap-2 ${isTBD ? 'text-ui-amber-fg' : 'text-ui-teal-fg'}`}>
        <Clock className="w-4 h-4" />
        <span className="font-semibold">
          Duration: {calculateDuration()}
        </span>
      </div>
      <div className={`text-sm mt-1 ${isTBD ? 'text-ui-amber-fg' : 'text-ui-teal-fg'}`}>
        {formatTimeDisplay()}
      </div>
    </motion.div>
  );
});

// Virtual Meeting Details Component - Extracted to prevent re-creation
const VirtualMeetingDetails = React.memo(({ 
  formData, 
  handlePlatformChange, 
  handleMeetingLinkChange, 
  handleDialInChange 
}) => (
  <motion.div
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: 'auto' }}
    exit={{ opacity: 0, height: 0 }}
    className="space-y-4 pt-4 border-t border-ui-teal-line"
  >
    <div>
      <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
        Video Conference Platform
      </Label>
      <select
        value={formData.component_data?.platform || ''}
        onChange={handlePlatformChange}
        className="w-full h-10 px-3 py-2 border border-ui-line rounded-md focus:border-ui-teal-solid focus:ring-1 focus:ring-ui-teal-solid focus:outline-none bg-ui-surface text-ui-ink"
      >
        <option value="">Select platform</option>
        <option value="zoom">Zoom</option>
        <option value="teams">Microsoft Teams</option>
        <option value="meet">Google Meet</option>
        <option value="webex">Cisco Webex</option>
        <option value="gotomeeting">GoToMeeting</option>
        <option value="other">Other</option>
      </select>
    </div>
    
    <div>
      <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
        Meeting Link / ID
      </Label>
      <Input
        key="meeting-link"
        value={formData.component_data?.meeting_link || ''}
        onChange={handleMeetingLinkChange}
        placeholder="Enter meeting URL or ID"
        className="h-10"
      />
    </div>
    
    <div>
      <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
        Dial-in Number (Optional)
      </Label>
      <Input
        key="dial-in"
        value={formData.component_data?.dial_in || ''}
        onChange={handleDialInChange}
        placeholder="Phone number for dial-in access"
        className="h-10"
      />
    </div>
  </motion.div>
));

// Virtual Meeting Toggle Component - Extracted to prevent re-creation  
const VirtualMeetingToggle = React.memo(({ 
  formData, 
  handleDetailsChange,
  handlePlatformChange, 
  handleMeetingLinkChange, 
  handleDialInChange 
}) => (
  <div className="p-4 bg-ui-surface rounded-lg border border-ui-teal-line">
    <div className="flex items-center justify-between mb-3">
      <Label className="text-base font-semibold text-ui-ink">
        Meeting Format
      </Label>
      <div className="flex items-center gap-4">
        <Label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="meeting-format"
            value="in_person"
            checked={formData.component_data?.meeting_type === 'in_person'}
            onChange={() => handleDetailsChange('meeting_type', 'in_person')}
            className="text-ui-teal-fg focus:ring-ui-teal-solid"
          />
          <span className="text-sm font-medium text-ui-ink-2">In Person</span>
        </Label>
        <Label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="meeting-format"
            value="virtual"
            checked={formData.component_data?.meeting_type === 'virtual'}
            onChange={() => handleDetailsChange('meeting_type', 'virtual')}
            className="text-ui-teal-fg focus:ring-ui-teal-solid"
          />
          <span className="text-sm font-medium text-ui-ink-2">Virtual</span>
        </Label>
        <Label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="meeting-format"
            value="hybrid"
            checked={formData.component_data?.meeting_type === 'hybrid'}
            onChange={() => handleDetailsChange('meeting_type', 'hybrid')}
            className="text-ui-teal-fg focus:ring-ui-teal-solid"
          />
          <span className="text-sm font-medium text-ui-ink-2">Hybrid</span>
        </Label>
      </div>
    </div>
    
    {(formData.component_data?.meeting_type === 'virtual' || formData.component_data?.meeting_type === 'hybrid') && (
      <VirtualMeetingDetails 
        formData={formData}
        handlePlatformChange={handlePlatformChange}
        handleMeetingLinkChange={handleMeetingLinkChange}
        handleDialInChange={handleDialInChange}
      />
    )}
  </div>
));

// Attendee Search Dropdown Component - Extracted to prevent re-creation
const AttendeeSearchDropdown = React.memo(({ 
  finalAvailableExecutives, 
  executiveSearchQuery, 
  onAddAttendee 
}) => {
  const filteredContacts = finalAvailableExecutives.filter(contact => 
    `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(executiveSearchQuery.toLowerCase()) ||
    (contact.email && contact.email.toLowerCase().includes(executiveSearchQuery.toLowerCase())) ||
    (contact.company && contact.company.toLowerCase().includes(executiveSearchQuery.toLowerCase()))
  );
  
  return (
    <div className="absolute z-20 w-full mt-1 bg-ui-surface border border-ui-line rounded-lg shadow-lg max-h-64 overflow-y-auto">
      {filteredContacts.length > 0 ? (
        <div className="py-2">
          {filteredContacts.map((contact) => (
            <div
              key={contact.id}
              onClick={() => onAddAttendee(contact)}
              className="px-4 py-3 hover:bg-ui-teal-bg cursor-pointer transition-colors border-b border-ui-divider last:border-b-0"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-ui-ink">
                    {`${contact.first_name} ${contact.last_name}`}
                  </div>
                  <div className="text-sm text-ui-ink-3">{contact.email}</div>
                  {contact.title && (
                    <div className="text-xs text-ui-muted">{contact.title}</div>
                  )}
                </div>
                <Plus className="w-4 h-4 text-ui-teal-fg mt-1" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-4 px-4 text-center text-ui-muted">
          No contacts found matching your search
        </div>
      )}
    </div>
  );
});

// Form Field Component - moved outside TripComponentForm to prevent recreation on every render
const FormField = React.memo(({ 
  id, 
  label, 
  type = "text", 
  required = false, 
  error,
  className = "",
  placeholder = "",
  value,
  onChange,
  ...props 
}) => (
  <div className="space-y-2">
    <label htmlFor={id} className={`text-sm font-semibold text-ui-ink-2 ${required ? 'after:content-["*"] after:text-ui-red-fg after:ml-1' : ''}`}>
      {label}
    </label>
    {type === 'textarea' ? (
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={`w-full h-24 px-4 py-3 border border-ui-line rounded-lg focus:border-ui-violet-solid focus:ring-ui-violet-solid focus:ring-2 focus:ring-opacity-20 transition-all resize-none bg-ui-surface text-ui-ink ${className} ${error ? 'border-ui-red-solid focus:border-ui-red-solid' : ''}`}
        {...props}
      />
    ) : (
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={`w-full h-12 px-4 border border-ui-line rounded-lg focus:border-ui-violet-solid focus:ring-ui-violet-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink ${className} ${error ? 'border-ui-red-solid focus:border-ui-red-solid' : ''}`}
        {...props}
      />
    )}
    {error && (
      <div className="text-ui-red-fg text-sm flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {error}
      </div>
    )}
  </div>
));

const TripComponentForm = ({ tripId, componentType, onSubmit, onCancel, tripExecutives, meetingExecutives = [], component, allContacts = [], initialComponentData }) => {
  // Paradigm-awareness: in speaker_bureau mode the program is scoped to a single
  // speaker, so per-component "assign executives" pickers are hidden and the
  // remaining person fields (passengers/guests/attendees) can link to Speakers.
  const { isSpeakerBureau, labels } = useParadigm();
  const [formData, setFormData] = useState({
    component_type: componentType,
    title: '',
    description: '',
    start_datetime: '',
    end_datetime: '',
    location: '',
    notes: '',
    confirmation_number: '', // Add confirmation_number to initial state for chauffeur services
    status: 'pending', // CRITICAL: Add status field to initial state to prevent form initialization issues
    details: {},
    component_data: {
      assigned_executives: [],
      passengers: [],
      flight_legs: [],
      ...(initialComponentData || {})
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Multi-leg flight state
  const [flightLegs, setFlightLegs] = useState([]);
  const [showFlightLegForm, setShowFlightLegForm] = useState(false);
  const [currentFlightLeg, setCurrentFlightLeg] = useState(null);
  
  // Private flight multi-leg state
  const [privateFlightLegs, setPrivateFlightLegs] = useState([]);
  const [showPrivateFlightLegForm, setShowPrivateFlightLegForm] = useState(false);
  const [currentPrivateFlightLeg, setCurrentPrivateFlightLeg] = useState(null);
  // Time format selectors for commercial leg editor
  const [depTimeFormat, setDepTimeFormat] = useState('24H');
  const [arrTimeFormat, setArrTimeFormat] = useState('24H');
  // Raw time input strings (allows user to type freely before parsing)
  const [depTimeRaw, setDepTimeRaw] = useState('');
  const [arrTimeRaw, setArrTimeRaw] = useState('');
  const [editingLegIndex, setEditingLegIndex] = useState(-1); // Track which leg index is being edited
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploadingConfirmation, setIsUploadingConfirmation] = useState(false);
  const [validationError, setValidationError] = useState('');
  
  // Hotel-specific state for confirmation upload
  const [isUploadingHotelConfirmation, setIsUploadingHotelConfirmation] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Private flight confirmation upload state
  const [isUploadingPrivateFlightConfirmation, setIsUploadingPrivateFlightConfirmation] = useState(false);

  // Meeting-specific state
  const [attendees, setAttendees] = useState([]);
  const [showAttendeeForm, setShowAttendeeForm] = useState(false);
  const [editingAttendee, setEditingAttendee] = useState(null);
  const [attendeeSearchQuery, setAttendeeSearchQuery] = useState('');
  const [timeFormat, setTimeFormat] = useState('24H');
  const [allExecutivesList, setAllExecutivesList] = useState([]);
  const [allContactsList, setAllContactsList] = useState([]);
  const [contactFilter, setContactFilter] = useState('all');
  const [executiveSearchQuery, setExecutiveSearchQuery] = useState('');
  const [showExecutiveSuggestions, setShowExecutiveSuggestions] = useState(false);
  
  // Meeting TBD state
  const [isDateTBD, setIsDateTBD] = useState(false);
  const [isStartTimeTBD, setIsStartTimeTBD] = useState(false);
  const [isEndTimeTBD, setIsEndTimeTBD] = useState(false);
  const [showAllAttendees, setShowAllAttendees] = useState(false);
  
  // Edit mode state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [originalComponentData, setOriginalComponentData] = useState(null);

  // Aircraft lookup state
  const {
    aircraftLookupLoading, aircraftLookupError, aircraftLookupSuccess, handleTailNumberChange,
  } = useAircraftLookup(setFormData, `${tripId}:${component?.id || ''}:${componentType}`);

  // Document viewing state
  const [viewingDocument, setViewingDocument] = useState(null);
  
  // Private flight specific state
  const [additionalPassengers, setAdditionalPassengers] = useState([]);
  const [passengerSearchQuery, setPassengerSearchQuery] = useState('');
  const [showPassengerSuggestions, setShowPassengerSuggestions] = useState(false);
  const [showNewPassengerModal, setShowNewPassengerModal] = useState(false);
  const [newPassengerForm, setNewPassengerForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    office_phone: '',
    mobile_phone: '',
    company: '',
    notes: '',
    contact_type: 'external',  // 'internal' or 'external'
    ea_name: '',
    ea_office_phone: '',
    ea_mobile_phone: '',
    is_private: false,
    saveToContacts: true  // Default to saving to contacts
  });
  const [editingContact, setEditingContact] = useState(null);  // For editing existing contacts
  const [documentContent, setDocumentContent] = useState(null);

  // State for trip-assigned executives
  const [tripAssignedExecutives, setTripAssignedExecutives] = useState([]);
  const [loadingTripData, setLoadingTripData] = useState(false);

  // Component confidentiality state
  const [isConfidential, setIsConfidential] = useState(false);
  const [accessList, setAccessList] = useState([]);
  const [tripIsConfidential, setTripIsConfidential] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [accessUserSearch, setAccessUserSearch] = useState('');
  const [showAccessUserDropdown, setShowAccessUserDropdown] = useState(false);

  // State for per-room guest assignment
  const [roomGuestSearchQueries, setRoomGuestSearchQueries] = useState({});
  const [pendingRoomGuestAssignment, setPendingRoomGuestAssignment] = useState(null);  // { roomIndex: number }

  // Flight verification state (for chauffeur service airport dropoff)
  const [flightVerificationLoading, setFlightVerificationLoading] = useState(false);
  const [flightVerificationError, setFlightVerificationError] = useState('');
  const [flightVerificationSuccess, setFlightVerificationSuccess] = useState(false);
  const [urbanrideApiConfigured, setUrbanrideApiConfigured] = useState(false);

  // Flight verification lock status (package restrictions)
  const [flightVerificationLocked, setFlightVerificationLocked] = useState(false);
  const [flightVerificationPackageName, setFlightVerificationPackageName] = useState('');

  // Airport VIP Service: list of flight segments (commercial + private legs) on the trip
  // Each entry: { segment_id, leg_index, component_type, label, airport, direction_hint, datetime }
  const [tripFlightSegments, setTripFlightSegments] = useState([]);
  const [tripFlightSegmentsLoading, setTripFlightSegmentsLoading] = useState(false);

  // Airport VIP Service: linked saved provider profile (org-scoped) and edit modal state
  const [vipSelectedProvider, setVipSelectedProvider] = useState(null);
  const [vipEditingProvider, setVipEditingProvider] = useState(null); // null | provider | { __new: true }

  // Initialize Urbanride API for chauffeur service
  useEffect(() => {
    if (componentType === 'chauffeur_service') {
      initializeUrbanrideApi().then(({ configured }) => {
        setUrbanrideApiConfigured(configured);
      }).catch(err => {
        console.error('Failed to initialize Urbanride API:', err);
      });
    }
  }, [componentType]);

  // Fetch existing flight segments on the trip for the Airport VIP Service segment selector.
  // Includes commercial flight legs and private flight legs.
  useEffect(() => {
    if (componentType !== 'airport_vip_service' || !tripId) return;

    // Resolve a leg's datetime by trying the multiple field shapes legs are
    // stored in: full ISO datetime, separate date+time, or date-only fallback.
    const resolveLegDatetime = (leg, prefix) => {
      // prefix is "departure" or "arrival"
      const fullDt = leg[`${prefix}_datetime`];
      if (fullDt && !isNaN(new Date(fullDt).getTime())) return fullDt;
      const candidate = leg[`${prefix}_time`];
      // departure_time can be either a full datetime (commercial flights) or
      // just "HH:mm" (private flights) — only use it directly if Date can parse it.
      if (candidate && !isNaN(new Date(candidate).getTime())) return candidate;
      const date = leg[`${prefix}_date`];
      const time = leg[`${prefix}_time`];
      if (date && time && /^\d{1,2}:\d{2}/.test(String(time))) {
        return `${date}T${time.length === 5 ? time + ':00' : time}`;
      }
      if (date && !isNaN(new Date(date).getTime())) return date;
      return null;
    };

    const fetchSegments = async () => {
      setTripFlightSegmentsLoading(true);
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const response = await axios.get(`${apiBase}/trips/${tripId}`, { headers });
        const trip = response.data || {};
        const segments = [];
        (trip.components || []).forEach((c) => {
          const cd = c.component_data || {};
          if (c.component_type === 'commercial_flight' || c.component_type === 'flight') {
            const legs = cd.flight_legs || [];
            legs.forEach((leg, idx) => {
              const dep = leg.departure_airport || '?';
              const arr = leg.arrival_airport || '?';
              const fn = leg.flight_number || '';
              const airline = leg.airline_code || leg.airline || '';
              const flightLabel = [airline, fn].filter(Boolean).join(' ').trim();
              segments.push({
                segment_id: `${c.id}::leg::${idx}`,
                component_id: c.id,
                component_type: 'commercial_flight',
                leg_index: idx,
                label: flightLabel ? `${dep} → ${arr} · ${flightLabel}` : `${dep} → ${arr}`,
                departure_airport: dep,
                arrival_airport: arr,
                departure_datetime: resolveLegDatetime(leg, 'departure') || c.start_datetime || null,
                arrival_datetime: resolveLegDatetime(leg, 'arrival') || c.end_datetime || null,
              });
            });
          } else if (c.component_type === 'private_flight') {
            const legs = cd.private_flight_legs || [];
            legs.forEach((leg, idx) => {
              const dep = leg.departure_airport_iata || leg.departure_airport_icao || leg.departure_airport || '?';
              const arr = leg.arrival_airport_iata || leg.arrival_airport_icao || leg.arrival_airport || '?';
              const tail = leg.tail_number || cd.tail_number || '';
              const opName = leg.operator_name || cd.charter_operator_name || '';
              const suffix = [tail, opName].filter(Boolean).join(' · ');
              segments.push({
                segment_id: `${c.id}::leg::${idx}`,
                component_id: c.id,
                component_type: 'private_flight',
                leg_index: idx,
                label: suffix ? `${dep} → ${arr} · ${suffix}` : `${dep} → ${arr}`,
                departure_airport: dep,
                arrival_airport: arr,
                departure_datetime: resolveLegDatetime(leg, 'departure') || c.start_datetime || null,
                arrival_datetime: resolveLegDatetime(leg, 'arrival') || c.end_datetime || null,
              });
            });
          }
        });
        setTripFlightSegments(segments);
      } catch (err) {
        console.error('Failed to load trip flight segments:', err);
        setTripFlightSegments([]);
      } finally {
        setTripFlightSegmentsLoading(false);
      }
    };

    fetchSegments();
  }, [componentType, tripId]);

  // Hydrate the linked VIP provider when editing an existing airport_vip_service component
  useEffect(() => {
    if (componentType !== 'airport_vip_service') return;
    const providerId = component?.component_data?.provider_id;
    if (!providerId) {
      setVipSelectedProvider(null);
      return;
    }
    let cancelled = false;
    const loadProvider = async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        // No GET-by-id endpoint; pull the org list and pick by id (small list, fine)
        const response = await axios.get(`${apiBase}/vip-service-providers`, { headers });
        if (cancelled) return;
        const match = (response.data || []).find(p => p.id === providerId);
        if (match) setVipSelectedProvider(match);
      } catch (err) {
        console.error('Failed to hydrate linked VIP provider', err);
      }
    };
    loadProvider();
    return () => { cancelled = true; };
  }, [componentType, component?.id]);

  // Fetch flight verification lock status for commercial flights
  useEffect(() => {
    if (componentType === 'commercial_flight' || componentType === 'flight') {
      const fetchFlightSettings = async () => {
        try {
          const response = await fetch(`${getApiBase()}/flightaware/settings`, {
            headers: getAuthHeaders(),
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setFlightVerificationLocked(data.flight_verification_locked_by_package || false);
            setFlightVerificationPackageName(data.active_package_name || '');
          }
        } catch (err) {
          console.error('Failed to fetch flight settings:', err);
        }
      };
      fetchFlightSettings();
    }
  }, [componentType]);

  // Fetch trip data to get assigned executives
  useEffect(() => {
    const fetchTripAssignedExecutives = async () => {
      if (!tripId) {
        setTripAssignedExecutives(tripExecutives || []);
        return;
      }

      try {
        setLoadingTripData(true);

        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

        const response = await fetch(`${backendUrl}/api/trips/${tripId}`, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch trip data: ${response.status}`);
        }

        const tripData = await response.json();

        // Extract assigned executives from trip data
        // The trip uses executive_ids field to store assigned executive IDs
        const assignedExecutiveIds = tripData.executive_ids || [];

        // Filter tripExecutives to only include those assigned to this trip
        const assignedExecs = (tripExecutives || []).filter(exec => {
          return assignedExecutiveIds.includes(exec.id);
        });

        setTripAssignedExecutives(assignedExecs);

        // Store trip confidentiality for default inheritance
        const tripConfidential = tripData.is_confidential || false;
        setTripIsConfidential(tripConfidential);

        // If creating a new component (not editing), inherit trip confidentiality
        if (!component) {
          setIsConfidential(tripConfidential);
        }

      } catch (error) {
        console.error('Failed to fetch trip assigned executives:', error);
        // Fallback to using all tripExecutives if fetch fails
        setTripAssignedExecutives(tripExecutives || []);
      } finally {
        setLoadingTripData(false);
      }
    };

    fetchTripAssignedExecutives();
  }, [tripId, tripExecutives]);

  // Fetch organization users for access list management
  useEffect(() => {
    const fetchOrgUsers = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/users`, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include'
        });
        if (response.ok) {
          const users = await response.json();
          setOrgUsers(users || []);
        }
      } catch (error) {
        console.error('Failed to fetch org users:', error);
      }
    };
    fetchOrgUsers();
  }, []);

  // Available executives for validation
  // For commercial flights, only show executives assigned to the trip
  const finalAvailableExecutives = useMemo(() => {
    const baseExecutives = tripExecutives || [];

    // For commercial flights, ensure we only show executives that are assigned to this trip
    if (componentType === 'commercial_flight' || componentType === 'flight') {
      // Only use filtered executives if we have successfully fetched trip data
      if (!loadingTripData && tripAssignedExecutives.length >= 0) {
        return tripAssignedExecutives;
      } else {
        return baseExecutives;
      }
    }

    return baseExecutives;
  }, [tripExecutives, tripAssignedExecutives, componentType, loadingTripData]);

  // Filtered executives for passenger search (must be at component level, not inside render function)
  // Uses allExecutivesList which contains ALL executives from the system (not just trip-assigned)
  const filteredExecutives = useMemo(() => {
    if (!passengerSearchQuery.trim()) return [];
    const searchLower = passengerSearchQuery.toLowerCase();
    // Filter allExecutivesList for executives only (type === 'executive')
    return (allExecutivesList || []).filter(exec => {
      if (!exec || exec.type !== 'executive') return false;
      const execName = exec.name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim();
      const execEmail = exec.email || '';
      const execCompany = exec.company || '';
      return (
        (execName && execName.toLowerCase().includes(searchLower)) ||
        (execEmail && execEmail.toLowerCase().includes(searchLower)) ||
        (execCompany && execCompany.toLowerCase().includes(searchLower))
      );
    });
  }, [allExecutivesList, passengerSearchQuery]);

  // Filtered contacts for passenger search (must be at component level, not inside render function)
  // Uses allExecutivesList which also contains external contacts (type === 'external')
  const filteredContacts = useMemo(() => {
    if (!passengerSearchQuery.trim()) return [];
    const searchLower = passengerSearchQuery.toLowerCase();
    // Filter allExecutivesList for external contacts only (type === 'external')
    // Also include allContacts prop as fallback
    const externalFromList = (allExecutivesList || []).filter(c => c.type === 'external');
    const contactsToSearch = externalFromList.length > 0 ? externalFromList : (allContacts || []);
    return contactsToSearch.filter(contact => {
      if (!contact) return false;
      const contactName = contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
      const contactEmail = contact.email || '';
      const contactCompany = contact.company || '';
      return (
        (contactName && contactName.toLowerCase().includes(searchLower)) ||
        (contactEmail && contactEmail.toLowerCase().includes(searchLower)) ||
        (contactCompany && contactCompany.toLowerCase().includes(searchLower))
      );
    });
  }, [allExecutivesList, allContacts, passengerSearchQuery]);

  // Change detection for unsaved changes warning (debounced to prevent focus issues during typing)
  useEffect(() => {
    if (originalComponentData) {
      // Debounce the change detection to avoid interrupting user input
      const timer = setTimeout(() => {
        const hasChanges = 
          JSON.stringify(formData) !== JSON.stringify(originalComponentData) ||
          JSON.stringify(flightLegs) !== JSON.stringify(originalComponentData.component_data?.flight_legs || []) ||
          attachedFiles.some(f => f.id?.startsWith('temp-'));
        
        setHasUnsavedChanges(hasChanges);
      }, 300); // 300ms debounce
      
      return () => clearTimeout(timer);
    }
  }, [formData, flightLegs, attachedFiles, originalComponentData]);

  // Auto-update component title & times based on flight legs
  useEffect(() => {
    if (flightLegs.length > 0 && (formData.component_type === 'commercial_flight' || formData.component_type === 'flight')) {
      const firstLeg = flightLegs[0];
      const lastLeg = flightLegs[flightLegs.length - 1];
      
      // Auto-update component title if it's default or contains route
      // Only update if current title is empty or auto-generated (to avoid overriding user input)
      if (firstLeg.departure_airport && lastLeg.arrival_airport) {
        const newTitle = `${firstLeg.departure_airport} → ${lastLeg.arrival_airport}`;
        if (!formData.title || formData.title.includes('→')) {
          // Use setTimeout to prevent interfering with active input focus
          setTimeout(() => {
            setFormData(prev => ({ ...prev, title: newTitle }));
          }, 0);
        }
      }
      
      // Auto-update component start/end times (only if not manually set)
      // Combine date and time fields to create proper datetime strings
      if (firstLeg.departure_time && !formData.start_datetime) {
        const startDatetime = firstLeg.departure_date && firstLeg.departure_time
          ? `${firstLeg.departure_date}T${firstLeg.departure_time}`
          : firstLeg.departure_date || firstLeg.departure_time;
        setTimeout(() => {
          setFormData(prev => ({ ...prev, start_datetime: startDatetime }));
        }, 0);
      }
      if (lastLeg.arrival_time && !formData.end_datetime) {
        const endDatetime = lastLeg.arrival_date && lastLeg.arrival_time
          ? `${lastLeg.arrival_date}T${lastLeg.arrival_time}`
          : lastLeg.arrival_date || lastLeg.arrival_time;
        setTimeout(() => {
          setFormData(prev => ({ ...prev, end_datetime: endDatetime }));
        }, 0);
      }
    }
  }, [flightLegs]);

  // Stable reference to executives to prevent useEffect from running on every render
  const stableExecutives = useMemo(() => finalAvailableExecutives, [finalAvailableExecutives?.length, finalAvailableExecutives?.[0]?.id]);

  // Auto-update component fields based on hotel check-in/out dates and initialize defaults
  useEffect(() => {
    if (formData.component_type === 'hotel') {
      // Initialize default hotel values only if no imported data exists
      const hasRoomDetails = Array.isArray(formData.component_data?.room_details) && formData.component_data.room_details.length > 0;
      if (!component?.id && !formData.component_data?.initialized && !hasRoomDetails) {
        handleDetailsChange('initialized', true);
        handleDetailsChange('total_guests', 1);
        handleDetailsChange('number_of_rooms', 1);
        handleDetailsChange('room_details', [{
          room_type: '',
          guests: 1,
          bed_preference: '',
          special_notes: ''
        }]);
        
        // Auto-assign first available executive if none assigned
        if (stableExecutives.length > 0 && !formData.component_data?.assigned_executives) {
          handleDetailsChange('assigned_executives', [stableExecutives[0].id]);
        }
      }

      // Auto-update component start/end times based on check-in/out dates
      if (formData.component_data?.check_in && !formData.start_datetime) {
        // check_in now contains full datetime, use it directly or ensure it has time component
        const checkInDateTime = formData.component_data.check_in.includes('T') 
          ? formData.component_data.check_in 
          : `${formData.component_data.check_in}T15:00`;
        handleInputChange('start_datetime', checkInDateTime);
      }
      
      if (formData.component_data?.check_out && !formData.end_datetime) {
        // check_out now contains full datetime, use it directly or ensure it has time component
        const checkOutDateTime = formData.component_data.check_out.includes('T') 
          ? formData.component_data.check_out 
          : `${formData.component_data.check_out}T11:00`;
        handleInputChange('end_datetime', checkOutDateTime);
      }
      
      // Auto-update title if not set
      if (!formData.title && formData.component_data?.hotel_name) {
        handleInputChange('title', formData.component_data.hotel_name);
      }
      
      // Auto-update location
      if (!formData.location && formData.component_data?.hotel_name) {
        handleInputChange('location', formData.component_data.hotel_name);
      }
    }
  }, [formData.component_type, formData.component_data?.check_in, formData.component_data?.check_out, formData.component_data?.hotel_name]);

  // Separate useEffect for meeting initialization to prevent focus loss
  useEffect(() => {
    // Initialize default meeting values if this is a new meeting component
    if (formData.component_type === 'meeting' && !component?.id && !formData.component_data?.initialized) {
      handleDetailsChange('initialized', true);
      handleDetailsChange('meeting_type', 'in_person');
      
      // Initialize attendees from existing data if available
      if (formData.component_data?.meeting_attendees && attendees.length === 0) {
        const existingAttendees = formData.component_data.meeting_attendees.map((attendee, index) => ({
          ...attendee,
          tempId: `existing-${index}-${Date.now()}`
        }));
        setAttendees(existingAttendees);
      }
    }
  }, [formData.component_type, component?.id]); // Only run when component type changes or component ID changes

  // Load existing component data for editing
  useEffect(() => {
    if (component?.id) {
      
      // DEBUG: Log status specifically for Board Meeting Paris - Fixed
      if (component.title === "Board Meeting Paris - Fixed") {
      }
      
      // Populate form with existing component data - CONSOLIDATED to prevent race condition
      setFormData({
        component_type: component.component_type || componentType,
        title: component.title || '',
        description: component.description || '',
        start_datetime: (() => {
          // For rental car components, prefer pickup_datetime (combined), fallback to pickup_date + pickup_time
          if (component.component_type === 'rental_car' || component.component_type === 'car_rental') {
            console.log('🚗 RENTAL CAR FORM LOADING DEBUG:');
            console.log('  component.component_data:', component.component_data);
            console.log('  component.component_data?.pickup_datetime:', component.component_data?.pickup_datetime);
            console.log('  component.start_datetime:', component.start_datetime);

            // First try the combined pickup_datetime field
            if (component.component_data?.pickup_datetime) {
              try {
                // Use parseFlexibleDate to handle various date formats
                const pickupDatetime = parseFlexibleDate(component.component_data.pickup_datetime);
                console.log('  parseFlexibleDate result:', pickupDatetime);
                if (pickupDatetime) {
                  const formatted = format(pickupDatetime, "yyyy-MM-dd'T'HH:mm");
                  console.log('  Formatted start_datetime:', formatted);
                  return formatted;
                }
              } catch (e) {
                console.log('  Error parsing pickup_datetime:', e);
                // Fall through to other options
              }
            }
            // Fallback to combining pickup_date + pickup_time
            if (component.component_data?.pickup_date) {
              try {
                const pickupDateStr = component.component_data.pickup_date;
                const pickupTimeStr = component.component_data.pickup_time || '12:00';

                // Use parseFlexibleDate which handles DD/MM/YYYY format
                const pickupDate = parseFlexibleDate(pickupDateStr, pickupTimeStr);
                if (pickupDate) {
                  return format(pickupDate, "yyyy-MM-dd'T'HH:mm");
                }
              } catch (e) {
                // Fall through to component-level start_datetime
              }
            }
          }
          // For other components (or fallback), use component-level start_datetime
          if (component.start_datetime && component.start_datetime !== 'null') {
            try {
              const raw = typeof component.start_datetime === 'string'
                ? component.start_datetime.trim()
                : component.start_datetime;
              if (raw) {
                const startDate = parseFlexibleDate(raw);
                if (startDate) {
                  return format(startDate, "yyyy-MM-dd'T'HH:mm");
                }
              }
            } catch (e) {
            }
          }
          // Events/occurrences: fall back to the parsed event fields the way
          // rental cars fall back to pickup_datetime above. Without this the
          // edit form showed an empty date for an event the timeline was
          // already displaying and sorting correctly.
          try {
            const eventStart = component.component_data?.start_local || component.component_data?.event_datetime
              || (component.component_data?.event_date ? `${component.component_data?.event_date}${component.component_data?.event_time ? 'T' + component.component_data?.event_time : ''}` : null);
            if (eventStart) {
              const parsed = parseFlexibleDate(eventStart);
              if (parsed) {
                return format(parsed, "yyyy-MM-dd'T'HH:mm");
              }
            }
          } catch (e) {
          }
          return '';
        })(),
        end_datetime: (() => {
          // For rental car components, prefer dropoff_datetime (combined), fallback to dropoff_date + dropoff_time
          if (component.component_type === 'rental_car' || component.component_type === 'car_rental') {
            // First try the combined dropoff_datetime field
            if (component.component_data?.dropoff_datetime) {
              try {
                // Use parseFlexibleDate to handle various date formats
                const dropoffDatetime = parseFlexibleDate(component.component_data.dropoff_datetime);
                if (dropoffDatetime) {
                  return format(dropoffDatetime, "yyyy-MM-dd'T'HH:mm");
                }
              } catch (e) {
                // Fall through to other options
              }
            }
            // Fallback to combining dropoff_date + dropoff_time
            if (component.component_data?.dropoff_date) {
              try {
                const dropoffDateStr = component.component_data.dropoff_date;
                const dropoffTimeStr = component.component_data.dropoff_time || '12:00';

                // Use parseFlexibleDate which handles DD/MM/YYYY format
                const dropoffDate = parseFlexibleDate(dropoffDateStr, dropoffTimeStr);
                if (dropoffDate) {
                  return format(dropoffDate, "yyyy-MM-dd'T'HH:mm");
                }
              } catch (e) {
                // Fall through to component-level end_datetime
              }
            }
          }
          // For other components (or fallback), use component-level end_datetime
          if (component.end_datetime && component.end_datetime !== 'null') {
            try {
              const raw = typeof component.end_datetime === 'string'
                ? component.end_datetime.trim()
                : component.end_datetime;
              if (raw) {
                const endDate = parseFlexibleDate(raw);
                if (endDate) {
                  return format(endDate, "yyyy-MM-dd'T'HH:mm");
                }
              }
            } catch (e) {
            }
          }
          // Events/occurrences: same fallback as the start above.
          try {
            const eventEnd = component.component_data?.end_local || component.component_data?.event_end_datetime
              || (component.component_data?.event_date && component.component_data?.event_end_time ? `${component.component_data?.event_date}T${component.component_data?.event_end_time}` : null);
            if (eventEnd) {
              const parsed = parseFlexibleDate(eventEnd);
              if (parsed) {
                return format(parsed, "yyyy-MM-dd'T'HH:mm");
              }
            }
          } catch (e) {
          }
          return '';
        })(),
        location: component.location || '',
        notes: component.notes || '',
        status: component.status === 'confirmed' ? 'confirmed' : 
                component.status === 'pending' ? 'pending' : 
                component.status || 'pending', // CRITICAL: Explicit status handling to prevent undefined issues
        // TBD flags for Timeline 2.0 drag-and-drop functionality
        date_is_tbd: component.date_is_tbd || false,
        time_is_tbd: component.time_is_tbd || false,
        details: component.details || {},
        component_data: {
          ...component.component_data,
          // Backward-compatible mapping for private_flight fields that may have been saved under details previously
          operator_type: component.component_data?.operator_type ?? component.component_data?.details?.operator_type ?? component.details?.operator_type ?? '',
          charter_operator_name: component.component_data?.charter_operator_name ?? component.component_data?.details?.charter_operator_name ?? component.details?.charter_operator_name ?? '',
          charter_operator_phone: component.component_data?.charter_operator_phone ?? component.component_data?.details?.charter_operator_phone ?? component.details?.charter_operator_phone ?? '',
          flight_department_request: component.component_data?.flight_department_request ?? component.component_data?.details?.flight_department_request ?? component.details?.flight_department_request ?? '',
          assigned_executives: component.component_data?.assigned_executives || [],
          passengers: component.component_data?.passengers || [],
          flight_legs: component.component_data?.flight_legs || [],
          // Ensure primary_executive_id is set from primary_traveler_id if it exists
          primary_executive_id: component.component_data?.primary_executive_id || component.primary_traveler_id || (component.primary_traveler_type === 'executive' ? component.primary_traveler_id : '')
        },
        // Also set primary_traveler_id if not set but we have an executive
        primary_traveler_id: component.primary_traveler_id || component.component_data?.primary_executive_id || '',
        primary_traveler_type: component.primary_traveler_type || (component.component_data?.primary_executive_id ? 'executive' : component.primary_traveler_type),
        // Add confirmation_number for chauffeur services
        confirmation_number: component.confirmation_number || ''
      });

      // Store original component data for change detection
      setOriginalComponentData(component);

      // Initialize confidentiality settings from existing component
      setIsConfidential(component.is_confidential || false);
      setAccessList(component.access_list || []);

      // Load existing flight legs with correct field mapping for Paris Meeting data
      if (component.component_data?.flight_legs && Array.isArray(component.component_data.flight_legs)) {
        const existingLegs = component.component_data.flight_legs.map((leg, index) => ({
          ...leg,
          // Map backend field names to frontend field names
          // Use airline (which includes code like "American Airlines (AA)") for display,
          // but keep airline_name as the raw name for the autocomplete
          airline_name: leg.airline_name || leg.airline || '',
          airline: leg.airline || leg.airline_name || '',
          airline_code: leg.airline_code || '',
          flight_number: leg.flight_number || String(leg.flight_number || ''),
          departure_airport: leg.departure_airport || leg.departure || leg.origin || '',
          // IMPORTANT: Prioritize departure_datetime for the datetime picker
          // departure_datetime contains the full datetime (e.g., "2019-02-07T17:29:00")
          // departure_time only contains time (e.g., "17:29") which breaks the picker
          departure_time: leg.departure_datetime || leg.departure_time || leg.departure_date || '',
          departure_date: leg.departure_date || (leg.departure_datetime ? leg.departure_datetime.split('T')[0] : ''),
          arrival_airport: leg.arrival_airport || leg.arrival || leg.destination || '',
          // IMPORTANT: Same for arrival - prioritize departure_datetime
          arrival_time: leg.arrival_datetime || leg.arrival_time || leg.arrival_date || '',
          arrival_date: leg.arrival_date || (leg.arrival_datetime ? leg.arrival_datetime.split('T')[0] : ''),
          // Generate leg number if missing
          leg_number: leg.leg_number || (index + 1)
        }));
        console.log('✈️ Loaded flight legs:', existingLegs);
        setFlightLegs(existingLegs);
      } else {
        setFlightLegs([]);
      }

      // Load existing private flight legs for editing
      if (component.component_data?.private_flight_legs && Array.isArray(component.component_data.private_flight_legs)) {
        setPrivateFlightLegs(component.component_data.private_flight_legs);
      } else {
        setPrivateFlightLegs([]);
      }

      // Note: Passengers are now stored per-leg in leg_passengers within private_flight_legs
      // They are loaded automatically when privateFlightLegs is set above

      // Load attached files
      if (component.component_data?.attached_files && Array.isArray(component.component_data.attached_files)) {
        setAttachedFiles(component.component_data.attached_files.map(f => ({
          ...f, 
          id: f.id || `temp-${Date.now()}-${Math.random()}`
        })));
      } else {
        // Handle case where attached_files is null/undefined or not an array
        setAttachedFiles([]);
      }

      // Set TBD states based on existing data (for meetings)
      if (componentType === 'meeting') {
        // Check for TBD indicators
        const hasStartDateTime = component.start_datetime && component.start_datetime !== '';
        const hasEndDateTime = component.end_datetime && component.end_datetime !== '';
        const timeIsTBD = component.component_data?.time_is_tbd === true;
        const meetingDate = component.component_data?.meeting_date;

        // Improved TBD state detection
        if (timeIsTBD) {
          // Component was saved with TBD flag - set appropriate TBD states
          if (meetingDate) {
            // Date was set but time was TBD - restore TBD format  
            setIsDateTBD(false);
            setIsStartTimeTBD(true);
            setIsEndTimeTBD(!hasEndDateTime);
            
            // Restore the TBD format in form data
            setFormData(prev => ({
              ...prev,
              start_datetime: `${meetingDate}-TBD-TIME`
            }));
          } else {
            // Both date and time are TBD or data is missing - set full TBD
            setIsDateTBD(true);
            setIsStartTimeTBD(true);
            setIsEndTimeTBD(true);
          }
        } else {
          // Component has fixed date/time - no TBD states
          setIsDateTBD(false);  
          setIsStartTimeTBD(false);
          setIsEndTimeTBD(false);
        }
      }
    } else {
      // Reset to initial state for new components
      setOriginalComponentData(null);
      setFormData({
        component_type: componentType,
        title: '',
        description: '',
        start_datetime: '',
        end_datetime: '',
        location: '',
        notes: '',
        status: 'pending', // Set default status for new components
        details: {},
        component_data: {
          assigned_executives: [],
          passengers: [],
          flight_legs: [],
          ...(initialComponentData || {})
        }
      });
      setFlightLegs([]);
      setAttachedFiles([]);
    }
  }, [component, componentType, initialComponentData]); // Run when component prop, type, or prefill changes

  // Remove auto-select primary executive for meetings - user should select manually
  // useEffect(() => {
  //   if (componentType === 'meeting' && tripExecutives && tripExecutives.length > 0) {
  //     if (!formData.component_data?.primary_executive_id) {
  //       setFormData(prevData => ({
  //         ...prevData,
  //         component_data: {
  //           ...prevData.component_data,
  //           primary_executive_id: tripExecutives[0].id
  //         },
  //         primary_traveler_id: tripExecutives[0].id,
  //         primary_traveler_type: 'executive'
  //       }));
  //     }
  //   }
  // }, [componentType, tripExecutives, formData.component_data?.primary_executive_id]);

  // Initialize TBD states from component data when editing existing components
  useEffect(() => {
    if (component) {
      // Set TBD states based on component flags and datetime format
      const compDateTBD = component.date_is_tbd === true;
      const compTimeTBD = component.time_is_tbd === true || component.component_data?.time_is_tbd === true;
      const hasStartDateTime = component.start_datetime && component.start_datetime !== null && component.start_datetime !== '';
      const hasTBDFormat = component.start_datetime && (
        component.start_datetime.includes('TBD') ||
        component.start_datetime === 'TBD-TIME'
      );

      setIsDateTBD(compDateTBD || (!hasStartDateTime && hasTBDFormat));
      setIsStartTimeTBD(compTimeTBD || hasTBDFormat);
      setIsEndTimeTBD(component.end_datetime === null || component.end_datetime === 'TBD' || compTimeTBD);
    }
  }, [component]);

  // Component type configurations
  const componentTypeConfigs = {
    flight: {
      title: 'Commercial Flight',
      description: 'Book or add commercial airline flights',
      icon: Plane,
      color: 'bg-ui-primary',
      lightColor: 'bg-ui-blue-bg',
      borderColor: 'border-ui-blue-line',
      gradient: 'from-ui-blue-bg to-ui-blue-bg'
    },
    commercial_flight: {
      title: 'Commercial Flight',
      description: 'Book or add commercial airline flights',
      icon: Plane,
      color: 'bg-ui-primary',
      lightColor: 'bg-ui-blue-bg',
      borderColor: 'border-ui-blue-line',
      gradient: 'from-ui-blue-bg to-ui-blue-bg'
    },
    private_flight: {
      title: 'Private Flight',
      description: 'Charter flights and private aviation',
      icon: Plane,
      color: 'bg-ui-violet-strong',
      lightColor: 'bg-ui-violet-bg',
      borderColor: 'border-ui-violet-line',
      gradient: 'from-ui-violet-bg to-ui-violet-bg'
    },
    hotel: {
      title: 'Hotel',
      description: 'Book accommodations and lodging',
      icon: Building,
      color: 'bg-ui-green-strong',
      lightColor: 'bg-ui-green-bg',
      borderColor: 'border-ui-green-line',
      gradient: 'from-ui-green-bg to-ui-green-bg'
    },
    restaurant: {
      title: 'Restaurant',
      description: 'Dining reservations and meals',
      icon: UtensilsCrossed,
      color: 'bg-ui-orange-strong',
      lightColor: 'bg-ui-orange-bg',
      borderColor: 'border-ui-orange-line',
      gradient: 'from-ui-orange-bg to-ui-orange-bg'
    },
    meeting: {
      title: 'Meeting',
      description: 'Business meetings and appointments',
      icon: Handshake,
      color: 'bg-ui-indigo-strong',
      lightColor: 'bg-ui-indigo-bg',
      borderColor: 'border-ui-indigo-line',
      gradient: 'from-ui-indigo-bg to-ui-indigo-bg'
    },
    speaker_meeting: {
      title: 'Speaker Meeting',
      description: 'Pharma & speaker-bureau speaking engagements',
      icon: Mic,
      color: 'bg-ui-red-strong',
      lightColor: 'bg-ui-red-bg',
      borderColor: 'border-ui-red-line',
      gradient: 'from-ui-red-bg to-ui-red-bg'
    },
    event: {
      title: 'Event',
      description: 'Conferences, seminars, and events',
      icon: CalendarIcon,
      color: 'bg-ui-pink-strong',
      lightColor: 'bg-ui-pink-bg',
      borderColor: 'border-ui-pink-line',
      gradient: 'from-ui-pink-bg to-ui-pink-bg'
    },
    rental_car: {
      title: 'Rental Car',
      description: 'Vehicle rentals and transportation',
      icon: Car,
      color: 'bg-ui-teal-strong',
      lightColor: 'bg-ui-teal-bg',
      borderColor: 'border-ui-teal-line',
      gradient: 'from-ui-teal-bg to-ui-teal-bg'
    },
    rental_car: {
      title: 'Rental Car',
      description: 'Vehicle rentals and transportation',
      icon: Car,
      color: 'bg-ui-teal-strong',
      lightColor: 'bg-ui-teal-bg',
      borderColor: 'border-ui-teal-line',
      gradient: 'from-ui-teal-bg to-ui-teal-bg'
    },
    chauffeur_service: {
      title: 'Chauffeur Service',
      description: 'Private driver and car service',
      icon: Users,
      color: 'bg-ui-faint',
      lightColor: 'bg-ui-sunk',
      borderColor: 'border-ui-line',
      gradient: 'from-ui-sunk to-ui-sunk'
    },
    airport_vip_service: {
      title: 'Airport VIP Service',
      description: 'Meet & greet for an arrival or departure flight',
      icon: BadgeCheck,
      color: 'bg-ui-amber-strong',
      lightColor: 'bg-ui-amber-bg',
      borderColor: 'border-ui-amber-line',
      gradient: 'from-ui-amber-bg to-ui-amber-bg'
    },
    memo: {
      title: 'Memo',
      description: 'Reminders, placeholders, and notes',
      icon: StickyNote,
      color: 'bg-ui-amber-strong',
      lightColor: 'bg-ui-amber-bg',
      borderColor: 'border-ui-amber-line',
      gradient: 'from-ui-amber-bg to-ui-amber-bg'
    }
  };

  const currentConfig = componentTypeConfigs[componentType] || componentTypeConfigs.meeting;
  const Icon = currentConfig.icon;

  const handleInputChange = useCallback((field, value) => {
    // DEBUG: Track start_datetime changes
    if (field === 'start_datetime') {
      console.log('🔄 handleInputChange start_datetime:', {
        newValue: value,
        stack: new Error().stack?.split('\n').slice(1, 5).join('\n')
      });
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleDetailsChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      component_data: {
        ...prev.component_data,
        [field]: value
      }
    }));
  }, []);

  // Handle flight verification for airport dropoff service
  const handleVerifyFlight = useCallback(async () => {
    const airportCode = formData.component_data?.airport_code;
    const airline = formData.component_data?.airline_code || formData.component_data?.airline;
    const flightNumber = formData.component_data?.flight_number;
    const flightDate = formData.component_data?.flight_date;
    const flightTime = formData.component_data?.flight_time || '12:00'; // Default time if not provided

    if (!airportCode || !airline || !flightNumber || !flightDate) {
      setFlightVerificationError('Please fill in airport code, airline, flight number, and flight date before verifying');
      return;
    }

    // Build dateTime in format "yyyy-MM-dd'T'HH:mm"
    const dateTime = `${flightDate}T${flightTime}`;

    setFlightVerificationLoading(true);
    setFlightVerificationError('');
    setFlightVerificationSuccess(false);

    try {
      const result = await validateFlight({
        airport: airportCode.toUpperCase(),
        airline: airline.toUpperCase().substring(0, 2), // Ensure 2-char airline code
        flightNumber: flightNumber.replace(/\D/g, ''), // Extract numeric flight number
        dateTime,
        departure: true // For airport dropoff, we're checking departure flights
      });


      // Update form with verified flight data
      if (result) {
        // Extract arrival/departure time from the result
        // The API returns scheduled times in the response
        if (result.scheduledTime || result.departureTime || result.time) {
          const verifiedTime = result.scheduledTime || result.departureTime || result.time;
          // Extract just the time portion if it's a full datetime
          const timePart = verifiedTime.includes('T') ? verifiedTime.split('T')[1]?.substring(0, 5) : verifiedTime.substring(0, 5);
          if (timePart) {
            handleDetailsChange('flight_time', timePart);
          }
        }

        // Extract origin airport from result
        if (result.origin || result.otherAirport || result.departureAirport) {
          handleDetailsChange('flight_origin', result.origin || result.otherAirport || result.departureAirport);
        }

        // Store flight status if available
        if (result.status) {
          handleDetailsChange('flight_status', result.status);
        }

        // Store terminal info if available
        if (result.terminal) {
          handleDetailsChange('flight_terminal', result.terminal);
        }

        setFlightVerificationSuccess(true);
        setTimeout(() => setFlightVerificationSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Flight verification failed:', err);
      setFlightVerificationError(err.response?.data?.detail || err.message || 'Failed to verify flight. Please check flight details and try again.');
    } finally {
      setFlightVerificationLoading(false);
    }
  }, [formData.component_data, handleDetailsChange]);

  // Handle private flight details (airport/FBO data)
  const handlePrivateFlightDetailsChange = useCallback((field, value) => {
    // Guard against empty resets for operator_type
    if (field === 'operator_type') {
      const allowed = ['flight_ops', 'charter'];
      if (!value || !allowed.includes(value)) {
        return; // do not clear existing operator_type
      }
    }
    setFormData(prev => ({
      ...prev,
      component_data: {
        ...prev.component_data,
        [field]: value
      }
    }));
  }, []);

  // Stable restaurant field handlers to prevent re-renders and focus loss
  const handleRestaurantNameChange = useCallback((value) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        component_data: {
          ...prev.component_data,
          restaurant_name: value
        }
      };
      
      // Update title if it matches the previous restaurant name or is empty
      if (!prev.title || prev.title === prev.component_data?.restaurant_name) {
        newFormData.title = value;
      }
      
      return newFormData;
    });
  }, []);

  // Handle restaurant place selection from Google Places API
  const handleRestaurantPlaceSelect = useCallback((place) => {
    const restaurantName = place.name || '';
    const address = place.formatted_address || place.address || '';
    
    
    // Enhanced cuisine type extraction with comprehensive mapping
    let detectedCuisine = '';
    
    if (place.types && place.types.length > 0) {
      
      // Enhanced cuisine type mapping from Google Places types
      const cuisineMap = {
        // Specific restaurant types
        'chinese_restaurant': 'Chinese',
        'italian_restaurant': 'Italian', 
        'japanese_restaurant': 'Japanese',
        'mexican_restaurant': 'Mexican',
        'indian_restaurant': 'Indian',
        'thai_restaurant': 'Thai',
        'french_restaurant': 'French',
        'american_restaurant': 'American',
        'spanish_restaurant': 'Spanish',
        'greek_restaurant': 'Greek',
        'korean_restaurant': 'Korean',
        'vietnamese_restaurant': 'Vietnamese',
        'turkish_restaurant': 'Turkish',
        'mediterranean_restaurant': 'Mediterranean',
        'seafood_restaurant': 'Seafood',
        'steak_house': 'Steakhouse',
        'pizza_restaurant': 'Pizza',
        'sushi_restaurant': 'Sushi',
        'barbecue_restaurant': 'BBQ',
        'fast_food_restaurant': 'Fast Food',
        'cafe': 'Cafe',
        'bakery': 'Bakery',
        'bar': 'Bar & Grill',
        'night_club': 'Nightclub'
        // Removed meal_takeaway and meal_delivery - these are services, not cuisine types
      };
      
      // Map of service/feature types (not cuisine)
      const serviceMap = {
        'meal_takeaway': 'Takeout Available',
        'meal_delivery': 'Delivery Available'
      };
      
      // First try specific cuisine mappings
      let cuisineFound = false;
      for (const type of place.types) {
        if (cuisineMap[type]) {
          detectedCuisine = cuisineMap[type];
          cuisineFound = true;
          break;
        }
      }
      
      // If no specific cuisine found, try pattern matching on place types themselves
      if (!cuisineFound) {
        for (const type of place.types) {
          // Look for cuisine indicators in the type name
          if (type.includes('chinese')) {
            detectedCuisine = 'Chinese';
            cuisineFound = true;
            break;
          } else if (type.includes('italian')) {
            detectedCuisine = 'Italian';
            cuisineFound = true;
            break;
          } else if (type.includes('japanese') || type.includes('sushi')) {
            detectedCuisine = 'Japanese';
            cuisineFound = true;
            break;
          } else if (type.includes('pizza')) {
            detectedCuisine = 'Pizza';
            cuisineFound = true;
            break;
          } else if (type.includes('mexican')) {
            detectedCuisine = 'Mexican';
            cuisineFound = true;
            break;
          } else if (type.includes('thai')) {
            detectedCuisine = 'Thai';
            cuisineFound = true;
            break;
          } else if (type.includes('indian')) {
            detectedCuisine = 'Indian';
            cuisineFound = true;
            break;
          } else if (type.includes('french')) {
            detectedCuisine = 'French';
            cuisineFound = true;
            break;
          }
        }
      }
      
      // If still no cuisine found, try pattern matching on restaurant name
      if (!cuisineFound && restaurantName) {
        const namePatterns = {
          'pizza': 'Pizza',
          'sushi': 'Sushi', 
          'thai': 'Thai',
          'chinese': 'Chinese',
          'italian': 'Italian',
          'mexican': 'Mexican',
          'indian': 'Indian',
          'french': 'French',
          'greek': 'Greek',
          'korean': 'Korean',
          'vietnamese': 'Vietnamese',
          'japanese': 'Japanese',
          'steakhouse': 'Steakhouse',
          'steak house': 'Steakhouse',
          'bbq': 'BBQ',
          'barbecue': 'BBQ',
          'cafe': 'Cafe',
          'bakery': 'Bakery',
          'bar': 'Bar & Grill',
          'grill': 'American',
          'diner': 'American',
          'bistro': 'French',
          'burger': 'American',
          'hamburger': 'American'
        };
        
        const lowerName = restaurantName.toLowerCase();
        for (const [pattern, cuisine] of Object.entries(namePatterns)) {
          if (lowerName.includes(pattern)) {
            detectedCuisine = cuisine;
            cuisineFound = true;
            break;
          }
        }
      }
      
      // If still no cuisine found, leave it empty rather than assuming
      if (!cuisineFound) {
        detectedCuisine = '';
      }
      
      if (!cuisineFound) {
      }
    } else {
    }

    // Update all restaurant data in a single state update to avoid race conditions
    setFormData(prev => {
      
      const newFormData = {
        ...prev,
        title: restaurantName,
        location: restaurantName,
        address: address,
        component_data: {
          ...prev.component_data,
          restaurant_name: restaurantName,
          address: address,
          place_id: place.place_id,
          // Extract additional restaurant data
          rating: place.rating,
          price_level: place.price_level ? 
            ['Inexpensive', 'Moderate', 'Expensive', 'Very Expensive'][place.price_level - 1] : undefined,
          phone: place.formatted_phone_number,
          website: place.website,
          // Set the detected cuisine type - clear if no cuisine detected
          cuisine_type: detectedCuisine
        }
      };
      
      return newFormData;
    });
  }, []);

  const handlePartySizeChange = useCallback((value) => {
    handleDetailsChange('party_size', parseInt(value) || '');
  }, [handleDetailsChange]);

  const handleCuisineTypeChange = useCallback((value) => {
    handleDetailsChange('cuisine_type', value);
  }, [handleDetailsChange]);

  const handleContactSearchChange = useCallback((value) => {
    setExecutiveSearchQuery(value);
    setShowExecutiveSuggestions(value.length > 0);
  }, []);

  // Memoized time options with 15-minute increments (same as meeting component)
  const restaurantTimeOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < 96; i++) { // 96 slots for 15-minute increments (24 hours * 4)
      const hour = Math.floor(i / 4);
      const minute = (i % 4) * 15; // 0, 15, 30, 45
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const display = format(new Date(2000, 0, 1, hour, minute), 'h:mm a');
      options.push({ value: time, display });
    }
    return options;
  }, []);

  // Handle restaurant reservation time change
  const handleRestaurantTimeChange = useCallback((timeValue) => {
    if (!timeValue) {
      handleInputChange('start_datetime', '');
      return;
    }

    const currentDateTime = formData.start_datetime || '';
    let date = new Date().toISOString().split('T')[0];
    
    if (currentDateTime.includes('T')) {
      const parts = currentDateTime.split('T');
      if (parts[0]) date = parts[0];
    }
    
    handleInputChange('start_datetime', `${date}T${timeValue}`);
  }, [formData.start_datetime, handleInputChange]);

  // Multi-leg flight handlers
  const handleFlightLegChange = (field, value) => {
    setCurrentFlightLeg(prev => ({...prev, [field]: value}));
  };

  const handleAddOrUpdateFlightLeg = () => {
    if (!currentFlightLeg.departure_airport || !currentFlightLeg.arrival_airport) {
      setValidationError("Departure and Arrival airports are required.");
      return;
    }
    
    setValidationError('');
    
    if (currentFlightLeg.index !== undefined) {
      // Update existing leg
      const updatedLegs = flightLegs.map((leg, index) => 
        index === currentFlightLeg.index ? {...currentFlightLeg} : leg
      );
      setFlightLegs(updatedLegs);
    } else {
      // Add new leg
      setFlightLegs([...flightLegs, currentFlightLeg]);
    }
    
    setShowFlightLegForm(false);
    setCurrentFlightLeg(null);
  };

  const handleEditFlightLeg = (leg, index) => {
    setCurrentFlightLeg({...leg, index});
    // Initialize raw time inputs from existing leg data
    const depTime = leg.departure_time || '';
    const arrTime = leg.arrival_time || '';
    setDepTimeRaw(depTime.includes('T') ? depTime.split('T')[1].substring(0,5) : '');
    setArrTimeRaw(arrTime.includes('T') ? arrTime.split('T')[1].substring(0,5) : '');
    setShowFlightLegForm(true);
  };

  const handleDeleteFlightLeg = (indexToDelete) => {
    setFlightLegs(flightLegs.filter((_, index) => index !== indexToDelete));
  };

  const handleCancelFlightLeg = () => {
    setShowFlightLegForm(false);
    setCurrentFlightLeg(null);
    setValidationError('');
    // Clear raw time inputs
    setDepTimeRaw('');
    setArrTimeRaw('');
  };

  // Handle document viewing - simplified for non-sandboxed environment
  const handleViewDocument = async (file, parsed = false) => {

    try {
      setViewingDocument({ ...file, name: parsed ? `Parsed data (JSON): ${file.name || file.filename}` : attachmentLabel(file) });

      // Check if file has a valid ID
      if (!file.id || file.id === 'undefined' || file.id.startsWith('temp-')) {
        console.error('Invalid file ID:', file.id);
        setDocumentContent({
          type: 'error',
          message: 'Cannot view this document: File ID is missing or invalid. This file may need to be re-uploaded.',
          fileInfo: { name: file.name || 'Unknown' }
        });
        return;
      }

      // Fetch document from backend with authentication
      const apiBase = getApiBase();
      const apiUrl = `${apiBase}/files/${file.id}/${parsed ? "view" : "original"}`;

      const response = await fetch(apiUrl, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText);
        console.error('Fetch failed:', response.status, errorText);
        throw new Error(`Failed to fetch document (${response.status}): ${errorText}`);
      }

      const contentType = response.headers.get('content-type') || 'application/octet-stream';
      const contentLength = response.headers.get('content-length');
      const blob = await response.blob();

      // Validate blob has content
      if (blob.size === 0) {
        throw new Error('Document is empty (0 bytes). The file may be corrupted or missing on the server.');
      }

      if (!parsed && contentType.includes('text/html')) {
        setDocumentContent({ type: 'email', content: await blob.text(), fileInfo: { size: blob.size },
          contentType, downloadUrl: apiUrl });
        return;
      }
      const textContent = await attachmentText(blob, file.name, contentType);
      // Text is rendered by React in a pre element, never as active HTML.
      const blobUrl = textContent === null ? URL.createObjectURL(blob) : null;

      // Detect PDF files
      const isPdfFile = (file.name && file.name.toLowerCase().endsWith('.pdf')) ||
                       contentType.includes('application/pdf');

      setDocumentContent({
        type: textContent !== null ? 'text' : isPdfFile ? 'pdf' : 'blob',
        content: textContent,
        blobUrl: blobUrl,
        contentType: isPdfFile ? 'application/pdf' : contentType,
        downloadUrl: `${apiBase}/files/${file.id}/download`,
        fileInfo: {
          name: file.name || 'Unknown',
          size: file.size || blob.size,
          label: file.label || null,
          id: file.id || null
        }
      });


    } catch (error) {
      console.error('=== ERROR IN DOCUMENT VIEWER ===');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('=== END ERROR ===');

      setDocumentContent({
        type: 'error',
        message: `Error loading document: ${error.message}`,
        fileInfo: { name: file.name || 'Unknown' }
      });
    }
  };

  // Cleanup blob URLs when document viewer closes or changes
  useEffect(() => {
    return () => {
      // Cleanup blob URL when component unmounts or document changes
      if (documentContent?.blobUrl) {
        URL.revokeObjectURL(documentContent.blobUrl);
      }
    };
  }, [documentContent?.blobUrl]);

  // Handle closing the document viewer
  const handleCloseDocumentViewer = () => {
    // Cleanup blob URL before closing
    if (documentContent?.blobUrl) {
      URL.revokeObjectURL(documentContent.blobUrl);
    }
    setViewingDocument(null);
    setDocumentContent(null);
  };

  // Handle cancel
  const handleCancel = () => {
    try {
      onCancel();
    } catch (error) {
      console.error('Error calling onCancel:', error);
    }
  };

  // File upload handlers
  const handleConfirmationUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploadingConfirmation(true);
    
    try {
      // Add file to attached files first for UI display
      const newFile = {
        id: `temp-${Date.now()}-${Math.random()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        label: 'Flight Confirmation',
        url: URL.createObjectURL(file) // Temporary URL for display
      };
      setAttachedFiles(prev => [...prev, newFile]);
      
      // Upload document to backend for AI parsing with enhanced schema
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'flight');
      
      // Add custom extraction schema that includes ticket numbers
      const enhancedFlightSchema = {
        type: "object",
        properties: {
          traveler_name: { type: "string", description: "Name of the traveler" },
          confirmation_number: { type: "string", description: "Flight confirmation number" },
          ticket_number: { type: "string", description: "E-ticket number or document number, often a long number like 176 2380300676" },
          ticket_no: { type: "string", description: "Ticket number (alternative field)" },
          document_number: { type: "string", description: "Document number (alternative ticket field)" },
          e_ticket_number: { type: "string", description: "Electronic ticket number" },
          reference_number: { type: "string", description: "Reference number that might be the ticket" },
          is_multi_leg: { type: "boolean", description: "Whether this is a multi-leg flight" },
          legs: {
            type: "array",
            description: "Array of flight legs/segments. For multi-leg flights (round trips, connections), extract ALL legs.",
            items: {
              type: "object",
              properties: {
                leg_number: { type: "number", description: "Sequential leg number starting from 1" },
                airline: { type: "string", description: "Airline name or code" },
                flight_number: { type: "string", description: "Flight number" },
                departure_airport: { type: "string", description: "Departure airport code (IATA 3-letter code)" },
                arrival_airport: { type: "string", description: "Arrival airport code (IATA 3-letter code)" },
                departure_date: { type: "string", format: "date-time", description: "Departure date and time in ISO 8601 format YYYY-MM-DDTHH:MM:SS. Extract both date and time." },
                arrival_date: { type: "string", format: "date-time", description: "Arrival date and time in ISO 8601 format YYYY-MM-DDTHH:MM:SS. Extract both date and time." },
                departure_time: { type: "string", format: "date-time", description: "Alias for departure_date - full departure date and time in ISO 8601 format YYYY-MM-DDTHH:MM:SS" },
                arrival_time: { type: "string", format: "date-time", description: "Alias for arrival_date - full arrival date and time in ISO 8601 format YYYY-MM-DDTHH:MM:SS" },
                aircraft_type: { type: "string", description: "Aircraft type or model" },
                seat: { type: "string", description: "Seat assignment for this leg (e.g., 12A)" },
                class_type: { type: "string", description: "Class of service for this leg (Economy, Business, First)" },
                ticket_number: { type: "string", description: "Ticket number specific to this leg if different from main ticket" }
              },
              required: ["leg_number", "airline", "flight_number", "departure_airport", "arrival_airport", "departure_date", "arrival_date"]
            }
          },
          passengers: {
            type: "array",
            items: {
              type: "object",
              properties: {
                name: { type: "string" },
                confirmation_number: { type: "string" },
                frequent_flyer_number: { type: "string" },
                ticket_number: { type: "string", description: "Passenger-specific ticket number" },
                ticket_no: { type: "string", description: "Alternative ticket number field" }
              }
            }
          }
        }
      };
      
      formData.append('extraction_schema', JSON.stringify(enhancedFlightSchema));

      const response = await axios.post('/api/documents/upload', formData);
      
      // Poll for parsing completion
      const documentId = response.data.document_id;
      if (!documentId) {
        throw new Error('No document ID returned from upload');
      }
      
      let attempts = 0;
      const maxAttempts = 60; // 60 seconds max wait (increased timeout)
      
      const pollForCompletion = async () => {
        try {
          attempts++;
          
          const docResponse = await axios.get('/documents');
          const document = docResponse.data.find(doc => doc.id === documentId);
          
          if (!document) {
            if (attempts < maxAttempts) {
              setTimeout(pollForCompletion, 1000);
              return;
            } else {
              throw new Error('Document not found after upload');
            }
          }
          
          
          if (document.processing_status === 'completed') {
            if (document.parsed_data && document.parsed_data.success && document.parsed_data.data) {
              // Update the temporary file with proper server information
              setAttachedFiles(prev => prev.map(f => 
                f.id === newFile.id 
                  ? { 
                      ...f, 
                      id: document.id, // Use document ID from database
                      url: document.file_path, // Use actual file path 
                      serverPath: document.file_path,
                      documentId: document.id
                    } 
                  : f
              ));
              
              // Process the actual parsed flight data
              processExtractedFlightData(document.parsed_data.data);
              setIsUploadingConfirmation(false);
              setValidationError('');
            } else {
              const errorMessage = document.parsed_data?.error || 'Document parsing failed or returned no data';
              throw new Error(errorMessage);
            }
          } else if (document.processing_status === 'failed') {
            const errorMessage = document.parsed_data?.error || 'Document processing failed';
            throw new Error(errorMessage);
          } else if (document.processing_status === 'processing' && attempts < maxAttempts) {
            // Still processing, wait and try again
            setTimeout(pollForCompletion, 2000); // Increased interval to 2 seconds
          } else {
            throw new Error(`Document processing timeout after ${attempts} attempts`);
          }
        } catch (pollError) {
          console.error('Error polling document status:', pollError);
          setValidationError(`Failed to process document: ${pollError.message}`);
          setIsUploadingConfirmation(false);
        }
      };
      
      // Start polling immediately after upload
      setTimeout(pollForCompletion, 500);
      
    } catch (error) {
      console.error('Error importing confirmation:', error);
      setValidationError(`Failed to import confirmation: ${error.message}`);
      setIsUploadingConfirmation(false);
    }
    
    if (e.target) e.target.value = '';
  };

  // Hotel confirmation upload handler
  const handleHotelConfirmationUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploadingHotelConfirmation(true);
    
    // Add file to attached files first for UI display
    const newFile = {
      id: `temp-${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      label: 'Hotel Confirmation'
    };
    
    try {
      setAttachedFiles(prev => [...prev, newFile]);

      // Create form data and upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'hotel');

      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/documents/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        let errorMessage = 'Unknown error occurred';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
        } catch (parseError) {
          // If JSON parsing fails, try to get text
          try {
            const text = await response.text();
            errorMessage = text || `HTTP ${response.status} ${response.statusText}`;
          } catch {
            errorMessage = `HTTP ${response.status} ${response.statusText}`;
          }
        }
        throw new Error(`Upload failed: ${errorMessage}`);
      }

      const result = await response.json();
      
      // Update the attached file with the server ID and URL
      setAttachedFiles(prev => prev.map(f => 
        f.id === newFile.id 
          ? { ...f, id: result.document_id, url: result.file_path } 
          : f
      ));

      // Start polling for document processing completion
      await pollForHotelDocumentCompletion(result.document_id);

    } catch (error) {
      console.error('Error uploading hotel confirmation:', error);
      setIsUploadingHotelConfirmation(false);
      setValidationError(`Upload error: ${error.message}`);
      
      // Remove the temp file from UI
      setAttachedFiles(prev => prev.filter(f => f.id !== newFile.id));
    }
  };

  // Hotel document polling function
  const pollForHotelDocumentCompletion = async (documentId) => {
    let attempts = 0;
    const maxAttempts = 60; // 60 seconds max wait
    
    const pollForCompletion = async () => {
      try {
        attempts++;

        const apiBase = getApiBase();
        const docResponse = await axios.get(`${apiBase}/documents`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        const document = docResponse.data.find(doc => doc.id === documentId);
        
        if (!document) {
          if (attempts < maxAttempts) {
            setTimeout(pollForCompletion, 1000);
            return;
          } else {
            throw new Error('Hotel document not found after upload');
          }
        }
        
        
        if (document.processing_status === 'completed') {
          if (document.parsed_data && document.parsed_data.success && document.parsed_data.data) {
            // Update the attached file with proper server information
            setAttachedFiles(prev => prev.map(f => 
              f.id === documentId 
                ? { 
                    ...f, 
                    id: document.id, // Use document ID from database
                    url: document.file_path, // Use actual file path
                    serverPath: document.file_path,
                    documentId: document.id
                  } 
                : f
            ));
            
            // Process the actual parsed hotel data
            processExtractedHotelData(document.parsed_data.data);
            setIsUploadingHotelConfirmation(false);
            setValidationError('');
          } else {
            const errorMessage = document.parsed_data?.error || 'Hotel document parsing failed or returned no data';
            throw new Error(errorMessage);
          }
        } else if (document.processing_status === 'failed') {
          const errorMessage = document.parsed_data?.error || 'Hotel document processing failed';
          throw new Error(errorMessage);
        } else if (document.processing_status === 'processing' && attempts < maxAttempts) {
          // Still processing, wait and try again
          setTimeout(pollForCompletion, 2000); // Increased interval to 2 seconds
        } else {
          throw new Error(`Hotel document processing timeout after ${attempts} attempts`);
        }
      } catch (pollError) {
        console.error('Error polling hotel document status:', pollError);
        setValidationError(`Failed to process hotel document: ${pollError.message}`);
        setIsUploadingHotelConfirmation(false);
      }
    };
    
    // Start polling immediately after upload
    setTimeout(pollForCompletion, 500);
  };

  // Process extracted hotel data with enhanced schema
  const processExtractedHotelData = (extractedData) => {
    
    // Update hotel name and location
    if (extractedData.hotel_name) {
      handleDetailsChange('hotel_name', extractedData.hotel_name);
      handleInputChange('title', extractedData.hotel_name);
      handleInputChange('location', extractedData.hotel_name);
    }
    
    if (extractedData.address) {
      handleDetailsChange('address', extractedData.address);
      handleDetailsChange('hotel_address', extractedData.address);
      handleInputChange('address', extractedData.address);
    }
    
    // Update check-in date and time
    if (extractedData.check_in_date) {
      const checkInTime = extractedData.check_in_time || '15:00';
      const checkInDateTime = `${extractedData.check_in_date}T${checkInTime}`;
      handleDetailsChange('check_in', checkInDateTime);
      handleInputChange('start_datetime', checkInDateTime);
    }
    
    // Update check-out date and time  
    if (extractedData.check_out_date) {
      const checkOutTime = extractedData.check_out_time || '11:00';
      const checkOutDateTime = `${extractedData.check_out_date}T${checkOutTime}`;
      handleDetailsChange('check_out', checkOutDateTime);
      handleInputChange('end_datetime', checkOutDateTime);
    }
    
    // Update confirmation number
    if (extractedData.confirmation_number) {
      handleDetailsChange('confirmation_number', extractedData.confirmation_number);
      handleInputChange('confirmation_number', extractedData.confirmation_number);
    }
    
    // Update room details
    if (extractedData.room_type || extractedData.number_of_rooms || extractedData.bed_preference) {
      const numRooms = extractedData.number_of_rooms || 1;
      const roomType = extractedData.room_type ? normalizeRoomType(extractedData.room_type) : '';
      const bedTypeFromPref = extractedData.bed_preference ? normalizeBedType(extractedData.bed_preference) : '';
      // Some confirmations put bed info inside room_type string (e.g., "Deluxe King Room"). Try to infer.
      const bedTypeFromRoom = !bedTypeFromPref && extractedData.room_type ? normalizeBedType(extractedData.room_type) : '';
      const bedType = bedTypeFromPref || bedTypeFromRoom || '';
      
      handleDetailsChange('number_of_rooms', numRooms);
      
      // Initialize room details based on extracted data
      const existingRooms = Array.isArray(formData.component_data?.room_details) ? formData.component_data.room_details : [];
      const roomDetails = Array.from({ length: numRooms }, (_, index) => ({
        room_type: existingRooms[index]?.room_type || roomType,
        guests: existingRooms[index]?.guests || Math.ceil((extractedData.guests?.length || 1) / numRooms),
        bed_preference: existingRooms[index]?.bed_preference || bedType,
        special_notes: existingRooms[index]?.special_notes || ''
      }));
      
      handleDetailsChange('room_details', roomDetails);
    }
    
    // Update room preferences
    if (extractedData.bed_preference) {
      handleDetailsChange('bed_preference', normalizeBedType(extractedData.bed_preference));
    }
    
    if (extractedData.floor_preference) {
      handleDetailsChange('floor_preference', extractedData.floor_preference.toLowerCase().replace(' ', '_'));
    }
    
    if (extractedData.view_preference) {
      handleDetailsChange('view_preference', extractedData.view_preference);
    }
    
    // Update cost information
    if (extractedData.total_cost) {
      handleDetailsChange('total_cost', extractedData.total_cost);
    }
    
    if (extractedData.currency) {
      handleDetailsChange('currency', extractedData.currency);
    }
    
    // Update hotel contact info
    if (extractedData.hotel_phone) {
      handleDetailsChange('hotel_phone', extractedData.hotel_phone);
    }
    
    if (extractedData.hotel_website) {
      handleDetailsChange('hotel_website', extractedData.hotel_website);
    }
    
    if (extractedData.hotel_rating) {
      handleDetailsChange('hotel_rating', extractedData.hotel_rating);
    }
    
    // Add special requests to notes
    if (extractedData.special_requests || extractedData.cancellation_policy) {
      const currentNotes = formData.notes || '';
      let newNotes = currentNotes;
      
      if (extractedData.special_requests) {
        handleDetailsChange('special_requests', extractedData.special_requests);
        newNotes += `${currentNotes ? '\n\n' : ''}Special Requests:\n${extractedData.special_requests}`;
      }
      
      if (extractedData.cancellation_policy) {
        newNotes += `${newNotes ? '\n\n' : ''}Cancellation Policy:\n${extractedData.cancellation_policy}`;
      }
      
      if (newNotes !== currentNotes) {
        handleInputChange('notes', newNotes);
      }
    }
    
    // Process guest information and smart executive matching
    if (extractedData.guests && Array.isArray(extractedData.guests) && extractedData.guests.length > 0) {
      const guestNotes = `Guests: ${extractedData.guests.join(', ')}`;
      const currentNotes = formData.notes || '';
      if (!currentNotes.includes('Guests:')) {
        handleInputChange('notes', currentNotes ? `${currentNotes}\n\n${guestNotes}` : guestNotes);
      }
      
      // Auto-set total guests
      if (!formData.component_data?.total_guests) {
        handleDetailsChange('total_guests', extractedData.guests.length);
      }
      
      // Smart executive matching based on guest names
      
      const matchedExecutives = smartMatchExecutivesToGuests(extractedData.guests, finalAvailableExecutives);
      
      if (matchedExecutives.length > 0) {
        const executiveIds = matchedExecutives.map(exec => exec.id);
        handleDetailsChange('assigned_executives', executiveIds);
        
        // Show success message for matched executives
        showExecutiveMatchingMessage(matchedExecutives, extractedData.guests);
      } else if (!formData.component_data?.assigned_executives && finalAvailableExecutives.length > 0) {
        // Fallback: Auto-assign first available executive if no matches found
        const executiveIds = [finalAvailableExecutives[0].id];
        handleDetailsChange('assigned_executives', executiveIds);
      } else {
      }
    }
    
    // Process amenities
    if (extractedData.amenities && Array.isArray(extractedData.amenities) && extractedData.amenities.length > 0) {
      const amenitiesNotes = `Amenities: ${extractedData.amenities.join(', ')}`;
      const currentNotes = formData.notes || '';
      if (!currentNotes.includes('Amenities:')) {
        handleInputChange('notes', currentNotes ? `${currentNotes}\n\n${amenitiesNotes}` : amenitiesNotes);
      }
    }

    
    // Show success message with extracted data
    showImportSuccessMessage(extractedData);
  };

  // Smart Executive Matching Function
  const smartMatchExecutivesToGuests = (guestNames, availableExecutives) => {
    const matchedExecutives = [];
    const usedExecutiveIds = new Set();
    
    // Helper function to normalize names for comparison
    const normalizeName = (name) => {
      if (!name) return '';
      
      // Remove common titles and normalize
      const titlePrefixes = ['mr', 'mrs', 'ms', 'dr', 'prof', 'sir', 'madam', 'miss'];
      const titleSuffixes = ['jr', 'sr', 'ii', 'iii', 'iv', 'phd', 'md', 'esq'];
      
      let normalized = name.toLowerCase()
        .replace(/[.,]/g, '') // Remove punctuation
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();
      
      // Remove title prefixes
      for (const title of titlePrefixes) {
        if (normalized.startsWith(title + ' ')) {
          normalized = normalized.substring(title.length + 1);
        }
      }
      
      // Remove title suffixes
      for (const suffix of titleSuffixes) {
        if (normalized.endsWith(' ' + suffix)) {
          normalized = normalized.substring(0, normalized.length - suffix.length - 1);
        }
      }
      
      return normalized.trim();
    };
    
    // Helper function to extract first and last names
    const extractFirstLastName = (fullName) => {
      const normalized = normalizeName(fullName);
      const parts = normalized.split(' ').filter(part => part.length > 0);
      
      if (parts.length === 0) return { first: '', last: '' };
      if (parts.length === 1) return { first: parts[0], last: '' };
      
      // For multiple parts, use first as first name and last as last name
      // Ignore middle names/initials
      return {
        first: parts[0],
        last: parts[parts.length - 1]
      };
    };
    
    // Helper function to check if names match
    const namesMatch = (guestName, executiveName) => {
      const guest = extractFirstLastName(guestName);
      const executive = {
        first: normalizeName(executiveName.first_name),
        last: normalizeName(executiveName.last_name)
      };
      
      // Exact match on both first and last name
      if (guest.first === executive.first && guest.last === executive.last) {
        return { score: 100, type: 'exact' };
      }
      
      // First name match with last name initial
      if (guest.first === executive.first && guest.last && executive.last && 
          guest.last[0] === executive.last[0]) {
        return { score: 90, type: 'first_and_initial' };
      }
      
      // Last name match with first name initial  
      if (guest.last === executive.last && guest.first && executive.first &&
          guest.first[0] === executive.first[0]) {
        return { score: 85, type: 'last_and_initial' };
      }
      
      // First name only match (if last name is missing from guest)
      if (guest.first === executive.first && !guest.last) {
        return { score: 75, type: 'first_only' };
      }
      
      // Last name only match (if first name is missing from guest)
      if (guest.last === executive.last && !guest.first) {
        return { score: 70, type: 'last_only' };
      }
      
      return { score: 0, type: 'none' };
    };
    
    // Match each guest name against available executives
    for (const guestName of guestNames) {
      let bestMatch = null;
      let bestScore = 0;
      
      for (const executive of availableExecutives) {
        if (usedExecutiveIds.has(executive.id)) continue; // Skip already matched executives
        
        const match = namesMatch(guestName, executive);
        if (match.score > bestScore && match.score >= 70) { // Minimum confidence threshold
          bestMatch = { executive, match, guestName };
          bestScore = match.score;
        }
      }
      
      if (bestMatch) {
        matchedExecutives.push({
          ...bestMatch.executive,
          matchInfo: {
            guestName: bestMatch.guestName,
            score: bestMatch.match.score,
            type: bestMatch.match.type
          }
        });
        usedExecutiveIds.add(bestMatch.executive.id);
      }
    }
    
    return matchedExecutives;
  };
  
  // Show executive matching success message
  const showExecutiveMatchingMessage = (matchedExecutives, guestNames) => {
    const successElement = document.createElement('div');
    successElement.className = 'fixed top-4 right-4 z-50 p-4 bg-ui-green-bg border border-ui-green-line rounded-lg shadow-lg max-w-md';
    
    const matchDetails = matchedExecutives.map(exec => {
      const matchType = {
        'exact': 'Exact match',
        'first_and_initial': 'First name + initial',
        'last_and_initial': 'Last name + initial',
        'first_only': 'First name only',
        'last_only': 'Last name only'
      }[exec.matchInfo.type] || 'Match';

      // SECURITY: Escape user-controlled data to prevent XSS
      const firstName = escapeHtml(exec.first_name);
      const lastName = escapeHtml(exec.last_name);
      const guestName = escapeHtml(exec.matchInfo.guestName);

      return `<div class="text-sm text-ui-green-fg">• <strong>${firstName} ${lastName}</strong> matched with "${guestName}" (${matchType})</div>`;
    }).join('');
    
    successElement.innerHTML = `
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0">
          <svg class="w-5 h-5 text-ui-green-fg mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div>
          <div class="font-semibold text-ui-green-fg mb-2">Smart Executive Matching Complete!</div>
          <div class="space-y-1">
            ${matchDetails}
          </div>
          ${matchedExecutives.length < guestNames.length ? 
            `<div class="text-xs text-ui-amber-fg mt-2">⚠️ ${guestNames.length - matchedExecutives.length} guest(s) could not be matched automatically</div>` : 
            ''}
        </div>
        <button onclick="this.parentElement.parentElement.remove()" class="ml-2 text-ui-green-fg hover:bg-ui-green-bg rounded-full p-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;
    
    document.body.appendChild(successElement);
    
    // Auto-remove after 8 seconds (longer for executive matching info)
    setTimeout(() => {
      if (successElement.parentElement) {
        successElement.remove();
      }
    }, 8000);
  };

  // Show import success message component
  const showImportSuccessMessage = (data) => {
    // Create a temporary success message that auto-disappears
    const successElement = document.createElement('div');
    successElement.className = 'fixed top-4 right-4 z-50 p-4 bg-ui-green-bg border border-ui-green-line rounded-lg shadow-lg max-w-md';
    successElement.innerHTML = `
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0">
          <svg class="w-5 h-5 text-ui-green-fg mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div>
          <div class="font-semibold text-ui-green-fg mb-2">Hotel confirmation imported successfully!</div>
          <div class="text-sm text-ui-green-fg space-y-1">
            ${data.hotel_name ? `<div>• Hotel: ${escapeHtml(data.hotel_name)}</div>` : ''}
            ${data.check_in_date && data.check_out_date ? `<div>• Dates: ${new Date(data.check_in_date).toLocaleDateString()} - ${new Date(data.check_out_date).toLocaleDateString()}</div>` : ''}
            ${data.room_type ? `<div>• Room: ${escapeHtml(data.room_type)}</div>` : ''}
            ${data.confirmation_number ? `<div>• Confirmation: ${escapeHtml(data.confirmation_number)}</div>` : ''}
          </div>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" class="ml-2 text-ui-green-fg hover:bg-ui-green-bg rounded-full p-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;
    
    document.body.appendChild(successElement);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (successElement.parentElement) {
        successElement.remove();
      }
    }, 5000);
  };

  // Helper function to update room details when number of rooms changes
  const updateRoomDetails = (numRooms) => {
    const currentRooms = formData.component_data?.room_details || [];
    const updatedRooms = [];

    // Keep existing room data and add new rooms if needed
    for (let i = 0; i < numRooms; i++) {
      if (i < currentRooms.length) {
        updatedRooms.push(currentRooms[i]);
      } else {
        // Add new room with default values
        updatedRooms.push({
          room_type: '',
          guests: 1,
          bed_preference: '',
          special_notes: '',
          guest_ids: []  // IDs of executives/contacts assigned to this room
        });
      }
    }

    handleDetailsChange('room_details', updatedRooms);
  };

  // Helper function to update rooms when total guests changes
  const updateRoomsBasedOnGuests = (totalGuests) => {
    const currentRooms = formData.component_data?.room_details || [];
    const numRooms = formData.component_data?.number_of_rooms || 1;
    
    if (currentRooms.length === 0) {
      // Initialize rooms if they don't exist
      updateRoomDetails(numRooms);
      return;
    }

    // Auto-distribute guests if rooms exist but no guests assigned
    const allocatedGuests = currentRooms.reduce((sum, room) => sum + (room.guests || 0), 0);
    
    if (allocatedGuests === 0 && totalGuests > 0) {
      const guestsPerRoom = Math.floor(totalGuests / numRooms);
      const remainder = totalGuests % numRooms;
      
      const updatedRooms = currentRooms.map((room, index) => ({
        ...room,
        guests: guestsPerRoom + (index < remainder ? 1 : 0)
      }));
      
      handleDetailsChange('room_details', updatedRooms);
    }
  };

  // Helper function to update individual room details
  const updateRoomDetail = (roomIndex, field, value) => {
    const currentRooms = formData.component_data?.room_details || [];
    const updatedRooms = [...currentRooms];

    if (!updatedRooms[roomIndex]) {
      updatedRooms[roomIndex] = {};
    }

    updatedRooms[roomIndex] = {
      ...updatedRooms[roomIndex],
      [field]: value
    };

    handleDetailsChange('room_details', updatedRooms);

    // Update total guests when individual room guest counts change
    if (field === 'guests') {
      const totalGuests = updatedRooms.reduce((sum, room) => sum + (room.guests || 0), 0);
      handleDetailsChange('total_guests', totalGuests);
    }

    // Sync assigned_executives when guest_ids change in any room
    // This ensures the hotel appears on each assigned executive's timeline
    if (field === 'guest_ids') {
      const allGuestIds = updatedRooms.reduce((ids, room) => {
        const roomGuestIds = room.guest_ids || [];
        return [...ids, ...roomGuestIds];
      }, []);
      // Remove duplicates and update assigned_executives
      const uniqueGuestIds = [...new Set(allGuestIds)];
      handleDetailsChange('assigned_executives', uniqueGuestIds);
    }
  };

  // Default form values for new passenger/contact (defined early for use in renderHotelFields)
  const defaultNewPassengerFormEarly = {
    first_name: '',
    last_name: '',
    email: '',
    office_phone: '',
    mobile_phone: '',
    company: '',
    notes: '',
    contact_type: 'external',
    ea_name: '',
    ea_office_phone: '',
    ea_mobile_phone: '',
    is_private: false,
    saveToContacts: true,
    speaker_id: null,      // speaker_bureau: optional link to a saved Speaker
    speaker_name: ''
  };

  // Handler for opening add contact modal from room guest selector
  const handleOpenRoomGuestContactModal = useCallback((roomIndex, searchQuery = '') => {
    const nameParts = searchQuery.trim().split(/\s+/);
    setNewPassengerForm({
      ...defaultNewPassengerFormEarly,
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || ''
    });
    setEditingContact(null);
    setPendingRoomGuestAssignment({ roomIndex });
    setShowNewPassengerModal(true);
  }, []);

  // Normalize room type to match predefined options (tolerant of extra descriptors)
  const normalizeRoomType = (roomType) => {
    if (!roomType) return '';
    const s = roomType.toLowerCase();
    // Prefer specific suite types first
    if (s.includes('presidential') && s.includes('suite')) return 'presidential_suite';
    if (s.includes('executive') && s.includes('suite')) return 'executive_suite';
    if (s.includes('junior') && s.includes('suite')) return 'junior_suite';
    if (s.includes('suite')) return 'suite';
    // Common room designations
    if (s.includes('deluxe')) return 'deluxe';
    if (s.includes('standard')) return 'standard';
    if (s.includes('connecting')) return 'connecting';
    if (s.includes('accessible')) return 'accessible';
    // Avoid mapping bed sizes to room types (handled separately)
    return 'other';
  };

  // Normalize bed type from a variety of phrases (e.g., "1 King Bed", "Two Queens", "Double Double")
  const normalizeBedType = (text) => {
    if (!text) return '';
    const s = text.toLowerCase();
    if (/(king)/.test(s)) return 'king';
    if (/(queens|queen)/.test(s)) return 'queen';
    if (/(double\s*double|two\s*doubles|2\s*double|double)/.test(s)) return 'double';
    if (/(twin|two\s*twin|2\s*twin)/.test(s)) return 'twin';
    if (/(single)/.test(s)) return 'single';
    return '';
  };

  // Meeting Attendee Management Handlers
  const handleEditAttendee = (attendee) => {
    setEditingAttendee(attendee);
    setShowAttendeeForm(true);
  };

  const handleDeleteAttendee = (tempId) => {
    setAttendees(prev => prev.filter(attendee => attendee.tempId !== tempId));
  };

  // speaker_bureau: link/unlink an attendee to a saved Speaker record.
  // speaker_id / speaker_name persist automatically (attendees are spread into
  // *_attendees on submit).
  const handleLinkAttendeeSpeaker = useCallback((tempId, speaker) => {
    setAttendees(prev => prev.map(a => {
      if (a.tempId !== tempId) return a;
      if (!speaker) return { ...a, speaker_id: null, speaker_name: '' };
      const nm = speaker.full_name || `${speaker.first_name || ''} ${speaker.last_name || ''}`.trim();
      return { ...a, speaker_id: speaker.id, speaker_name: nm, name: a.name || nm };
    }));
  }, []);

  const handleAddAttendee = useCallback((attendeeData) => {
    const newAttendee = {
      ...attendeeData,
      tempId: `new-${Date.now()}-${Math.random()}`
    };
    
    if (editingAttendee) {
      // Update existing attendee
      setAttendees(prev => prev.map(attendee => 
        attendee.tempId === editingAttendee.tempId ? newAttendee : attendee
      ));
    } else {
      // Add new attendee
      setAttendees(prev => [...prev, newAttendee]);
    }
    
    setShowAttendeeForm(false);
    setEditingAttendee(null);
  }, [editingAttendee]);

  // Executive search handler to prevent focus loss
  const handleExecutiveSearchChange = useCallback((e) => {
    const value = e.target.value;
    setExecutiveSearchQuery(value);
    setShowExecutiveSuggestions(value.length > 0);
  }, []);

  // Stable onFocus handler to prevent focus loss
  const handleExecutiveSearchFocus = useCallback(() => {
    setShowExecutiveSuggestions(executiveSearchQuery.length > 0);
  }, [executiveSearchQuery]);

  // Meeting-specific stable handlers to prevent focus loss
  const handleEndTimeChange = useCallback((value) => {
    if (value) {
      // Use functional state update to access current formData
      setFormData(prevFormData => {
        if (prevFormData.start_datetime) {
          // Combine the start date with the selected end time
          const startDate = new Date(prevFormData.start_datetime);
          const [hours, minutes] = value.split(':').map(Number);
          const endDateTime = new Date(startDate);
          endDateTime.setHours(hours, minutes, 0, 0);
          
          // Ensure end time is after start time
          if (endDateTime <= startDate) {
            // If end time is before start time, assume it's the next day
            endDateTime.setDate(endDateTime.getDate() + 1);
          }
          
          return {
            ...prevFormData,
            end_datetime: format(endDateTime, "yyyy-MM-dd'T'HH:mm")
          };
        }
        return prevFormData;
      });
    } else {
      // Clear end time
      handleInputChange('end_datetime', '');
    }
  }, []);

  const handleMeetingLocationSelect = useCallback((place) => {
    const address = place.formatted_address || '';
    const name = place.name || '';

    handleInputChange('location', address || name);
    handleInputChange('address', address);

    // Store Google Place ID for Urbanride API integration
    setFormData(prev => ({
      ...prev,
      component_data: {
        ...prev.component_data,
        meeting_location: address || name,
        place_id: place.place_id || ''
      }
    }));
  }, [handleInputChange]);

  const handleEventLocationSelect = useCallback((place) => {
    const address = place.formatted_address || '';
    const name = place.name || '';
    const types = place.types || [];

    // Determine if this is an establishment (venue) vs a plain address
    const isAddress = types.includes('street_address') || types.includes('route') || types.includes('premise') || types.includes('subpremise') || types.includes('geocode');

    // Only treat as a venue name if it's an establishment and the name differs from the address
    const isVenue = !isAddress && name && name !== address && !address.startsWith(name);
    const venueName = isVenue ? name : '';

    // For display: show "Venue Name, Address" when both exist, otherwise just the address/name
    const displayLocation = venueName && address ? `${venueName}, ${address}` : (address || name);
    handleInputChange('location', displayLocation);
    handleInputChange('address', address);

    // Store Google Place ID and venue name for display in timeline/dashboard
    setFormData(prev => ({
      ...prev,
      component_data: {
        ...prev.component_data,
        event_location: address || name,
        event_venue_name: venueName,
        place_id: place.place_id || ''
      }
    }));
  }, [handleInputChange]);

  // Stable handlers for meeting details to prevent focus loss
  const handlePlatformChange = useCallback((e) => handleDetailsChange('platform', e.target.value), [handleDetailsChange]);
  const handleMeetingLinkChange = useCallback((e) => handleDetailsChange('meeting_link', e.target.value), [handleDetailsChange]);
  const handleDialInChange = useCallback((e) => handleDetailsChange('dial_in', e.target.value), [handleDetailsChange]);

  // Memoized time options with 15-minute increments
  const timeOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < 96; i++) { // 96 slots for 15-minute increments (24 hours * 4)
      const hour = Math.floor(i / 4);
      const minute = (i % 4) * 15; // 0, 15, 30, 45
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const display = format(new Date(2000, 0, 1, hour, minute), 'h:mm a');
      options.push({ value: time, display });
    }
    return options;
  }, []);

  // Smart end time options that start from the meeting start time
  const endTimeOptions = useMemo(() => {
    if (!formData.start_datetime) {
      return timeOptions; // Return all options if no start time selected
    }
    
    // Handle TBD format - return all options since there's no specific start time
    if (formData.start_datetime.includes('-TBD-TIME') || formData.start_datetime.includes('TBD')) {
      return timeOptions;
    }
    
    try {
      // Validate and parse the start datetime
      if (!formData.start_datetime || typeof formData.start_datetime !== 'string') {
        return timeOptions;
      }
      
      const startDateTime = new Date(formData.start_datetime);
      if (isNaN(startDateTime.getTime())) {
        return timeOptions;
      }
      
      const startTimeStr = format(startDateTime, 'HH:mm');
      
      // Find the index of the start time in timeOptions
      const startIndex = timeOptions.findIndex(option => option.value >= startTimeStr);
      
      if (startIndex === -1) {
        return timeOptions; // Fallback to all options
      }
      
      // Return options starting from the next time slot after start time
      return timeOptions.slice(startIndex + 1);
    } catch (error) {
      return timeOptions; // Fallback to all options
    }
  }, [formData.start_datetime, timeOptions]);

  // Stable handler for attendee form cancellation
  const handleCancelAttendeeForm = useCallback(() => {
    setShowAttendeeForm(false);
    setEditingAttendee(null);
  }, []);

  // Stable input handlers for main form fields to prevent focus loss
  const handleTitleChange = useCallback((value) => handleInputChange('title', value), [handleInputChange]);
  const handleStartDateTimeChange = useCallback((value) => handleInputChange('start_datetime', value), [handleInputChange]);
  const handleEndDateTimeChange = useCallback((value) => handleInputChange('end_datetime', value), [handleInputChange]);
  const handleLocationChange = useCallback((value) => handleInputChange('location', value), [handleInputChange]);
  const handleNotesChange = useCallback((value) => handleInputChange('notes', value), [handleInputChange]);

  // Handlers for separate date and time fields
  const handleMeetingDateChange = useCallback((dateValue) => {
    if (!dateValue) {
      handleInputChange('start_datetime', '');
      return;
    }

    // Only set time if start time is not marked as TBD

    if (isStartTimeTBD) {
      // If start time is TBD, just store the date without time
      const tbdFormat = `${dateValue}-TBD-TIME`;
      handleInputChange('start_datetime', tbdFormat);
    } else {
      // Get current time part or default to 9:00 AM
      let timeStr = '09:00';
      if (formData.start_datetime) {
        try {
          // Only extract time if the current value is not TBD format
          if (!formData.start_datetime.includes('-TBD-TIME')) {
            const currentDateTime = new Date(formData.start_datetime);
            timeStr = format(currentDateTime, 'HH:mm');
          }
        } catch (error) {
        }
      }

      const newDateTime = `${dateValue}T${timeStr}`;
      handleInputChange('start_datetime', newDateTime);

      // Also update end_datetime to use the new date (preserving the end time)
      // This fixes the bug where changing the date leaves end_datetime on the old date
      if (formData.end_datetime && formData.end_datetime !== 'TBD' && !isEndTimeTBD) {
        try {
          const endDateTime = new Date(formData.end_datetime);
          const endTimeStr = format(endDateTime, 'HH:mm');
          const newEndDateTime = `${dateValue}T${endTimeStr}`;
          handleInputChange('end_datetime', newEndDateTime);
        } catch (error) {
        }
      }
    }
  }, [formData.start_datetime, formData.end_datetime, handleInputChange, isStartTimeTBD, isEndTimeTBD]);

  const handleMeetingTimeChange = useCallback((timeValue) => {
    if (!timeValue) return;
    
    // Get current date part or default to today
    let dateStr = format(new Date(), 'yyyy-MM-dd');
    if (formData.start_datetime) {
      try {
        const currentDateTime = new Date(formData.start_datetime);
        dateStr = format(currentDateTime, 'yyyy-MM-dd');
      } catch (error) {
      }
    }
    
    const newDateTime = `${dateStr}T${timeValue}`;
    handleInputChange('start_datetime', newDateTime);
  }, [formData.start_datetime, handleInputChange]);

  // TBD handlers for meeting date and time
  const handleDateTBDToggle = useCallback(() => {
    setIsDateTBD(!isDateTBD);
    if (!isDateTBD) {
      // Setting to TBD - clear the date part but keep time if exists
      if (formData.start_datetime) {
        try {
          const currentDateTime = new Date(formData.start_datetime);
          const timeStr = format(currentDateTime, 'HH:mm');
          handleInputChange('start_datetime', `TBD-${timeStr}`);
        } catch (error) {
          handleInputChange('start_datetime', 'TBD');
        }
      } else {
        handleInputChange('start_datetime', 'TBD');
      }
    } else {
      // Removing TBD - set to today's date
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      if (formData.start_datetime && formData.start_datetime.includes('TBD-')) {
        const timeStr = formData.start_datetime.split('TBD-')[1];
        handleInputChange('start_datetime', `${todayStr}T${timeStr}`);
      } else {
        handleInputChange('start_datetime', '');
      }
    }
  }, [isDateTBD, formData.start_datetime, handleInputChange]);

  const handleStartTimeTBDToggle = useCallback(() => {
    const newTBDState = !isStartTimeTBD;
    
    setIsStartTimeTBD(newTBDState);
    
    // Update the stored datetime format based on TBD state
    if (newTBDState) {
      // Switching TO TBD - convert existing datetime to TBD format
      if (formData.start_datetime) {
        try {
          const currentDate = new Date(formData.start_datetime);
          const dateStr = format(currentDate, 'yyyy-MM-dd');
          const tbdFormat = `${dateStr}-TBD-TIME`;
          handleInputChange('start_datetime', tbdFormat);
        } catch (error) {
          handleInputChange('start_datetime', 'TBD-TIME');
        }
      } else {
        handleInputChange('start_datetime', 'TBD-TIME');
      }
    } else {
      // Switching FROM TBD to regular time
      if (formData.start_datetime && formData.start_datetime.includes('-TBD-TIME')) {
        const dateOnly = formData.start_datetime.split('-TBD-TIME')[0];
        handleInputChange('start_datetime', dateOnly);
      }
    }
    
  }, [isStartTimeTBD, formData.start_datetime, handleInputChange]);

  const handleEndTimeTBDToggle = useCallback(() => {
    setIsEndTimeTBD(!isEndTimeTBD);
    if (!isEndTimeTBD) {
      handleInputChange('end_datetime', 'TBD');
    } else {
      handleInputChange('end_datetime', '');
    }
  }, [isEndTimeTBD, handleInputChange]);
  const handleDescriptionChange = useCallback((value) => handleInputChange('description', value), [handleInputChange]);
  const handleConfirmationNumberChange = useCallback((value) => handleInputChange('confirmation_number', value), [handleInputChange]);
  const handleAddressChange = useCallback((value) => handleInputChange('address', value), [handleInputChange]);
  const handleMeetingStartDateTimeChange = useCallback((e) => handleInputChange('start_datetime', e.target.value), [handleInputChange]);

  // File management handlers
  const handleFileDelete = (fileId) => {
    setAttachedFiles(files => files.filter(file => file.id !== fileId));
  };

  const handleFileLabelChange = (fileId, newLabel) => {
    setAttachedFiles(files => 
      files.map(file => 
        file.id === fileId ? { ...file, label: newLabel } : file
      )
    );
  };

  const handleFileNameChange = (fileId, newName) => {
    setAttachedFiles(files => 
      files.map(file => 
        file.id === fileId ? { ...file, name: newName } : file
      )
    );
  };

  const handleDownloadDocument = async (file) => {
    try {
      if (!file.id || file.id === 'undefined' || file.id.startsWith('temp-')) {
        alert('Cannot download this document: File ID is missing or invalid. This file may need to be re-uploaded.');
        return;
      }

      // Fetch document from backend with authentication
      const response = await fetch(`/api/files/${file.id}/download`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders()
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText);
        throw new Error(`Download failed (${response.status}): ${errorText}`);
      }

      // Create temporary download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('❌ Download failed:', error);
      // Fallback: try to open the file in a new window if it has a URL
      if (file.url) {
        window.open(file.url, '_blank');
      } else {
        alert('Download failed. Please try again or contact support.');
      }
    }
  };

  // Generic file upload handler for additional documents  
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploadingFile(true);
    
    try {
      // Upload each file
      for (const file of files) {
        
        // Create form data and upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', componentType === 'speaker_meeting' ? 'speaker_meeting' : (componentType === 'meeting' ? 'meeting' : 'general'));

        const apiBase = getApiBase();
        const response = await fetch(`${apiBase}/documents/upload`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders()
          },
          body: formData
        });

        if (!response.ok) {
          let errorData = 'Unknown error';
          try {
            const jsonError = await response.json();
            errorData = jsonError.detail || jsonError.message || JSON.stringify(jsonError);
          } catch {
            try {
              errorData = await response.text() || `HTTP ${response.status}`;
            } catch {
              errorData = `HTTP ${response.status}`;
            }
          }
          console.error('Upload failed for file:', file.name, errorData);
          
          // For demo purposes, create a temporary file entry if upload fails
          const tempFile = {
            id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: file.name,
            size: file.size,
            type: file.type,
            label: componentType === 'meeting' ? 'Meeting Document' : 'Document',
            url: URL.createObjectURL(file) // Create blob URL for viewing
          };
          
          setAttachedFiles(prev => [...prev, tempFile]);
          continue;
        }

        const result = await response.json();
        
        // Add successfully uploaded file to attached files list
        const newFile = {
          id: result.document_id || `uploaded-${Date.now()}`,
          name: file.name,
          size: file.size,
          type: file.type,
          label: componentType === 'meeting' ? 'Meeting Document' : 'Document',
          url: result.url || null
        };
        
        setAttachedFiles(prev => [...prev, newFile]);
      }
      
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(`Upload error: ${error.message}`);
    } finally {
      setIsUploadingFile(false);
      // Clear the input
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  // Function to match passenger name with trip executives
  const matchPassengerToExecutive = (passengerName, executives) => {
    if (!passengerName || !executives || executives.length === 0) {
      return null;
    }
    
    // Clean passenger name - remove titles and normalize
    const cleanPassengerName = cleanNameForMatching(passengerName);
    
    // Try exact matches first
    for (const executive of executives) {
      if (!executive || !executive.first_name || !executive.last_name) continue;
      const executiveName = `${executive.first_name} ${executive.last_name}`.toLowerCase().trim();
      if (cleanPassengerName === executiveName) {
        return executive;
      }
    }
    
    // Try partial matches (first name or last name)
    for (const executive of executives) {
      if (!executive || !executive.first_name || !executive.last_name) continue;
      const firstName = executive.first_name.toLowerCase().trim();
      const lastName = executive.last_name.toLowerCase().trim();
      
      // Check if passenger name contains both first and last name
      if (cleanPassengerName.includes(firstName) && cleanPassengerName.includes(lastName)) {
        return executive;
      }
    }
    
    // Try single name matches
    for (const executive of executives) {
      if (!executive || !executive.first_name || !executive.last_name) continue;
      const firstName = executive.first_name.toLowerCase().trim();
      const lastName = executive.last_name.toLowerCase().trim();
      
      // Check if passenger name contains executive's first or last name
      if (cleanPassengerName.includes(firstName) || cleanPassengerName.includes(lastName)) {
        return executive;
      }
    }
    
    // Try fuzzy matching - split passenger name and match parts
    const passengerNameParts = cleanPassengerName.split(/\s+/).filter(part => part.length > 2);
    for (const executive of executives) {
      if (!executive || !executive.first_name || !executive.last_name) continue;
      const firstName = executive.first_name.toLowerCase().trim();
      const lastName = executive.last_name.toLowerCase().trim();
      
      for (const part of passengerNameParts) {
        if (firstName.includes(part) || lastName.includes(part) || 
            part.includes(firstName) || part.includes(lastName)) {
          return executive;
        }
      }
    }
    
    // No match found
    return null;
  };

  // Helper function to clean passenger names for matching
  const cleanNameForMatching = (name) => {
    // Common titles to remove
    const titles = ['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'professor', 'sir', 'madam', 'lord', 'lady'];
    
    let cleanName = name.toLowerCase().trim();
    
    // Remove titles from the beginning
    for (const title of titles) {
      if (cleanName.startsWith(title + ' ') || cleanName.startsWith(title + '.')) {
        cleanName = cleanName.replace(new RegExp(`^${title}[\\s\\.]+`), '');
        break;
      }
    }
    
    // Remove middle initials and names (keep only first and last)
    const nameParts = cleanName.split(/\s+/).filter(part => part.length > 0);
    if (nameParts.length >= 3) {
      // Assume first and last name, remove middle parts that are likely initials or middle names
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      cleanName = `${firstName} ${lastName}`;
    }
    
    return cleanName;
  };

  const processExtractedFlightData = (extractedData) => {
    
    // Update confirmation number at component level
    if (extractedData.confirmation_number) {
      handleInputChange('confirmation_number', extractedData.confirmation_number);
    }
    
    // Enhanced ticket number extraction with comprehensive debugging
    
    const possibleTicketFields = [
      'ticket_number', 'ticketNumber', 'ticket', 'ticket_no', 'ticketNo',
      'e_ticket_number', 'eTicketNumber', 'electronic_ticket_number',
      'document_number', 'documentNumber', 'document_no', 'documentNo',
      'tkt_number', 'tktNumber', 'tkt_no', 'tktNo',
      'reference_number', 'referenceNumber', 'ref_number', 'refNumber'
    ];
    
    let ticketNumber = null;
    let foundField = null;
    
    // Check top-level fields
    for (const field of possibleTicketFields) {
      if (extractedData[field]) {
        ticketNumber = extractedData[field];
        foundField = field;
        break;
      }
    }
    
    // Check in passenger data
    if (!ticketNumber && extractedData.passengers && extractedData.passengers.length > 0) {
      const passenger = extractedData.passengers[0];
      
      for (const field of possibleTicketFields) {
        if (passenger[field]) {
          ticketNumber = passenger[field];
          foundField = `passenger.${field}`;
          break;
        }
      }
    }
    
    // Check any field that might contain the ticket number pattern
    if (!ticketNumber) {
      const scanObject = (obj, prefix = '') => {
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'string' || typeof value === 'number') {
            const stringValue = String(value);
            // Look for patterns that might be ticket numbers (digits with optional spaces/hyphens)
            if (/\d{3,}\s*\d{7,}/.test(stringValue) || /\d{10,}/.test(stringValue)) {
              if (!ticketNumber && stringValue.includes('176') && stringValue.includes('2380300676')) {
                ticketNumber = stringValue;
                foundField = `${prefix}${key}`;
              }
            }
          } else if (typeof value === 'object' && value !== null) {
            scanObject(value, `${prefix}${key}.`);
          }
        }
      };
      
      scanObject(extractedData);
    }
    
    if (ticketNumber) {
      // Clean up ticket number (normalize spaces)
      const cleanTicketNumber = String(ticketNumber).replace(/\s+/g, ' ').trim();
      handleDetailsChange('ticket_number', cleanTicketNumber);
    } else {
    }
    
    // Process flight legs - handle both legacy flight_legs and new legs structure
    const flightLegsData = extractedData.legs || extractedData.flight_legs;
    if (flightLegsData && flightLegsData.length > 0) {
      const legs = flightLegsData.map((leg, legIndex) => {
        // Start with basic leg info
        const legInfo = {
          airline_name: leg.airline || leg.airline_name || extractedData.airline || '',
          flight_number: leg.flight_number || '',
          departure_airport: leg.departure_airport || '',
          departure_time: leg.departure_time || leg.departure_date || '',
          arrival_airport: leg.arrival_airport || '',
          arrival_time: leg.arrival_time || leg.arrival_date || '',
          aircraft_type: leg.aircraft_type || ''
        };

        // Add seat and class information for this leg - now comes directly from leg data
        if (leg.seat !== undefined) {
          legInfo.seat = leg.seat || '';  // Use leg's seat assignment (may be null/empty)
        }
        if (leg.class_type !== undefined) {
          legInfo.class_type = leg.class_type || '';  // Use leg's class (may be null/empty)
        }

        // Fallback: If leg doesn't have seat/class, try passenger data (for backwards compatibility)
        if (!legInfo.seat && extractedData.passengers && extractedData.passengers.length > 0) {
          const passenger = extractedData.passengers[0]; // Use first passenger for now
          
          // Check if passenger has leg-specific seat/class data (old format)
          if (passenger.seats && passenger.seats[legIndex]) {
            legInfo.seat = passenger.seats[legIndex];
          } else if (passenger.seat) {
            legInfo.seat = passenger.seat;  // Fallback to general seat
          }
        }
        
        if (!legInfo.class_type && extractedData.passengers && extractedData.passengers.length > 0) {
          const passenger = extractedData.passengers[0];
          
          if (passenger.classes && passenger.classes[legIndex]) {
            legInfo.class_type = passenger.classes[legIndex];
          } else if (passenger.class_type || passenger.class) {
            legInfo.class_type = passenger.class_type || passenger.class;  // Fallback to general class
          }
        }

        // Final fallback for very old data format
        if (!legInfo.seat && extractedData.seat) {
          legInfo.seat = extractedData.seat;
        }
        if (!legInfo.class_type && (extractedData.class_type || extractedData.class)) {
          legInfo.class_type = extractedData.class_type || extractedData.class || '';
        }

        return legInfo;
      });
      setFlightLegs(legs);
      handleDetailsChange('flight_legs', legs);
    }
    
    // Process passenger data with automatic executive matching (remove class info)
    if (extractedData.passengers && extractedData.passengers.length > 0) {
      const passengers = extractedData.passengers.map(passenger => {
        const passengerData = {
          name: passenger.name || extractedData.traveler_name || '',
          confirmation_number: passenger.confirmation_number || extractedData.confirmation_number || '',
          frequent_flyer_number: passenger.frequent_flyer_number || '',
          executive_id: null, // Initialize as null
          // Enhanced ticket number extraction for passengers
          ticket_number: (() => {
            const possibleTicketFields = [
              'ticket_number', 'ticketNumber', 'ticket', 'ticket_no', 'ticketNo',
              'e_ticket_number', 'eTicketNumber', 'electronic_ticket_number',
              'document_number', 'documentNumber', 'document_no', 'documentNo',
              'tkt_number', 'tktNumber', 'tkt_no', 'tktNo'
            ];
            
            // First try passenger-specific fields
            for (const field of possibleTicketFields) {
              if (passenger[field]) {
                return String(passenger[field]).replace(/\s+/g, ' ').trim();
              }
            }
            
            // Use the main ticket number we found
            if (ticketNumber) {
              return String(ticketNumber).replace(/\s+/g, ' ').trim();
            }
            
            return '';
          })()
        };
        
        // Try to match passenger with trip executives and auto-select them
        if (passengerData.name && finalAvailableExecutives.length > 0) {
          const matchedExecutive = matchPassengerToExecutive(passengerData.name, finalAvailableExecutives);
          if (matchedExecutive) {
            passengerData.executive_id = matchedExecutive.id;
            
            // Auto-select this executive in the trip
            const currentAssigned = formData.component_data.assigned_executives || [];
            if (!currentAssigned.includes(matchedExecutive.id)) {
              handleDetailsChange('assigned_executives', [...currentAssigned, matchedExecutive.id]);
            }
            
          } else {
          }
        }
        
        return passengerData;
      });
      handleDetailsChange('passengers', passengers);
    } else if (extractedData.traveler_name) {
      // Single traveler case
      const singlePassenger = [{
        name: extractedData.traveler_name,
        confirmation_number: extractedData.confirmation_code || extractedData.confirmation_number || '',
        frequent_flyer_number: '',
        executive_id: null,
        // Use the main ticket number we found
        ticket_number: ticketNumber ? String(ticketNumber).replace(/\s+/g, ' ').trim() : ''
      }];
      
      // Try to match single traveler with trip executives and auto-select them
      if (finalAvailableExecutives.length > 0) {
        const matchedExecutive = matchPassengerToExecutive(extractedData.traveler_name, finalAvailableExecutives);
        if (matchedExecutive) {
          singlePassenger[0].executive_id = matchedExecutive.id;
          
          // Auto-select this executive in the trip
          const currentAssigned = formData.component_data.assigned_executives || [];
          if (!currentAssigned.includes(matchedExecutive.id)) {
            handleDetailsChange('assigned_executives', [...currentAssigned, matchedExecutive.id]);
          }
          
        }
      }
      
      handleDetailsChange('passengers', singlePassenger);
    }

  };

  // Private Flight Confirmation Upload Handler
  const handlePrivateFlightConfirmationUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploadingPrivateFlightConfirmation(true);

    try {
      // Add file to attached files first for UI display
      const newFile = {
        id: `temp-${Date.now()}-${Math.random()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        label: 'Private Flight Itinerary',
        url: URL.createObjectURL(file)
      };
      setAttachedFiles(prev => [...prev, newFile]);

      // Upload document to backend for AI parsing
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'private_flight');

      // Note: Using the default private_flight prompt from backend (ai_service.py lines 245-286)
      // which is specifically designed for private flight trip sheets with:
      // - ICAO airport codes and FBO name extraction for matching
      // - Passenger manifest parsing (numbered lists per leg)
      // - Trip number, tail number, aircraft type, operator name
      // - Date format: YYYY-MM-DD, Time format: HH:MM
      // DO NOT send extraction_schema - the default prompt is optimized for trip sheets

      const response = await axios.post('/api/documents/upload', formData);

      // Poll for parsing completion
      const documentId = response.data.document_id;
      if (!documentId) {
        throw new Error('No document ID returned from upload');
      }

      let attempts = 0;
      const maxAttempts = 60;

      const pollForCompletion = async () => {
        try {
          attempts++;

          const docResponse = await axios.get('/documents');
          const document = docResponse.data.find(doc => doc.id === documentId);

          if (!document) {
            if (attempts < maxAttempts) {
              setTimeout(pollForCompletion, 1000);
              return;
            } else {
              throw new Error('Document not found after upload');
            }
          }


          if (document.processing_status === 'completed') {
            if (document.parsed_data && document.parsed_data.success && document.parsed_data.data) {
              // Update the temporary file with proper server information
              setAttachedFiles(prev => prev.map(f =>
                f.id === newFile.id
                  ? {
                      ...f,
                      id: document.id,
                      url: document.file_path,
                      serverPath: document.file_path,
                      documentId: document.id
                    }
                  : f
              ));

              // Process the extracted private flight data
              processExtractedPrivateFlightData(document.parsed_data.data);
              setIsUploadingPrivateFlightConfirmation(false);
              setValidationError('');
            } else {
              const errorMessage = document.parsed_data?.error || 'Document parsing failed or returned no data';
              throw new Error(errorMessage);
            }
          } else if (document.processing_status === 'failed') {
            const errorMessage = document.parsed_data?.error || 'Document processing failed';
            throw new Error(errorMessage);
          } else if (document.processing_status === 'processing' && attempts < maxAttempts) {
            setTimeout(pollForCompletion, 2000);
          } else {
            throw new Error(`Document processing timeout after ${attempts} attempts`);
          }
        } catch (pollError) {
          console.error('Error polling document status:', pollError);
          setValidationError(`Failed to process document: ${pollError.message}`);
          setIsUploadingPrivateFlightConfirmation(false);
        }
      };

      // Start polling
      setTimeout(pollForCompletion, 500);

    } catch (error) {
      console.error('Error importing private flight itinerary:', error);
      setValidationError(`Failed to import itinerary: ${error.message}`);
      setIsUploadingPrivateFlightConfirmation(false);
    }

    if (e.target) e.target.value = '';
  };

  // Process Extracted Private Flight Data
  const processExtractedPrivateFlightData = (extractedData) => {

    // Process aircraft details
    if (extractedData.tail_number) {
      handlePrivateFlightDetailsChange('tail_number', extractedData.tail_number);
    }

    if (extractedData.aircraft_type) {
      handlePrivateFlightDetailsChange('aircraft_type', extractedData.aircraft_type);
    }

    // Handle operator information
    // Default schema uses 'operator_name', custom schema uses 'operator_type' and 'charter_operator_name'
    if (extractedData.operator_name) {
      // Default schema format - use operator_name as charter_operator_name
      handlePrivateFlightDetailsChange('charter_operator_name', extractedData.operator_name);
      // Assume charter if operator name is provided
      handlePrivateFlightDetailsChange('operator_type', 'charter');
    } else if (extractedData.operator_type) {
      // Custom schema format (legacy)
      handlePrivateFlightDetailsChange('operator_type', extractedData.operator_type);
    }

    if (extractedData.charter_operator_name) {
      handlePrivateFlightDetailsChange('charter_operator_name', extractedData.charter_operator_name);
    }

    if (extractedData.charter_operator_phone) {
      handlePrivateFlightDetailsChange('charter_operator_phone', extractedData.charter_operator_phone);
    }

    // Handle trip number if available
    if (extractedData.trip_number) {
      handlePrivateFlightDetailsChange('trip_number', extractedData.trip_number);
    }

    // Process flight legs
    if (extractedData.legs && extractedData.legs.length > 0) {
      const legs = extractedData.legs.map((leg, legIndex) => {
        return {
          departure_airport_name: leg.departure_airport_name || '',
          departure_airport_iata: leg.departure_airport_iata || '',
          departure_airport_icao: leg.departure_airport_icao || '',
          arrival_airport_name: leg.arrival_airport_name || '',
          arrival_airport_iata: leg.arrival_airport_iata || '',
          arrival_airport_icao: leg.arrival_airport_icao || '',
          departure_date: leg.departure_date || '',
          departure_time: leg.departure_time || '',
          arrival_date: leg.arrival_date || '',
          arrival_time: leg.arrival_time || '',
          // FBO information (from AI extraction)
          departure_fbo_name: leg.departure_fbo_name || '',
          departure_fbo_address: leg.departure_fbo_address || '',
          departure_fbo_phone: leg.departure_fbo_phone || '',
          arrival_fbo_name: leg.arrival_fbo_name || '',
          arrival_fbo_address: leg.arrival_fbo_address || '',
          arrival_fbo_phone: leg.arrival_fbo_phone || '',
          // FBO matched data (from backend FBO database matching)
          departure_fbo_id: leg.departure_fbo_id || null,
          departure_fbo_matched: leg.departure_fbo_matched || null,
          arrival_fbo_id: leg.arrival_fbo_id || null,
          arrival_fbo_matched: leg.arrival_fbo_matched || null,
          // Store leg-specific passengers if available
          leg_passengers: leg.passengers || []
        };
      });
      setPrivateFlightLegs(legs);
    }

    // Process passengers - add all unique passengers from the document
    // IMPORTANT: Only add passengers from the parsed document, NOT trip executives
    if (extractedData.passengers && extractedData.passengers.length > 0) {
      const passengers = extractedData.passengers.map(passenger => ({
        tempId: `passenger-${Date.now()}-${Math.random()}`,
        name: passenger.name || '',
        special_requests: passenger.special_requests || '',
        traveler_type: 'contact', // Default to contact type
        email: '',
        company: ''
      }));

      // Filter out any passengers that match trip executives by name
      const filteredPassengers = passengers.filter(passenger => {
        const passengerName = passenger.name.toLowerCase().trim();
        return !tripAssignedExecutives.some(exec => {
          const execName = (exec.name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim()).toLowerCase();
          return passengerName === execName;
        });
      });

      // Replace (not append) the additional passengers list with parsed passengers
      // This ensures we don't accidentally keep executives or other pre-existing entries
      setAdditionalPassengers(filteredPassengers);
      if (passengers.length !== filteredPassengers.length) {
      }
    }

  };

  // Validation function
  const validateCommercialFlight = () => {
    const errors = [];
    
    // Check for at least one flight leg
    if (flightLegs.length === 0) {
      errors.push("Please add at least one flight leg.");
    }
    
    // Validate each flight leg
    flightLegs.forEach((leg, index) => {
      if (!leg.departure_airport) {
        errors.push(`Flight leg ${index + 1}: Departure airport is required.`);
      }
      if (!leg.arrival_airport) {
        errors.push(`Flight leg ${index + 1}: Arrival airport is required.`);
      }
      if (!leg.departure_time) {
        errors.push(`Flight leg ${index + 1}: Departure time is required.`);
      }
      if (!leg.arrival_time) {
        errors.push(`Flight leg ${index + 1}: Arrival time is required.`);
      }
    });
    
    return errors;
  };

  // Restaurant-Specific Validation
  const validateRestaurantForm = () => {
    const errors = [];
    
    // Required fields validation - only require date/time if not TBD
    if (!formData.date_is_tbd && !formData.time_is_tbd && !formData.start_datetime) {
      errors.push("Reservation Date & Time is required for Restaurant (unless marked as TBD).");
    }
    
    if (!formData.title && !formData.details?.restaurant_name) {
      errors.push("Restaurant name is required.");
    }
    
    // Business logic validation
    // Reservation time validation - removed past date restriction to allow historical reservations
    // Restaurant reservations can be in the past for recording historical visits
    
    // Party size validation
    if (formData.component_data?.party_size && (formData.component_data.party_size < 1 || formData.component_data.party_size > 50)) {
      errors.push("Party size must be between 1 and 50 people.");
    }
    
    // Expected duration validation
    const duration = formData.details?.expected_duration;
    if (duration && (duration < 15 || duration > 480)) { // 15 minutes to 8 hours
      errors.push("Expected duration must be between 15 minutes and 8 hours.");
    }
    
    return errors;
  };

  // Hotel-Specific Validation
  const validateHotelForm = () => {
    const errors = [];
    
    // Required fields validation
    if (!formData.component_data?.hotel_name) {
      errors.push("Hotel name is required");
    }
    
    if (!formData.component_data?.check_in) {
      errors.push("Check-in date is required");
    }
    
    if (!formData.component_data?.check_out) {
      errors.push("Check-out date is required");
    }

    // Executive assignment validation — not applicable in speaker_bureau mode
    // (program is scoped to a single speaker; the exec selector is hidden)
    if (!isSpeakerBureau && (!formData.component_data?.assigned_executives || formData.component_data.assigned_executives.length === 0)) {
      errors.push("At least one executive must be assigned to this hotel booking");
    }

    // Guest count validation
    if (!formData.component_data?.total_guests || formData.component_data.total_guests < 1) {
      errors.push("Total number of guests must be at least 1");
    }
    
    // Date validation
    if (formData.component_data?.check_in && formData.component_data?.check_out) {
      const checkIn = new Date(formData.component_data.check_in);
      const checkOut = new Date(formData.component_data.check_out);
      
      if (checkOut <= checkIn) {
        errors.push("Check-out date must be after check-in date");
      }
      
      // Check-in date validation - removed past date restriction to allow historical hotel stays
      // Hotel bookings can be in the past for recording historical stays
    }
    
    // Room validation
    const numberOfRooms = formData.component_data?.number_of_rooms || 1;
    if (numberOfRooms < 1) {
      errors.push("Number of rooms must be at least 1");
    }

    // Room details validation
    const rooms = formData.component_data?.room_details || [];
    if (rooms.length > 0) {
      const totalAllocatedGuests = rooms.reduce((sum, room) => sum + (room.guests || 0), 0);
      const totalGuests = formData.component_data?.total_guests || 0;

      if (totalAllocatedGuests !== totalGuests) {
        errors.push(`Guest allocation mismatch: ${totalAllocatedGuests} allocated vs ${totalGuests} total guests`);
      }

      // Room type and bed preference are optional - only validate guest count
      rooms.forEach((room, index) => {
        if (!room.guests || room.guests < 1) {
          errors.push(`Room ${index + 1} must have at least 1 guest`);
        }
      });
    }
    
    return errors;
  };

  // Meeting-Specific Validation
  const validateMeetingForm = () => {
    const errors = [];
    
    // Required fields validation - but allow TBD meetings
    if (!formData.start_datetime && !isDateTBD && !isStartTimeTBD) {
      errors.push("Meeting date and time is required (or set as TBD)");
    }
    
    // Primary executive is now optional - not required for meetings
    // if (!formData.component_data?.primary_executive_id) {
    //   if (!tripExecutives || tripExecutives.length === 0) {
    //     errors.push("No executives are assigned to this trip. Please assign an executive to the trip first.");
    //   } else {
    //     errors.push("Please select a Primary Executive for the meeting.");
    //   }
    // }
    
    // Attendees are optional - no validation required
    
    // Location is optional - can be TBD
    
    // Virtual meeting validation
    if (formData.component_data?.meeting_type === 'virtual' || formData.component_data?.meeting_type === 'hybrid') {
      if (!formData.component_data?.platform) {
        errors.push("Video conference platform is required for virtual meetings");
      }
    }
    
    // Date validation - removed past date restriction to allow historical meetings
    // Meeting dates can be in the past for recording historical meetings
    
    // End time validation - only if both times are set (not TBD)
    if (formData.end_datetime && formData.start_datetime && !isStartTimeTBD && !isEndTimeTBD) {
      const startTime = new Date(formData.start_datetime);
      const endTime = new Date(formData.end_datetime);
      
      if (endTime <= startTime) {
        errors.push("Meeting end time must be after start time");
      }
    }
    
    return errors;
  };

  const validateRentalCarForm = () => {
    const errors = [];
    
    // More lenient validation - only check critical fields
    if (!formData.start_datetime) {
      errors.push("Pickup date and time is required");
    }
    
    if (!formData.end_datetime) {
      errors.push("Drop-off date and time is required");
    }
    
    // Make other fields optional for now to avoid save failures
    if (!formData.component_data?.pickup_location && !formData.location) {
      errors.push("Pickup location is required");
    }
    
    // Date validation
    if (formData.start_datetime && formData.end_datetime) {
      const pickupDate = new Date(formData.start_datetime);
      const dropoffDate = new Date(formData.end_datetime);
      
      if (dropoffDate <= pickupDate) {
        errors.push("Drop-off date must be after pickup date");
      }
    }
    
    return errors;
  };

  // Private Flight Validation
  const validatePrivateFlightForm = () => {
    const errors = [];

    // Note: Request to Flight Department field is optional
    // Note: Date and time fields are optional and can be TBD

    return errors;
  };

  // Chauffeur Service Validation
  const validateChauffeurServiceForm = () => {
    const errors = [];

    if (!formData.start_datetime) {
      errors.push("Pickup date and time is required");
    }

    if (!formData.component_data?.pickup_location && !formData.location) {
      errors.push("Pickup location is required");
    }

    if (!formData.component_data?.dropoff_location) {
      errors.push("Dropoff location is required");
    }

    return errors;
  };

  // Airport VIP Service Validation
  const validateAirportVipServiceForm = () => {
    const errors = [];
    const cd = formData.component_data || {};

    if (!cd.linked_segment_id) {
      errors.push("Please select the flight segment this VIP service is for.");
    }
    if (!cd.service_direction) {
      errors.push("Please select Arrival or Departure.");
    }
    if (!cd.provider_name || !cd.provider_name.trim()) {
      errors.push("VIP service provider is required.");
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setValidationError('');

    try {
      // Validate commercial flight specific requirements
      if (componentType === 'commercial_flight' || componentType === 'flight') {
        const validationErrors = validateCommercialFlight();
        if (validationErrors.length > 0) {
          setValidationError(validationErrors.join(' '));
          setLoading(false);
          return;
        }
      }

      // Validate hotel specific requirements
      if (componentType === 'hotel') {
        const validationErrors = validateHotelForm();
        if (validationErrors.length > 0) {
          setValidationError(validationErrors[0]);
          setLoading(false);
          return;
        }
      }

      // Validate meeting specific requirements
      if (componentType === 'meeting') {
        const validationErrors = validateMeetingForm();
        if (validationErrors.length > 0) {
          setValidationError(validationErrors[0]);
          setLoading(false);
          return;
        }
      }

      // Validate speaker meeting specific requirements
      if (componentType === 'speaker_meeting') {
        const cd = formData.component_data || {};
        const hasTitle = (formData.title && formData.title.trim()) || (cd.program_topic && cd.program_topic.trim());
        if (!hasTitle) {
          setValidationError('Please provide a Topic / Presentation Title for the speaker program.');
          setLoading(false);
          return;
        }
      }

      // Validate restaurant specific requirements
      if (componentType === 'restaurant') {
        const validationErrors = validateRestaurantForm();
        if (validationErrors.length > 0) {
          setValidationError(validationErrors[0]);
          setLoading(false);
          return;
        }
      }

      // Validate rental car specific requirements
      if (componentType === 'rental_car') {

        const validationErrors = validateRentalCarForm();
        if (validationErrors.length > 0) {
          setValidationError(validationErrors.join(' '));
          setLoading(false);
          return;
        }
      }

      // Validate chauffeur service specific requirements
      if (componentType === 'chauffeur_service') {

        const validationErrors = validateChauffeurServiceForm();
        if (validationErrors.length > 0) {
          setValidationError(validationErrors.join(' '));
          setLoading(false);
          return;
        }
      }

      // Validate airport VIP service specific requirements
      if (componentType === 'airport_vip_service') {
        const validationErrors = validateAirportVipServiceForm();
        if (validationErrors.length > 0) {
          setValidationError(validationErrors.join(' '));
          setLoading(false);
          return;
        }
      }

      // Validate private flight specific requirements
      if (componentType === 'private_flight') {

        const validationErrors = validatePrivateFlightForm();
        if (validationErrors.length > 0) {
          setValidationError(validationErrors[0]);
          setLoading(false);
          return;
        }
      }

      // Validate event specific requirements
      if (componentType === 'event') {
        const validationErrors = [];

        if (!formData.title || !formData.title.trim()) {
          validationErrors.push('Event title is required.');
        }

        if (!formData.location || !formData.location.trim()) {
          validationErrors.push('Event location is required.');
        }

        // Require date/time only if not marked as TBD
        if (!isDateTBD && !isStartTimeTBD && !formData.start_datetime) {
          validationErrors.push('Event date and time are required (or mark as TBD).');
        }

        if (validationErrors.length > 0) {
          setValidationError(validationErrors.join(' '));
          setLoading(false);
          return;
        }
      }

      // Build updated component data
      let componentData = { ...formData };
      let detailsUpdates = { ...componentData.component_data };
      
      // Map frontend component types to backend component types if needed
      const componentTypeMapping = {
        // Note: Most component types now match directly between frontend and backend
        // 'rental_car' stays as 'rental_car' (updated in migration)
        // 'commercial_flight' stays as 'commercial_flight' (updated in migration)
        // 'private_flight' stays as 'private_flight' (updated in migration)
      };
      
      // Use mapped component type or original if no mapping exists
      componentData.component_type = componentTypeMapping[componentType] || componentType;
      
      // Update flight legs for commercial flights
      if (componentType === 'commercial_flight' || componentType === 'flight') {
        detailsUpdates.flight_legs = flightLegs;

        // Auto-update component title and times based on flight legs
        if (flightLegs.length > 0) {
          const firstLeg = flightLegs[0];
          const lastLeg = flightLegs[flightLegs.length - 1];

          if (!componentData.title || componentData.title.includes('→')) {
            componentData.title = `${firstLeg.departure_airport} → ${lastLeg.arrival_airport}`;
          }

          // Helper function to check if a date value is valid
          const isValidDate = (dateValue) => {
            if (!dateValue || dateValue === 'null' || dateValue === 'undefined') {
              return false;
            }
            try {
              const date = new Date(dateValue);
              return !isNaN(date.getTime());
            } catch (error) {
              return false;
            }
          };

          // Only set start_datetime if departure_time is valid, otherwise set to null for TBD
          componentData.start_datetime = isValidDate(firstLeg.departure_time) ? firstLeg.departure_time : null;
          componentData.end_datetime = isValidDate(lastLeg.arrival_time) ? lastLeg.arrival_time : null;
          componentData.location = firstLeg.departure_airport || '';
        }
      }
      
      // Auto-populate title and process passengers for private flights
      if (componentType === 'private_flight') {
        // Combine separate date/time fields into datetime values
        const combineDateTime = (date, time, timeFormat) => {
          
          if (!date || !time || time.toLowerCase() === 'tbd') {
            return null;  // Return null for TBD times
          }
          
          // Parse the time input
          let parsedTime = time.trim();
          
          // Handle AM/PM format conversion to 24H
          if (timeFormat === 'AM' || timeFormat === 'PM') {
            // Convert AM/PM time to 24H format
            const timeParts = parsedTime.split(':');
            if (timeParts.length === 2) {
              let hours = parseInt(timeParts[0]);
              const minutes = timeParts[1];
              
              if (timeFormat === 'PM' && hours !== 12) {
                hours += 12;
              } else if (timeFormat === 'AM' && hours === 12) {
                hours = 0;
              }
              
              parsedTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
            }
          }
          
          // Ensure time has seconds
          if (!parsedTime.includes(':')) {
            return null; // Invalid time format
          }
          
          const timeParts = parsedTime.split(':');
          if (timeParts.length === 2) {
            parsedTime += ':00'; // Add seconds
          }
          
          const result = `${date}T${parsedTime}`;
          return result;
        };
        
        
        // Set departure and arrival datetime from separate fields
        try {
          const departureDateTime = combineDateTime(
            formData.component_data?.departure_date,
            formData.component_data?.departure_time,
            formData.component_data?.departure_time_format
          );
          const arrivalDateTime = combineDateTime(
            formData.component_data?.arrival_date,
            formData.component_data?.arrival_time,
            formData.component_data?.arrival_time_format
          );
          
          
          if (departureDateTime) {
            componentData.start_datetime = departureDateTime;
          }
          if (arrivalDateTime) {
            componentData.end_datetime = arrivalDateTime;
          }
        } catch (error) {
          console.error('🛩️ Error parsing datetime:', error);
          setValidationError('Invalid date/time format. Please check your time entries.');
          setLoading(false);
          return;
        }
        
        if (!componentData.title && detailsUpdates.departure_airport_name && detailsUpdates.arrival_airport_name) {
          componentData.title = `${detailsUpdates.departure_airport_name} to ${detailsUpdates.arrival_airport_name}`;
        } else if (!componentData.title) {
          componentData.title = "Private Flight";
        }
        // Note: additional passengers are processed later and added to processedData.component_data
      }
      
      // Auto-update component fields for hotel bookings
      if (componentType === 'hotel') {
        // Ensure component dates match booking dates
        if (detailsUpdates.check_in && !componentData.start_datetime) {
          // check_in now contains full datetime, use it directly or ensure it has time component
          componentData.start_datetime = detailsUpdates.check_in.includes('T') 
            ? detailsUpdates.check_in 
            : `${detailsUpdates.check_in}T15:00`;
        }
        
        if (detailsUpdates.check_out && !componentData.end_datetime) {
          // check_out now contains full datetime, use it directly or ensure it has time component
          componentData.end_datetime = detailsUpdates.check_out.includes('T') 
            ? detailsUpdates.check_out 
            : `${detailsUpdates.check_out}T11:00`;
        }
        
        // Ensure title is set
        if (!componentData.title && detailsUpdates.hotel_name) {
          componentData.title = detailsUpdates.hotel_name;
        }
        
        // Update address if set in details
        if (detailsUpdates.address && !componentData.address) {
          componentData.address = detailsUpdates.address;
        }

        // Ensure executive assignment is propagated
        if (detailsUpdates.assigned_executives && detailsUpdates.assigned_executives.length > 0) {
          componentData.assigned_executives = detailsUpdates.assigned_executives;
        }

        // Add guest information to notes if significant details exist
        if (detailsUpdates.total_guests && detailsUpdates.room_details) {
          const guestSummary = `${detailsUpdates.total_guests} guests in ${detailsUpdates.number_of_rooms || 1} room(s)`;
          if (!componentData.notes || !componentData.notes.includes(guestSummary)) {
            componentData.notes = componentData.notes 
              ? `${componentData.notes}\n\nGuest Details: ${guestSummary}` 
              : `Guest Details: ${guestSummary}`;
          }
        }
      }
      
      // Auto-update component fields for rental car bookings
      if (componentType === 'rental_car') {
        
        // Copy rental car data from formData.component_data to detailsUpdates
        if (formData.component_data?.pickup_location) {
          detailsUpdates.pickup_location = formData.component_data.pickup_location;
        }
        if (formData.component_data?.dropoff_location) {
          detailsUpdates.dropoff_location = formData.component_data.dropoff_location;
        }
        if (formData.component_data?.vehicle_type) {
          detailsUpdates.vehicle_type = formData.component_data.vehicle_type;
        }
        if (formData.component_data?.rental_car_company) {
          detailsUpdates.rental_car_company = formData.component_data.rental_car_company;
        }
        if (formData.component_data?.primary_renter_name) {
          detailsUpdates.primary_renter_name = formData.component_data.primary_renter_name;
        }
        if (formData.component_data?.primary_renter_executive_id) {
          detailsUpdates.primary_renter_executive_id = formData.component_data.primary_renter_executive_id;
        }
        if (formData.confirmation_number) {
          detailsUpdates.confirmation_number = formData.confirmation_number;
        }
        
        
        // Ensure component dates match rental dates
        if (componentData.start_datetime) {
          detailsUpdates.pickup_date = componentData.start_datetime;
          detailsUpdates.pickup_datetime = componentData.start_datetime;
        }
        if (componentData.end_datetime) {
          detailsUpdates.dropoff_date = componentData.end_datetime;
          detailsUpdates.dropoff_datetime = componentData.end_datetime;
        }
        
        // Auto-update component title based on rental details
        if (detailsUpdates.rental_car_company && detailsUpdates.vehicle_type) {
          if (!componentData.title || componentData.title.includes('@ ')) {
            componentData.title = `${detailsUpdates.rental_car_company} ${detailsUpdates.vehicle_type}`;
          }
        }
        
        // Set location to pickup location
        if (detailsUpdates.pickup_location && !componentData.location) {
          componentData.location = detailsUpdates.pickup_location;
        }
        
        // Set confirmation number at component level
        if (detailsUpdates.confirmation_number && !componentData.confirmation_number) {
          componentData.confirmation_number = detailsUpdates.confirmation_number;
        }
      }
      
      // Auto-update component fields for meeting bookings
      if (componentType === 'meeting') {
        // Process attendees - ensure they have proper structure
        if (attendees.length > 0) {
          const processedAttendees = attendees.map(attendee => {
            const { tempId, ...rest } = attendee;
            return rest;
          });
          detailsUpdates.meeting_attendees = processedAttendees;
        }

        // Ensure primary traveler is set for meetings
        if (detailsUpdates.primary_executive_id) {
          // Use the selected primary executive
          componentData.primary_traveler_id = detailsUpdates.primary_executive_id;
          componentData.primary_traveler_type = 'executive';
        } else {
          // Don't auto-assign - let user choose manually
        }

        // Auto-update component title if not set
        if (!componentData.title) {
          const meetingTitle = `Meeting${formData.location ? ` at ${formData.location}` : ''}`;
          componentData.title = meetingTitle;
        }
      }

      // Auto-update component fields for speaker meetings
      if (componentType === 'speaker_meeting') {
        // Derive a title from the program topic when not explicitly set
        if (!componentData.title || !componentData.title.trim()) {
          componentData.title = detailsUpdates.program_topic
            || (detailsUpdates.sponsor ? `${detailsUpdates.sponsor} Speaker Program` : 'Speaker Meeting');
        }
        // Keep program_id and confirmation_number in sync
        if (detailsUpdates.program_id && !componentData.confirmation_number) {
          componentData.confirmation_number = detailsUpdates.program_id;
        }
        // Build a combined location label from venue parts when location is empty
        if ((!componentData.location || !componentData.location.trim()) && !detailsUpdates.is_virtual) {
          const parts = [detailsUpdates.venue_name, detailsUpdates.venue_address].filter(Boolean);
          if (parts.length) componentData.location = parts.join(', ');
        }
      }

      // Auto-update component fields for memos
      if (componentType === 'memo') {
        // Process attendees - ensure they have proper structure
        if (attendees.length > 0) {
          const processedAttendees = attendees.map(attendee => {
            const { tempId, ...rest } = attendee;
            return rest;
          });
          detailsUpdates.memo_attendees = processedAttendees;
        }

        // Set primary executive if selected
        if (detailsUpdates.primary_executive_id) {
          componentData.primary_traveler_id = detailsUpdates.primary_executive_id;
          componentData.primary_traveler_type = 'executive';
        }
      }

      // Auto-update component fields for restaurant reservations
      if (componentType === 'restaurant') {
        // Process attendees if any
        if (attendees.length > 0) {
          const processedAttendees = attendees.map(attendee => {
            const { tempId, ...rest } = attendee;
            return rest;
          });
          detailsUpdates.restaurant_attendees = processedAttendees;
        }
        
        // Process reservation holder data
        if (formData.component_data?.reservation_holder_id) {
          detailsUpdates.reservation_holder_id = formData.component_data.reservation_holder_id;
          detailsUpdates.reservation_holder_name = formData.component_data.reservation_holder_name;
        }
        
        // Calculate end_datetime based on start_datetime + expected_duration
        if (componentData.start_datetime && detailsUpdates.expected_duration) {
          const startDate = new Date(componentData.start_datetime);
          if (!isNaN(startDate.getTime())) {
            const durationMinutes = parseInt(detailsUpdates.expected_duration) || 120; // Default 2 hours
            const endDate = new Date(startDate.getTime() + (durationMinutes * 60 * 1000));
            componentData.end_datetime = endDate.toISOString().slice(0, 16); // Format for datetime-local
          }
        }
        
        // Auto-set title from restaurant name if not provided
        if (!componentData.title && detailsUpdates.restaurant_name) {
          componentData.title = detailsUpdates.restaurant_name;
        }
        
        // Ensure primary traveler is set
        if (!componentData.primary_traveler_id && finalAvailableExecutives.length > 0) {
          componentData.primary_traveler_id = finalAvailableExecutives[0].id;
          componentData.primary_traveler_type = 'executive';
        }
      }
      
      // Update attached files
      if (attachedFiles.length > 0) {
        detailsUpdates.attached_files = attachedFiles.map((file) => {
          const { id: fileId, tempId, ...rest } = file;
          return { ...rest, id: fileId };
        });
      }
      
      componentData.component_data = detailsUpdates;

      // Validate required fields based on component type
      if (componentType === 'commercial_flight' || componentType === 'flight') {
        // For commercial flights, we need flight legs
        if (flightLegs.length === 0) {
          throw new Error('Please add flight legs or import a flight confirmation');
        }
      } else if (componentType === 'private_flight') {
        // For private flights, we need at least one flight leg
        if (privateFlightLegs.length === 0) {
          throw new Error('Please add at least one flight leg for the private flight');
        }
      } else {
        // For other components, validate standard required fields
        // Private flights, hotels, and airport VIP services don't require title (auto-generated or handled separately)
        if (componentType !== 'private_flight' && componentType !== 'hotel' && componentType !== 'airport_vip_service' && !componentData.title) {
          throw new Error('Title is required');
        }

        // Start datetime is optional for meetings (can be TBD), memos (can be TBD), events (can be TBD), rental cars (handled in their section), private flights (can be TBD), hotels (use check-in date), airport VIP services (derived from linked segment), and handled separately for restaurants
        if (componentType !== 'meeting' && componentType !== 'memo' && componentType !== 'event' && componentType !== 'restaurant' && componentType !== 'rental_car' && componentType !== 'private_flight' && componentType !== 'hotel' && componentType !== 'airport_vip_service' && !componentData.start_datetime) {
          throw new Error('Start date and time are required');
        }
      }

      // Convert dates to ISO format - omit null/invalid values entirely
      const processedData = { ...componentData };

      // Add confidentiality settings
      processedData.is_confidential = isConfidential;
      processedData.access_list = accessList;

      // Ensure component_data is explicitly included (for rental cars and other components)
      if (componentData.component_data) {
        processedData.component_data = componentData.component_data;
      }

      // Handle start_datetime - check TBD states for meetings, memos, and events
      if (componentType === 'meeting' || componentType === 'memo' || componentType === 'event') {
        // For meetings/memos/events, handle TBD states and ensure time_is_tbd flag is correctly set

        // Always set the time_is_tbd flag based on current TBD states
        processedData.component_data = {
          ...processedData.component_data,
          time_is_tbd: isStartTimeTBD || isDateTBD
        };

        // Also set component-level TBD flags for Timeline2 detection
        processedData.date_is_tbd = isDateTBD;
        processedData.time_is_tbd = isStartTimeTBD || isDateTBD;

        if (isDateTBD) {
          // If date is TBD, omit start_datetime entirely (no date at all)
          delete processedData.start_datetime;
        } else if (isStartTimeTBD) {
          // Date is set but time is TBD - omit start_datetime entirely and store date in component_data
          if (componentData.start_datetime && componentData.start_datetime.includes('-TBD-TIME')) {
            // Extract just the date part for component_data
            const dateOnly = componentData.start_datetime.split('-TBD-TIME')[0];
            
            // Completely omit start_datetime from backend submission
            delete processedData.start_datetime;
            
            // Store the date information in component_data with TBD flag
            processedData.component_data = {
              ...processedData.component_data,
              meeting_date: dateOnly,
              time_is_tbd: true
            };
          } else if (formData.start_datetime && formData.start_datetime !== '') {
            // Use formData if componentData doesn't have TBD format (conversion from fixed to TBD)
            try {
              const currentDate = new Date(formData.start_datetime);
              const dateStr = format(currentDate, 'yyyy-MM-dd');
              
              // Completely omit start_datetime from backend submission
              delete processedData.start_datetime;
              
              // Store the date information in component_data with TBD flag
              processedData.component_data = {
                ...processedData.component_data,
                meeting_date: dateStr,
                time_is_tbd: true
              };
            } catch (error) {
              delete processedData.start_datetime;
            }
          } else {
            delete processedData.start_datetime;
          }
        } else if (componentData.start_datetime && componentData.start_datetime !== '') {
          
          // Check if this is a TBD time format
          if (componentData.start_datetime.includes('-TBD-TIME')) {
            delete processedData.start_datetime;
          } else {
            const startDate = new Date(componentData.start_datetime);
            if (!isNaN(startDate.getTime())) {
              // Preserve local time format instead of converting to UTC
              processedData.start_datetime = componentData.start_datetime.includes('T') ? 
                componentData.start_datetime : 
                startDate.toISOString();
              
              // Ensure time_is_tbd is false for fixed meetings
              processedData.component_data = {
                ...processedData.component_data,
                time_is_tbd: false
              };
            } else {
              delete processedData.start_datetime;
            }
          }
        } else {
          delete processedData.start_datetime;
        }
      } else {
        // For non-meetings, handle normally
        if (componentData.start_datetime && componentData.start_datetime !== '') {
          const startDate = new Date(componentData.start_datetime);
          if (!isNaN(startDate.getTime())) {
            // Preserve local time format instead of converting to UTC
            processedData.start_datetime = componentData.start_datetime.includes('T') ?
              componentData.start_datetime :
              startDate.toISOString();
            // Clear TBD flags when a valid datetime is set
            processedData.date_is_tbd = false;
            processedData.time_is_tbd = false;
            // Also clear TBD flags in component_data for Timeline2 compatibility
            // (Timeline2 checks both component-level and component_data-level flags)
            if (processedData.component_data) {
              processedData.component_data = {
                ...processedData.component_data,
                time_is_tbd: false,
                date_is_tbd: false
              };
            }
          } else {
            delete processedData.start_datetime;
          }
        } else {
          delete processedData.start_datetime;
        }
      }

      // Handle end_datetime - check TBD states for meetings, memos, and events
      if (componentType === 'meeting' || componentType === 'memo' || componentType === 'event') {
        // For meetings/memos/events, check TBD flag for end time
        if (isEndTimeTBD) {
          // If end time is TBD, omit end_datetime entirely
          delete processedData.end_datetime;
        } else if (componentData.end_datetime && componentData.end_datetime !== '') {
          const endDate = new Date(componentData.end_datetime);
          if (!isNaN(endDate.getTime())) {
            // Preserve local time format instead of converting to UTC
            processedData.end_datetime = componentData.end_datetime.includes('T') ? 
              componentData.end_datetime : 
              endDate.toISOString();
          } else {
            delete processedData.end_datetime;
          }
        } else {
          delete processedData.end_datetime;
        }
      } else {
        // For non-meetings, handle normally
        if (componentData.end_datetime && componentData.end_datetime !== '') {
          const endDate = new Date(componentData.end_datetime);
          if (!isNaN(endDate.getTime())) {
            // Preserve local time format instead of converting to UTC
            processedData.end_datetime = componentData.end_datetime.includes('T') ? 
              componentData.end_datetime : 
              endDate.toISOString();
          } else {
            delete processedData.end_datetime;
          }
        } else {
          delete processedData.end_datetime;
        }
      }

      // Clean up undefined fields to prevent backend validation issues
      Object.keys(processedData).forEach(key => {
        if (processedData[key] === undefined) {
          delete processedData[key];
        }
      });

      // For commercial flights, ensure we have at least a title (times can be TBD/null)
      if (componentType === 'commercial_flight' || componentType === 'flight') {
        if (!processedData.title) {
          throw new Error('Unable to generate flight details from flight legs');
        }
        // Note: start_datetime and end_datetime can be null for TBD times
      } else {
        // For other components, ensure required fields exist (except meetings, memos, rental cars, private flights, and restaurants which handle their own validation including TBD support)
        if (componentType !== 'meeting' && componentType !== 'memo' && componentType !== 'rental_car' && componentType !== 'private_flight' && componentType !== 'restaurant' && componentType !== 'event' && componentType !== 'airport_vip_service' && !processedData.start_datetime) {
          throw new Error('Valid start date and time are required');
        }
      }

      // Normalize private_flight operator fields into component_data to guarantee persistence
      if (componentType === 'private_flight') {
        processedData.component_data = { ...(processedData.component_data || {}) };
        if (formData?.component_data?.operator_type !== undefined) {
          processedData.component_data.operator_type = formData.component_data.operator_type;
        }
        if (formData?.component_data?.charter_operator_name !== undefined) {
          processedData.component_data.charter_operator_name = formData.component_data.charter_operator_name;
        }
        if (formData?.component_data?.charter_operator_phone !== undefined) {
          processedData.component_data.charter_operator_phone = formData.component_data.charter_operator_phone;
        }
        if (formData?.component_data?.flight_department_request !== undefined) {
          processedData.component_data.flight_department_request = formData.component_data.flight_department_request;
        }
        
        // Add private flight legs to component_data for persistence
        // Note: Each leg contains its own leg_passengers array for per-leg passenger assignment
        if (privateFlightLegs && privateFlightLegs.length > 0) {
          processedData.component_data.private_flight_legs = privateFlightLegs;

          // Recompute start_datetime for the component from the first leg's anchor setting
          // so the DB record stays consistent with the frontend display logic.
          const firstLeg = privateFlightLegs[0];
          const firstHasDep = firstLeg.departure_date && firstLeg.departure_time &&
            firstLeg.departure_time.trim() !== '' && firstLeg.departure_time.toLowerCase() !== 'tbd';
          const firstHasArr = firstLeg.arrival_date && firstLeg.arrival_time &&
            firstLeg.arrival_time.trim() !== '' && firstLeg.arrival_time.toLowerCase() !== 'tbd';
          let anchorDatetime = null;
          if (firstHasDep && firstHasArr) {
            anchorDatetime = firstLeg.anchor_to_arrival
              ? `${firstLeg.arrival_date}T${firstLeg.arrival_time}:00`
              : `${firstLeg.departure_date}T${firstLeg.departure_time}:00`;
          } else if (firstHasDep) {
            anchorDatetime = `${firstLeg.departure_date}T${firstLeg.departure_time}:00`;
          } else if (firstHasArr) {
            anchorDatetime = `${firstLeg.arrival_date}T${firstLeg.arrival_time}:00`;
          }
          if (anchorDatetime) {
            processedData.start_datetime = anchorDatetime;
          }
        }
      }

      // Normalize restaurant fields into component_data to guarantee persistence
      if (componentType === 'restaurant') {
        processedData.component_data = { ...(processedData.component_data || {}) };
        // Ensure all restaurant-specific fields are preserved during updates
        if (formData?.component_data?.cuisine_type !== undefined) {
          processedData.component_data.cuisine_type = formData.component_data.cuisine_type;
        }
        if (formData?.component_data?.restaurant_name !== undefined) {
          processedData.component_data.restaurant_name = formData.component_data.restaurant_name;
        }
        if (formData?.component_data?.address !== undefined) {
          processedData.component_data.address = formData.component_data.address;
        }
        if (formData?.component_data?.phone !== undefined) {
          processedData.component_data.phone = formData.component_data.phone;
        }
        if (formData?.component_data?.party_size !== undefined) {
          processedData.component_data.party_size = formData.component_data.party_size;
        }
        if (formData?.component_data?.place_id !== undefined) {
          processedData.component_data.place_id = formData.component_data.place_id;
        }
        if (formData?.component_data?.rating !== undefined) {
          processedData.component_data.rating = formData.component_data.rating;
        }
        if (formData?.component_data?.price_level !== undefined) {
          processedData.component_data.price_level = formData.component_data.price_level;
        }
        if (formData?.component_data?.website !== undefined) {
          processedData.component_data.website = formData.component_data.website;
        }
      }

      // Normalize airport_vip_service fields, derive title/location/datetime from linked segment
      if (componentType === 'airport_vip_service') {
        processedData.component_data = { ...(processedData.component_data || {}) };
        const cd = processedData.component_data;

        // Resolve linked segment to enrich denormalized fields used by the timeline
        const linked = (tripFlightSegments || []).find(s => s.segment_id === cd.linked_segment_id);
        if (linked) {
          cd.linked_component_id = linked.component_id;
          cd.linked_component_type = linked.component_type;
          cd.linked_leg_index = linked.leg_index;
          cd.linked_segment_label = linked.label;

          // Airport for the VIP service depends on direction:
          // - departure greet happens at the departure airport of the segment
          // - arrival greet happens at the arrival airport of the segment
          const direction = cd.service_direction;
          const segmentAirport = direction === 'departure'
            ? linked.departure_airport
            : linked.arrival_airport;
          if (segmentAirport && !cd.airport_code) {
            cd.airport_code = segmentAirport;
          }

          // Anchor the VIP service start/end_datetime to the segment so it sorts correctly
          // on the timeline. Departures: greet starts ~3h before takeoff and ends at takeoff
          // (just above the flight). Arrivals: greet starts at landing and runs ~1h
          // (just below the flight).
          const segmentDt = direction === 'departure'
            ? linked.departure_datetime
            : linked.arrival_datetime;
          if (segmentDt) {
            try {
              const flightDt = new Date(segmentDt);
              if (!isNaN(flightDt.getTime())) {
                if (direction === 'departure') {
                  const start = new Date(flightDt.getTime() - 3 * 60 * 60 * 1000);
                  processedData.start_datetime = start.toISOString();
                  processedData.end_datetime = flightDt.toISOString();
                } else {
                  const end = new Date(flightDt.getTime() + 60 * 60 * 1000);
                  processedData.start_datetime = flightDt.toISOString();
                  processedData.end_datetime = end.toISOString();
                }
                // Anchored to a real flight time — explicitly not TBD
                processedData.date_is_tbd = false;
                processedData.time_is_tbd = false;
                cd.date_is_tbd = false;
                cd.time_is_tbd = false;
              }
            } catch (_) { /* ignore date parse errors */ }
          }
        }

        // Auto-build a sensible title if user did not provide one
        if (!processedData.title || !String(processedData.title).trim()) {
          const dirLabel = cd.service_direction === 'departure' ? 'Departure'
            : cd.service_direction === 'arrival' ? 'Arrival'
            : '';
          const airport = cd.airport_code || '';
          const provider = cd.provider_name || 'VIP Service';
          processedData.title = [airport, dirLabel, 'VIP Greet', provider ? `· ${provider}` : '']
            .filter(Boolean)
            .join(' ');
        }

        // Use the airport (or greet location) as the component-level location
        if (!processedData.location) {
          processedData.location = cd.greet_location || cd.airport_code || '';
        }

        // Persist the provider profile for reuse:
        //   - If linked to an existing provider AND the user changed any field → PUT
        //   - If no linked provider but a name is set → POST a new provider
        // Either way, store provider_id on the component so future edits stay linked.
        const providerName = (cd.provider_name || '').trim();
        if (providerName) {
          const providerPayload = {
            name: providerName,
            website: (cd.provider_website || '').trim() || null,
            phone: (cd.provider_phone || '').trim() || null,
            email: (cd.provider_email || '').trim() || null,
          };
          try {
            const apiBase = getApiBase();
            const headers = getAuthHeaders();
            if (vipSelectedProvider?.id) {
              const changed = (
                providerPayload.name !== (vipSelectedProvider.name || '') ||
                (providerPayload.website || '') !== (vipSelectedProvider.website || '') ||
                (providerPayload.phone || '') !== (vipSelectedProvider.phone || '') ||
                (providerPayload.email || '') !== (vipSelectedProvider.email || '')
              );
              if (changed) {
                await axios.put(
                  `${apiBase}/vip-service-providers/${vipSelectedProvider.id}`,
                  providerPayload,
                  { headers }
                );
              }
              cd.provider_id = vipSelectedProvider.id;
            } else {
              const created = await axios.post(
                `${apiBase}/vip-service-providers`,
                providerPayload,
                { headers }
              );
              if (created?.data?.id) cd.provider_id = created.data.id;
            }
          } catch (err) {
            // Non-fatal: surface a console warning but still submit the component.
            console.warn('VIP service provider save failed; continuing with component submit.', err);
          }
        }
      }


      const result = await onSubmit(processedData);
      
      // Success handling
      if (component?.id) {
        // Edit mode - show success message briefly then close
        setUpdateStatus('success');
        setTimeout(() => {
          setUpdateStatus(null);
          setShowFlightLegForm(false);
          setCurrentFlightLeg(null);
        }, 1500);
      } else {
        // Add mode - close immediately 
        setShowFlightLegForm(false);
        setCurrentFlightLeg(null);
      }
      
      return result;
    } catch (err) {
      console.error('Component submit error:', err);
      console.error('Error type:', typeof err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      
      let errorMessage = err.message;
      
      // Handle different types of errors
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (!errorMessage) {
        errorMessage = `Failed to ${component?.id ? 'update' : 'create'} component`;
      }
      
      setError(errorMessage);
      setValidationError(errorMessage);
      
      // Throw a properly formatted error
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // FormField component moved outside TripComponentForm to prevent recreation

  // Format date for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // ===========================================
  // CHAUFFEUR SERVICE FIELDS
  // ===========================================
  const renderChauffeurServiceFields = () => {
    const ServiceTypeOptions = () => {
      const serviceTypes = [
        { value: 'transfer', label: 'Point-to-Point Transfer', description: 'Direct transfer between locations', icon: '🚗' },
        { value: 'airport_pickup', label: 'Airport Pickup', description: 'Meet & greet at the airport', icon: '✈️' },
        { value: 'airport_dropoff', label: 'Airport Dropoff', description: 'Drop-off at the airport', icon: '🛫' },
        { value: 'hourly', label: 'Hourly Charter', description: 'Driver at your disposal', icon: '⏱️' },
        { value: 'full_day', label: 'Full Day', description: 'Driver for the entire day', icon: '📅' },
      ];

      return (
        <>
          {serviceTypes.map((type) => (
            <SelectItem key={type.value} value={type.value}>
              <div className="flex items-center gap-3 py-1">
                <span className="text-lg">{type.icon}</span>
                <div>
                  <div className="font-medium">{type.label}</div>
                  <div className="text-xs text-ui-muted">{type.description}</div>
                </div>
              </div>
            </SelectItem>
          ))}
        </>
      );
    };

    const VehicleClassOptions = () => {
      const vehicleClasses = [
        { value: 'sedan', label: 'Sedan', description: 'Standard executive sedan', icon: '🚗' },
        { value: 'luxury_sedan', label: 'Luxury Sedan', description: 'Premium luxury sedan', icon: '🏎️' },
        { value: 'suv', label: 'SUV', description: 'Comfortable SUV', icon: '🚙' },
        { value: 'luxury_suv', label: 'Luxury SUV', description: 'Premium luxury SUV', icon: '🚐' },
        { value: 'van', label: 'Van', description: 'For larger groups', icon: '🚐' },
        { value: 'sprinter', label: 'Sprinter', description: 'Executive sprinter van', icon: '🚌' },
      ];

      // Check if the current value matches any predefined option
      const currentValue = formData.component_data?.vehicle_class || '';
      const predefinedValues = vehicleClasses.map(v => v.value);
      const isCustomValue = currentValue && !predefinedValues.includes(currentValue);

      return (
        <>
          {/* Show the custom value from Urbanride API if it doesn't match predefined options */}
          {isCustomValue && (
            <SelectItem key={currentValue} value={currentValue}>
              <div className="flex items-center gap-3 py-1">
                <span className="text-lg">🚗</span>
                <div>
                  <div className="font-medium">{currentValue}</div>
                  <div className="text-xs text-ui-muted">From booking</div>
                </div>
              </div>
            </SelectItem>
          )}
          {vehicleClasses.map((type) => (
            <SelectItem key={type.value} value={type.value}>
              <div className="flex items-center gap-3 py-1">
                <span className="text-lg">{type.icon}</span>
                <div>
                  <div className="font-medium">{type.label}</div>
                  <div className="text-xs text-ui-muted">{type.description}</div>
                </div>
              </div>
            </SelectItem>
          ))}
        </>
      );
    };

    const isAirportService = formData.component_data?.service_type === 'airport_pickup' ||
                             formData.component_data?.service_type === 'airport_dropoff';

    // Helper function to safely parse dates for chauffeur service
    const safeParseDate = (dateStr) => {
      if (!dateStr || dateStr === 'null' || dateStr === 'undefined') return null;
      // parseLocalDate keeps date-only values on the correct calendar day
      // regardless of the viewer's timezone (avoids the UTC-midnight off-by-one).
      return parseLocalDate(dateStr);
    };

    const getDateLabel = () => {
      const date = safeParseDate(formData.start_datetime);
      return date ? format(date, 'EEE, MMM d, yyyy') : 'Select date';
    };

    const getTimeValue = () => {
      if (!formData.start_datetime) return '';
      const parts = formData.start_datetime.split('T');
      if (parts.length < 2) return '';
      const time = parts[1]?.substr(0, 5);
      // Validate that it looks like a time (HH:MM format)
      return time && time.match(/^\d{2}:\d{2}$/) ? time : '';
    };

    const getDatePart = () => {
      if (!formData.start_datetime) return format(new Date(), 'yyyy-MM-dd');
      const parts = formData.start_datetime.split('T');
      // Validate that it looks like a date (YYYY-MM-DD format)
      return parts[0] && parts[0].match(/^\d{4}-\d{2}-\d{2}$/) ? parts[0] : format(new Date(), 'yyyy-MM-dd');
    };

    return (
      <div className="space-y-6">
        {/* Service Type Section */}
        <div className="p-4 bg-ui-sunk rounded-xl border border-ui-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Car className="w-5 h-5 text-ui-ink-3" />
            Service Details
          </Label>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Service Type */}
            <div>
              <Label htmlFor="service_type" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Service Type *
              </Label>
              <Select
                value={formData.component_data?.service_type || 'transfer'}
                onValueChange={(value) => handleDetailsChange('service_type', value)}
              >
                <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-control focus:ring-ui-control">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  <ServiceTypeOptions />
                </SelectContent>
              </Select>
            </div>

            {/* Vehicle Class */}
            <div>
              <Label htmlFor="vehicle_class" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Vehicle Class
              </Label>
              <Select
                value={formData.component_data?.vehicle_class || ''}
                onValueChange={(value) => handleDetailsChange('vehicle_class', value)}
              >
                <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-control focus:ring-ui-control">
                  <SelectValue placeholder="Select vehicle class" />
                </SelectTrigger>
                <SelectContent>
                  <VehicleClassOptions />
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Pickup Date & Time */}
          <div className="mb-4">
            <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
              <Clock className="w-4 h-4 text-ui-ink-3" />
              Pickup Date & Time *
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <DatePickerButton
                  label={getDateLabel()}
                  selectedDate={safeParseDate(formData.start_datetime) || undefined}
                  onPick={(date) => {
                    const iso = date ? format(date, 'yyyy-MM-dd') : '';
                    const currentTime = getTimeValue() || '10:00';
                    handleInputChange('start_datetime', `${iso}T${currentTime}`);
                  }}
                />
              </div>
              <div>
                <Select
                  value={getTimeValue()}
                  onValueChange={(time) => {
                    const date = getDatePart();
                    handleInputChange('start_datetime', `${date}T${time}`);
                  }}
                >
                  <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-control focus:ring-ui-control">
                    <SelectValue placeholder="Time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {Array.from({ length: 96 }, (_, i) => {
                      const hour = Math.floor(i / 4);
                      const minute = (i % 4) * 15;
                      const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                      return <SelectItem key={time} value={time}>{time}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Itinerary Section */}
        <div className="p-4 bg-ui-blue-bg rounded-xl border border-ui-blue-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <MapPin className="w-5 h-5 text-ui-link" />
            Itinerary
          </Label>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Pickup Location */}
            <div>
              <Label htmlFor="pickup_location" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Pickup Location *
              </Label>
              <Input
                id="pickup_location"
                value={formData.component_data?.pickup_location || ''}
                onChange={(e) => handleDetailsChange('pickup_location', e.target.value)}
                placeholder="Enter pickup address..."
                className="h-12 text-base border-ui-line focus:border-ui-control focus:ring-ui-control"
              />
            </div>

            {/* Dropoff Location */}
            <div>
              <Label htmlFor="dropoff_location" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Dropoff Location *
              </Label>
              <Input
                id="dropoff_location"
                value={formData.component_data?.dropoff_location || ''}
                onChange={(e) => handleDetailsChange('dropoff_location', e.target.value)}
                placeholder="Enter dropoff address..."
                className="h-12 text-base border-ui-line focus:border-ui-control focus:ring-ui-control"
              />
            </div>
          </div>

          {/* Airport/Flight Details - shown for airport pickup/dropoff */}
          {isAirportService && (
            <div className="mt-4 p-3 bg-ui-surface rounded-lg border border-ui-blue-line">
              <Label className="text-sm font-semibold text-ui-ink mb-3 block flex items-center gap-2">
                <Plane className="w-4 h-4 text-ui-link" />
                Flight Details
              </Label>

              {/* Basic flight info - Airport, Airline, Flight Number */}
              <div className="grid md:grid-cols-3 gap-3 mb-3">
                <div>
                  <Label className="text-xs font-medium text-ui-ink-3 mb-1 block">Airport Code *</Label>
                  <Input
                    value={formData.component_data?.airport_code || ''}
                    onChange={(e) => handleDetailsChange('airport_code', e.target.value.toUpperCase())}
                    placeholder="e.g., JFK"
                    maxLength={3}
                    className="h-10 text-sm border-ui-line"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-ui-ink-3 mb-1 block">Airline Code *</Label>
                  <Input
                    value={formData.component_data?.airline || ''}
                    onChange={(e) => handleDetailsChange('airline', e.target.value.toUpperCase())}
                    placeholder="e.g., DL"
                    maxLength={2}
                    className="h-10 text-sm border-ui-line"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-ui-ink-3 mb-1 block">Flight Number *</Label>
                  <Input
                    value={formData.component_data?.flight_number || ''}
                    onChange={(e) => handleDetailsChange('flight_number', e.target.value)}
                    placeholder="e.g., 123"
                    className="h-10 text-sm border-ui-line"
                  />
                </div>
              </div>

              {/* Airport Dropoff specific: Flight Date (required) and Flight Time (optional) */}
              {formData.component_data?.service_type === 'airport_dropoff' && (
                <>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-xs font-medium text-ui-ink-3 mb-1 block">
                        Flight Date *
                      </Label>
                      <DatePickerButton
                        label={formData.component_data?.flight_date
                          ? format(parseLocalDate(formData.component_data.flight_date), 'EEE, MMM d, yyyy')
                          : 'Select flight date'}
                        selectedDate={formData.component_data?.flight_date ? parseLocalDate(formData.component_data.flight_date) : undefined}
                        onPick={(date) => {
                          const iso = date ? format(date, 'yyyy-MM-dd') : '';
                          handleDetailsChange('flight_date', iso);
                          // Reset verification status when date changes
                          setFlightVerificationSuccess(false);
                          setFlightVerificationError('');
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-ui-ink-3 mb-1 block">
                        Flight Time {formData.component_data?.flight_time ? '' : '(optional - verify to auto-fill)'}
                      </Label>
                      <Select
                        value={formData.component_data?.flight_time || ''}
                        onValueChange={(time) => handleDetailsChange('flight_time', time)}
                      >
                        <SelectTrigger className="h-10 text-sm border-ui-line">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {Array.from({ length: 96 }, (_, i) => {
                            const hour = Math.floor(i / 4);
                            const minute = (i % 4) * 15;
                            const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                            return <SelectItem key={time} value={time}>{time}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Flight Origin (populated by verification) */}
                  {formData.component_data?.flight_origin && (
                    <div className="mb-3">
                      <Label className="text-xs font-medium text-ui-ink-3 mb-1 block">
                        Flight Origin
                      </Label>
                      <Input
                        value={formData.component_data?.flight_origin || ''}
                        onChange={(e) => handleDetailsChange('flight_origin', e.target.value.toUpperCase())}
                        placeholder="Origin airport"
                        maxLength={3}
                        className="h-10 text-sm border-ui-line bg-ui-green-bg"
                        readOnly
                      />
                    </div>
                  )}

                  {/* Verify Flight Button */}
                  <div className="flex items-center gap-3 mt-3">
                    <Button
                      type="button"
                      onClick={handleVerifyFlight}
                      disabled={flightVerificationLoading || !urbanrideApiConfigured || flightVerificationLocked}
                      className="bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary text-sm h-9 px-4 disabled:bg-ui-control disabled:cursor-not-allowed"
                    >
                      {flightVerificationLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Verify Flight
                        </>
                      )}
                    </Button>

                    {flightVerificationSuccess && (
                      <span className="text-ui-green-fg text-sm flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Flight verified
                      </span>
                    )}

                    {flightVerificationLocked && (
                      <span className="text-ui-amber-fg text-xs">
                        Flight verification not included in {flightVerificationPackageName || 'current'} package
                      </span>
                    )}

                    {!flightVerificationLocked && !urbanrideApiConfigured && (
                      <span className="text-ui-amber-fg text-xs">
                        Configure Urbanride API in settings to verify flights
                      </span>
                    )}
                  </div>

                  {/* Verification Error */}
                  {flightVerificationError && (
                    <div className="mt-2 p-2 bg-ui-red-bg border border-ui-red-line rounded text-ui-red-fg text-xs flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {flightVerificationError}
                    </div>
                  )}

                  {/* Verified flight details display */}
                  {(formData.component_data?.flight_terminal || formData.component_data?.flight_status) && (
                    <div className="mt-2 p-2 bg-ui-blue-bg border border-ui-blue-line rounded">
                      <div className="text-xs text-ui-blue-fg space-y-1">
                        {formData.component_data?.flight_terminal && (
                          <p><strong>Terminal:</strong> {formData.component_data.flight_terminal}</p>
                        )}
                        {formData.component_data?.flight_status && (
                          <p><strong>Status:</strong> {formData.component_data.flight_status}</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              <p className="text-xs text-ui-muted mt-2">
                {formData.component_data?.service_type === 'airport_pickup'
                  ? 'Flight arrival information for meet & greet'
                  : 'Enter flight details and click "Verify Flight" to auto-fill departure time and origin'
                }
              </p>
            </div>
          )}
        </div>

        {/* Passenger Section */}
        <div className="p-4 bg-ui-violet-bg rounded-xl border border-ui-violet-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <User className="w-5 h-5 text-ui-violet-fg" />
            Passenger Details
          </Label>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Passenger Name */}
            <div>
              <Label htmlFor="passenger_name" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Passenger Name
              </Label>
              {/* In speaker_bureau mode: free-text passenger name + optional Speaker link
                  (no executive roster to choose from) */}
              {isSpeakerBureau ? (
                <div className="space-y-2">
                  <Input
                    id="passenger_name_speaker"
                    value={formData.component_data?.passenger_name || ''}
                    onChange={(e) => handleDetailsChange('passenger_name', e.target.value)}
                    placeholder="Passenger name"
                    className="h-12 text-base border-ui-line"
                  />
                  <SpeakerLinkField
                    speakerId={formData.component_data?.passenger_speaker_id || null}
                    speakerName={formData.component_data?.passenger_speaker_name || formData.component_data?.passenger_name || ''}
                    onPick={(sp) => {
                      if (!sp) {
                        handleDetailsChange('passenger_speaker_id', null);
                        handleDetailsChange('passenger_speaker_name', '');
                        return;
                      }
                      const nm = sp.full_name || `${sp.first_name || ''} ${sp.last_name || ''}`.trim();
                      handleDetailsChange('passenger_speaker_id', sp.id);
                      handleDetailsChange('passenger_speaker_name', nm);
                      if (!formData.component_data?.passenger_name && nm) handleDetailsChange('passenger_name', nm);
                    }}
                  />
                </div>
              ) : formData.component_data?.passenger_name && !formData.component_data?.passenger_executive_id ? (
                <Input
                  id="passenger_name_display"
                  value={formData.component_data?.passenger_name || ''}
                  onChange={(e) => handleDetailsChange('passenger_name', e.target.value)}
                  placeholder="Passenger name"
                  className="h-12 text-base border-ui-line"
                />
              ) : (
                <Select
                  value={formData.component_data?.passenger_executive_id || ''}
                  onValueChange={(value) => {
                    handleDetailsChange('passenger_executive_id', value);
                    const exec = tripExecutives?.find(e => e.id === value);
                    if (exec) {
                      handleDetailsChange('passenger_name', `${exec.first_name} ${exec.last_name}`);
                    }
                  }}
                >
                  <SelectTrigger className="h-12 text-base border-ui-line">
                    <SelectValue placeholder="Select passenger" />
                  </SelectTrigger>
                  <SelectContent>
                    {tripExecutives && tripExecutives.map((executive) => (
                      <SelectItem key={executive.id} value={executive.id}>
                        {executive.first_name} {executive.last_name}
                      </SelectItem>
                    ))}
                    <SelectItem value="other">Other (specify below)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Number of Passengers */}
            <div>
              <Label htmlFor="num_passengers" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Number of Passengers
              </Label>
              <Select
                value={formData.component_data?.num_passengers || '1'}
                onValueChange={(value) => handleDetailsChange('num_passengers', value)}
              >
                <SelectTrigger className="h-12 text-base border-ui-line">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} passenger{n > 1 ? 's' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Other passenger name input - shown when selecting "Other" from dropdown */}
          {formData.component_data?.passenger_executive_id === 'other' && (
            <div className="mb-4">
              <Label htmlFor="passenger_name_manual" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Passenger Name (Manual Entry)
              </Label>
              <Input
                id="passenger_name_manual"
                value={formData.component_data?.passenger_name || ''}
                onChange={(e) => handleDetailsChange('passenger_name', e.target.value)}
                placeholder="Enter passenger name"
                className="h-12 text-base border-ui-line"
              />
            </div>
          )}

          {/* Special Requests */}
          <div>
            <Label htmlFor="special_requests" className="text-sm font-bold text-ui-ink-2 mb-2 block">
              Special Requests
            </Label>
            <Textarea
              id="special_requests"
              value={formData.component_data?.special_requests || ''}
              onChange={(e) => handleDetailsChange('special_requests', e.target.value)}
              placeholder="Child seat, wheelchair accessible, meet & greet sign, etc."
              className="text-base border-ui-line"
              rows={3}
            />
          </div>
        </div>

        {/* Booking Reference Section */}
        <div className="p-4 bg-ui-green-bg rounded-xl border border-ui-green-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <FileText className="w-5 h-5 text-ui-green-fg" />
            Booking Information
          </Label>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Provider - Hardcoded as Urbanride */}
            <div>
              <Label htmlFor="provider" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Service Provider
              </Label>
              <Input
                id="provider"
                value="Urbanride"
                readOnly
                className="h-12 text-base border-ui-line bg-ui-sunk"
              />
            </div>

            {/* Confirmation Number */}
            <div>
              <Label htmlFor="confirmation_number" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Confirmation Number
              </Label>
              <Input
                id="confirmation_number"
                value={formData.confirmation_number || formData.component_data?.urbanride_urcn || ''}
                onChange={(e) => handleInputChange('confirmation_number', e.target.value)}
                className="h-12 text-base border-ui-line"
              />
            </div>
          </div>

          {/* Rate/Cost */}
          <div className="mt-4">
            <Label htmlFor="rate" className="text-sm font-bold text-ui-ink-2 mb-2 block">
              Rate / Cost
            </Label>
            <Input
              id="rate"
              value={formData.component_data?.rate || ''}
              onChange={(e) => handleDetailsChange('rate', e.target.value)}
              placeholder="e.g., $150.00"
              className="h-12 text-base border-ui-line w-48"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderCommercialFlightFields = () => {
    return (
      <div className="space-y-6">
        {/* Executive validation warning if no executives —
            not shown in speaker_bureau mode, where executives aren't used */}
        {!isSpeakerBureau && finalAvailableExecutives.length === 0 && (
          <div className="p-4 my-4 bg-ui-amber-bg border-l-4 border-ui-amber-solid rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-ui-amber-fg" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-ui-amber-fg">
                  No executives available for assignment. You can still create the flight and assign executives later.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Import Flight Confirmation Section - Moved to Top */}
        <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line">
          <div className="flex items-center justify-between mb-4">
            <Label className="text-lg font-semibold flex items-center gap-2">
              <Upload className="w-5 h-5 text-ui-teal-fg" />
              Import Flight Confirmation & Itinerary
            </Label>
            <div className="relative">
              <input
                type="file"
                id="confirmation-upload"
                className="hidden"
                onChange={handleConfirmationUpload}
                accept=".pdf,.png,.jpg,.jpeg"
                disabled={isUploadingConfirmation}
              />
              <Button
                type="button"
                onClick={() => document.getElementById('confirmation-upload').click()}
                size="sm"
                className="bg-ui-teal-strong hover:bg-ui-teal-strong/90 text-ui-on-primary"
                disabled={isUploadingConfirmation}
              >
                {isUploadingConfirmation ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" /> Upload Document
                  </>
                )}
              </Button>
            </div>
          </div>
          <div className="text-sm text-ui-ink-3 space-y-2">
            <p>
              <strong>Supported documents:</strong> Flight confirmations, boarding passes, travel itineraries
            </p>
            <p>
              <strong>File formats:</strong> PDF, PNG, JPG, JPEG
            </p>
            <p className="text-xs text-ui-muted italic">
              Tip: For email confirmations, save as PDF first. Our AI will extract flight details, passenger info, and create multiple legs automatically.
            </p>
          </div>
        </div>

        {/* Existing Documents Display */}
        {attachedFiles.length > 0 && (
          <div className="space-y-3">
            <Label className="text-sm font-bold text-ui-ink-2">Existing Documents</Label>
            <div className="space-y-3">
              {attachedFiles.map((file) => (
                <div key={file.id} className="p-3 bg-ui-surface rounded-lg border border-ui-line hover:border-ui-control transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-ui-blue-bg rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-ui-link" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-ui-ink">{attachmentLabel(file)}</div>
                        <div className="text-xs text-ui-muted">
                          {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {file.name && file.id && !file.id.startsWith('temp-') && file.id !== 'undefined' ? (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleViewDocument(file);
                            }}
                            className="text-ui-link hover:text-ui-blue-fg"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {hasParsedAttachment(file) && (
                            <Button type="button" variant="ghost" size="sm"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewDocument(file, true); }}>
                              Parsed data (JSON)
                            </Button>
                          )}
                        </div>
                      ) : file.url && file.url.startsWith('blob:') ? (
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-ui-amber-fg font-medium">⚠️ Legacy File</span>
                          <span className="text-xs text-ui-muted">Re-upload needed to view</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-ui-red-fg font-medium">❌ Invalid File</span>
                          <span className="text-xs text-ui-muted">File ID missing</span>
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFileDelete(file.id)}
                        className="text-ui-red-fg hover:text-ui-red-fg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor={`file-label-${file.id}`} className="text-xs text-ui-ink-3">
                      Document Label
                    </Label>
                    <Input
                      id={`file-label-${file.id}`}
                      value={file.label || ''}
                      onChange={(e) => handleFileLabelChange(file.id, e.target.value)}
                      placeholder="Enter description for this file"
                      className="mt-1 h-8 text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Validation Error Display */}
        {validationError && (
          <div className="p-4 bg-ui-red-bg border border-ui-red-line rounded-xl">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-ui-red-fg" />
              <p className="text-ui-red-fg font-medium">{validationError}</p>
            </div>
          </div>
        )}

        {/* Executive/Traveler Assignment Section — hidden in speaker_bureau mode
            (the program is scoped to one speaker; only passenger assignment is needed) */}
        {!isSpeakerBureau && (
        <div className="bg-ui-surface p-6 rounded-2xl border border-ui-line shadow-sm">
          <h3 className="text-lg font-semibold text-ui-ink mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-ui-violet-fg" />
            Traveler Assignment
          </h3>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-3 block">
                Assign Executives to this Flight
              </Label>
              {finalAvailableExecutives.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-3">
                  {finalAvailableExecutives.map((executive) => (
                    <div key={executive.id} className="flex items-center space-x-3 p-3 bg-ui-sunk rounded-lg border border-ui-line">
                      <input
                        type="checkbox"
                        id={`exec-${executive.id}`}
                        checked={formData.component_data.assigned_executives?.includes(executive.id) || false}
                        onChange={(e) => {
                          const assignedExecs = formData.component_data.assigned_executives || [];
                          const updated = e.target.checked 
                            ? [...assignedExecs, executive.id]
                            : assignedExecs.filter(id => id !== executive.id);
                          handleDetailsChange('assigned_executives', updated);
                        }}
                        className="w-4 h-4 text-ui-violet-fg"
                      />
                      <label htmlFor={`exec-${executive.id}`} className="flex-1 cursor-pointer">
                        <div className="font-medium text-ui-ink">
                          {executive.first_name} {executive.last_name}
                        </div>
                        <div className="text-sm text-ui-muted">{executive.title}</div>
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-ui-muted bg-ui-sunk rounded-lg border-2 border-dashed border-ui-control">
                  <Users className="w-10 h-10 text-ui-faint mx-auto mb-2" />
                  <p className="font-medium">No executives available for assignment.</p>
                  <p className="text-sm mt-1">You can assign executives later from the trip details page.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Flight Legs Section */}
        <div className="p-4 bg-ui-blue-bg rounded-xl border border-ui-blue-line space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-lg font-semibold flex items-center gap-2">
              <Plane className="w-5 h-5 text-ui-link"/>
              Flight Legs
              {flightLegs.length > 0 && (
                <Badge variant="outline" className="ml-2">
                  {flightLegs.length} leg{flightLegs.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </Label>
            <Button
              type="button"
              size="sm"
              className="bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary"
              onClick={() => {
                setCurrentFlightLeg({});
                setDepTimeRaw('');
                setArrTimeRaw('');
                setShowFlightLegForm(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Flight Leg
            </Button>
          </div>
          
          {/* Flight Legs Display */}
          {flightLegs.length > 0 ? (
            <div className="space-y-2">
              {/* Header row */}
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-ui-ink-3 uppercase tracking-wide px-3 py-2 bg-ui-sunk rounded">
                <div className="col-span-2">Airline</div>
                <div className="col-span-1">Flight #</div>
                <div className="col-span-2">From</div>
                <div className="col-span-2">To</div>
                <div className="col-span-2">Departure</div>
                <div className="col-span-2">Arrival</div>
                <div className="col-span-1">Actions</div>
              </div>
              
              {/* Flight leg rows */}
              {flightLegs.map((leg, index) => (
                <div 
                  key={index}
                  className="grid grid-cols-12 gap-2 items-center p-3 bg-ui-surface rounded-lg border border-ui-blue-line hover:border-ui-blue-line transition-colors"
                >
                  {/* Airline */}
                  <div className="col-span-2">
                    <div className="font-medium text-ui-ink">
                      {leg.airline_name || 'TBD'}
                    </div>
                  </div>
                  
                  {/* Flight Number */}
                  <div className="col-span-1">
                    <div className="text-sm text-ui-ink-3">
                      {leg.flight_number || 'TBD'}
                    </div>
                  </div>
                  
                  {/* Departure Airport */}
                  <div className="col-span-2">
                    <div className="font-medium text-ui-ink">
                      {leg.departure_airport || 'TBD'}
                    </div>
                  </div>
                  
                  {/* Arrival Airport */}
                  <div className="col-span-2">
                    <div className="font-medium text-ui-ink">
                      {leg.arrival_airport || 'TBD'}
                    </div>
                  </div>
                  
                  {/* Departure Time */}
                  <div className="col-span-2">
                    <div className="text-sm text-ui-ink-3">
                      {formatFlightTime(leg.departure_time, 'MMM d, h:mm a', 'TBD')}
                    </div>
                  </div>

                  {/* Arrival Time */}
                  <div className="col-span-2">
                    <div className="text-sm text-ui-ink-3">
                      {formatFlightTime(leg.arrival_time, 'MMM d, h:mm a', 'TBD')}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 hover:bg-ui-blue-bg"
                      onClick={() => handleEditFlightLeg(leg, index)}
                    >
                      <Edit className="w-3 h-3 text-ui-link" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon" 
                      className="h-7 w-7 text-ui-red-fg hover:text-ui-red-fg hover:bg-ui-red-bg"
                      onClick={() => handleDeleteFlightLeg(index)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {/* Status indicators below the main row */}
                  {(!leg.departure_airport || !leg.arrival_airport || !leg.departure_time) && (
                    <div className="col-span-12 flex gap-2 mt-1">
                      {!leg.departure_airport && (
                        <Badge variant="outline" className="text-xs text-ui-amber-fg border-ui-amber-line bg-ui-amber-bg">
                          Missing departure
                        </Badge>
                      )}
                      {!leg.arrival_airport && (
                        <Badge variant="outline" className="text-xs text-ui-amber-fg border-ui-amber-line bg-ui-amber-bg">
                          Missing arrival
                        </Badge>
                      )}
                      {!leg.departure_time && (
                        <Badge variant="outline" className="text-xs text-ui-amber-fg border-ui-amber-line bg-ui-amber-bg">
                          Missing time
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-ui-muted bg-ui-surface rounded-lg border-2 border-dashed border-ui-blue-line">
              <Plane className="w-12 h-12 text-ui-link mx-auto mb-3" />
              <p className="font-medium">No flight legs added yet.</p>
              <p className="text-sm mt-1">Add flight segments to build your itinerary</p>
            </div>
          )}
        </div>

        {/* Fare & Bags Section (optional) */}
        <FareTermsFormSection
          value={formData.component_data?.fare_terms || null}
          onChange={(terms) => handleDetailsChange('fare_terms', terms)}
        />

        {/* Passengers/Traveler Details Section */}
        <div className="p-4 bg-ui-green-bg rounded-xl border border-ui-green-line space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-ui-green-fg"/>
              Passenger Details
            </Label>
            <Button
              type="button"
              size="sm"
              className="bg-ui-green-strong hover:bg-ui-green-strong/90 text-ui-on-primary"
              onClick={() => {
                const newPassenger = {
                  passenger_type: 'executive',
                  name: '',
                  seat_preference: '',
                  meal_preference: '',
                  special_requests: '',
                  confirmation_number: '',
                  frequent_flyer_number: '',
                  ticket_number: ''
                };
                const updatedPassengers = [...(formData.component_data?.passengers || []), newPassenger];
                handleDetailsChange('passengers', updatedPassengers);
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Passenger
            </Button>
          </div>
          
          {/* Passengers Display */}
          {formData.component_data.passengers && formData.component_data.passengers.length > 0 ? (
            <div className="space-y-3">
              {formData.component_data.passengers.map((passenger, index) => (
                <div key={index} className="p-4 bg-ui-surface rounded-lg border border-ui-green-line">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="font-medium text-ui-ink">Passenger {index + 1}</h5>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-ui-red-fg hover:text-ui-red-fg hover:bg-ui-red-bg"
                      onClick={() => {
                        const updatedPassengers = formData.component_data.passengers.filter((_, i) => i !== index);
                        handleDetailsChange('passengers', updatedPassengers);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <Input
                      placeholder="Passenger Name"
                      value={passenger.name || ''}
                      onChange={(e) => {
                        const updatedPassengers = [...formData.component_data.passengers];
                        updatedPassengers[index] = { ...updatedPassengers[index], name: e.target.value };
                        handleDetailsChange('passengers', updatedPassengers);
                      }}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Confirmation Number"
                      value={passenger.confirmation_number || ''}
                      onChange={(e) => {
                        const updatedPassengers = [...formData.component_data.passengers];
                        updatedPassengers[index] = { ...updatedPassengers[index], confirmation_number: e.target.value };
                        handleDetailsChange('passengers', updatedPassengers);
                      }}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Frequent Flyer Number"
                      value={passenger.frequent_flyer_number || ''}
                      onChange={(e) => {
                        const updatedPassengers = [...formData.component_data.passengers];
                        updatedPassengers[index] = { ...updatedPassengers[index], frequent_flyer_number: e.target.value };
                        handleDetailsChange('passengers', updatedPassengers);
                      }}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Ticket Number"
                      value={passenger.ticket_number || ''}
                      onChange={(e) => {
                        const updatedPassengers = [...formData.component_data.passengers];
                        updatedPassengers[index] = { ...updatedPassengers[index], ticket_number: e.target.value };
                        handleDetailsChange('passengers', updatedPassengers);
                      }}
                      className="text-sm"
                    />
                    {/* Optional link to a saved Speaker (speaker_bureau mode only) */}
                    {isSpeakerBureau && (
                      <div>
                        <Label className="text-xs font-medium text-ui-muted mb-1 block">
                          Link to {labels.executive} (optional)
                        </Label>
                        <SpeakerLinkField
                          speakerId={passenger.speaker_id || null}
                          speakerName={passenger.speaker_name || passenger.name || ''}
                          onPick={(sp) => {
                            const updatedPassengers = [...formData.component_data.passengers];
                            if (!sp) {
                              updatedPassengers[index] = { ...updatedPassengers[index], speaker_id: null, speaker_name: '' };
                            } else {
                              const nm = sp.full_name || `${sp.first_name || ''} ${sp.last_name || ''}`.trim();
                              updatedPassengers[index] = {
                                ...updatedPassengers[index],
                                speaker_id: sp.id,
                                speaker_name: nm,
                                name: updatedPassengers[index].name || nm,
                              };
                            }
                            handleDetailsChange('passengers', updatedPassengers);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-ui-muted bg-ui-surface rounded-lg border-2 border-dashed border-ui-green-line">
              <Users className="w-10 h-10 text-ui-green-fg mx-auto mb-2" />
              <p className="font-medium">No passengers added yet.</p>
              <p className="text-sm mt-1">Add passenger details for this flight</p>
            </div>
          )}
        </div>

        {/* Validation Error Display */}
        {validationError && (
          <div className="p-4 bg-ui-red-bg border border-ui-red-line rounded-xl">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-ui-red-fg" />
              <p className="text-ui-red-fg font-medium">{validationError}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Speaker Meeting Component — pharma / speaker-bureau speaking engagements
  const renderSpeakerMeetingFields = () => {
    const cd = formData.component_data || {};
    const isVirtual = !!cd.is_virtual;

    // Local datetime input helper: store as plain "YYYY-MM-DDTHH:MM" string
    const toDateTimeLocal = (val) => {
      if (!val) return '';
      // Trim seconds/timezone if present
      const s = String(val);
      const m = s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
      return m ? m[1] : '';
    };

    return (
      <div className="space-y-6">
        {/* Original parsed source — when this speaker meeting was created from a
            parsed email or uploaded document, surface a link to view/download
            the original so the user can verify the parsed fields against it.
            Reuses the established attached_files convention (source +
            documentId/parsingLogId), with fallbacks to root-level refs. */}
        {(() => {
          const attachedFiles = Array.isArray(cd.attached_files) ? cd.attached_files : [];
          const file = attachedFiles[0] || {};
          const parsingLogId = file.parsingLogId || cd.parsing_log_id || formData.parsing_log_id || null;
          const docId = file.documentId || file.id || cd.document_id || formData.document_id || null;
          const isEmailBody = (file.source === 'inbound_email_body') || (!!parsingLogId && !docId);
          const viewUrl = isEmailBody && parsingLogId
            ? `${getApiBase()}/parsing-logs/${parsingLogId}/email`
            : (docId
                ? `${getApiBase()}/files/${docId}/view`
                : (parsingLogId ? `${getApiBase()}/parsing-logs/${parsingLogId}/email` : null));
          if (!viewUrl) return null;

          return (
            <div className="p-4 bg-ui-blue-bg border border-ui-blue-line rounded-xl flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-5 h-5 text-ui-link flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ui-ink">
                    Parsed from {isEmailBody ? 'email' : 'document'}
                  </p>
                  <p className="text-xs text-ui-muted truncate">
                    {isEmailBody ? 'Original email body' : (file.name || 'Original document')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-ui-link bg-ui-surface border border-ui-blue-line rounded-md hover:bg-ui-blue-bg transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  View {isEmailBody ? 'Email' : 'Document'}
                </a>
                {!isEmailBody && docId && (
                  <button
                    type="button"
                    onClick={() => handleDownloadDocument({ id: docId, name: file.name, url: file.url })}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-ui-link bg-ui-surface border border-ui-blue-line rounded-md hover:bg-ui-blue-bg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Program Overview */}
        <div className="p-4 bg-ui-red-bg rounded-xl border border-ui-red-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Mic className="w-5 h-5 text-ui-red-fg" />
            Program Overview
          </Label>
          {/* Link to a saved Speaker (speaker_bureau) — keeps reusable speaker info */}
          <div className="mb-4">
            <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Speaker (saved profile)</Label>
            <SpeakerLinkField
              speakerId={cd.speaker_id || null}
              speakerName={cd.speaker_name || ''}
              onPick={(sp) => {
                if (!sp) {
                  handleDetailsChange('speaker_id', null);
                  return;
                }
                handleDetailsChange('speaker_id', sp.id);
                const nm = sp.full_name || `${sp.first_name || ''} ${sp.last_name || ''}`.trim();
                if (nm) handleDetailsChange('speaker_name', nm);
                if (sp.email) handleDetailsChange('speaker_email', sp.email);
                if (sp.default_fee && !cd.speaker_fee) handleDetailsChange('speaker_fee', sp.default_fee);
              }}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Sponsor (Pharma Company)</Label>
              <Input
                value={cd.sponsor || ''}
                onChange={(e) => handleDetailsChange('sponsor', e.target.value)}
                placeholder="e.g. Rigel, Neurelis"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Product / Brand</Label>
              <Input
                value={cd.product_name || ''}
                onChange={(e) => handleDetailsChange('product_name', e.target.value)}
                placeholder="e.g. REZLIDHIA, VALTOCO"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Program ID</Label>
              <Input
                value={cd.program_id || ''}
                onChange={(e) => {
                  handleDetailsChange('program_id', e.target.value);
                  handleInputChange('confirmation_number', e.target.value);
                }}
                placeholder="e.g. RI-34308"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Program Format</Label>
              <Select
                value={cd.program_format || ''}
                onValueChange={(value) => {
                  handleDetailsChange('program_format', value);
                  handleDetailsChange('is_virtual', value === 'Virtual');
                }}
              >
                <SelectTrigger className="h-11"><SelectValue placeholder="Select format" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Live">Live (In Person)</SelectItem>
                  <SelectItem value="Virtual">Virtual</SelectItem>
                  <SelectItem value="Hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Topic / Presentation Title</Label>
              <Textarea
                value={cd.program_topic || ''}
                onChange={(e) => {
                  handleDetailsChange('program_topic', e.target.value);
                  // Keep the component title in sync if it is empty or matched the topic
                  if (!formData.title || formData.title === cd.program_topic) {
                    handleInputChange('title', e.target.value);
                  }
                }}
                placeholder="Full topic of the speaker program"
                className="min-h-[60px]"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Status</Label>
              <Select
                value={formData.status || 'confirmed'}
                onValueChange={(value) => handleInputChange('status', value)}
              >
                <SelectTrigger className="h-11"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Speaker Bureau / Agency</Label>
              <Input
                value={cd.speaker_bureau || ''}
                onChange={(e) => handleDetailsChange('speaker_bureau', e.target.value)}
                placeholder="Agency coordinating the program"
                className="h-11"
              />
            </div>
          </div>
        </div>

        {/* Speaker */}
        <div className="p-4 bg-ui-red-bg rounded-xl border border-ui-red-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Users className="w-5 h-5 text-ui-red-fg" />
            Speaker
          </Label>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Speaker Name</Label>
              <Input
                value={cd.speaker_name || ''}
                onChange={(e) => handleDetailsChange('speaker_name', e.target.value)}
                placeholder="e.g. Dr. Timothy Lynch"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Speaker Email</Label>
              <Input
                type="email"
                value={cd.speaker_email || ''}
                onChange={(e) => handleDetailsChange('speaker_email', e.target.value)}
                placeholder="speaker@example.com"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Speaker Fee</Label>
              <Input
                value={cd.speaker_fee || ''}
                onChange={(e) => handleDetailsChange('speaker_fee', e.target.value)}
                placeholder="e.g. Per Contracted Rate"
                className="h-11"
              />
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="p-4 bg-ui-red-bg rounded-xl border border-ui-red-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-ui-red-fg" />
            Schedule
          </Label>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Start Date &amp; Time</Label>
              <Input
                type="datetime-local"
                value={toDateTimeLocal(formData.start_datetime)}
                onChange={(e) => handleStartDateTimeChange(e.target.value)}
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">End Date &amp; Time</Label>
              <Input
                type="datetime-local"
                value={toDateTimeLocal(formData.end_datetime)}
                onChange={(e) => handleEndDateTimeChange(e.target.value)}
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Displayed Time (as shown)</Label>
              <Input
                value={cd.program_time || ''}
                onChange={(e) => handleDetailsChange('program_time', e.target.value)}
                placeholder="e.g. 06:00 PM PT"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Timezone</Label>
              <Input
                value={cd.timezone || ''}
                onChange={(e) => handleDetailsChange('timezone', e.target.value)}
                placeholder="e.g. PT, ET, CT"
                className="h-11"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="p-4 bg-ui-red-bg rounded-xl border border-ui-red-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <MapPin className="w-5 h-5 text-ui-red-fg" />
            Location
          </Label>
          <div className="space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isVirtual}
                onChange={(e) => handleDetailsChange('is_virtual', e.target.checked)}
                className="w-4 h-4 text-ui-red-fg focus:ring-ui-red-solid rounded"
              />
              <span className="text-sm font-medium text-ui-ink-2">This is a virtual program</span>
            </label>

            {isVirtual ? (
              <div>
                <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Virtual Meeting Link</Label>
                <Input
                  value={cd.virtual_link || ''}
                  onChange={(e) => handleDetailsChange('virtual_link', e.target.value)}
                  placeholder="Join URL"
                  className="h-11"
                />
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Venue Name</Label>
                  <Input
                    value={cd.venue_name || ''}
                    onChange={(e) => handleDetailsChange('venue_name', e.target.value)}
                    placeholder="e.g. The Raymond 1886"
                    className="h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Venue Address</Label>
                  <VenueAutocomplete
                    placeholder="Search for venue or address..."
                    value={formData.location || cd.venue_address || ''}
                    onChange={(value) => {
                      handleLocationChange(value);
                      handleDetailsChange('venue_address', value);
                    }}
                    onPlaceSelect={handleMeetingLocationSelect}
                    className="w-full max-w-full h-11 text-base bg-ui-surface text-ui-ink"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Contacts */}
        <div className="p-4 bg-ui-red-bg rounded-xl border border-ui-red-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Phone className="w-5 h-5 text-ui-red-fg" />
            Contacts
          </Label>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Program Host / Field Rep</Label>
              <Input
                value={cd.host_name || ''}
                onChange={(e) => handleDetailsChange('host_name', e.target.value)}
                placeholder="e.g. Sandra Spinelli"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Host Title</Label>
              <Input
                value={cd.host_title || ''}
                onChange={(e) => handleDetailsChange('host_title', e.target.value)}
                placeholder="e.g. Territory Manager"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Host Email</Label>
              <Input
                type="email"
                value={cd.host_email || ''}
                onChange={(e) => handleDetailsChange('host_email', e.target.value)}
                placeholder="host@sponsor.com"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Program Coordinator</Label>
              <Input
                value={cd.coordinator_name || ''}
                onChange={(e) => handleDetailsChange('coordinator_name', e.target.value)}
                placeholder="e.g. Tracy Schaede"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Coordinator Email</Label>
              <Input
                type="email"
                value={cd.coordinator_email || ''}
                onChange={(e) => handleDetailsChange('coordinator_email', e.target.value)}
                placeholder="coordinator@bureau.com"
                className="h-11"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="p-4 bg-ui-red-bg rounded-xl border border-ui-red-line">
          <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Notes</Label>
          <Textarea
            value={formData.notes || cd.notes || ''}
            onChange={(e) => {
              handleInputChange('notes', e.target.value);
              handleDetailsChange('notes', e.target.value);
            }}
            placeholder="Any additional details about this speaker program"
            className="min-h-[80px]"
          />
        </div>
      </div>
    );
  };

  // Comprehensive Meeting Component with Attendee Management and Scheduling
  const renderMeetingFields = () => {
    // Add from contacts handler
    const handleAddFromContacts = (contact) => {
      const newAttendee = {
        tempId: `exec-${Date.now()}-${Math.random()}`,
        id: contact.id,
        name: `${contact.first_name} ${contact.last_name}`,
        email: contact.email || '',
        phone: contact.phone || '',
        company: contact.company || '',
        title: contact.title || '',
        ea_name: '',
        ea_email: '',
        ea_phone: ''
      };
      
      // Check if already added
      if (!attendees.some(attendee => attendee.id === contact.id)) {
        setAttendees(prev => [...prev, newAttendee]);
      }
      
      setExecutiveSearchQuery('');
      setShowExecutiveSuggestions(false);
    };


    // Compute display status to avoid initial mismatch when editing existing components
    const computedDisplayStatus = (() => {
      const compStatus = component?.status;
      const formStatus = formData.status;
      if (component?.id) {
        if (!formStatus || formStatus === '') return compStatus || 'pending';
        if (compStatus && compStatus !== 'pending' && formStatus === 'pending') return compStatus;
      }
      return formStatus || 'pending';
    })();

    return (
      <div className="space-y-6">
        {/* Meeting Details Section - moved first */}
        <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <MapPin className="w-5 h-5 text-ui-teal-fg" />
            Meeting Details
          </Label>
          <div className="space-y-4">
            {/* Venue & Location Section */}
            <div>
              <Label htmlFor="location" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                <MapPin className="w-4 h-4 text-ui-teal-fg" />
                Location / Venue (Optional - can be TBD)
              </Label>
              <VenueAutocomplete
                placeholder="Search for meeting location or address..."
                value={formData.location || ''}
                onChange={handleLocationChange}
                onPlaceSelect={handleMeetingLocationSelect}
                className="w-full max-w-full h-12 text-base border-ui-line focus:border-ui-teal-solid focus:ring-ui-teal-solid bg-ui-surface text-ui-ink"
              />
              <p className="text-xs text-ui-muted mt-1">
                Search by venue name, company office, or street address
              </p>
            </div>
            
            {/* Meeting Status Section */}
            <div>
              <Label htmlFor="meeting_status" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                <svg className="w-4 h-4 text-ui-teal-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Meeting Status
              </Label>
              {(() => {
                const computedDisplayStatus = (() => {
                  // Prefer actual component.status during edit if form state is unset or shows a default 'pending'
                  const compStatus = component?.status;
                  const formStatus = formData.status;
                  if (component?.id) {
                    if (!formStatus || formStatus === '') return compStatus || 'pending';
                    // If component is confirmed but form shows pending due to race, prefer confirmed
                    if (compStatus && compStatus !== 'pending' && formStatus === 'pending') return compStatus;
                  }
                  return formStatus || 'pending';
                })();
                return (
                  <Select
                    value={computedDisplayStatus}
                    onValueChange={(value) => handleInputChange('status', value)}
                  >
                    <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-teal-solid focus:ring-ui-teal-solid">
                      <SelectValue placeholder="Select meeting status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-ui-amber-solid rounded-full"></div>
                          Pending
                        </div>
                      </SelectItem>
                      <SelectItem value="confirmed">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-ui-green-solid rounded-full"></div>
                          Confirmed
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                );
              })()}
              <p className="text-xs text-ui-muted mt-1">
                Current status of the meeting confirmation
              </p>
            </div>
          </div>
        </div>

        {/* Meeting Schedule Section - moved after location */}
        <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-ui-teal-fg" />
            Meeting Schedule
          </Label>
          <div className="space-y-4">
            {/* Meeting Date & Time Input - Separated Fields */}
            <div className="grid md:grid-cols-3 gap-4">
              {/* Date Field */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="meeting_date" className="text-sm font-bold text-ui-ink-2 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-ui-teal-fg" />
                    Meeting Date *
                  </Label>
                  <Button
                    type="button"
                    variant={isDateTBD ? "default" : "outline"}
                    size="sm"
                    onClick={handleDateTBDToggle}
                    className={`text-xs h-7 px-2 ${isDateTBD ? 'bg-ui-amber-strong hover:bg-ui-amber-strong/90 text-ui-on-primary' : 'text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-bg'}`}
                  >
                    {isDateTBD ? '✓ TBD' : 'TBD'}
                  </Button>
                </div>
                
                {isDateTBD ? (
                  <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                    <span className="text-ui-amber-fg font-medium">📅 Date To Be Determined</span>
                  </div>
                ) : (
                  <DatePickerButton
                    label={(() => {
                      if (!formData.start_datetime) return 'Select date';
                      try {
                        const base = formData.start_datetime.includes('-TBD-TIME')
                          ? formData.start_datetime.split('-TBD-TIME')[0]
                          : formData.start_datetime.split('T')[0];
                        return format(parseLocalDate(base), 'EEE, MMM d, yyyy');
                      } catch { return 'Select date'; }
                    })()}
                    selectedDate={(() => {
                      if (!formData.start_datetime) return undefined;
                      try {
                        const base = formData.start_datetime.includes('-TBD-TIME')
                          ? formData.start_datetime.split('-TBD-TIME')[0]
                          : formData.start_datetime.split('T')[0];
                        return parseLocalDate(base);
                      } catch { return undefined; }
                    })()}
                    onPick={(date) => {
                      const iso = date ? format(date, 'yyyy-MM-dd') : '';
                      if (iso) {
                        handleMeetingDateChange(iso);
                      }
                    }}
                  />
                )}
                
                <p className="text-xs text-ui-muted mt-1">
                  {isDateTBD ? 'Meeting date will be determined later' : 'Select meeting date or set to TBD'}
                </p>
              </div>

              {/* Start Time Field */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="meeting_start_time" className="text-sm font-bold text-ui-ink-2 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-ui-teal-fg" />
                    Start Time *
                  </Label>
                  <Button
                    type="button"
                    variant={isStartTimeTBD ? "default" : "outline"}
                    size="sm"
                    onClick={handleStartTimeTBDToggle}
                    className={`text-xs h-7 px-2 ${isStartTimeTBD ? 'bg-ui-amber-strong hover:bg-ui-amber-strong/90 text-ui-on-primary' : 'text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-bg'}`}
                  >
                    {isStartTimeTBD ? '✓ TBD' : 'TBD'}
                  </Button>
                </div>

                {isStartTimeTBD ? (
                  <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                    <span className="text-ui-amber-fg font-medium">🕐 Time To Be Determined</span>
                  </div>
                ) : (
                  <select
                    id="meeting_start_time"
                    value={formData.start_datetime ? (() => {
                      try {
                        if (formData.start_datetime.includes('TBD')) return '';
                        return format(new Date(formData.start_datetime), 'HH:mm');
                      } catch (error) {
                        return '';
                      }
                    })() : ''}
                    onChange={(e) => handleMeetingTimeChange(e.target.value)}
                    className="h-12 w-full text-base border border-ui-line rounded-md px-3 focus:border-ui-teal-solid focus:ring-ui-teal-solid focus:ring-1 focus:outline-none bg-ui-surface text-ui-ink"
                  >
                    <option value="">Select time</option>
                    {timeOptions.map(({ value, display }) => (
                      <option key={value} value={value}>
                        {display}
                      </option>
                    ))}
                  </select>
                )}

                <p className="text-xs text-ui-muted mt-1">
                  {isStartTimeTBD ? 'Meeting time will be determined later' : '15-minute increments or set to TBD'}
                </p>
              </div>
              
              {/* End Time Field */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="meeting_end_time" className="text-sm font-bold text-ui-ink-2 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-ui-teal-fg" />
                    End Time (Optional)
                  </Label>
                  <Button
                    type="button"
                    variant={isEndTimeTBD ? "default" : "outline"}
                    size="sm"
                    onClick={handleEndTimeTBDToggle}
                    className={`text-xs h-7 px-2 ${isEndTimeTBD ? 'bg-ui-amber-strong hover:bg-ui-amber-strong/90 text-ui-on-primary' : 'text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-bg'}`}
                  >
                    {isEndTimeTBD ? '✓ TBD' : 'TBD'}
                  </Button>
                </div>

                {isEndTimeTBD ? (
                  <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                    <span className="text-ui-amber-fg font-medium">🕐 End Time To Be Determined</span>
                  </div>
                ) : (
                  <select
                    id="meeting_end_time"
                    value={formData.end_datetime ? (() => {
                      try {
                        if (formData.end_datetime === 'TBD') return '';
                        return format(new Date(formData.end_datetime), 'HH:mm');
                      } catch (error) {
                        return '';
                      }
                    })() : ''}
                    onChange={(e) => handleEndTimeChange(e.target.value)}
                    disabled={!formData.start_datetime || isStartTimeTBD}
                    className="h-12 w-full text-base border border-ui-line rounded-md px-3 focus:border-ui-teal-solid focus:ring-ui-teal-solid focus:ring-1 focus:outline-none disabled:bg-ui-hover disabled:cursor-not-allowed bg-ui-surface text-ui-ink"
                  >
                    <option value="">No end time</option>
                    {endTimeOptions.map(({ value, display }) => (
                      <option key={value} value={value}>
                        {display}
                      </option>
                    ))}
                  </select>
                )}

                <p className="text-xs text-ui-muted mt-1">
                  {isEndTimeTBD ? 'End time will be determined later' : 
                   isStartTimeTBD ? 'Set start time first' :
                   formData.start_datetime ? 'Starts from meeting time or set to TBD' : 'Select start time first'}
                </p>
              </div>
            </div>

            {/* Meeting Duration Display */}
            {(formData.start_datetime && formData.end_datetime) || isDateTBD || isStartTimeTBD || isEndTimeTBD ? (
              <MeetingDurationDisplay 
                startTime={formData.start_datetime}
                endTime={formData.end_datetime}
              />
            ) : null}
          </div>
        </div>

        {/* Primary Executive Selection — hidden in speaker_bureau mode */}
        {!isSpeakerBureau && (
        <div className="p-4 bg-ui-blue-bg rounded-xl border border-ui-blue-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Users className="w-5 h-5 text-ui-link" />
            Primary Executive (Optional)
          </Label>

          <div>
            <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
              Select the main executive for this meeting
            </Label>
            <select
              value={formData.component_data?.primary_executive_id || ''}
              onChange={(e) => handleDetailsChange('primary_executive_id', e.target.value)}
              className="w-full h-12 px-4 py-3 border border-ui-line rounded-lg focus:border-ui-primary focus:ring-ui-primary focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
            >
              <option value="">Select Primary Executive</option>
              {(meetingExecutives?.length ? meetingExecutives : tripExecutives).map(executive => (
                <option key={executive.id} value={executive.id}>
                  {executive.first_name} {executive.last_name} - {executive.title}
                </option>
              ))}
            </select>
            {(!tripExecutives || tripExecutives.length === 0) && (
              <p className="text-xs text-ui-orange-fg mt-1">
                ⚠️ No executives available. Please add executives to the trip first.
              </p>
            )}
          </div>
        </div>
        )}

        {/* Attendees Management Section */}
        <div className="p-4 bg-ui-sunk rounded-xl border border-ui-line">
          <div className="flex items-center justify-between mb-4">
            <Label className="text-lg font-semibold text-ui-ink flex items-center gap-2">
              <Users className="w-5 h-5 text-ui-ink-3" />
              Meeting Attendees (Optional)
            </Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowAttendeeForm(true)}
                className="bg-ui-surface hover:bg-ui-hover border-ui-control"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Contact
              </Button>
            </div>
          </div>
          
          {/* Attendee Search Section */}
          <div className="mb-4">
            <Label className="text-sm font-medium text-ui-ink-3 mb-2 block">
              Quick Add from Contacts
            </Label>
            <div className="relative">
              <Input
                key="executive-search"
                placeholder="Search existing contacts..."
                value={executiveSearchQuery}
                onChange={handleExecutiveSearchChange}
                onFocus={handleExecutiveSearchFocus}
                className="h-11 pr-10 bg-ui-surface border-ui-control focus:border-ui-teal-solid focus:ring-ui-teal-solid"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ui-muted" />
              
              {showExecutiveSuggestions && (
                <AttendeeSearchDropdown 
                  finalAvailableExecutives={finalAvailableExecutives}
                  executiveSearchQuery={executiveSearchQuery}
                  onAddAttendee={handleAddFromContacts}
                />
              )}
            </div>
            <p className="text-xs text-ui-muted mt-1">
              Start typing to search existing contacts by name, company, or email
            </p>
          </div>
          
          <CurrentAttendeesSection
            attendees={attendees}
            showAllAttendees={showAllAttendees}
            setShowAllAttendees={setShowAllAttendees}
            onEditAttendee={handleEditAttendee}
            onDeleteAttendee={handleDeleteAttendee}
            isSpeakerBureau={isSpeakerBureau}
            speakerLabel={labels.executive}
            onLinkSpeaker={handleLinkAttendeeSpeaker}
          />
          
          {attendees.length === 0 && (
            <EmptyAttendeesState onAddNewContact={() => setShowAttendeeForm(true)} />
          )}
        </div>

        {/* File Management Section */}
        <div className="space-y-6">
          {/* Document Import Section */}
          <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line" style={{border: '2px solid red', background: 'yellow'}}>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-lg font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5 mr-2 text-ui-teal-fg" />
                Meeting Documents
              </Label>
              <div className="relative">
                <input
                  type="file"
                  id="meeting-file-upload"
                  className="hidden"
                  onChange={(e) => {
                    handleFileUpload(e);
                  }}
                  disabled={isUploadingFile}
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg"
                />
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    const fileInput = document.getElementById('meeting-file-upload');
                    if (fileInput) {
                      fileInput.click();
                    } else {
                      console.error('File input not found');
                    }
                  }}
                  size="sm"
                  className="bg-ui-teal-strong hover:bg-ui-teal-strong/90 text-ui-on-primary"
                  disabled={isUploadingFile}
                >
                  {isUploadingFile ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" /> Upload Documents
                    </>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-sm text-ui-ink-3">
              Upload agendas, presentations, contracts, or other meeting documents.
              <br />
              <span className="text-xs text-ui-muted">Supported formats: PDF, Word, PowerPoint, Excel, Images</span>
            </p>
          </div>
          
          {/* Attached Documents Display */}
          <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-ui-teal-fg" />
              <Label className="text-lg font-semibold text-ui-ink">
                Attached Documents ({attachedFiles.length})
              </Label>
            </div>

            {attachedFiles.length > 0 ? (
              <div className="space-y-3">
                {attachedFiles.map((file) => (
                  <div key={file.id} className="p-4 bg-ui-surface rounded-lg border border-ui-teal-line hover:border-ui-teal-line transition-colors shadow-sm">
                    {/* File Header with Name and Actions */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-ui-blue-bg rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-ui-link" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-ui-ink mb-1">
                            <Input
                              value={attachmentLabel(file)}
                              onChange={(e) => handleFileNameChange(file.id, e.target.value)}
                              className="h-8 text-sm font-medium border-none p-0 bg-transparent hover:bg-ui-hover focus:bg-ui-surface focus:border-ui-line"
                              placeholder="Document name"
                            />
                          </div>
                          <div className="text-sm text-ui-muted">
                            {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'} • 
                            {file.label || 'No description'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleViewDocument(file);
                          }}
                          className="h-8 w-8 text-ui-link hover:text-ui-blue-fg hover:bg-ui-blue-bg p-0"
                          title="View document"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDownloadDocument(file);
                          }}
                          className="h-8 w-8 text-ui-green-fg hover:text-ui-green-fg hover:bg-ui-green-bg p-0"
                          title="Download document"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFileDelete(file.id)}
                          className="h-8 w-8 text-ui-red-fg hover:text-ui-red-fg hover:bg-ui-red-bg p-0"
                          title="Delete document"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Document Description */}
                    <div className="space-y-2">
                      <Label htmlFor={`meeting-file-label-${file.id}`} className="text-sm text-ui-ink-3">
                        Document Description
                      </Label>
                      <Input
                        id={`meeting-file-label-${file.id}`}
                        value={file.label || ''}
                        onChange={(e) => handleFileLabelChange(file.id, e.target.value)}
                        placeholder="Enter a description for this document (e.g., Meeting Agenda, Presentation Slides, Contract)"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-ui-muted bg-ui-surface rounded-lg border-2 border-dashed border-ui-teal-line">
                <Upload className="w-12 h-12 text-ui-teal-fg mx-auto mb-3" />
                <p className="font-medium">No documents attached yet</p>
                <p className="text-sm">Upload agendas, presentations, contracts, or other meeting materials</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Comprehensive Hotel Component with Google Places Integration and OCR Processing
  const renderHotelFields = () => {
    // Executive Validation Check — not applicable in speaker_bureau mode
    // (the program is scoped to a single speaker and the executive selector is
    // hidden, so there are never "trip assigned executives" to gate on).
    if (!isSpeakerBureau && tripAssignedExecutives.length === 0 && !component?.id) {
      return (
        <div className="p-4 my-4 bg-ui-amber-bg border-l-4 border-ui-amber-solid rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-ui-amber-fg" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-ui-amber-fg">
                Please add at least one executive to the trip before adding a hotel.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Hotel Location Selection Handler with Google Places Integration
    const handleLocationSelect = (place) => {
      const address = place.formatted_address || '';
      const name = place.name || '';

      if (formData.component_type === 'hotel') {
        // Update hotel-specific details
        handleDetailsChange('hotel_name', name);
        if (address) handleDetailsChange('address', address);
        if (address) handleDetailsChange('hotel_address', address);

        // Store Google Place ID for chauffeur service booking integration
        if (place.place_id) handleDetailsChange('place_id', place.place_id);

        // Auto-update main form fields if empty
        if (!formData.title) handleInputChange('title', name);
        if (!formData.location) handleInputChange('location', name);
        if (!formData.address) handleInputChange('address', address);

        // Extract additional hotel information if available
        if (place.rating) handleDetailsChange('hotel_rating', place.rating);
        if (place.price_level) handleDetailsChange('price_level', place.price_level);
        if (place.formatted_phone_number) handleDetailsChange('hotel_phone', place.formatted_phone_number);
        if (place.website) handleDetailsChange('hotel_website', place.website);
      }
    };

    // Stay Duration Display - simple inline render (no hooks allowed in render functions)
    const renderStayDuration = (checkIn, checkOut) => {
      const checkInDate = new Date(checkIn);
      const checkOutDate = new Date(checkOut);
      const diffTime = Math.abs(checkOutDate - checkInDate);
      const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return (
        <div className="p-3 bg-ui-green-bg border border-ui-green-line rounded-lg">
          <div className="flex items-center gap-2 text-ui-green-fg">
            <CalendarIcon className="w-4 h-4" />
            <span className="font-semibold">
              {nights} night{nights !== 1 ? 's' : ''} stay
            </span>
          </div>
          <div className="text-sm text-ui-green-fg mt-1">
            From {format(checkInDate, 'MMM d, yyyy')} to {format(checkOutDate, 'MMM d, yyyy')}
          </div>
        </div>
      );
    };

    // Executive Selector Component
    const ExecutiveSelector = ({ executives, selectedExecutives, onExecutiveChange }) => {
      return (
        <div className="space-y-2">
          {(!executives || executives.length === 0) ? (
            <div className="p-3 bg-ui-amber-bg border border-ui-amber-line rounded-lg">
              <p className="text-sm text-ui-amber-fg">No executives available for this trip.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {executives.map((executive) => (
                <div key={executive.id} className="flex items-center space-x-3 p-2 border border-ui-line rounded-lg hover:bg-ui-hover">
                  <input
                    type="checkbox"
                    id={`executive-${executive.id}`}
                    checked={selectedExecutives.includes(executive.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onExecutiveChange([...selectedExecutives, executive.id]);
                      } else {
                        onExecutiveChange(selectedExecutives.filter(id => id !== executive.id));
                      }
                    }}
                    className="rounded border-ui-control text-ui-green-fg focus:ring-ui-green-solid"
                  />
                  <label 
                    htmlFor={`executive-${executive.id}`}
                    className="flex-1 text-sm font-medium text-ui-ink-2 cursor-pointer"
                  >
                    {executive.first_name} {executive.last_name}
                    <span className="text-ui-muted ml-2">({executive.title || 'Executive'})</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    // Room Details Manager - inline JSX to prevent focus loss on re-render
    const hotelRooms = formData.component_data?.room_details || [];
    // Use number_of_rooms if set, otherwise use the actual room_details length, default to 1
    const hotelNumberOfRooms = formData.component_data?.number_of_rooms || hotelRooms.length || 1;

    // Guest Distribution Summary Component (must be defined before roomDetailsContent)
    const GuestDistributionSummary = ({ rooms, totalGuests }) => {
      const allocatedGuests = rooms.reduce((sum, room) => sum + (room.guests || 0), 0);
      const remainingGuests = (totalGuests || 0) - allocatedGuests;

      return (
        <div className="p-3 bg-ui-sunk border border-ui-line rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-ui-ink-2">Guest Distribution Summary:</span>
            <div className="flex gap-4">
              <span className="text-ui-ink-3">
                Allocated: <span className="font-semibold text-ui-green-fg">{allocatedGuests}</span>
              </span>
              <span className="text-ui-ink-3">
                Total: <span className="font-semibold">{totalGuests || 0}</span>
              </span>
              {remainingGuests !== 0 && (
                <span className={`font-semibold ${remainingGuests < 0 ? 'text-ui-red-fg' : 'text-ui-orange-fg'}`}>
                  {remainingGuests < 0 ? 'Overallocated' : 'Remaining'}: {Math.abs(remainingGuests)}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    };

    const roomDetailsContent = (
      <div className="space-y-4">
          <Label className="text-base font-semibold text-ui-ink block">
            Room Details & Guest Distribution
          </Label>
          
          {Array.from({ length: hotelNumberOfRooms }, (_, index) => {
            const room = hotelRooms[index] || {};
            return (
              <div key={index} className="p-4 bg-ui-surface border border-ui-green-line rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-ui-ink">Room {index + 1}</h4>
                  {hotelNumberOfRooms > 1 && (
                    <Badge variant="outline" className="text-ui-green-fg border-ui-green-line">
                      Room {index + 1} of {hotelNumberOfRooms}
                    </Badge>
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
                      Room Type
                    </Label>
                    <select
                      value={room.room_type || ''}
                      onChange={(e) => updateRoomDetail(index, 'room_type', e.target.value)}
                      className="w-full h-10 px-3 py-2 border border-ui-line rounded-md focus:border-ui-green-solid focus:ring-1 focus:ring-ui-green-solid focus:outline-none bg-ui-surface text-ui-ink"
                    >
                      <option value="">Select room type</option>
                      <option value="standard">Standard Room</option>
                      <option value="deluxe">Deluxe Room</option>
                      <option value="suite">Suite</option>
                      <option value="junior_suite">Junior Suite</option>
                      <option value="executive_suite">Executive Suite</option>
                      <option value="presidential_suite">Presidential Suite</option>
                      <option value="king">King Room</option>
                      <option value="queen">Queen Room</option>
                      <option value="double">Double Room</option>
                      <option value="twin">Twin Room</option>
                      <option value="connecting">Connecting Rooms</option>
                      <option value="accessible">Accessible Room</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
                      Guests in this Room
                    </Label>
                    <Input
                      type="number"
                      value={room.guests || ''}
                      onChange={(e) => updateRoomDetail(index, 'guests', parseInt(e.target.value) || 0)}
                      placeholder="Number of guests"
                      min="1"
                      max="6"
                      className="h-10"
                    />
                  </div>
                </div>
                
                {/* Room-specific preferences */}
                <div className="mt-4 space-y-3">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-ui-ink-2 mb-1 block">
                        Bed type
                      </Label>
                      <select
                        value={room.bed_preference || ''}
                        onChange={(e) => updateRoomDetail(index, 'bed_preference', e.target.value)}
                        className="w-full h-9 px-2 py-1 text-sm border border-ui-line rounded-md focus:border-ui-green-solid focus:ring-1 focus:ring-ui-green-solid focus:outline-none bg-ui-surface text-ui-ink"
                      >
                        <option value="">Select bed type</option>
                        <option value="king">King Bed</option>
                        <option value="queen">Queen Bed</option>
                        <option value="double">Double Beds</option>
                        <option value="twin">Twin Beds</option>
                        <option value="no_preference">No Preference</option>
                      </select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-ui-ink-2 mb-1 block">
                        Special Notes
                      </Label>
                      <Input
                        value={room.special_notes || ''}
                        onChange={(e) => updateRoomDetail(index, 'special_notes', e.target.value)}
                        placeholder="Room-specific requests"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>

                  {/* Per-room guest assignment - only show when multiple rooms */}
                  {hotelNumberOfRooms > 1 && (
                    <div className="mt-3 pt-3 border-t border-ui-line">
                      <Label className="text-sm font-medium text-ui-ink-2 mb-2 block flex items-center gap-2">
                        <User className="w-4 h-4 text-ui-green-fg" />
                        Assigned Guests (Optional)
                      </Label>

                      {/* Search input for filtering guests */}
                      <div className="relative mb-2">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ui-muted" />
                        <Input
                          type="text"
                          placeholder={isSpeakerBureau ? 'Search guests or contacts...' : 'Search executives or contacts...'}
                          value={roomGuestSearchQueries[index] || ''}
                          onChange={(e) => {
                            setRoomGuestSearchQueries(prev => ({
                              ...prev,
                              [index]: e.target.value
                            }));
                          }}
                          className="h-8 text-sm pl-8 pr-3"
                        />
                      </div>

                      {/* Guest list with search filtering */}
                      <div className="space-y-1 max-h-40 overflow-y-auto border border-ui-line rounded-md p-2 bg-ui-sunk">
                        {(() => {
                          const searchQuery = (roomGuestSearchQueries[index] || '').toLowerCase().trim();
                          const roomGuestIds = room.guest_ids || [];

                          // Filter allExecutivesList based on search query
                          const filteredGuests = (allExecutivesList || []).filter(person => {
                            if (!searchQuery) return true;
                            const name = person.name || `${person.first_name || ''} ${person.last_name || ''}`;
                            const email = person.email || '';
                            const company = person.company || '';
                            return name.toLowerCase().includes(searchQuery) ||
                                   email.toLowerCase().includes(searchQuery) ||
                                   company.toLowerCase().includes(searchQuery);
                          });

                          // Show assigned guests at the top, then others
                          const assignedGuests = filteredGuests.filter(p => roomGuestIds.includes(p.id));
                          const unassignedGuests = filteredGuests.filter(p => !roomGuestIds.includes(p.id));
                          const sortedGuests = [...assignedGuests, ...unassignedGuests];

                          if (sortedGuests.length === 0 && searchQuery) {
                            return (
                              <div className="text-center py-3 text-ui-muted text-sm">
                                No matching guests found.
                                <button
                                  type="button"
                                  onClick={() => handleOpenRoomGuestContactModal(index, searchQuery)}
                                  className="ml-2 text-ui-green-fg hover:text-ui-green-fg underline"
                                >
                                  Add new contact
                                </button>
                              </div>
                            );
                          }

                          if (sortedGuests.length === 0) {
                            return (
                              <div className="text-center py-3 text-ui-muted text-sm">
                                No executives or contacts available.
                                <button
                                  type="button"
                                  onClick={() => handleOpenRoomGuestContactModal(index, '')}
                                  className="ml-2 text-ui-green-fg hover:text-ui-green-fg underline"
                                >
                                  Add new contact
                                </button>
                              </div>
                            );
                          }

                          return sortedGuests.map((person) => {
                            const isAssigned = roomGuestIds.includes(person.id);
                            const personName = person.name || `${person.first_name || ''} ${person.last_name || ''}`.trim();
                            // Check if assigned to another room
                            const assignedToOtherRoom = hotelRooms.some((otherRoom, otherIndex) =>
                              otherIndex !== index && (otherRoom.guest_ids || []).includes(person.id)
                            );
                            const isExecutive = person.type === 'executive' || person.source === 'executive';

                            return (
                              <div
                                key={person.id}
                                className={`flex items-center space-x-2 p-2 rounded-md transition-colors ${
                                  isAssigned ? 'bg-ui-green-bg border border-ui-green-line' :
                                  assignedToOtherRoom ? 'bg-ui-hover opacity-50' : 'bg-ui-surface hover:bg-ui-hover'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  id={`room-${index}-guest-${person.id}`}
                                  checked={isAssigned}
                                  disabled={assignedToOtherRoom && !isAssigned}
                                  onChange={(e) => {
                                    const currentGuestIds = room.guest_ids || [];
                                    const currentGuestNames = room.guest_names || [];
                                    let newGuestIds;
                                    let newGuestNames;
                                    if (e.target.checked) {
                                      newGuestIds = [...currentGuestIds, person.id];
                                      newGuestNames = [...currentGuestNames, personName];
                                    } else {
                                      const removeIndex = currentGuestIds.indexOf(person.id);
                                      newGuestIds = currentGuestIds.filter(id => id !== person.id);
                                      newGuestNames = currentGuestNames.filter((_, i) => i !== removeIndex);
                                    }
                                    // Update both guest_ids and guest_names
                                    const currentRooms = formData.component_data?.room_details || [];
                                    const updatedRooms = [...currentRooms];
                                    if (!updatedRooms[index]) {
                                      updatedRooms[index] = {};
                                    }
                                    // Ensure room guest count is at least the number of assigned guests
                                    const currentRoomGuests = updatedRooms[index].guests || 1;
                                    const newGuestCount = Math.max(currentRoomGuests, newGuestIds.length, 1);
                                    updatedRooms[index] = {
                                      ...updatedRooms[index],
                                      guest_ids: newGuestIds,
                                      guest_names: newGuestNames,
                                      guests: newGuestCount
                                    };
                                    handleDetailsChange('room_details', updatedRooms);

                                    // Update total_guests to be the sum of all room guests (minimum 1)
                                    const totalGuests = updatedRooms.reduce((sum, rm) => sum + (rm.guests || 1), 0);
                                    handleDetailsChange('total_guests', Math.max(totalGuests, 1));

                                    // Sync assigned_executives
                                    const allGuestIds = updatedRooms.reduce((ids, rm) => {
                                      const rmGuestIds = rm.guest_ids || [];
                                      return [...ids, ...rmGuestIds];
                                    }, []);
                                    const uniqueGuestIds = [...new Set(allGuestIds)];
                                    handleDetailsChange('assigned_executives', uniqueGuestIds);
                                  }}
                                  className="rounded border-ui-control text-ui-green-fg focus:ring-ui-green-solid"
                                />
                                <label
                                  htmlFor={`room-${index}-guest-${person.id}`}
                                  className={`flex-1 text-sm cursor-pointer ${
                                    assignedToOtherRoom && !isAssigned ? 'text-ui-muted' : 'text-ui-ink-2'
                                  }`}
                                >
                                  <span className="font-medium">{personName}</span>
                                  {person.title && <span className="text-ui-muted ml-1">({person.title})</span>}
                                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                                    isExecutive ? 'bg-ui-blue-bg text-ui-link' : 'bg-ui-violet-bg text-ui-violet-fg'
                                  }`}>
                                    {isExecutive ? labels.executive : 'Contact'}
                                  </span>
                                  {assignedToOtherRoom && !isAssigned && (
                                    <span className="text-xs text-ui-muted ml-2">(in another room)</span>
                                  )}
                                </label>
                              </div>
                            );
                          });
                        })()}
                      </div>

                      {/* Add new contact button */}
                      <button
                        type="button"
                        onClick={() => handleOpenRoomGuestContactModal(index, '')}
                        className="mt-2 w-full flex items-center justify-center gap-2 py-2 px-3 text-sm text-ui-green-fg hover:text-ui-green-fg hover:bg-ui-green-bg border border-dashed border-ui-green-line rounded-md transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add New Contact
                      </button>

                      {/* Summary of assigned guests */}
                      {(room.guest_ids || []).length > 0 && (
                        <div className="mt-2 text-xs text-ui-green-fg">
                          {(room.guest_ids || []).length} guest{(room.guest_ids || []).length !== 1 ? 's' : ''} assigned to this room
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* Guest Distribution Summary */}
          <GuestDistributionSummary rooms={hotelRooms} totalGuests={formData.component_data?.total_guests} />
        </div>
    );

    // Attached Documents Section Component  
    const AttachedDocumentsSection = () => (
      <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line">
        <div className="flex items-center justify-between mb-4">
          <Label className="text-lg font-semibold flex items-center gap-2">
            <Upload className="w-5 h-5 text-ui-teal-fg" />
            Attached Documents
          </Label>
          <div className="relative">
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploadingFile}
            />
            <Button
              type="button"
              onClick={() => document.getElementById('file-upload').click()}
              size="sm"
              className="bg-ui-teal-strong hover:bg-ui-teal-strong/90 text-ui-on-primary"
              disabled={isUploadingFile}
            >
              {isUploadingFile ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" /> Upload File
                </>
              )}
            </Button>
          </div>
        </div>

        {attachedFiles.length > 0 ? (
          <AttachedFilesList files={attachedFiles} />
        ) : (
          <EmptyDocumentsState />
        )}
      </div>
    );

    const EmptyDocumentsState = () => (
      <div className="text-center py-8 text-ui-muted bg-ui-surface rounded-lg border-2 border-dashed border-ui-teal-line">
        <Upload className="w-12 h-12 text-ui-teal-fg mx-auto mb-3" />
        <p>No documents attached yet.</p>
        <p className="text-sm mt-1">Upload confirmations, receipts, or other hotel documents.</p>
      </div>
    );

    const AttachedFilesList = ({ files }) => (
      <div className="space-y-3">
        {files.map((file) => (
          <div key={file.id} className="p-3 bg-ui-surface rounded-lg border border-ui-line hover:border-ui-control transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-ui-blue-bg rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-ui-link" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-ui-ink">{attachmentLabel(file)}</div>
                  <div className="text-xs text-ui-muted">
                    {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleViewDocument(file);
                  }}
                  className="text-ui-link hover:text-ui-blue-fg"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                          {hasParsedAttachment(file) && (
                            <Button type="button" variant="ghost" size="sm"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewDocument(file, true); }}>
                              Parsed data (JSON)
                            </Button>
                          )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFileDelete(file.id)}
                  className="text-ui-red-fg hover:text-ui-red-fg"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-ui-ink-3 mb-1 block">Document Label</Label>
              <Input
                value={file.label || ''}
                onChange={(e) => handleFileLabelChange(file.id, e.target.value)}
                placeholder="Enter description for this file"
                className="h-7 text-xs"
              />
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Hotel Details Section */}
        <div className="p-4 bg-ui-green-bg rounded-xl border border-ui-green-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Building className="w-5 h-5 text-ui-green-fg" />
            Hotel Details
          </Label>
          <div className="space-y-4">
            {/* Hotel Import Section - Moved to top as requested */}
            <div className="p-3 bg-ui-blue-bg border border-ui-blue-line rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-ui-blue-fg flex items-center gap-2">
                  <Upload className="w-4 h-4 text-ui-link" />
                  Import Hotel Confirmation
                </Label>
                <div className="relative">
                  <input
                    type="file"
                    id="hotel-confirmation-upload"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                    onChange={handleHotelConfirmationUpload}
                    disabled={isUploadingHotelConfirmation}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('hotel-confirmation-upload').click()}
                    size="sm"
                    className="bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary"
                    disabled={isUploadingHotelConfirmation}
                  >
                    {isUploadingHotelConfirmation ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" /> Choose File
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-ui-link">
                Upload your hotel confirmation email or document to auto-fill booking details
              </p>
            </div>

            {/* Hotel Search & Address Section */}
            <div>
              <Label htmlFor="hotel_name" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Hotel Name *
              </Label>
              <HotelAutocomplete
                placeholder="Search for hotel name or address..."
                value={formData.component_data?.hotel_name || ''}
                onChange={(value) => handleDetailsChange('hotel_name', value)}
                onPlaceSelect={handleLocationSelect}
                className="h-12 text-base border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid bg-ui-surface text-ui-ink"
              />
              <p className="text-xs text-ui-muted mt-1">
                Search by hotel name, brand, or street address
              </p>
            </div>

            {/* Hotel Address Field */}
            <div>
              <Label htmlFor="hotel_address" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Address
              </Label>
              <Input
                id="hotel_address"
                placeholder="Hotel address..."
                value={formData.component_data?.address || formData.component_data?.hotel_address || ''}
                onChange={(e) => {
                  handleDetailsChange('address', e.target.value);
                  handleDetailsChange('hotel_address', e.target.value);
                }}
                className="h-12 text-base border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid bg-ui-surface text-ui-ink"
              />
              <p className="text-xs text-ui-muted mt-1">
                Auto-filled from confirmation or Google Places
              </p>
            </div>

            {/* Booking Dates Section */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Check-in Date and Time */}
              <div>
                <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-ui-green-fg" />
                  Check-in Date & Time *
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <DatePickerButton
                    label={(() => {
                      if (!formData.component_data?.check_in) return 'Select date';
                      try {
                        const base = formData.component_data.check_in.split('T')[0];
                        return format(parseLocalDate(base), 'EEE, MMM d, yyyy');
                      } catch { return 'Select date'; }
                    })()}
                    selectedDate={(() => {
                      if (!formData.component_data?.check_in) return undefined;
                      try {
                        const base = formData.component_data.check_in.split('T')[0];
                        return parseLocalDate(base);
                      } catch { return undefined; }
                    })()}
                    onPick={(date) => {
                      const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                      if (isoDate) {
                        // Preserve existing time if available, otherwise use default 15:00.
                        // Keep only HH:MM — the stored value may carry seconds and a UTC
                        // offset (added server-side for the hotel's timezone) that must
                        // not be written back as part of the wall-clock time.
                        const existingDateTime = formData.component_data?.check_in || '';
                        const existingTime = (existingDateTime.includes('T') && existingDateTime.split('T')[1].substring(0, 5)) || '15:00';
                        const newDateTime = `${isoDate}T${existingTime}`;
                        handleDetailsChange('check_in', newDateTime);
                        handleInputChange('start_datetime', newDateTime);
                      } else {
                        handleDetailsChange('check_in', '');
                        handleInputChange('start_datetime', '');
                      }
                    }}
                  />
                  <Input
                    type="time"
                    value={(() => {
                      const checkIn = formData.component_data?.check_in;
                      if (checkIn && checkIn.includes('T')) {
                        const timePart = checkIn.split('T')[1];
                        // Extract just HH:MM from time string (handles HH:MM:SS, HH:MM:SS.sss, timezone offsets)
                        return timePart ? timePart.substring(0, 5) : '15:00';
                      }
                      return '15:00';
                    })()}
                    onChange={(e) => {
                      const timeValue = e.target.value;
                      const dateValue = formData.component_data?.check_in ?
                        formData.component_data.check_in.split('T')[0] :
                        (formData.start_datetime ? formData.start_datetime.split('T')[0] : new Date().toISOString().split('T')[0]);
                      const newDateTime = `${dateValue}T${timeValue}`;
                      handleDetailsChange('check_in', newDateTime);
                      handleInputChange('start_datetime', newDateTime);
                    }}
                    className="h-10 text-sm border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid"
                    placeholder="Time"
                  />
                </div>
              </div>
              
              {/* Check-out Date and Time */}
              <div>
                <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-ui-green-fg" />
                  Check-out Date & Time *
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <DatePickerButton
                    label={(() => {
                      if (!formData.component_data?.check_out) return 'Select date';
                      try {
                        const base = formData.component_data.check_out.split('T')[0];
                        return format(parseLocalDate(base), 'EEE, MMM d, yyyy');
                      } catch { return 'Select date'; }
                    })()}
                    selectedDate={(() => {
                      if (!formData.component_data?.check_out) return undefined;
                      try {
                        const base = formData.component_data.check_out.split('T')[0];
                        return parseLocalDate(base);
                      } catch { return undefined; }
                    })()}
                    onPick={(date) => {
                      const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                      // HH:MM only — strip seconds/UTC offset the server may have appended
                      const timePart = (formData.component_data?.check_out?.split('T')[1] || '11:00').substring(0, 5);
                      if (isoDate) {
                        handleDetailsChange('check_out', `${isoDate}T${timePart}`);
                        handleInputChange('end_datetime', `${isoDate}T${timePart}`);
                      } else {
                        handleDetailsChange('check_out', '');
                      }
                    }}
                  />
                  <Input
                    type="time"
                    value={(() => {
                      const checkOut = formData.component_data?.check_out;
                      if (checkOut && checkOut.includes('T')) {
                        const timePart = checkOut.split('T')[1];
                        // Extract just HH:MM from time string (handles HH:MM:SS, HH:MM:SS.sss, timezone offsets)
                        return timePart ? timePart.substring(0, 5) : '11:00';
                      }
                      return '11:00';
                    })()}
                    onChange={(e) => {
                      const timeValue = e.target.value;
                      const dateValue = formData.component_data?.check_out ?
                        formData.component_data.check_out.split('T')[0] :
                        (formData.end_datetime ? formData.end_datetime.split('T')[0] : new Date().toISOString().split('T')[0]);
                      const newDateTime = `${dateValue}T${timeValue}`;
                      handleDetailsChange('check_out', newDateTime);
                      handleInputChange('end_datetime', newDateTime);
                    }}
                    className="h-10 text-sm border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid"
                    placeholder="Time"
                  />
                </div>
              </div>
            </div>

            {/* Stay Duration Display */}
            {formData.component_data?.check_in && formData.component_data?.check_out &&
              renderStayDuration(formData.component_data.check_in, formData.component_data.check_out)
            }

            {/* Executive Assignment Section — hidden in speaker_bureau mode
                (program is scoped to one speaker; guests are linked per-room below) */}
            {!isSpeakerBureau && (
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Assigned Executives *
              </Label>
              <ExecutiveSelector
                executives={tripAssignedExecutives}
                selectedExecutives={formData.component_data?.assigned_executives || []}
                onExecutiveChange={(executives) => handleDetailsChange('assigned_executives', executives)}
              />
            </div>
            )}

            {/* Primary Guest — speaker_bureau mode only. No executive roster to
                pick from, so allow a free-text guest name + optional Speaker link
                (mirrors the flight passenger / rental-car renter pattern). */}
            {isSpeakerBureau && (
            <div>
              <Label htmlFor="hotel_primary_guest" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                <User className="w-4 h-4 text-ui-green-fg" />
                Primary Guest
              </Label>
              <div className="space-y-2">
                <Input
                  id="hotel_primary_guest"
                  value={formData.component_data?.primary_guest_name || ''}
                  onChange={(e) => handleDetailsChange('primary_guest_name', e.target.value)}
                  placeholder="Guest name"
                  className="h-12 text-base border-ui-line"
                />
                <SpeakerLinkField
                  speakerId={formData.component_data?.primary_guest_speaker_id || null}
                  speakerName={formData.component_data?.primary_guest_speaker_name || formData.component_data?.primary_guest_name || ''}
                  onPick={(sp) => {
                    if (!sp) {
                      handleDetailsChange('primary_guest_speaker_id', null);
                      handleDetailsChange('primary_guest_speaker_name', '');
                      return;
                    }
                    const nm = sp.full_name || `${sp.first_name || ''} ${sp.last_name || ''}`.trim();
                    handleDetailsChange('primary_guest_speaker_id', sp.id);
                    handleDetailsChange('primary_guest_speaker_name', nm);
                    if (!formData.component_data?.primary_guest_name && nm) handleDetailsChange('primary_guest_name', nm);
                  }}
                />
              </div>
            </div>
            )}

            {/* Total Guests */}
            <div>
              <Label htmlFor="total_guests" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Total Number of Guests
              </Label>
              <Input
                type="number"
                value={formData.component_data?.total_guests || ''}
                onChange={(e) => {
                  const totalGuests = parseInt(e.target.value) || 1;
                  handleDetailsChange('total_guests', totalGuests);
                  // Auto-adjust rooms if needed
                  updateRoomsBasedOnGuests(totalGuests);
                }}
                placeholder="1"
                min="1"
                max="20"
                className="h-12 text-base border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid bg-ui-surface text-ui-ink"
              />
            </div>

            {/* Number of Rooms */}
            <div>
              <Label htmlFor="number_of_rooms" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Number of Rooms
              </Label>
              <Input
                type="number"
                value={formData.component_data?.number_of_rooms || 1}
                onChange={(e) => {
                  const numRooms = parseInt(e.target.value) || 1;
                  handleDetailsChange('number_of_rooms', numRooms);
                  updateRoomDetails(numRooms);
                }}
                placeholder="1"
                min="1"
                max="10"
                className="h-12 text-base border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid bg-ui-surface text-ui-ink"
              />
            </div>

            {/* Room Details Management */}
            {roomDetailsContent}
          </div>
        </div>

        {/* File Management Section */}
        <div className="space-y-6">
          <AttachedDocumentsSection />
        </div>
      </div>
    );
  };

  // Comprehensive Restaurant Component with Search, Attendees, and OCR
  const renderRestaurantFields = () => {
    // Restaurant location selection handler
    const handleRestaurantLocationSelect = (place, type) => {
      const address = place.formatted_address || '';
      const name = place.name || '';
      
      if (formData.component_type === 'restaurant') {
        // Update restaurant-specific details
        handleDetailsChange('restaurant_name', name); 
        handleInputChange('title', name);
        handleInputChange('location', name);
        handleInputChange('address', address);
        
        // Extract additional place data with enhanced cuisine type detection
        if (place.types) {
          // Enhanced cuisine type mapping from Google Places types
          const cuisineMap = {
            'chinese_restaurant': 'Chinese',
            'italian_restaurant': 'Italian',
            'japanese_restaurant': 'Japanese',
            'mexican_restaurant': 'Mexican',
            'indian_restaurant': 'Indian',
            'thai_restaurant': 'Thai',
            'french_restaurant': 'French',
            'american_restaurant': 'American',
            'spanish_restaurant': 'Spanish',
            'greek_restaurant': 'Greek',
            'korean_restaurant': 'Korean',
            'vietnamese_restaurant': 'Vietnamese',
            'turkish_restaurant': 'Turkish',
            'mediterranean_restaurant': 'Mediterranean',
            'seafood_restaurant': 'Seafood',
            'steak_house': 'Steakhouse',
            'pizza_restaurant': 'Pizza',
            'sushi_restaurant': 'Sushi',
            'barbecue_restaurant': 'BBQ',
            'fast_food_restaurant': 'Fast Food',
            'cafe': 'Cafe',
            'bakery': 'Bakery',
            'bar': 'Bar & Grill'
          };
          
          // First try specific cuisine mappings
          let cuisineFound = false;
          for (const type of place.types) {
            if (cuisineMap[type]) {
              handleDetailsChange('cuisine_type', cuisineMap[type]);
              cuisineFound = true;
              break;
            }
          }
          
          // If no specific cuisine found, try generic restaurant types
          if (!cuisineFound) {
            const genericTypes = place.types.filter(type => 
              type.includes('food') || type.includes('restaurant') || type.includes('meal') || type === 'establishment'
            );
            if (genericTypes.length > 0) {
              // Use the first restaurant-related type, cleaned up
              const cleanType = genericTypes[0].replace('_', ' ').replace(/restaurant$/i, '').trim();
              if (cleanType && cleanType !== 'food' && cleanType !== 'establishment') {
                handleDetailsChange('cuisine_type', cleanType.charAt(0).toUpperCase() + cleanType.slice(1));
              }
            }
          }
        }
        
        // Also try to extract cuisine from business types or categories if available
        if (place.business_status && place.business_status === 'OPERATIONAL') {
          // Place is confirmed operational
        }
        
        // Extract website if available for additional context
        if (place.website) {
          handleDetailsChange('website', place.website);
        }
        
        // Extract phone number if available
        if (place.formatted_phone_number || place.international_phone_number) {
          handleDetailsChange('phone', place.formatted_phone_number || place.international_phone_number);
        }
        
        // Extract price level if available
        if (place.price_level) {
          const priceLabels = ['Inexpensive', 'Moderate', 'Expensive', 'Very Expensive'];
          handleDetailsChange('price_level', priceLabels[place.price_level - 1] || 'Unknown');
        }
      }
    };

    // Add from contacts handler
    const handleAddFromContacts = (contact) => {
      const newAttendee = {
        tempId: `exec-${Date.now()}-${Math.random()}`,
        id: contact.id,
        name: `${contact.first_name} ${contact.last_name}`,
        email: contact.email || '',
        phone: contact.phone || '',
        company: contact.company || '',
        title: contact.title || '',
        ea_name: '',
        ea_email: '',
        ea_phone: ''
      };
      
      // Check if already added
      if (!attendees.some(attendee => attendee.id === contact.id)) {
        setAttendees(prev => [...prev, newAttendee]);
      }
      
      setExecutiveSearchQuery('');
      setShowExecutiveSuggestions(false);
    };

    // Restaurant confirmation upload handler
    const handleRestaurantConfirmationUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setIsUploadingFile(true);
      
      try {
        // Upload file using existing API pattern
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', 'restaurant');

        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/documents/upload`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders()
          },
          body: formData
        });

        let fileUrl = null;
        let uploadedFile = null;

        if (response.ok) {
          const result = await response.json();
          fileUrl = result.url;
          uploadedFile = {
            id: result.document_id || `uploaded-${Date.now()}`,
            name: file.name,
            size: file.size,
            type: file.type,
            label: 'Restaurant Confirmation',
            url: result.url || null
          };
        } else {
          // Create temporary file for demo if upload fails
          uploadedFile = {
            id: `temp-${Date.now()}-${Math.random()}`,
            name: file.name,
            url: URL.createObjectURL(file),
            size: file.size,
            type: file.type,
            label: 'Restaurant Confirmation'
          };
          fileUrl = uploadedFile.url;
        }
        
        // Add to attached files
        setAttachedFiles(prev => [...prev, uploadedFile]);
        
        // Try to extract data using backend OCR
        try {
          // Use the backend /documents/upload endpoint for OCR processing
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          formDataUpload.append('document_type', 'restaurant');

          const uploadResponse = await axios.post('/documents/upload', formDataUpload, {
            headers: {
              ...getAuthHeaders()
            }
          });

          if (uploadResponse.data) {
            
            // Note: The backend processes documents asynchronously
            // For now, we'll provide feedback that the document was uploaded
            alert('Restaurant confirmation document uploaded successfully. The system will process it in the background. Please manually fill in the details from your confirmation for now.');
          }
        } catch (error) {
          console.error('❌ Error uploading restaurant document:', error);
          alert('Failed to upload restaurant confirmation. Please manually enter the details.');
        }
        
      } catch (error) {
        console.error('Error importing confirmation:', error);
        let errorMessage = `Failed to import confirmation: ${error.message || 'An unknown error occurred.'}`;
        if (error.message?.toLowerCase().includes('unsupported file type')) {
          errorMessage = "Unsupported file type. Please upload a PDF or image file.";
        }
        alert(errorMessage);
      } finally {
        setIsUploadingFile(false);
        if (e.target) e.target.value = '';
      }
    };

    // Contact Suggestions List Component
    const ContactSuggestionsList = ({ contacts, searchQuery, onSelectContact }) => {
      const filteredContacts = contacts.filter(contact => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return false;
        
        // Search in full name
        const fullNameMatch = contact.name?.toLowerCase().includes(query);
        
        // Search in individual name parts (first name, last name, middle names)
        const nameParts = contact.name?.toLowerCase().split(/\s+/) || [];
        const namePartsMatch = nameParts.some(part => part.includes(query));
        
        // Search in email
        const emailMatch = contact.email?.toLowerCase().includes(query);
        
        // Search in company
        const companyMatch = contact.company?.toLowerCase().includes(query);
        
        // Search in phone (if exists)
        const phoneMatch = contact.phone?.replace(/\D/g, '').includes(query.replace(/\D/g, ''));
        
        return fullNameMatch || namePartsMatch || emailMatch || companyMatch || phoneMatch;
      }).slice(0, 10);

      return (
        <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredContacts.length > 0 ? (
            filteredContacts.map((contact) => (
              <div
                key={contact.id}
                className="p-3 hover:bg-ui-orange-bg cursor-pointer border-b border-ui-divider last:border-b-0 transition-colors"
                onClick={() => onSelectContact(contact)}
              >
                <div className="font-medium text-ui-ink">{contact.name}</div>
                {contact.company && (
                  <div className="text-sm text-ui-muted">{contact.company}</div>
                )}
                {contact.email && (
                  <div className="text-xs text-ui-muted">{contact.email}</div>
                )}
              </div>
            ))
          ) : (
            <div className="p-3 text-ui-muted text-center">
              No contacts found matching "{searchQuery}"
            </div>
          )}
        </div>
      );
    };

    // Attendees List Component
    const AttendeesList = ({ attendees, onEdit, onDelete }) => (
      <>
        {attendees.length > 0 ? (
          <div className="space-y-3">
            {attendees.map((attendee) => (
              <AttendeeCard
                key={attendee.tempId}
                attendee={attendee}
                onEdit={() => onEdit(attendee)}
                onDelete={() => onDelete(attendee.tempId)}
              />
            ))}
          </div>
        ) : (
          <EmptyAttendeesState />
        )}
      </>
    );

    const AttendeeCard = ({ attendee, onEdit, onDelete }) => (
      <div className="flex flex-col gap-2 p-3 bg-ui-surface rounded-lg border border-ui-line hover:border-ui-orange-line hover:bg-ui-orange-bg/30 transition-all">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-ui-hover rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-ui-ink-3" />
            </div>
            <div>
              <div className="font-medium text-ui-ink">{attendee.name}</div>
              <div className="text-sm text-ui-muted">
                {attendee.company && `${attendee.company} • `}
                {attendee.email || 'No email'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-ui-orange-bg"
              onClick={onEdit}
            >
              <Edit className="w-4 h-4 text-ui-muted" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-ui-red-fg hover:text-ui-red-fg hover:bg-ui-red-bg"
              onClick={onDelete}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {/* speaker_bureau: optional link to a saved Speaker */}
        {isSpeakerBureau && (
          <SpeakerLinkField
            speakerId={attendee.speaker_id || null}
            speakerName={attendee.speaker_name || attendee.name || ''}
            onPick={(sp) => handleLinkAttendeeSpeaker(attendee.tempId, sp)}
          />
        )}
      </div>
    );

    const EmptyAttendeesState = () => (
      <div className="text-center py-8 text-ui-muted bg-ui-surface rounded-lg border-2 border-dashed border-ui-control">
        <Users className="w-12 h-12 text-ui-faint mx-auto mb-3" />
        <p className="text-base font-medium mb-1">No additional attendees added</p>
        <p className="text-sm">Additional attendees are optional - the reservation holder is sufficient</p>
      </div>
    );

    // Attached Documents Section Component  
    const RestaurantAttachedDocumentsSection = () => (
      <div className="p-4 bg-ui-orange-bg rounded-xl border border-ui-orange-line">
        <div className="flex items-center justify-between mb-4">
          <Label className="text-lg font-semibold flex items-center gap-2">
            <Upload className="w-5 h-5 text-ui-orange-fg" />
            Attached Documents
          </Label>
          <div className="relative">
            <input
              type="file"
              id="restaurant-file-upload"
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploadingFile}
            />
            <Button
              type="button"
              onClick={() => document.getElementById('restaurant-file-upload').click()}
              size="sm"
              className="bg-ui-orange-strong hover:bg-ui-orange-strong/90 text-ui-on-primary"
              disabled={isUploadingFile}
            >
              {isUploadingFile ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" /> Upload File
                </>
              )}
            </Button>
          </div>
        </div>

        {attachedFiles.length > 0 ? (
          <AttachedFilesList files={attachedFiles} />
        ) : (
          <RestaurantEmptyDocumentsState />
        )}
      </div>
    );

    const RestaurantEmptyDocumentsState = () => (
      <div className="text-center py-8 text-ui-muted bg-ui-surface rounded-lg border-2 border-dashed border-ui-orange-line">
        <Upload className="w-12 h-12 text-ui-orange-fg mx-auto mb-3" />
        <p>No documents attached yet.</p>
        <p className="text-sm mt-1">Upload confirmations, receipts, or other restaurant documents.</p>
      </div>
    );

    const AttachedFilesList = ({ files }) => (
      <div className="space-y-3">
        {files.map((file) => (
          <div key={file.id} className="p-3 bg-ui-surface rounded-lg border border-ui-line hover:border-ui-control transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-ui-orange-bg rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-ui-orange-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-ui-ink">{attachmentLabel(file)}</div>
                  <div className="text-xs text-ui-muted">
                    {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleViewDocument(file);
                  }}
                  className="text-ui-orange-fg hover:text-ui-orange-fg"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                          {hasParsedAttachment(file) && (
                            <Button type="button" variant="ghost" size="sm"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewDocument(file, true); }}>
                              Parsed data (JSON)
                            </Button>
                          )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFileDelete(file.id)}
                  className="text-ui-red-fg hover:text-ui-red-fg"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Badge variant="outline" className="text-xs">
                {file.label || 'Restaurant Document'}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Restaurant Details Section */}
        <div className="p-4 bg-ui-orange-bg rounded-xl border border-ui-orange-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <UtensilsCrossed className="w-5 h-5 text-ui-orange-fg" />
            Restaurant Details
          </Label>
          
          <div className="space-y-4">
            {/* Restaurant Name & Location Section */}
            <div>
              <Label htmlFor="restaurant_name_details" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Restaurant Name & Location
              </Label>
              <RestaurantAutocomplete
                value={formData.component_data?.restaurant_name || formData.title || ''}
                onChange={handleRestaurantNameChange}
                onPlaceSelect={handleRestaurantPlaceSelect}
                placeholder="Search for restaurant name or address..."
                className="h-12 text-base border-ui-line focus:border-ui-orange-solid focus:ring-ui-orange-solid"
              />
              <p className="text-xs text-ui-muted mt-1">
                Search by restaurant name, cuisine type, or address
              </p>
            </div>

            {/* Reservation Details Section */}
            <div className="space-y-4">
              {/* Reservation Date and Time - With TBD Option */}
              <div>
                <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-ui-orange-fg" />
                  Reservation Date & Time
                </Label>
                
                {/* TBD Checkbox */}
                <div className="mb-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.date_is_tbd || formData.time_is_tbd || false}
                      onChange={(e) => {
                        const isTBD = e.target.checked;
                        handleInputChange('date_is_tbd', isTBD);
                        handleInputChange('time_is_tbd', isTBD);
                        // Also update component_data.time_is_tbd for Timeline2 compatibility
                        handleDetailsChange('time_is_tbd', isTBD);
                        if (isTBD) {
                          // Clear the datetime when TBD is selected
                          handleInputChange('start_datetime', null);
                        }
                      }}
                      className="rounded border-ui-control text-ui-orange-fg focus:ring-ui-orange-solid"
                    />
                    <span className="text-ui-ink-2">
                      <span className="bg-ui-orange-bg text-ui-orange-fg px-2 py-1 rounded text-xs font-medium mr-2">TBD</span>
                      Make this reservation date/time To Be Determined (draggable on Timeline 2.0)
                    </span>
                  </label>
                </div>

                {/* Show date/time inputs only when not TBD */}
                {!(formData.date_is_tbd || formData.time_is_tbd) && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Date */}
                    <div>
                      <Label className="text-xs text-ui-ink-3 mb-1 block">Date</Label>
                      <DatePickerButton
                        label={formData.start_datetime ? format(new Date(formData.start_datetime), 'EEE, MMM d, yyyy') : 'Select date'}
                        onPick={(date) => {
                          try {
                            const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                            if (isoDate) {
                              const currentDateTime = formData.start_datetime || '';
                              let time = '19:00';
                              if (currentDateTime.includes('T')) {
                                const timePart = currentDateTime.split('T')[1];
                                if (timePart) {
                                  time = timePart.substring(0, 5);
                                }
                              }
                              handleInputChange('start_datetime', `${isoDate}T${time}`);
                            }
                          } catch (e) {
                            console.error('Date change error:', e);
                          }
                        }}
                        selectedDate={formData.start_datetime ? new Date(formData.start_datetime) : undefined}
                      />
                    </div>
                    
                    {/* Time - Single Dropdown */}
                    <div>
                      <Label className="text-xs text-ui-ink-3 mb-1 block">Time</Label>
                      <select
                        value={(() => {
                          try {
                            if (!formData.start_datetime || !formData.start_datetime.includes('T')) return '';
                            const timePart = formData.start_datetime.split('T')[1];
                            if (!timePart) return '';
                            return timePart.substring(0, 5); // HH:MM format
                          } catch (e) {
                            return '';
                          }
                        })()}
                        onChange={(e) => handleRestaurantTimeChange(e.target.value)}
                        className="h-10 w-full text-sm border border-ui-line rounded-md px-2 focus:border-ui-orange-solid focus:ring-ui-orange-solid bg-ui-surface"
                        required={!(formData.date_is_tbd || formData.time_is_tbd)}
                      >
                        <option value="">Select time</option>
                        {restaurantTimeOptions.map(({ value, display }) => (
                          <option key={value} value={value}>
                            {display}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <p className="text-xs text-ui-muted mt-1">
                  {formData.date_is_tbd || formData.time_is_tbd 
                    ? "This reservation will be marked as TBD and can be repositioned on Timeline 2.0" 
                    : "Required - when is your reservation?"
                  }
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="party_size" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                    <Users className="w-4 h-4 text-ui-orange-fg" />
                    Party Size
                  </Label>
                  <FormField
                    id="party_size"
                    type="number"
                    value={formData.component_data?.party_size || ''}
                    onChange={handlePartySizeChange}
                    placeholder="Number of people"
                    min="1"
                    max="50"
                    className="h-12 text-base border-ui-line focus:border-ui-orange-solid focus:ring-ui-orange-solid"
                  />
                  <p className="text-xs text-ui-muted mt-1">
                    How many people will be dining?
                  </p>
                </div>

                <div>
                  <Label htmlFor="expected_duration" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                    <Clock className="w-4 h-4 text-ui-orange-fg" />
                    Expected Duration
                  </Label>
                  <select
                    id="expected_duration"
                    value={formData.details?.expected_duration || 120}
                    onChange={(e) => handleDetailsChange('expected_duration', parseInt(e.target.value))}
                    className="h-12 w-full text-base border border-ui-line rounded-md px-3 focus:border-ui-orange-solid focus:ring-ui-orange-solid focus:ring-2 focus:ring-opacity-20 bg-ui-surface"
                  >
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={75}>1 hour 15 minutes</option>
                    <option value={90}>1 hour 30 minutes</option>
                    <option value={105}>1 hour 45 minutes</option>
                    <option value={120}>2 hours</option>
                    <option value={135}>2 hours 15 minutes</option>
                    <option value={150}>2 hours 30 minutes</option>
                    <option value={165}>2 hours 45 minutes</option>
                    <option value={180}>3 hours</option>
                    <option value={195}>3 hours 15 minutes</option>
                    <option value={210}>3 hours 30 minutes</option>
                    <option value={225}>3 hours 45 minutes</option>
                    <option value={240}>4 hours</option>
                  </select>
                  <p className="text-xs text-ui-muted mt-1">
                    How long do you expect the meal to last?
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cuisine_type" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                    <ChefHat className="w-4 h-4 text-ui-orange-fg" />
                    Cuisine Type
                  </Label>
                  <FormField
                    id="cuisine_type"
                    value={formData.component_data?.cuisine_type || ''}
                    onChange={handleCuisineTypeChange}
                    placeholder="e.g., Italian, Japanese, American, French"
                    className="h-12 text-base border-ui-line focus:border-ui-orange-solid focus:ring-ui-orange-solid"
                  />
                  <p className="text-xs text-ui-muted mt-1">
                    What type of cuisine or restaurant style?
                  </p>
                </div>

                <div>
                  <Label htmlFor="reservation_holder" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                    <User className="w-4 h-4 text-ui-orange-fg" />
                    Reservation Holder
                  </Label>
                  <select
                    id="reservation_holder"
                    value={formData.component_data?.reservation_holder_id || ''}
                    onChange={(e) => {
                      const selectedExecutiveId = e.target.value;
                      const selectedExecutive = finalAvailableExecutives.find(exec => exec.id === selectedExecutiveId);
                      handleDetailsChange('reservation_holder_id', selectedExecutiveId);
                      handleDetailsChange('reservation_holder_name', selectedExecutive ? `${selectedExecutive.first_name} ${selectedExecutive.last_name}` : '');
                    }}
                    className="h-12 w-full text-base border border-ui-line rounded-md px-3 focus:border-ui-orange-solid focus:ring-ui-orange-solid focus:ring-2 focus:ring-opacity-20 bg-ui-surface"
                  >
                    <option value="">Select reservation holder</option>
                    {finalAvailableExecutives.map((executive) => (
                      <option key={executive.id} value={executive.id}>
                        {executive.first_name} {executive.last_name}
                        {executive.company_title && ` (${executive.company_title})`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-ui-muted mt-1">
                    Who is making the reservation?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Attendees Management Section (Optional) */}
        <div className="p-4 bg-ui-sunk rounded-xl border border-ui-line">
          <div className="flex items-center justify-between mb-4">
            <Label className="text-lg font-semibold text-ui-ink flex items-center gap-2">
              <Users className="w-5 h-5 text-ui-ink-3" />
              Restaurant Attendees (Optional)
            </Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowAttendeeForm(true)}
                className="hover:bg-ui-orange-bg hover:border-ui-orange-line"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Contact
              </Button>
            </div>
          </div>

          {/* Quick Add from Contacts */}
          <div className="mb-4">
            <Label className="text-sm font-medium text-ui-ink-3 mb-2 block">
              Quick Add from Contacts
            </Label>
            <div className="relative">
              <FormField
                placeholder="Search existing contacts..."
                value={executiveSearchQuery}
                onChange={handleContactSearchChange}
                onFocus={() => setShowExecutiveSuggestions(executiveSearchQuery.length > 0)}
                className="h-10 border-ui-line focus:border-ui-orange-solid focus:ring-ui-orange-solid"
              />
              {showExecutiveSuggestions && (
                <ContactSuggestionsList 
                  contacts={finalAvailableExecutives}
                  searchQuery={executiveSearchQuery}
                  onSelectContact={handleAddFromContacts}
                />
              )}
            </div>
          </div>

          <AttendeesList 
            attendees={attendees}
            onEdit={handleEditAttendee}
            onDelete={handleDeleteAttendee}
          />
        </div>

        {/* File Management Section */}
        <div className="space-y-6">
          <div className="p-4 bg-ui-orange-bg rounded-xl border border-ui-orange-line">
            <div className="flex items-center justify-between mb-4">
              <Label className="text-lg font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5 mr-2 text-ui-orange-fg" />
                Import Restaurant Confirmation
              </Label>
              <div className="relative">
                <input
                  type="file"
                  id="restaurant-confirmation-upload"
                  className="hidden"
                  onChange={handleRestaurantConfirmationUpload}
                  accept=".pdf,.png,.jpg,.jpeg"
                  disabled={isUploadingFile}
                />
                <Button
                  type="button"
                  onClick={() => document.getElementById('restaurant-confirmation-upload').click()}
                  size="sm"
                  className="bg-ui-orange-strong hover:bg-ui-orange-strong/90"
                  disabled={isUploadingFile}
                >
                  {isUploadingFile ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" /> Upload Confirmation
                    </>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-sm text-ui-ink-3">
              Upload your restaurant confirmation (PDF or image) to automatically fill in the details.
              <br />
              <span className="text-xs text-ui-muted">Tip: For email confirmations, please save them as a PDF first.</span>
            </p>
          </div>

          <RestaurantAttachedDocumentsSection />
        </div>
      </div>
    );
  };

  // Comprehensive Rental Car Component with Location Search, Vehicle Selection, Airport Coordination
  const renderRentalCarFields = () => {
    // State for same location toggle
    const [sameAsPickupLocation, setSameAsPickupLocation] = useState(false);
    
    // State for uploading confirmation
    const [isUploadingConfirmation, setIsUploadingConfirmation] = useState(false);

    // Vehicle Type Options Component
    const VehicleTypeOptions = () => {
      const vehicleTypes = [
        { value: 'economy', label: 'Economy', description: 'Most affordable, good fuel economy', icon: '🚗' },
        { value: 'compact', label: 'Compact', description: 'Small and efficient', icon: '🚙' },
        { value: 'midsize', label: 'Mid-size', description: 'Comfortable for 4-5 people', icon: '🚗' },
        { value: 'fullsize', label: 'Full-size', description: 'Spacious sedan', icon: '🚘' },
        { value: 'premium', label: 'Premium', description: 'Luxury features and comfort', icon: '🚗' },
        { value: 'luxury', label: 'Luxury', description: 'High-end vehicles', icon: '🏎️' },
        { value: 'suv_small', label: 'Small SUV', description: 'Compact SUV for city driving', icon: '🚙' },
        { value: 'suv_midsize', label: 'Mid-size SUV', description: 'Family-friendly SUV', icon: '🚐' },
        { value: 'suv_standard', label: 'Standard SUV', description: 'Larger SUV with more space', icon: '🚐' },
        { value: 'suv_premium', label: 'Premium SUV', description: 'Luxury SUV with premium features', icon: '🚐' },
        { value: 'convertible', label: 'Convertible', description: 'Open-top driving experience', icon: '🏎️' },
        { value: 'van', label: 'Van', description: 'For large groups or cargo', icon: '🚐' },
        { value: 'truck', label: 'Truck', description: 'For hauling and towing', icon: '🛻' },
        { value: 'other', label: 'Other', description: 'Specialty or custom vehicle', icon: '🚗' }
      ];

      // Check if the current value matches any predefined option
      const currentValue = formData.component_data?.vehicle_type || formData.component_data?.car_type || '';
      const predefinedValues = vehicleTypes.map(v => v.value);
      const isCustomValue = currentValue && !predefinedValues.includes(currentValue);

      return (
        <>
          {/* Show the custom value from parsing if it doesn't match predefined options */}
          {isCustomValue && (
            <SelectItem key={currentValue} value={currentValue}>
              <div className="flex items-center gap-3 py-1">
                <span className="text-lg">🚗</span>
                <div>
                  <div className="font-medium">{currentValue}</div>
                  <div className="text-xs text-ui-muted">From booking</div>
                </div>
              </div>
            </SelectItem>
          )}
          {vehicleTypes.map((type) => (
            <SelectItem key={type.value} value={type.value}>
              <div className="flex items-center gap-3 py-1">
                <span className="text-lg">{type.icon}</span>
                <div>
                  <div className="font-medium">{type.label}</div>
                  <div className="text-xs text-ui-muted">{type.description}</div>
                </div>
              </div>
            </SelectItem>
          ))}
        </>
      );
    };

    // Rental Duration Display
    const RentalDurationDisplay = ({ startTime, endTime }) => {
      if (!startTime || !endTime) return null;
      
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffMs = end - start;
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
      
      return (
        <div className="mt-4 p-3 bg-ui-surface rounded-lg border border-ui-teal-line">
          <div className="flex items-center gap-2 text-sm">
            <Timer className="w-4 h-4 text-ui-teal-fg" />
            <span className="font-medium text-ui-ink">
              Rental Duration: 
            </span>
            <span className="text-ui-teal-fg font-semibold">
              {diffDays} day{diffDays !== 1 ? 's' : ''} ({diffHours} hours)
            </span>
          </div>
          {diffDays > 7 && (
            <div className="mt-2 text-xs text-ui-amber-fg bg-ui-amber-bg p-2 rounded border border-ui-amber-line">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              Long-term rental - consider weekly/monthly rates
            </div>
          )}
        </div>
      );
    };

    // OCR processing for rental confirmations - Enhanced for better extraction
    const rentalCarSchema = {
      type: "object",
      properties: {
        rental_car_company: { 
          type: "string", 
          description: "Name of the rental car company (Alamo, Enterprise, Hertz, Avis, Budget, etc.). Look for company names in headers, logos, or 'Thanks for choosing [Company]' messages." 
        },
        pickup_location: { 
          type: "string", 
          description: "Pickup location including airport name and address. Look for 'Pick-up' section or airport codes like LAX, JFK, etc." 
        },
        dropoff_location: { 
          type: "string", 
          description: "Drop-off/return location including airport name and address. Look for 'Return' section." 
        },
        pickup_date: {
          type: "string",
          description: "Pickup date in YYYY-MM-DD format. Convert from formats like 'Mon, Sep 03, 2018' to '2018-09-03'"
        },
        pickup_time: {
          type: "string", 
          description: "Pickup time in HH:MM format (24-hour). Convert from formats like '06:00 PM' to '18:00'"
        },
        pickup_datetime: { 
          type: "string", 
          description: "Combined pickup date and time in ISO format YYYY-MM-DDTHH:MM. Example: '2018-09-03T18:00'" 
        },
        dropoff_date: {
          type: "string",
          description: "Drop-off/return date in YYYY-MM-DD format. Convert from formats like 'Wed, Sep 05, 2018' to '2018-09-05'"
        },
        dropoff_time: {
          type: "string",
          description: "Drop-off/return time in HH:MM format (24-hour). Convert from formats like '06:00 PM' to '18:00'"
        },
        dropoff_datetime: { 
          type: "string", 
          description: "Combined return date and time in ISO format YYYY-MM-DDTHH:MM. Example: '2018-09-05T18:00'" 
        },
        vehicle_type: { 
          type: "string", 
          description: "Vehicle category like 'Midsize SUV', 'Economy Car', 'Full Size Car', 'Compact Car', 'Premium', etc. Look in 'Car Summary' or vehicle description sections." 
        },
        vehicle_make_model: {
          type: "string",
          description: "Specific vehicle make and model like 'Toyota RAV4 or similar', 'Nissan Altima or similar'"
        },
        confirmation_number: { 
          type: "string", 
          description: "Reservation/confirmation number. Look for 'Confirmation Number', 'Reservation Number', or numeric codes like '1272131767'" 
        },
        primary_renter_name: {
          type: "string",
          description: "Name of the primary renter/driver. Look for 'Driver Name:', 'Driver 1 Name:', or similar fields. Example: 'JEREMY MILIKOW'"
        },
        renter_email: {
          type: "string",
          description: "Email address of the renter"
        },
        rental_duration: {
          type: "string",
          description: "Duration of rental like '2 Day(s)', '1 week', etc."
        },
        daily_rate: { 
          type: "string", 
          description: "Daily rental rate including currency. Look for rate per day like '$52.29' or similar patterns" 
        },
        total_cost: { 
          type: "string", 
          description: "Total estimated cost including taxes and fees. Look for 'Estimated Total', 'Total:', etc." 
        },
        charges_breakdown: { 
          type: "string", 
          description: "Detailed breakdown of all charges, fees, taxes, insurance (CDW, EP), airport fees, etc. Include all line items from rates section." 
        },
        insurance_coverage: {
          type: "string",
          description: "Insurance options like 'Collision Damage Waiver', 'Extended Protection', 'CDW', etc."
        },
        special_features: {
          type: "string",
          description: "Vehicle features like 'Power Windows', 'Automatic Transmission', 'Air Conditioning', 'Unlimited Mileage', etc."
        },
        additional_info: {
          type: "string",
          description: "Any additional important information, terms, or special notes about the rental"
        }
      },
      required: ["rental_car_company", "confirmation_number", "pickup_location"]
    };

    // Vehicle type normalization - Enhanced for rental car confirmations
    const normalizeVehicleType = (rawType) => {
      if (!rawType) return 'other';
      
      const vehicleTypeMap = {
        // Common rental car terminology
        'midsize suv': 'suv_midsize',
        'mid-size suv': 'suv_midsize', 
        'midsize': 'midsize',
        'mid-size': 'midsize',
        'full-size': 'fullsize',
        'full size': 'fullsize',
        'compact car': 'compact',
        'economy car': 'economy',
        'small suv': 'suv_small',
        'standard suv': 'suv_standard',
        'premium suv': 'suv_premium',
        'large suv': 'suv_premium',
        'luxury suv': 'suv_premium',
        'minivan': 'van',
        'mini van': 'van',
        'pickup truck': 'truck',
        'pickup': 'truck',
        // Handle with or without "car" suffix
        'economy': 'economy',
        'compact': 'compact', 
        'premium': 'premium',
        'luxury': 'luxury',
        'convertible': 'convertible'
      };

      const vehicleOptions = [
        "economy", "compact", "midsize", "fullsize", "premium", "luxury",
        "suv_small", "suv_midsize", "suv_standard", "suv_premium",
        "convertible", "van", "truck", "other"
      ];

      const normalizedInput = rawType.toLowerCase().trim();
      
      // Try exact match first
      let normalizedType = vehicleTypeMap[normalizedInput];
      
      // Try partial matching for complex descriptions
      if (!normalizedType) {
        for (const [key, value] of Object.entries(vehicleTypeMap)) {
          if (normalizedInput.includes(key) || key.includes(normalizedInput)) {
            normalizedType = value;
            break;
          }
        }
      }
      
      // Try direct match with available options
      if (!normalizedType && vehicleOptions.includes(normalizedInput)) {
        normalizedType = normalizedInput;
      }
      
      return normalizedType || 'other';
    };

    // Process extracted rental data
    const processExtractedRentalData = async (extractedData) => {
      
      // Build updated form data object directly instead of relying on async state updates
      const updatedFormData = { ...formData };
      const updatedComponentData = { ...formData.component_data };
      
      const constructDateTime = (date, time) => {
        if (!date) return null;
        
        try {
          // If date already contains time (has 'T' and time part), validate and use it
          if (date.includes('T') && date.split('T')[1]) {
            const testDate = new Date(date);
            if (!isNaN(testDate.getTime())) {
              return date;
            }
          }
          
          // If no time provided, default to 10:00 AM
          const finalTime = time || '10:00';
          
          // Validate time format (HH:mm)
          if (!/^\d{2}:\d{2}$/.test(finalTime)) {
            return `${date}T10:00`;
          }
          
          const result = `${date}T${finalTime}`;
          
          // Validate the complete datetime string
          const testDate = new Date(result);
          if (isNaN(testDate.getTime())) {
            return `${date}T10:00`;
          }
          
          return result;
        } catch (error) {
          return date ? `${date}T10:00` : null;
        }
      };
      
      // Update date/time fields - try multiple formats
      let pickupDateTime = extractedData.pickup_datetime;
      if (!pickupDateTime && extractedData.pickup_date) {
        pickupDateTime = constructDateTime(extractedData.pickup_date, extractedData.pickup_time);
      }
      if (pickupDateTime) {
        updatedFormData.start_datetime = pickupDateTime;
      }
      
      let dropoffDateTime = extractedData.dropoff_datetime;
      if (!dropoffDateTime && extractedData.dropoff_date) {
        dropoffDateTime = constructDateTime(extractedData.dropoff_date, extractedData.dropoff_time);
      }
      if (dropoffDateTime) {
        updatedFormData.end_datetime = dropoffDateTime;
      }
      
      // Update confirmation number
      if (extractedData.confirmation_number) {
        updatedFormData.confirmation_number = extractedData.confirmation_number;
      }
      
      // Update locations
      if (extractedData.pickup_location) {
        updatedComponentData.pickup_location = extractedData.pickup_location;
      }
      
      if (extractedData.dropoff_location) {
        updatedComponentData.dropoff_location = extractedData.dropoff_location;
      }
      
      // Update vehicle type with normalization
      if (extractedData.car_type || extractedData.vehicle_type) {
        const vehicleType = extractedData.car_type || extractedData.vehicle_type;
        const normalizedType = normalizeVehicleType(vehicleType);
        updatedComponentData.vehicle_type = normalizedType;
      }
      
      // Update company
      if (extractedData.rental_company || extractedData.rental_car_company) {
        const company = extractedData.rental_company || extractedData.rental_car_company;
        updatedComponentData.rental_car_company = company;
      }
      
      // Update primary renter and try to match with executives
      if (extractedData.driver_name || extractedData.primary_renter_name) {
        const renterName = extractedData.driver_name || extractedData.primary_renter_name;
        updatedComponentData.primary_renter_name = renterName;
        
        // Try to match with existing executives
        if (tripExecutives && tripExecutives.length > 0) {
          const renterNameLower = renterName.toLowerCase();
          const matchedExecutive = tripExecutives.find(exec => {
            if (!exec || (!exec.name && !exec.first_name && !exec.last_name)) return false;
            
            // Try to get executive name from available fields
            const execName = exec.name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim();
            if (!execName) return false;
            
            const execNameLower = execName.toLowerCase();
            // Check if names match (full or partial)
            return execNameLower.includes(renterNameLower) || renterNameLower.includes(execNameLower) ||
                   execNameLower.split(' ').some(part => renterNameLower.includes(part));
          });
          
          if (matchedExecutive) {
            updatedComponentData.primary_renter_executive_id = matchedExecutive.id;
          } else {
          }
        }
      }
      
      // Build comprehensive rate and details information for notes
      const rateDetails = [];
      
      if (extractedData.rental_duration) {
        rateDetails.push(`Duration: ${extractedData.rental_duration}`);
      }
      if (extractedData.daily_rate) {
        rateDetails.push(`Daily Rate: ${extractedData.daily_rate}`);
      }
      if (extractedData.total_cost) {
        rateDetails.push(`Total Cost: ${extractedData.total_cost}`);
      }
      if (extractedData.vehicle_make_model) {
        rateDetails.push(`Vehicle: ${extractedData.vehicle_make_model}`);
      }
      if (extractedData.special_features) {
        rateDetails.push(`Features: ${extractedData.special_features}`);
      }
      if (extractedData.insurance_coverage) {
        rateDetails.push(`Insurance: ${extractedData.insurance_coverage}`);
      }
      
      // Add comprehensive details section
      let detailsSection = '';
      if (rateDetails.length > 0) {
        detailsSection += `RENTAL DETAILS:\n${rateDetails.join('\n')}\n\n`;
      }
      
      if (extractedData.charges_breakdown) {
        detailsSection += `CHARGES & FEES:\n${extractedData.charges_breakdown}\n\n`;
      }
      
      if (extractedData.additional_info) {
        detailsSection += `ADDITIONAL INFO:\n${extractedData.additional_info}`;
      }
      
      if (detailsSection) {
        const currentNotes = formData.notes || '';
        const newNotes = currentNotes 
          ? `${currentNotes}\n\n${detailsSection}`
          : detailsSection;
        updatedFormData.notes = newNotes;
      }

      // Auto-generate title if not set
      if (!formData.title) {
        const titleParts = [];
        if (extractedData.rental_car_company) {
          titleParts.push(extractedData.rental_car_company);
        }
        if (extractedData.vehicle_type) {
          titleParts.push(extractedData.vehicle_type);
        }
        if (extractedData.pickup_location) {
          titleParts.push(`@ ${extractedData.pickup_location}`);
        }
        
        const newTitle = titleParts.length > 0 ? titleParts.join(' ') : "Rental Car";
        updatedFormData.title = newTitle;
      }
      
      // Apply all updates at once
      updatedFormData.component_data = updatedComponentData;
      setFormData(updatedFormData);
      
    };

    // Rental car confirmation upload handler
    const handleRentalCarConfirmationUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setIsUploadingConfirmation(true);
      
      try {
        // Add file to attached files first for UI display
        const newFile = {
          id: `temp-${Date.now()}-${Math.random()}`,
          name: file.name,
          size: file.size,
          type: file.type,
          label: 'Rental Car Confirmation',
          url: URL.createObjectURL(file) // Temporary URL for display
        };
        setAttachedFiles(prev => [...prev, newFile]);
        
        // Upload document to backend for AI parsing
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', 'rental_car');
        formData.append('extraction_schema', JSON.stringify(rentalCarSchema));

        const apiBase = getApiBase();
        const response = await fetch(`${apiBase}/documents/upload`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders()
          },
          body: formData
        });

        if (!response.ok) {
          let errorMessage = 'Unknown error occurred';
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
          } catch (parseError) {
            try {
              const text = await response.text();
              errorMessage = text || `${response.status} ${response.statusText}`;
            } catch {
              errorMessage = `${response.status} ${response.statusText}`;
            }
          }
          throw new Error(`Upload failed: ${errorMessage}`);
        }

        const uploadData = await response.json();
        
        // Poll for parsing completion
        const documentId = uploadData.document_id;
        if (!documentId) {
          throw new Error('No document ID returned from upload');
        }
        
        let attempts = 0;
        const maxAttempts = 60; // 60 seconds max wait
        
        const pollForCompletion = async () => {
          try {
            attempts++;
            
            const docResponse = await fetch(`${backendUrl}/api/documents`, {
              headers: {
                ...getAuthHeaders()
              }
            });
            
            if (!docResponse.ok) {
              throw new Error(`Failed to fetch documents: ${docResponse.statusText}`);
            }
            
            const docData = await docResponse.json();
            const document = docData.find(doc => doc.id === documentId);
            
            if (!document) {
              if (attempts < maxAttempts) {
                setTimeout(pollForCompletion, 1000);
                return;
              } else {
                throw new Error('Document not found after upload');
              }
            }
            
            
            if (document.processing_status === 'completed') {
              if (document.parsed_data && document.parsed_data.success && document.parsed_data.data) {
                // Process the actual parsed rental car data
                await processExtractedRentalData(document.parsed_data.data);
                setIsUploadingConfirmation(false);
                setValidationError('');
                alert('Rental car confirmation imported successfully!');
              } else {
                const errorMessage = document.parsed_data?.error || 'Document parsing failed or returned no data';
                throw new Error(errorMessage);
              }
            } else if (document.processing_status === 'failed') {
              const errorMessage = document.parsed_data?.error || 'Document processing failed';
              throw new Error(errorMessage);
            } else if (document.processing_status === 'processing' && attempts < maxAttempts) {
              // Still processing, wait and try again
              setTimeout(pollForCompletion, 2000);
            } else {
              throw new Error(`Document processing timeout after ${attempts} attempts`);
            }
          } catch (pollError) {
            console.error('Polling error:', pollError);
            throw pollError;
          }
        };

        // Start polling
        setTimeout(pollForCompletion, 2000);
        
      } catch (error) {
        console.error('Error importing confirmation:', error);
        setIsUploadingConfirmation(false);
        setValidationError('Failed to import confirmation: ' + error.message);
        alert('Failed to import confirmation: ' + error.message);
      } finally {
        if (e.target) e.target.value = '';
      }
    };

    return (
      <div className="space-y-6">
        {/* Import Rental Confirmation - Available in both Add and Edit modes */}
        <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line">
          <div className="flex items-center justify-between mb-4">
            <Label className="text-lg font-semibold flex items-center gap-2">
              <Upload className="w-5 h-5 mr-2 text-ui-teal-fg" />
              Import Rental Confirmation
            </Label>
            <div className="relative">
              <input
                type="file"
                id="rental-confirmation-upload"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleRentalCarConfirmationUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isUploadingConfirmation}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isUploadingConfirmation}
                className="border-ui-teal-line text-ui-teal-fg hover:bg-ui-teal-bg"
              >
                {isUploadingConfirmation ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Confirmation
                  </>
                )}
              </Button>
            </div>
          </div>
          <p className="text-sm text-ui-ink-3">
            Upload your rental car confirmation email or document. We'll automatically extract the details to fill out the form.
            <br />
            <span className="text-xs text-ui-muted">Supported formats: PDF, PNG, JPG</span>
          </p>
        </div>

        {/* Rental Details Section - Combined */}
        <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line">
          <Label className="text-lg font-semibold text-ui-ink mb-6 block flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-ui-teal-fg" />
            Rental Details
          </Label>
          
          {/* Pickup and Drop-off Date & Time */}
          <div className="mb-6">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Pickup Date & Time */}
              <div>
                <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                  <Clock className="w-4 h-4 text-ui-teal-fg" />
                  Pickup Date & Time *
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <DatePickerButton
                      label={(() => {
                        try {
                          if (!formData.start_datetime || formData.start_datetime === '') return 'Select date';
                          // Extract date part only (before T or the whole string)
                          const datePart = formData.start_datetime.includes('T')
                            ? formData.start_datetime.split('T')[0]
                            : formData.start_datetime;
                          if (!datePart || datePart === '') return 'Select date';
                          const d = new Date(datePart + 'T12:00:00'); // Add noon time to avoid timezone issues
                          if (isNaN(d.getTime())) return 'Select date';
                          return format(d, 'EEE, MMM d, yyyy');
                        } catch { return 'Select date'; }
                      })()}
                      selectedDate={(() => {
                        try {
                          if (!formData.start_datetime || formData.start_datetime === '') return undefined;
                          const datePart = formData.start_datetime.includes('T')
                            ? formData.start_datetime.split('T')[0]
                            : formData.start_datetime;
                          if (!datePart || datePart === '') return undefined;
                          const d = new Date(datePart + 'T12:00:00');
                          return isNaN(d.getTime()) ? undefined : d;
                        } catch { return undefined; }
                      })()}
                      onPick={(date) => {
                        if (!date) return;
                        const iso = format(date, 'yyyy-MM-dd');
                        // Get current time if exists, otherwise default to 10:00
                        let currentTime = '10:00';
                        if (formData.start_datetime && formData.start_datetime.includes('T')) {
                          const timePart = formData.start_datetime.split('T')[1];
                          if (timePart && timePart.length >= 5) {
                            currentTime = timePart.substring(0, 5);
                          }
                        }
                        handleInputChange('start_datetime', `${iso}T${currentTime}`);
                      }}
                    />
                    <Label className="text-xs text-ui-muted mt-1 block">Date</Label>
                  </div>
                  <div>
                    {(() => {
                      const selectValue = formData.start_datetime ? formData.start_datetime.split('T')[1]?.substr(0,5) : '';
                      console.log('🕐 PICKUP TIME SELECT RENDER:', {
                        'formData.start_datetime': formData.start_datetime,
                        'computed selectValue': selectValue,
                        'selectValue type': typeof selectValue
                      });
                      return (
                        <Select
                          value={selectValue}
                          onValueChange={(time) => {
                            // Guard: Don't update if time is empty (can happen with controlled Select edge cases)
                            if (!time) {
                              console.log('🕐 PICKUP TIME SELECT: onValueChange called with empty time, ignoring');
                              return;
                            }
                            const date = formData.start_datetime ? formData.start_datetime.split('T')[0] : new Date().toISOString().split('T')[0];
                            handleInputChange('start_datetime', `${date}T${time}`);
                          }}
                        >
                          <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-teal-solid focus:ring-ui-teal-solid">
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {(() => {
                              const times = [];
                              for (let hour = 0; hour < 24; hour++) {
                                for (let minute = 0; minute < 60; minute += 15) {
                                  const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                                  times.push(
                                    <SelectItem key={timeString} value={timeString}>
                                      {timeString}
                                    </SelectItem>
                                  );
                                }
                              }
                              return times;
                            })()}
                          </SelectContent>
                        </Select>
                      );
                    })()}
                    <Label className="text-xs text-ui-muted mt-1 block">Time</Label>
                  </div>
                </div>
                <p className="text-xs text-ui-muted mt-1">
                  Required - when do you need to pick up the vehicle?
                </p>
              </div>

              {/* Drop-off Date & Time */}
              <div>
                <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                  <Clock className="w-4 h-4 text-ui-teal-fg" />
                  Drop-off Date & Time *
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <DatePickerButton
                      label={(() => {
                        try {
                          if (!formData.end_datetime || formData.end_datetime === '') return 'Select date';
                          const datePart = formData.end_datetime.includes('T')
                            ? formData.end_datetime.split('T')[0]
                            : formData.end_datetime;
                          if (!datePart || datePart === '') return 'Select date';
                          const d = new Date(datePart + 'T12:00:00');
                          if (isNaN(d.getTime())) return 'Select date';
                          return format(d, 'EEE, MMM d, yyyy');
                        } catch { return 'Select date'; }
                      })()}
                      selectedDate={(() => {
                        try {
                          if (!formData.end_datetime || formData.end_datetime === '') return undefined;
                          const datePart = formData.end_datetime.includes('T')
                            ? formData.end_datetime.split('T')[0]
                            : formData.end_datetime;
                          if (!datePart || datePart === '') return undefined;
                          const d = new Date(datePart + 'T12:00:00');
                          return isNaN(d.getTime()) ? undefined : d;
                        } catch { return undefined; }
                      })()}
                      onPick={(date) => {
                        if (!date) return;
                        const iso = format(date, 'yyyy-MM-dd');
                        let currentTime = '10:00';
                        if (formData.end_datetime && formData.end_datetime.includes('T')) {
                          const timePart = formData.end_datetime.split('T')[1];
                          if (timePart && timePart.length >= 5) {
                            currentTime = timePart.substring(0, 5);
                          }
                        }
                        handleInputChange('end_datetime', `${iso}T${currentTime}`);
                      }}
                    />
                    <Label className="text-xs text-ui-muted mt-1 block">Date</Label>
                  </div>
                  <div>
                    <Select
                      value={formData.end_datetime ? formData.end_datetime.split('T')[1]?.substr(0,5) : ''}
                      onValueChange={(time) => {
                        // Guard: Don't update if time is empty (can happen with controlled Select edge cases)
                        if (!time) return;
                        const date = formData.end_datetime ? formData.end_datetime.split('T')[0] : new Date().toISOString().split('T')[0];
                        handleInputChange('end_datetime', `${date}T${time}`);
                      }}
                    >
                      <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-teal-solid focus:ring-ui-teal-solid">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {(() => {
                          const times = [];
                          for (let hour = 0; hour < 24; hour++) {
                            for (let minute = 0; minute < 60; minute += 15) {
                              const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                              times.push(
                                <SelectItem key={timeString} value={timeString}>
                                  {timeString}
                                </SelectItem>
                              );
                            }
                          }
                          return times;
                        })()}
                      </SelectContent>
                    </Select>
                    <Label className="text-xs text-ui-muted mt-1 block">Time</Label>
                  </div>
                </div>
                <p className="text-xs text-ui-muted mt-1">
                  Required - when will you return the vehicle?
                </p>
              </div>
            </div>
            
            <RentalDurationDisplay 
              startTime={formData.start_datetime} 
              endTime={formData.end_datetime} 
            />
          </div>

          {/* Rental Company */}
          <div className="mb-6">
            <Label htmlFor="rental_company" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
              <Building className="w-4 h-4 text-ui-teal-fg" />
              Select Rental Car Company
            </Label>
            <Input
              id="rental_company"
              value={formData.component_data?.rental_car_company || formData.component_data?.rental_company || ''}
              onChange={(e) => handleDetailsChange('rental_car_company', e.target.value)}
              placeholder="Enter rental company name (Enterprise, Hertz, Avis, etc.)"
              className="h-12 text-base border-ui-line focus:border-ui-teal-solid focus:ring-ui-teal-solid"
            />
            <p className="text-xs text-ui-muted mt-1">
              Choose from major rental car companies or enter a custom name
            </p>
          </div>

          {/* Rental Locations */}
          <div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {/* Pickup Location */}
              <div>
                <Label htmlFor="pickup_location" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-ui-teal-fg" />
                  Pickup Location *
                </Label>
                <Input
                  id="pickup_location"
                  value={formData.component_data?.pickup_location || ''}
                  onChange={(e) => handleDetailsChange('pickup_location', e.target.value)}
                  placeholder="Enter pickup address or location..."
                  className="h-12 text-base border-ui-line focus:border-ui-teal-solid focus:ring-ui-teal-solid"
                />
                <p className="text-xs text-ui-muted mt-1">
                  Required - where will you pick up the rental car?
                </p>
              </div>

              {/* Drop-off Location */}
              <div>
                <Label htmlFor="dropoff_location" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-ui-teal-fg" />
                  Drop-off Location
                </Label>
                <Input
                  id="dropoff_location"
                  value={formData.component_data?.dropoff_location || ''}
                  onChange={(e) => handleDetailsChange('dropoff_location', e.target.value)}
                  placeholder="Enter drop-off address or location..."
                  className="h-12 text-base border-ui-line focus:border-ui-teal-solid focus:ring-ui-teal-solid"
                  disabled={sameAsPickupLocation}
                />
                <p className="text-xs text-ui-muted mt-1">
                  {sameAsPickupLocation ? 'Same as pickup location' : 'Where will you return the rental car?'}
                </p>
              </div>
            </div>

            {/* Same Location Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="same_location"
                checked={sameAsPickupLocation}
                onCheckedChange={(checked) => {
                  setSameAsPickupLocation(checked);
                  if (checked) {
                    handleDetailsChange('dropoff_location', formData.component_data?.pickup_location || '');
                  } else {
                    handleDetailsChange('dropoff_location', '');
                  }
                }}
              />
              <Label htmlFor="same_location" className="text-sm font-medium text-ui-ink-2 cursor-pointer">
                Return to same location
              </Label>
            </div>
          </div>
        </div>

        {/* Vehicle Details Section */}
        <div className="p-4 bg-ui-violet-bg rounded-xl border border-ui-violet-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Car className="w-5 h-5 text-ui-violet-fg" />
            Vehicle Details
          </Label>
          
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {/* Vehicle Type */}
            <div>
              <Label htmlFor="vehicle_type" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Vehicle Type
              </Label>
              <Select
                value={formData.component_data?.vehicle_type || formData.component_data?.car_type || ''}
                onValueChange={(value) => handleDetailsChange('vehicle_type', value)}
              >
                <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid">
                  <SelectValue placeholder="Select vehicle type" />
                </SelectTrigger>
                <SelectContent>
                  <VehicleTypeOptions />
                </SelectContent>
              </Select>
              <p className="text-xs text-ui-muted mt-1">
                Choose the category that best fits your needs
              </p>
            </div>

            {/* Primary Renter */}
            <div>
              <Label htmlFor="primary_renter" className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                <User className="w-4 h-4 text-ui-violet-fg" />
                Primary Renter
              </Label>
              {/* In speaker_bureau mode: free-text renter name + optional Speaker link */}
              {isSpeakerBureau ? (
                <div className="space-y-2">
                  <Input
                    value={formData.component_data?.primary_renter_name || ''}
                    onChange={(e) => handleDetailsChange('primary_renter_name', e.target.value)}
                    placeholder="Primary renter / driver name"
                    className="h-12 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid"
                  />
                  <SpeakerLinkField
                    speakerId={formData.component_data?.primary_renter_speaker_id || null}
                    speakerName={formData.component_data?.primary_renter_speaker_name || formData.component_data?.primary_renter_name || ''}
                    onPick={(sp) => {
                      if (!sp) {
                        handleDetailsChange('primary_renter_speaker_id', null);
                        handleDetailsChange('primary_renter_speaker_name', '');
                        return;
                      }
                      const nm = sp.full_name || `${sp.first_name || ''} ${sp.last_name || ''}`.trim();
                      handleDetailsChange('primary_renter_speaker_id', sp.id);
                      handleDetailsChange('primary_renter_speaker_name', nm);
                      if (!formData.component_data?.primary_renter_name && nm) handleDetailsChange('primary_renter_name', nm);
                    }}
                  />
                </div>
              ) : (
              <Select
                value={formData.component_data?.primary_renter_executive_id || ''}
                onValueChange={(value) => handleDetailsChange('primary_renter_executive_id', value)}
              >
                <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid">
                  <SelectValue placeholder="Select executive" />
                </SelectTrigger>
                <SelectContent>
                  {tripExecutives && tripExecutives.map((executive, index) => {
                    // Ensure we have valid executive data with null checks
                    if (!executive || !executive.id) {
                      return null;
                    }

                    const firstName = executive.first_name || '';
                    const lastName = executive.last_name || '';
                    const title = executive.title || 'Executive';
                    const displayName = `${firstName} ${lastName}`.trim() || 'Unknown Executive';

                    // Log to debug potential duplicates
                    if (index < 5) { // Only log first 5 to avoid spam
                    }

                    return (
                      <SelectItem key={executive.id} value={executive.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {displayName} - {title}
                        </div>
                      </SelectItem>
                    );
                  })}
                  <SelectItem value="other">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Other/External Renter
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              )}
              <p className="text-xs text-ui-muted mt-1">
                Who is the primary driver?
              </p>
            </div>

            {/* Preferences Section */}
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Additional Preferences
              </Label>
              <Input
                value={formData.component_data?.preferences || ''}
                onChange={(e) => handleDetailsChange('preferences', e.target.value)}
                placeholder="GPS, car seats, etc."
                className="h-12 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid"
              />
              <p className="text-xs text-ui-muted mt-1">
                Any special equipment or preferences
              </p>
            </div>
          </div>

          {/* Primary Renter Name (if Other selected or from OCR) — speaker mode has its own name field above */}
          {!isSpeakerBureau && (formData.component_data?.primary_renter_executive_id === 'other' || formData.component_data?.primary_renter_name) && (
            <div className="mb-4">
              <Label htmlFor="primary_renter_name" className="text-sm font-bold text-ui-ink-2 mb-2 block">
                Primary Renter Name
              </Label>
              <Input
                id="primary_renter_name"
                value={formData.component_data?.primary_renter_name || ''}
                onChange={(e) => handleDetailsChange('primary_renter_name', e.target.value)}
                placeholder="Enter the primary renter's full name"
                className="h-12 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid"
              />
              <p className="text-xs text-ui-muted mt-1">
                {formData.component_data?.primary_renter_executive_id === 'other' 
                  ? 'Full name of the external/other renter'
                  : 'Name extracted from rental confirmation'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // File Management Render Function
  const renderFileManagement = (title, uploadLabel, emptyStateText) => (
    <div className="space-y-6">
      {/* Import Documents Section */}
      <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line">
        <div className="flex items-center justify-between mb-4">
          <Label className="text-lg font-semibold flex items-center gap-2">
            <Upload className="w-5 h-5 mr-2 text-ui-teal-fg" />
            {title}
          </Label>
          <div className="relative">
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploadingFile}
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              multiple
            />
            <Button
              type="button"
              onClick={() => document.getElementById('file-upload').click()}
              size="sm"
              className="bg-ui-teal-strong hover:bg-ui-teal-strong/90"
              disabled={isUploadingFile}
            >
              {isUploadingFile ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" /> {uploadLabel}
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="text-sm text-ui-ink-3">
          <p>{emptyStateText}</p>
          <p className="text-xs text-ui-muted mt-1">
            Accepted formats: PDF, Images, Word documents
          </p>
        </div>
      </div>

      {/* Attached Documents Section */}
      <div className="bg-ui-surface p-4 rounded-xl border border-ui-line">
        <h4 className="text-lg font-semibold text-ui-ink mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-ui-ink-3" />
          Attached Documents ({attachedFiles.length})
        </h4>
        
        {attachedFiles.length > 0 ? (
          <div className="space-y-3">
            {attachedFiles.map((file, index) => (
              <div key={file.id || index} className="flex items-center justify-between p-3 bg-ui-sunk rounded-lg border border-ui-line">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-ui-blue-bg rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-ui-link" />
                  </div>
                  <div>
                    <p className="font-medium text-ui-ink">{file.filename}</p>
                    {file.size && (
                      <p className="text-xs text-ui-muted">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewingDocument(file)}
                    className="text-ui-link hover:text-ui-blue-fg"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownloadDocument(file)}
                    className="text-ui-green-fg hover:text-ui-green-fg"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFileDelete(file.id)}
                    className="text-ui-red-fg hover:text-ui-red-fg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-ui-muted">
            <FileText className="w-12 h-12 text-ui-faint mx-auto mb-3" />
            <p>No documents attached</p>
            <p className="text-sm mt-1">Upload documents using the button above</p>
          </div>
        )}
      </div>
    </div>
  );

  // Private Flight Handler Functions (memoized to prevent re-renders)
  const handleAddPassengerFromSearch = useCallback((contact) => {
    // Check if passenger is already added
    const alreadyAdded = additionalPassengers.some(p => 
      (p.traveler_id && contact.id && p.traveler_id === contact.id) ||
      (p.email && contact.email && p.email.toLowerCase() === contact.email.toLowerCase()) ||
      (p.name && (contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()) && 
       p.name.toLowerCase() === (contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()).toLowerCase())
    );
    
    if (alreadyAdded) {
      alert('This person is already added as a passenger.');
      return;
    }

    // Determine if this is an executive or attendee
    const isExecutive = tripExecutives.some(exec => exec.id === contact.id);
    
    const newPassenger = {
      tempId: `temp-${Date.now()}-${Math.random()}`,
      traveler_id: contact.id,
      traveler_type: isExecutive ? "executive" : "attendee",
      name: contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
      email: contact.email || '',
      phone: contact.phone || '',
      company: contact.company || '',
      title: contact.title || '',
      special_requests: ''
    };

    // Add passenger to current leg's leg_passengers array
    setCurrentPrivateFlightLeg(prev => ({
      ...prev,
      leg_passengers: [...(prev?.leg_passengers || []), newPassenger]
    }));
    setPassengerSearchQuery('');
    setShowPassengerSuggestions(false);
  }, [tripExecutives]);

  const handleRemovePassenger = useCallback((tempId) => {
    // Remove passenger from current leg's leg_passengers array
    setCurrentPrivateFlightLeg(prev => ({
      ...prev,
      leg_passengers: (prev?.leg_passengers || []).filter(p => p.tempId !== tempId)
    }));
  }, []);

  const handlePassengerUpdate = useCallback((tempId, field, value) => {
    setCurrentPrivateFlightLeg(prev => ({
      ...prev,
      leg_passengers: (prev?.leg_passengers || []).map(p =>
        p.tempId === tempId ? { ...p, [field]: value } : p
      )
    }));
  }, []);

  // Default form values for new passenger
  const defaultNewPassengerForm = {
    first_name: '',
    last_name: '',
    email: '',
    office_phone: '',
    mobile_phone: '',
    company: '',
    notes: '',
    contact_type: 'external',
    ea_name: '',
    ea_office_phone: '',
    ea_mobile_phone: '',
    is_private: false,
    saveToContacts: true,
    speaker_id: null,      // speaker_bureau: optional link to a saved Speaker
    speaker_name: ''
  };

  // Handler for opening new passenger modal
  const handleOpenNewPassengerModal = useCallback(() => {
    // Pre-fill name with current search query if available
    // Try to split into first and last name
    const nameParts = passengerSearchQuery.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    setNewPassengerForm({
      ...defaultNewPassengerForm,
      first_name: firstName,
      last_name: lastName
    });
    setEditingContact(null);
    setShowPassengerSuggestions(false);
    setShowNewPassengerModal(true);
  }, [passengerSearchQuery]);

  // Handler for opening edit contact modal
  const handleEditContact = useCallback((contact) => {
    // Handle both name formats: separate first_name/last_name or combined name/full_name
    let firstName = contact.first_name || '';
    let lastName = contact.last_name || '';

    // If first_name is empty but we have a combined name, split it
    if (!firstName && (contact.name || contact.full_name)) {
      const fullName = contact.name || contact.full_name || '';
      const nameParts = fullName.trim().split(/\s+/);
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }

    setNewPassengerForm({
      first_name: firstName,
      last_name: lastName,
      email: contact.email || '',
      office_phone: contact.office_phone || '',
      mobile_phone: contact.mobile_phone || contact.phone || '',
      company: contact.company || '',
      notes: contact.notes || contact.internal_comments || '',
      contact_type: contact.contact_type || 'external',
      ea_name: contact.ea_name || '',
      ea_office_phone: contact.ea_office_phone || '',
      ea_mobile_phone: contact.ea_mobile_phone || '',
      is_private: contact.is_private || false,
      saveToContacts: true  // When editing, always save
    });
    setEditingContact(contact);
    setShowNewPassengerModal(true);
  }, []);

  // Handler for adding or updating a passenger/contact from the modal
  const handleAddNewPassenger = useCallback(async () => {
    if (!newPassengerForm.first_name.trim()) {
      alert('First name is required.');
      return;
    }

    // Construct full name from first and last name
    const fullName = [newPassengerForm.first_name.trim(), newPassengerForm.last_name.trim()]
      .filter(Boolean)
      .join(' ');

    let contactId = editingContact?.id || null;

    // If saveToContacts is checked, create or update the contact in the database
    if (newPassengerForm.saveToContacts) {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();

        const contactData = {
          first_name: newPassengerForm.first_name.trim(),
          last_name: newPassengerForm.last_name.trim(),
          name: fullName,
          full_name: fullName,
          email: newPassengerForm.email.trim(),
          phone: newPassengerForm.mobile_phone.trim() || newPassengerForm.office_phone.trim(),
          office_phone: newPassengerForm.office_phone.trim(),
          mobile_phone: newPassengerForm.mobile_phone.trim(),
          company: newPassengerForm.company.trim(),
          notes: newPassengerForm.notes.trim(),
          contact_type: newPassengerForm.contact_type,
          ea_name: newPassengerForm.ea_name.trim(),
          ea_office_phone: newPassengerForm.ea_office_phone.trim(),
          ea_mobile_phone: newPassengerForm.ea_mobile_phone.trim(),
          is_private: newPassengerForm.is_private,
          source: 'external'
        };

        let response;
        if (editingContact?.id) {
          // Update existing contact
          response = await axios.put(`${apiBase}/contacts/${editingContact.id}`, contactData, { headers });
        } else {
          // Create new contact
          response = await axios.post(`${apiBase}/contacts`, contactData, { headers });
        }

        if (response.data?.id) {
          contactId = response.data.id;
          // Update allExecutivesList to include/update the contact
          setAllExecutivesList(prev => {
            const updatedContact = {
              id: response.data.id,
              name: fullName,
              full_name: fullName,
              first_name: newPassengerForm.first_name.trim(),
              last_name: newPassengerForm.last_name.trim(),
              email: response.data.email,
              phone: response.data.phone,
              company: response.data.company || '',
              title: response.data.title || '',
              source: 'external',
              type: 'external'
            };
            // Remove existing if updating, then add
            const filtered = prev.filter(c => c.id !== response.data.id);
            return [...filtered, updatedContact];
          });
        }
      } catch (error) {
        console.error('Error saving contact:', error);
        // Continue adding passenger even if contact save fails
      }
    }

    // Handle room guest assignment if triggered from hotel room selector
    if (pendingRoomGuestAssignment && contactId) {
      const roomIndex = pendingRoomGuestAssignment.roomIndex;
      const currentRooms = formData.component_data?.room_details || [];
      const currentRoom = currentRooms[roomIndex] || {};
      const currentGuestIds = currentRoom.guest_ids || [];
      const currentGuestNames = currentRoom.guest_names || [];

      // Add the new contact to the room's guest_ids if not already there
      if (!currentGuestIds.includes(contactId)) {
        const updatedRooms = [...currentRooms];
        if (!updatedRooms[roomIndex]) {
          updatedRooms[roomIndex] = {};
        }
        // Ensure room guest count is at least the number of assigned guests
        const newGuestIds = [...currentGuestIds, contactId];
        const currentRoomGuests = updatedRooms[roomIndex].guests || 1;
        const newGuestCount = Math.max(currentRoomGuests, newGuestIds.length, 1);
        updatedRooms[roomIndex] = {
          ...updatedRooms[roomIndex],
          guest_ids: newGuestIds,
          guest_names: [...currentGuestNames, fullName],
          guests: newGuestCount
        };
        handleDetailsChange('room_details', updatedRooms);

        // Update total_guests to be the sum of all room guests (minimum 1)
        const totalGuests = updatedRooms.reduce((sum, rm) => sum + (rm.guests || 1), 0);
        handleDetailsChange('total_guests', Math.max(totalGuests, 1));

        // Also sync assigned_executives
        const allGuestIds = updatedRooms.reduce((ids, room) => {
          const roomGuestIds = room.guest_ids || [];
          return [...ids, ...roomGuestIds];
        }, []);
        const uniqueGuestIds = [...new Set(allGuestIds)];
        handleDetailsChange('assigned_executives', uniqueGuestIds);
      }

      setPendingRoomGuestAssignment(null);
    }
    // Only add to leg_passengers if not just editing an existing contact and not a room guest assignment
    else if (!editingContact && !pendingRoomGuestAssignment) {
      const newPassenger = {
        tempId: `temp-${Date.now()}-${Math.random()}`,
        traveler_id: contactId,
        traveler_type: contactId ? 'attendee' : 'new_contact',
        name: fullName,
        first_name: newPassengerForm.first_name.trim(),
        last_name: newPassengerForm.last_name.trim(),
        email: newPassengerForm.email.trim(),
        phone: newPassengerForm.mobile_phone.trim() || newPassengerForm.office_phone.trim(),
        office_phone: newPassengerForm.office_phone.trim(),
        mobile_phone: newPassengerForm.mobile_phone.trim(),
        company: newPassengerForm.company.trim(),
        title: '',
        special_requests: '',
        speaker_id: newPassengerForm.speaker_id || null,
        speaker_name: newPassengerForm.speaker_name || ''
      };

      // Add passenger to current leg's leg_passengers array
      setCurrentPrivateFlightLeg(prev => ({
        ...prev,
        leg_passengers: [...(prev?.leg_passengers || []), newPassenger]
      }));
    }

    setNewPassengerForm(defaultNewPassengerForm);
    setEditingContact(null);
    setShowNewPassengerModal(false);
    setPassengerSearchQuery('');
    setPendingRoomGuestAssignment(null);
  }, [newPassengerForm, editingContact, pendingRoomGuestAssignment, formData.component_data?.room_details, handleDetailsChange]);

  // Handler for closing new passenger modal
  const handleCloseNewPassengerModal = useCallback(() => {
    setShowNewPassengerModal(false);
    setNewPassengerForm(defaultNewPassengerForm);
    setEditingContact(null);
    setPendingRoomGuestAssignment(null);
  }, []);

  const handleDepartureFBOSelect = useCallback(async (fbo) => {
    // FBOAutocomplete returns fields: name, airport_icao (not fbo_name/icao_code)
    handleDetailsChange('departure_fbo_id', fbo.id);
    handleDetailsChange('departure_fbo_name', fbo.name);
    // Keep the currently selected airport ICAO stable; do not clear it
    handleDetailsChange('departure_airport_icao', fbo.airport_icao);

    // Try to enrich with address/phone if missing on the incoming fbo
    try {
      let enriched = fbo;
      if (!fbo.address || !fbo.phone) {
        const res = await fetch(`/api/fbos/${encodeURIComponent(fbo.id)}`, { headers: getAuthHeaders(), credentials: 'include' });
        if (res.ok) {
          enriched = await res.json();
        }
      }
      if (enriched?.address) {
        handleDetailsChange('departure_fbo_address', enriched.address);
      }
      if (enriched?.phone) {
        handleDetailsChange('departure_fbo_phone', enriched.phone);
      }
    } catch (e) {
    }

    // Auto-populate airport name if not set yet
    if (!formData.component_data?.departure_airport_name) {
      handleDetailsChange('departure_airport_name', fbo.airport_name || fbo.airport_icao || '');
    }
  }, [handleDetailsChange, formData.component_data?.departure_airport_name]);

  const handleArrivalFBOSelect = useCallback(async (fbo) => {
    handleDetailsChange('arrival_fbo_id', fbo.id);
    handleDetailsChange('arrival_fbo_name', fbo.name);
    // Keep the currently selected airport ICAO stable; do not clear it
    handleDetailsChange('arrival_airport_icao', fbo.airport_icao);

    // Enrich with address/phone if missing
    try {
      let enriched = fbo;
      if (!fbo.address || !fbo.phone) {
        const res = await fetch(`/api/fbos/${encodeURIComponent(fbo.id)}`, { headers: getAuthHeaders(), credentials: 'include' });
        if (res.ok) {
          enriched = await res.json();
        }
      }
      if (enriched?.address) {
        handleDetailsChange('arrival_fbo_address', enriched.address);
      }
      if (enriched?.phone) {
        handleDetailsChange('arrival_fbo_phone', enriched.phone);
      }
    } catch (e) {
    }

    if (!formData.component_data?.arrival_airport_name) {
      handleDetailsChange('arrival_airport_name', fbo.airport_name || fbo.airport_icao || '');
    }
  }, [handleDetailsChange, formData.component_data?.arrival_airport_name]);

  // Private Flight Location Selection Handler (Airport-Specific)
  const handlePrivateFlightLocationSelect = useCallback((place, type) => {
    const address = place.formatted_address || '';
    const name = place.name || '';
    
    // Extract ICAO/IATA code from place name or components for airports
    let airportIdentifier = '';
    const nameMatch = name.match(/\(([^)]+)\)$/); // Matches (JFK) from "JFK Airport (JFK)"
    if (nameMatch && nameMatch[1]) {
      airportIdentifier = nameMatch[1].toUpperCase();
    } else if (place.address_components) {
      const airportComp = place.address_components.find(comp => 
        comp.types.includes('airport') || 
        comp.types.includes('political') || 
        comp.types.includes('locality')
      );
      if (airportComp && airportComp.short_name) {
        airportIdentifier = airportComp.short_name.toUpperCase();
      }
    }
    
    // If all else fails, and the name itself is a short uppercase string, use it
    if (!airportIdentifier && name.length <= 4 && name === name.toUpperCase()) {
      airportIdentifier = name;
    }

    if (formData.component_type === 'private_flight') {
      if (type === 'departure') {
        handlePrivateFlightDetailsChange('departure_airport_name', name);
        handlePrivateFlightDetailsChange('departure_airport_icao', airportIdentifier);
      } else if (type === 'arrival') {
        handlePrivateFlightDetailsChange('arrival_airport_name', name);
        handlePrivateFlightDetailsChange('arrival_airport_icao', airportIdentifier);
      }
    }
  }, [formData.component_type, handleDetailsChange]);

  // Handle airport selection from the new comprehensive airport database
  const handleAirportSelect = useCallback((airport, type) => {
    
    if (formData.component_type === 'private_flight') {
      if (type === 'departure') {
        handlePrivateFlightDetailsChange('departure_airport_name', airport.name);
        handlePrivateFlightDetailsChange('departure_airport_icao', airport.icao_code);
        handlePrivateFlightDetailsChange('departure_airport_iata', airport.iata_code);
        handlePrivateFlightDetailsChange('departure_airport_display', airport.displayName);
        handlePrivateFlightDetailsChange('departure_airport_country', airport.country_name);
        handlePrivateFlightDetailsChange('departure_airport_municipality', airport.municipality);
        handlePrivateFlightDetailsChange('departure_airport_lat', airport.latitude_deg);
        handlePrivateFlightDetailsChange('departure_airport_lng', airport.longitude_deg);
      } else if (type === 'arrival') {
        handlePrivateFlightDetailsChange('arrival_airport_name', airport.name);
        handlePrivateFlightDetailsChange('arrival_airport_icao', airport.icao_code);
        handlePrivateFlightDetailsChange('arrival_airport_iata', airport.iata_code);
        handlePrivateFlightDetailsChange('arrival_airport_display', airport.displayName);
        handlePrivateFlightDetailsChange('arrival_airport_country', airport.country_name);
        handlePrivateFlightDetailsChange('arrival_airport_municipality', airport.municipality);
        handlePrivateFlightDetailsChange('arrival_airport_lat', airport.latitude_deg);
        handlePrivateFlightDetailsChange('arrival_airport_lng', airport.longitude_deg);
      }
    }
  }, [formData.component_type, handleDetailsChange]);

  // Event-specific handlers
  const handleEventDateChange = useCallback((dateValue) => {

    if (!dateValue) {
      handleInputChange('start_datetime', '');
      return;
    }

    if (isStartTimeTBD) {
      // If time is TBD, just store date with placeholder time
      handleInputChange('start_datetime', `${dateValue}T00:00`);
      return;
    }

    // If we already have a valid datetime, preserve the time; otherwise set empty time
    let timeToUse = '';
    if (formData.start_datetime && formData.start_datetime !== 'TBD') {
      try {
        const existingTime = format(new Date(formData.start_datetime), 'HH:mm');
        timeToUse = existingTime;
      } catch {
        timeToUse = ''; // If parsing fails, leave time empty
      }
    }

    // Set the new datetime - empty time is ok, validation will catch it
    const newDateTime = timeToUse ? `${dateValue}T${timeToUse}` : `${dateValue}T00:00`;
    handleInputChange('start_datetime', newDateTime);
  }, [formData.start_datetime, handleInputChange, isStartTimeTBD]);

  const handleEventTimeChange = useCallback((timeValue) => {
    
    if (formData.start_datetime && !isDateTBD) {
      try {
        const currentDate = format(new Date(formData.start_datetime), 'yyyy-MM-dd');
        const newDateTime = `${currentDate}T${timeValue}`;
        handleInputChange('start_datetime', newDateTime);
      } catch (error) {
      }
    }
  }, [formData.start_datetime, handleInputChange, isDateTBD]);

  // Generate time options in 15-minute increments for Event component
  const generateEventTimeOptions = useCallback(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(timeValue);
      }
    }
    return options;
  }, []);

  // Real bookings do not respect a 15-minute grid. The Zoo Atlanta timed-entry
  // tour runs 12:55 to 13:40, and with only :00/:15/:30/:45 on offer the
  // select had no matching option, fell back to its "Select start time"
  // placeholder, and would have written the wrong time back on save.
  // Keep the grid, but carry whatever time this component actually has.
  const eventTimeOptions = useMemo(() => {
    const options = generateEventTimeOptions();

    const toHHMM = (value) => {
      if (!value || value === 'TBD') return null;
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return null;
        return format(date, 'HH:mm');
      } catch (error) {
        return null;
      }
    };

    const offGrid = [toHHMM(formData.start_datetime), toHHMM(formData.end_datetime)]
      .filter((time) => time && !options.includes(time));

    if (offGrid.length === 0) return options;

    return [...new Set([...options, ...offGrid])].sort();
  }, [generateEventTimeOptions, formData.start_datetime, formData.end_datetime]);

  // Handle start time change from dropdown
  const handleEventStartTimeChange = useCallback((timeValue) => {
    if (!formData.start_datetime || isDateTBD) return;

    try {
      const currentDate = format(new Date(formData.start_datetime), 'yyyy-MM-dd');
      const newDateTime = `${currentDate}T${timeValue}`;
      handleInputChange('start_datetime', newDateTime);
    } catch (error) {
    }
  }, [formData.start_datetime, handleInputChange, isDateTBD]);

  // Handle start time dropdown focus - set default to 09:00 if not set
  const handleEventStartTimeFocus = useCallback(() => {
    if (!formData.start_datetime || isDateTBD) return;

    try {
      const currentDateTime = new Date(formData.start_datetime);
      const currentTime = format(currentDateTime, 'HH:mm');

      // If time is 00:00 (default), set to 09:00
      if (currentTime === '00:00') {
        const currentDate = format(currentDateTime, 'yyyy-MM-dd');
        const newDateTime = `${currentDate}T09:00`;
        handleInputChange('start_datetime', newDateTime);
      }
    } catch (error) {
    }
  }, [formData.start_datetime, handleInputChange, isDateTBD]);

  // Handle end time change from dropdown
  const handleEventEndTimeChange = useCallback((timeValue) => {
    if (!formData.start_datetime || isDateTBD) return;

    try {
      // Use the same date as start_datetime for end_datetime
      const currentDate = format(new Date(formData.start_datetime), 'yyyy-MM-dd');
      const newDateTime = `${currentDate}T${timeValue}`;
      handleInputChange('end_datetime', newDateTime);
    } catch (error) {
    }
  }, [formData.start_datetime, handleInputChange, isDateTBD]);

  // Handle end time dropdown focus - set default to start time or 09:00 if not set
  const handleEventEndTimeFocus = useCallback(() => {
    if (!formData.start_datetime || isDateTBD) return;

    try {
      const currentDate = format(new Date(formData.start_datetime), 'yyyy-MM-dd');

      // If end_datetime is not set or is 00:00, set it to start time or 09:00
      if (!formData.end_datetime || formData.end_datetime === '') {
        // Get start time
        const startDateTime = new Date(formData.start_datetime);
        const startTime = format(startDateTime, 'HH:mm');

        // Use start time if it's not 00:00, otherwise use 09:00
        const defaultTime = startTime !== '00:00' ? startTime : '09:00';
        const newDateTime = `${currentDate}T${defaultTime}`;
        handleInputChange('end_datetime', newDateTime);
      } else {
        // Check if end time is 00:00
        try {
          const endDateTime = new Date(formData.end_datetime);
          const endTime = format(endDateTime, 'HH:mm');

          if (endTime === '00:00') {
            // Get start time
            const startDateTime = new Date(formData.start_datetime);
            const startTime = format(startDateTime, 'HH:mm');

            // Use start time if it's not 00:00, otherwise use 09:00
            const defaultTime = startTime !== '00:00' ? startTime : '09:00';
            const newDateTime = `${currentDate}T${defaultTime}`;
            handleInputChange('end_datetime', newDateTime);
          }
        } catch (e) {
          // If parsing fails, set to start time or 09:00
          const startDateTime = new Date(formData.start_datetime);
          const startTime = format(startDateTime, 'HH:mm');
          const defaultTime = startTime !== '00:00' ? startTime : '09:00';
          const newDateTime = `${currentDate}T${defaultTime}`;
          handleInputChange('end_datetime', newDateTime);
        }
      }
    } catch (error) {
    }
  }, [formData.start_datetime, formData.end_datetime, handleInputChange, isDateTBD]);

  // Load all executives and contacts for attendee search (same as Global Contacts page)
  useEffect(() => {
    const loadAllContactsAndExecutives = async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        
        // Load both executives and external contacts (same approach as Global Contacts page)
        const [executivesResponse, contactsResponse] = await Promise.all([
          axios.get(`${apiBase}/executives`, { headers }).catch(err => {
            return { data: [] };
          }),
          axios.get(`${apiBase}/contacts`, { headers }).catch(err => {
            return { data: [] };
          })
        ]);
        
        // Convert executives to contact format (same as Global Contacts page)
        const executivesAsContacts = (executivesResponse.data || []).map(exec => ({
          id: exec.id,
          name: `${exec.first_name} ${exec.last_name}`,
          title: exec.title || 'Executive',
          email: exec.email,
          company: exec.company,
          phone: exec.phone,
          source: 'executive',
          type: 'executive'
        }));
        
        // External contacts keep their existing structure
        const externalContacts = (contactsResponse.data || []).map(contact => ({
          id: contact.id,
          name: contact.full_name || contact.name,
          title: contact.title,
          email: contact.email,
          company: contact.company,
          phone: contact.primary_phone || contact.phone,
          source: 'external',
          type: 'external'
        }));
        
        // Combine all contacts into unified list
        const combinedContactList = [...executivesAsContacts, ...externalContacts];
        setAllExecutivesList(combinedContactList);  // Reuse existing state for combined list
        
      } catch (error) {
        console.error('Error loading contacts and executives:', error);
        setAllExecutivesList([]);
      }
    };

    loadAllContactsAndExecutives();
  }, []);

  const handleTimeFormatChange = useCallback((format) => {
    // Update time display based on format preference
    // This would ideally update user preferences, but for now just update display
  }, []);

  // Private Flight Render Function
  const renderPrivateFlightFields = () => {
    // Note: filteredExecutives and filteredContacts are now defined at component level
    // to comply with React's rules of hooks (hooks cannot be called inside nested functions)

    return (
      <div className="space-y-6">
        {/* Import Itinerary Section - At Top */}
        <div className="p-4 bg-ui-violet-bg rounded-xl border border-ui-violet-line">
          <div className="flex items-center justify-between mb-4">
            <Label className="text-lg font-semibold flex items-center gap-2">
              <Upload className="w-5 h-5 text-ui-violet-fg" />
              Import Private Flight Itinerary
            </Label>
            <div className="relative">
              <input
                type="file"
                id="private-flight-itinerary-upload"
                className="hidden"
                onChange={handlePrivateFlightConfirmationUpload}
                accept=".pdf,.png,.jpg,.jpeg"
                disabled={isUploadingPrivateFlightConfirmation}
              />
              <Button
                type="button"
                onClick={() => document.getElementById('private-flight-itinerary-upload').click()}
                size="sm"
                className="bg-ui-violet-strong hover:bg-ui-violet-strong/90 text-ui-on-primary"
                disabled={isUploadingPrivateFlightConfirmation}
              >
                {isUploadingPrivateFlightConfirmation ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" /> Upload Document
                  </>
                )}
              </Button>
            </div>
          </div>
          <div className="text-sm text-ui-ink-3 space-y-2">
            <p>
              <strong>Supported documents:</strong> Private flight itineraries, flight plans, trip summaries
            </p>
            <p>
              <strong>File formats:</strong> PDF, PNG, JPG, JPEG
            </p>
            <div className="p-3 bg-ui-surface rounded-lg border border-ui-violet-line mt-3">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-ui-violet-fg flex-shrink-0 mt-0.5" />
                <div className="text-xs text-ui-ink-3 space-y-1">
                  <p className="font-semibold text-ui-ink-2">Important Note:</p>
                  <p>
                    This document is <strong>not a confirmation for a specific passenger</strong>. Private flight itineraries
                    typically include <strong>one or more passengers on each leg</strong>, and the passenger list can
                    <strong> change from leg to leg</strong>. Our AI will extract all flight segments and passengers mentioned
                    in the document.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-xs text-ui-muted italic">
              Tip: Upload your itinerary document and our AI will automatically extract flight legs, aircraft details, and passenger information.
            </p>
          </div>
        </div>

        {/* Existing Documents Display */}
        {attachedFiles.length > 0 && (
          <div className="space-y-3">
            <Label className="text-sm font-bold text-ui-ink-2">Existing Documents</Label>
            <div className="space-y-3">
              {attachedFiles.map((file) => (
                <div key={file.id} className="p-3 bg-ui-surface rounded-lg border border-ui-line hover:border-ui-control transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-ui-violet-bg rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-ui-violet-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-ui-ink">{attachmentLabel(file)}</div>
                        <div className="text-xs text-ui-muted">
                          {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {file.name && file.id && !file.id.startsWith('temp-') && file.id !== 'undefined' ? (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleViewDocument(file);
                            }}
                            className="text-ui-violet-fg hover:text-ui-violet-fg"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {hasParsedAttachment(file) && (
                            <Button type="button" variant="ghost" size="sm"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewDocument(file, true); }}>
                              Parsed data (JSON)
                            </Button>
                          )}
                        </div>
                      ) : file.url && file.url.startsWith('blob:') ? (
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-ui-amber-fg font-medium">⚠️ Legacy File</span>
                          <span className="text-xs text-ui-muted">Re-upload needed to view</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-ui-red-fg font-medium">❌ Invalid File</span>
                          <span className="text-xs text-ui-muted">File ID missing</span>
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFileDelete(file.id)}
                        className="text-ui-red-fg hover:text-ui-red-fg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor={`file-label-${file.id}`} className="text-xs text-ui-ink-3">
                      Document Label
                    </Label>
                    <Input
                      id={`file-label-${file.id}`}
                      value={file.label || ''}
                      onChange={(e) => handleFileLabelChange(file.id, e.target.value)}
                      placeholder="Enter description for this file"
                      className="mt-1 h-8 text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Validation Error Display */}
        {validationError && (
          <div className="p-4 bg-ui-red-bg border border-ui-red-line rounded-xl">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-ui-red-fg" />
              <p className="text-ui-red-fg font-medium">{validationError}</p>
            </div>
          </div>
        )}

        {/* Operator Section */}
        <div className="p-4 bg-ui-blue-bg rounded-xl border border-ui-blue-line">
            {/* Compute operator value robustly to prevent transient resets */}
            {(() => {
              const operatorValue = (component?.id)
                ? (formData.component_data?.operator_type || component?.component_data?.operator_type || component?.component_data?.details?.operator_type || component?.details?.operator_type || '')
                : (formData.component_data?.operator_type || '');
              // Expose for debugging if needed
              window.__pf_operator_value = operatorValue;
              return (
                <>
                  <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                    <Settings className="w-4 h-4 text-ui-link" />
                    Operator
                  </Label>
                  <Select
                    value={operatorValue || undefined}
                    onValueChange={(value) => {
                      if (!value) return; // ignore empty resets from uncontrolled transitions
                      handlePrivateFlightDetailsChange('operator_type', value);
                    }}
                  >
                    <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-primary focus:ring-ui-primary">
                      <SelectValue placeholder="Select operator type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flight_ops">
                        <div className="flex items-center gap-2">
                          <Crown className="w-4 h-4 text-ui-violet-fg" />
                          <span>Flight Ops</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="charter">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-ui-amber-fg" />
                          <span>Charter</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {/* Charter Operator Details (Conditional) */}
                  {operatorValue === 'charter' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="p-4 bg-ui-amber-bg rounded-lg border border-ui-amber-line mt-4"
                    >
                      <Label className="text-base font-semibold text-ui-ink mb-3 block flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-ui-amber-fg" />
                        Charter Operator Details
                      </Label>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Charter Operator Name</Label>
                          <Input
                            value={formData.component_data?.charter_operator_name || ''}
                            onChange={(e) => handlePrivateFlightDetailsChange('charter_operator_name', e.target.value)}
                            placeholder="Enter charter company name"
                            className="h-10 text-sm border-ui-line focus:border-ui-amber-solid focus:ring-ui-amber-solid"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-bold text-ui-ink-2 mb-2 block">Charter Operator Phone</Label>
                          <Input
                            value={formData.component_data?.charter_operator_phone || ''}
                            onChange={(e) => handlePrivateFlightDetailsChange('charter_operator_phone', e.target.value)}
                            placeholder="Enter phone number"
                            className="h-10 text-sm border-ui-line focus:border-ui-amber-solid focus:ring-ui-amber-solid"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </>
              );
            })()}
        </div>

        {/* Aircraft Section */}
        <div className="p-4 bg-ui-blue-bg rounded-xl border border-ui-blue-line space-y-4">
          <Label className="text-lg font-semibold flex items-center gap-2">
            <Plane className="w-5 h-5 text-ui-link"/>
            Aircraft
          </Label>
          
          {/* Aircraft Details */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                <Settings className="w-4 h-4 text-ui-link" />
                Operator
              </Label>
              <Select
                value={formData.component_data?.operator_type || ''}
                onValueChange={(value) => handlePrivateFlightDetailsChange('operator_type', value)}
              >
                <SelectTrigger className="h-12 text-base border-ui-line focus:border-ui-primary focus:ring-ui-primary">
                  <SelectValue placeholder="Select operator type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flight_ops">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-ui-violet-fg" />
                      <span>Flight Ops</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="charter">
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4 text-ui-link" />
                      <span>Charter</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-ui-muted mt-1">
                Choose flight operations or charter service
              </p>
            </div>
            
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                <Hash className="w-4 h-4 text-ui-link" />
                Tail Number
              </Label>
              <div className="relative">
                <Input
                  value={formData.component_data?.tail_number || ''}
                  onChange={(e) => handleTailNumberChange(e.target.value)}
                  placeholder="e.g., N123AB"
                  className="h-12 text-base border-ui-line focus:border-ui-primary focus:ring-ui-primary"
                />
                {aircraftLookupLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-ui-primary border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              <p className="text-xs text-ui-muted mt-1">
                Aircraft tail number - type will be auto-filled if found
              </p>
              {aircraftLookupError && (
                <p className="text-xs text-ui-red-fg mt-1">
                  {aircraftLookupError}
                </p>
              )}
              {aircraftLookupSuccess && (
                <p className="text-xs text-ui-green-fg mt-1">
                  ✓ Aircraft found; existing details preserved
                </p>
              )}
            </div>
            
            <div>
              <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                <Plane className="w-4 h-4 text-ui-link" />
                Type
              </Label>
              <Input
                value={formData.component_data?.aircraft_type || ''}
                onChange={(e) => handlePrivateFlightDetailsChange('aircraft_type', e.target.value)}
                placeholder="e.g., Citation X, Gulfstream G650"
                className="h-12 text-base border-ui-line focus:border-ui-primary focus:ring-ui-primary"
              />
              <p className="text-xs text-ui-muted mt-1">
                Aircraft type or model
              </p>
            </div>
          </div>
        </div>

        {/* Private Flight Legs Section */}
        <div className="p-4 bg-ui-violet-bg rounded-xl border border-ui-violet-line space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-lg font-semibold flex items-center gap-2">
              <Navigation className="w-5 h-5 text-ui-violet-fg"/>
              Flight Legs
              {privateFlightLegs.length > 0 && (
                <Badge variant="outline" className="ml-2">
                  {privateFlightLegs.length} leg{privateFlightLegs.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </Label>
            <Button
              type="button"
              size="sm"
              className="bg-ui-violet-strong hover:bg-ui-violet-strong/90 text-ui-on-primary"
              onClick={() => {
                setCurrentPrivateFlightLeg({});
                setEditingLegIndex(-1); // -1 indicates adding new leg
                setShowPrivateFlightLegForm(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Leg
            </Button>
          </div>
          
          {/* Private Flight Legs Display */}
          {privateFlightLegs.length > 0 ? (
            <div className="space-y-2">
              {/* Header row */}
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-ui-ink-3 uppercase tracking-wide px-3 py-2 bg-ui-sunk rounded">
                <div className="col-span-3">From Airport</div>
                <div className="col-span-3">To Airport</div>
                <div className="col-span-2">Departure</div>
                <div className="col-span-2">Arrival</div>
                <div className="col-span-2">Actions</div>
              </div>
              
              {/* Private flight leg rows */}
              {privateFlightLegs.map((leg, index) => (
                <div
                  key={index}
                  className="p-4 bg-ui-surface rounded-lg border border-ui-violet-line hover:border-ui-violet-line transition-colors space-y-3"
                >
                  {/* Main Route and Times Grid */}
                  <div className="grid grid-cols-12 gap-2 items-center">
                    {/* Departure Airport */ }
                    <div className="col-span-3">
                      <div className="font-medium text-ui-ink">
                        {leg.departure_airport_name || 'TBD'}
                      </div>
                      <div className="text-xs text-ui-muted">
                        {leg.departure_airport_icao || leg.departure_airport_iata || ''}
                      </div>
                    </div>

                    {/* Arrival Airport */}
                    <div className="col-span-3">
                      <div className="font-medium text-ui-ink">
                        {leg.arrival_airport_name || 'TBD'}
                      </div>
                      <div className="text-xs text-ui-muted">
                        {leg.arrival_airport_icao || leg.arrival_airport_iata || ''}
                      </div>
                    </div>

                    {/* Departure Date/Time */}
                    <div className="col-span-2">
                      <div className="text-sm text-ui-ink">
                        {leg.departure_date ? new Date(leg.departure_date).toLocaleDateString() : 'TBD'}
                      </div>
                      <div className="text-xs text-ui-muted">
                        {leg.departure_time || 'TBD'}
                      </div>
                    </div>

                    {/* Arrival Date/Time */}
                    <div className="col-span-2">
                      <div className="text-sm text-ui-ink">
                        {leg.arrival_date ? new Date(leg.arrival_date).toLocaleDateString() : 'TBD'}
                      </div>
                      <div className="text-xs text-ui-muted">
                        {leg.arrival_time || 'TBD'}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-2 flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCurrentPrivateFlightLeg({...leg}); // Create a copy to avoid direct mutation
                          setEditingLegIndex(index); // Track which leg we're editing
                          setShowPrivateFlightLegForm(true);
                        }}
                        className="p-1 h-7 w-7 text-ui-violet-fg hover:text-ui-violet-fg hover:bg-ui-violet-bg"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setPrivateFlightLegs(prev => prev.filter((_, i) => i !== index));
                        }}
                        className="p-1 h-7 w-7 text-ui-red-fg hover:text-ui-red-fg hover:bg-ui-red-bg"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* FBO Information Section */}
                  {(leg.departure_fbo_name || leg.arrival_fbo_name) && (
                    <div className="grid grid-cols-2 gap-4 pt-3 border-t border-ui-violet-line">
                      {/* Departure FBO */}
                      {leg.departure_fbo_name && (
                        <div className="space-y-1">
                          <div className="text-xs font-semibold text-ui-violet-fg uppercase tracking-wide">Departure FBO</div>
                          <div className="text-sm font-medium text-ui-ink">{leg.departure_fbo_name}</div>
                          {leg.departure_fbo_address && (
                            <div className="text-xs text-ui-muted">{leg.departure_fbo_address}</div>
                          )}
                          {leg.departure_fbo_phone && (
                            <div className="text-xs text-ui-ink-3">{leg.departure_fbo_phone}</div>
                          )}
                        </div>
                      )}

                      {/* Arrival FBO */}
                      {leg.arrival_fbo_name && (
                        <div className="space-y-1">
                          <div className="text-xs font-semibold text-ui-violet-fg uppercase tracking-wide">Arrival FBO</div>
                          <div className="text-sm font-medium text-ui-ink">{leg.arrival_fbo_name}</div>
                          {leg.arrival_fbo_address && (
                            <div className="text-xs text-ui-muted">{leg.arrival_fbo_address}</div>
                          )}
                          {leg.arrival_fbo_phone && (
                            <div className="text-xs text-ui-ink-3">{leg.arrival_fbo_phone}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Passengers on this Leg */}
                  {leg.leg_passengers && leg.leg_passengers.length > 0 && (
                    <div className="pt-3 border-t border-ui-violet-line">
                      <div className="text-xs font-semibold text-ui-violet-fg uppercase tracking-wide mb-2">
                        Passengers on this Leg ({leg.leg_passengers.length})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {leg.leg_passengers.map((passenger, pIndex) => (
                          <div
                            key={pIndex}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-ui-violet-bg rounded-full border border-ui-violet-line"
                          >
                            <Users className="w-3 h-3 text-ui-violet-fg" />
                            <span className="text-sm text-ui-ink-2">{passenger.name}</span>
                            {passenger.special_requests && (
                              <span className="text-xs text-ui-muted" title={passenger.special_requests}>
                                *
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-ui-muted mt-2 italic">
                        Note: Passenger list may vary from leg to leg
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-ui-muted bg-ui-sunk rounded-lg border-2 border-dashed border-ui-control">
              <Navigation className="w-10 h-10 text-ui-faint mx-auto mb-2" />
              <p className="font-medium">No flight legs added yet</p>
              <p className="text-sm mt-1">Click "Add Leg" to create your first flight leg</p>
            </div>
          )}
        </div>

        {/* New/Edit Passenger Modal */}
        {showNewPassengerModal && (
          <Dialog open={showNewPassengerModal} onOpenChange={handleCloseNewPassengerModal}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {editingContact ? (
                    <>
                      <Edit className="w-5 h-5 text-ui-link" />
                      Edit Contact
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5 text-ui-green-fg" />
                      Add New Passenger
                    </>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Contact Type Radio */}
                <div className="p-3 bg-ui-sunk rounded-lg border border-ui-line">
                  <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">Contact Type</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contact_type"
                        value="external"
                        checked={newPassengerForm.contact_type === 'external'}
                        onChange={(e) => setNewPassengerForm(prev => ({ ...prev, contact_type: e.target.value }))}
                        className="w-4 h-4 text-ui-green-fg border-ui-control focus:ring-ui-green-solid"
                      />
                      <span className="text-sm text-ui-ink-2">External</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contact_type"
                        value="internal"
                        checked={newPassengerForm.contact_type === 'internal'}
                        onChange={(e) => setNewPassengerForm(prev => ({ ...prev, contact_type: e.target.value }))}
                        className="w-4 h-4 text-ui-green-fg border-ui-control focus:ring-ui-green-solid"
                      />
                      <span className="text-sm text-ui-ink-2">Internal</span>
                    </label>
                  </div>
                </div>

                {/* First Name and Last Name - Required */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
                      First Name <span className="text-ui-red-fg">*</span>
                    </Label>
                    <Input
                      value={newPassengerForm.first_name}
                      onChange={(e) => setNewPassengerForm(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="First name"
                      className="h-10 text-sm border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid"
                      autoFocus
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
                      Last Name
                    </Label>
                    <Input
                      value={newPassengerForm.last_name}
                      onChange={(e) => setNewPassengerForm(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Last name"
                      className="h-10 text-sm border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid"
                    />
                  </div>
                </div>

                {/* Optional link to a saved Speaker (speaker_bureau mode only) */}
                {isSpeakerBureau && (
                  <div>
                    <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
                      Link to {labels.executive} (optional)
                    </Label>
                    <SpeakerLinkField
                      speakerId={newPassengerForm.speaker_id || null}
                      speakerName={newPassengerForm.speaker_name || ''}
                      onPick={(sp) => {
                        if (!sp) {
                          setNewPassengerForm(prev => ({ ...prev, speaker_id: null, speaker_name: '' }));
                          return;
                        }
                        const nm = sp.full_name || `${sp.first_name || ''} ${sp.last_name || ''}`.trim();
                        const [fn, ...rest] = nm.split(/\s+/);
                        setNewPassengerForm(prev => ({
                          ...prev,
                          speaker_id: sp.id,
                          speaker_name: nm,
                          first_name: prev.first_name || fn || '',
                          last_name: prev.last_name || rest.join(' ') || '',
                          email: prev.email || sp.email || '',
                        }));
                      }}
                    />
                  </div>
                )}

                {/* Company Name */}
                <div>
                  <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">Company Name</Label>
                  <Input
                    value={newPassengerForm.company}
                    onChange={(e) => setNewPassengerForm(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Company name (optional)"
                    className="h-10 text-sm border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">Email Address</Label>
                  <Input
                    type="email"
                    value={newPassengerForm.email}
                    onChange={(e) => setNewPassengerForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@example.com (optional)"
                    className="h-10 text-sm border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid"
                  />
                </div>

                {/* Contact Phone Numbers */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">Office Phone</Label>
                    <Input
                      type="tel"
                      value={newPassengerForm.office_phone}
                      onChange={(e) => setNewPassengerForm(prev => ({ ...prev, office_phone: e.target.value }))}
                      placeholder="(optional)"
                      className="h-10 text-sm border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">Mobile Phone</Label>
                    <Input
                      type="tel"
                      value={newPassengerForm.mobile_phone}
                      onChange={(e) => setNewPassengerForm(prev => ({ ...prev, mobile_phone: e.target.value }))}
                      placeholder="(optional)"
                      className="h-10 text-sm border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid"
                    />
                  </div>
                </div>

                {/* EA Information Section */}
                <div className="p-3 bg-ui-blue-bg rounded-lg border border-ui-blue-line space-y-3">
                  <Label className="text-sm font-semibold text-ui-blue-fg block">Executive Assistant (EA) Information</Label>
                  <div>
                    <Label className="text-xs font-medium text-ui-ink-3 mb-1 block">EA Name</Label>
                    <Input
                      value={newPassengerForm.ea_name}
                      onChange={(e) => setNewPassengerForm(prev => ({ ...prev, ea_name: e.target.value }))}
                      placeholder="EA name (optional)"
                      className="h-9 text-sm border-ui-line focus:border-ui-primary focus:ring-ui-primary"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-ui-ink-3 mb-1 block">EA Office Phone</Label>
                      <Input
                        type="tel"
                        value={newPassengerForm.ea_office_phone}
                        onChange={(e) => setNewPassengerForm(prev => ({ ...prev, ea_office_phone: e.target.value }))}
                        placeholder="(optional)"
                        className="h-9 text-sm border-ui-line focus:border-ui-primary focus:ring-ui-primary"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-ui-ink-3 mb-1 block">EA Mobile Phone</Label>
                      <Input
                        type="tel"
                        value={newPassengerForm.ea_mobile_phone}
                        onChange={(e) => setNewPassengerForm(prev => ({ ...prev, ea_mobile_phone: e.target.value }))}
                        placeholder="(optional)"
                        className="h-9 text-sm border-ui-line focus:border-ui-primary focus:ring-ui-primary"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">Notes</Label>
                  <Textarea
                    value={newPassengerForm.notes}
                    onChange={(e) => setNewPassengerForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes about this contact (optional)"
                    className="h-20 text-sm border-ui-line focus:border-ui-green-solid focus:ring-ui-green-solid resize-none"
                  />
                </div>

                {/* Privacy and Save Options */}
                <div className="space-y-3 p-3 bg-ui-sunk rounded-lg border border-ui-line">
                  {/* Private Contact Checkbox */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isPrivate"
                      checked={newPassengerForm.is_private}
                      onChange={(e) => setNewPassengerForm(prev => ({ ...prev, is_private: e.target.checked }))}
                      className="w-4 h-4 text-ui-amber-fg border-ui-control rounded focus:ring-ui-amber-solid"
                    />
                    <label htmlFor="isPrivate" className="text-sm text-ui-ink-2 cursor-pointer">
                      <span className="font-medium">Private Contact</span>
                      <span className="text-ui-muted block text-xs">Only you can see this contact (not shared with organization)</span>
                    </label>
                  </div>

                  {/* Save to Contacts Checkbox - only show when not editing */}
                  {!editingContact && (
                    <div className="flex items-center gap-3 pt-2 border-t border-ui-line">
                      <input
                        type="checkbox"
                        id="saveToContacts"
                        checked={newPassengerForm.saveToContacts}
                        onChange={(e) => setNewPassengerForm(prev => ({ ...prev, saveToContacts: e.target.checked }))}
                        className="w-4 h-4 text-ui-green-fg border-ui-control rounded focus:ring-ui-green-solid"
                      />
                      <label htmlFor="saveToContacts" className="text-sm text-ui-ink-2 cursor-pointer">
                        <span className="font-medium">Save to Contacts</span>
                        <span className="text-ui-muted block text-xs">Add this person to your contacts list for future trips</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseNewPassengerModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleAddNewPassenger}
                  className={`flex-1 text-ui-on-primary ${editingContact ? 'bg-ui-primary hover:bg-ui-primary-hover' : 'bg-ui-green-strong hover:bg-ui-green-strong/90'}`}
                >
                  {editingContact ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Passenger
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Private Flight Leg Form Modal */}
        {showPrivateFlightLegForm && (
          <Dialog open={showPrivateFlightLegForm} onOpenChange={setShowPrivateFlightLegForm}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
              <PortalContainerProvider>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Navigation className="w-5 h-5 text-ui-violet-fg" />
                    {currentPrivateFlightLeg && Object.keys(currentPrivateFlightLeg).length > 0 ? 'Edit' : 'Add'} Private Flight Leg
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                {/* Flight Timing Section */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Departure Date and Time */}
                  <div className="space-y-3">
                    <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                      <Clock className="w-4 h-4 text-ui-violet-fg" />
                      Departure Date & Time
                    </Label>
                    
                    <div>
                      <Label className="text-xs text-ui-ink-3 mb-1 block">Date</Label>
                      <DatePickerButton
                        label={currentPrivateFlightLeg?.departure_date ? format(new Date(currentPrivateFlightLeg.departure_date), 'EEE, MMM d, yyyy') : 'Select date'}
                        onPick={(date) => {
                          const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                          setCurrentPrivateFlightLeg(prev => ({
                            ...prev,
                            departure_date: isoDate,
                            // Model previous behavior: always default arrival date to departure date when departure is set/updated
                            arrival_date: isoDate
                          }));
                        }}
                        selectedDate={currentPrivateFlightLeg?.departure_date ? new Date(currentPrivateFlightLeg.departure_date) : undefined}
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs text-ui-ink-3 mb-1 block">Time</Label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={currentPrivateFlightLeg?.departure_time || ''}
                          onChange={(e) => setCurrentPrivateFlightLeg(prev => ({...prev, departure_time: e.target.value}))}
                          placeholder="e.g., 14:30, 2:30, TBD"
                          className="h-10 text-sm border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid flex-1"
                        />
                        <Select
                          value={currentPrivateFlightLeg?.departure_time_format || '24H'}
                          onValueChange={(value) => setCurrentPrivateFlightLeg(prev => ({...prev, departure_time_format: value}))}
                        >
                          <SelectTrigger className="h-10 text-sm w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="24H">24H</SelectItem>
                            <SelectItem value="AM">AM</SelectItem>
                            <SelectItem value="PM">PM</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-ui-muted mt-1">
                        Enter time (e.g., 14:30, 2:30) or "TBD"
                      </p>
                    </div>
                  </div>
                  
                  {/* Arrival Date and Time */}
                  <div className="space-y-3">
                    <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                      <Clock className="w-4 h-4 text-ui-violet-fg" />
                      Arrival Date & Time
                    </Label>
                    
                    <div>
                      <Label className="text-xs text-ui-ink-3 mb-1 block">Date</Label>
                      <DatePickerButton
                        label={currentPrivateFlightLeg?.arrival_date ? format(new Date(currentPrivateFlightLeg.arrival_date), 'EEE, MMM d, yyyy') : 'Select date'}
                        onPick={(date) => {
                          const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                          setCurrentPrivateFlightLeg(prev => ({
                            ...prev,
                            arrival_date: isoDate
                          }));
                        }}
                        selectedDate={currentPrivateFlightLeg?.arrival_date ? new Date(currentPrivateFlightLeg.arrival_date) : undefined}
                      />
                      <p className="text-xs text-ui-muted mt-1">
                        Auto-fills from departure date, but can be changed
                      </p>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-ui-ink-3 mb-1 block">Time</Label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={currentPrivateFlightLeg?.arrival_time || ''}
                          onChange={(e) => setCurrentPrivateFlightLeg(prev => ({...prev, arrival_time: e.target.value}))}
                          placeholder="e.g., 14:30, 2:30, TBD"
                          className="h-10 text-sm border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid flex-1"
                        />
                        <Select
                          value={currentPrivateFlightLeg?.arrival_time_format || '24H'}
                          onValueChange={(value) => setCurrentPrivateFlightLeg(prev => ({...prev, arrival_time_format: value}))}
                        >
                          <SelectTrigger className="h-10 text-sm w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="24H">24H</SelectItem>
                            <SelectItem value="AM">AM</SelectItem>
                            <SelectItem value="PM">PM</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-ui-muted mt-1">
                        Enter time (e.g., 14:30, 2:30) or "TBD"
                      </p>
                    </div>
                  </div>
                </div>

                {/* Timeline Anchor Setting */}
                {(() => {
                  const hasDep = currentPrivateFlightLeg?.departure_date &&
                    currentPrivateFlightLeg?.departure_time &&
                    currentPrivateFlightLeg.departure_time.trim() !== '' &&
                    currentPrivateFlightLeg.departure_time.toLowerCase() !== 'tbd';
                  const hasArr = currentPrivateFlightLeg?.arrival_date &&
                    currentPrivateFlightLeg?.arrival_time &&
                    currentPrivateFlightLeg.arrival_time.trim() !== '' &&
                    currentPrivateFlightLeg.arrival_time.toLowerCase() !== 'tbd';
                  const isUserControlled = hasDep && hasArr;
                  const currentAnchor = currentPrivateFlightLeg?.anchor_to_arrival;

                  return (
                    <div className="border border-ui-line rounded-lg p-4 bg-ui-sunk">
                      <Label className="text-sm font-bold text-ui-ink-2 mb-3 block flex items-center gap-2">
                        <svg className="w-4 h-4 text-ui-violet-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                        Timeline Anchor Point
                      </Label>
                      {isUserControlled ? (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setCurrentPrivateFlightLeg(prev => ({...prev, anchor_to_arrival: false}))}
                            className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
                              !currentAnchor
                                ? 'bg-ui-violet-strong text-ui-on-primary border-ui-violet-solid'
                                : 'bg-ui-surface text-ui-ink-3 border-ui-control hover:border-ui-violet-solid'
                            }`}
                          >
                            Departure (default)
                          </button>
                          <button
                            type="button"
                            onClick={() => setCurrentPrivateFlightLeg(prev => ({...prev, anchor_to_arrival: true}))}
                            className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
                              currentAnchor
                                ? 'bg-ui-violet-strong text-ui-on-primary border-ui-violet-solid'
                                : 'bg-ui-surface text-ui-ink-3 border-ui-control hover:border-ui-violet-solid'
                            }`}
                          >
                            Arrival
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-ui-muted italic">
                          {hasArr && !hasDep
                            ? 'Auto: anchored to arrival time (no departure entered).'
                            : hasDep && !hasArr
                            ? 'Auto: anchored to departure time (no arrival entered).'
                            : 'Auto: will be TBD until at least one complete date + time is entered.'}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Airport Selection Section */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-ui-violet-fg" />
                      Departure Airport
                    </Label>
                    <AirportAutocomplete
                      placeholder="Search departure airport..."
                      value={currentPrivateFlightLeg?.departure_airport_display || ''}
                      onValueChange={(value) => setCurrentPrivateFlightLeg(prev => ({...prev, departure_airport_display: value}))}
                      onAirportSelect={(airport) => setCurrentPrivateFlightLeg(prev => ({
                        ...prev,
                        departure_airport_name: airport.name,
                        departure_airport_icao: airport.icao_code,
                        departure_airport_iata: airport.iata_code,
                        departure_airport_display: `${airport.name} (${airport.iata_code || airport.icao_code})`
                      }))}
                      className="h-12 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-ui-violet-fg" />
                      Arrival Airport
                    </Label>
                    <AirportAutocomplete
                      placeholder="Search arrival airport..."
                      value={currentPrivateFlightLeg?.arrival_airport_display || ''}
                      onValueChange={(value) => setCurrentPrivateFlightLeg(prev => ({...prev, arrival_airport_display: value}))}
                      onAirportSelect={(airport) => setCurrentPrivateFlightLeg(prev => ({
                        ...prev,
                        arrival_airport_name: airport.name,
                        arrival_airport_icao: airport.icao_code,
                        arrival_airport_iata: airport.iata_code,
                        arrival_airport_display: `${airport.name} (${airport.iata_code || airport.icao_code})`
                      }))}
                      className="h-12 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid"
                    />
                  </div>
                </div>

                {/* FBO Selection Section */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                      <Building className="w-4 h-4 text-ui-violet-fg" />
                      Departure FBO
                    </Label>
                    <FBOAutocomplete
                      airportIcao={currentPrivateFlightLeg?.departure_airport_icao}
                      onFboSelect={(fbo) => {
                        // Single state commit to avoid multiple renders/clicks perception
                        setCurrentPrivateFlightLeg(prev => ({
                          ...prev,
                          departure_fbo_name: fbo.name,
                          departure_fbo_id: fbo.id,
                          departure_airport_icao: fbo.airport_icao,
                          departure_fbo_address: fbo.address || prev.departure_fbo_address || '',
                          departure_fbo_phone: fbo.phone || prev.departure_fbo_phone || ''
                        }));
                      }}
                      initialFboName={currentPrivateFlightLeg?.departure_fbo_name || ''}
                      className="h-12 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid"
                      placeholder="Select departure FBO..."
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                      <Building className="w-4 h-4 text-ui-violet-fg" />
                      Arrival FBO
                    </Label>
                    <FBOAutocomplete
                      airportIcao={currentPrivateFlightLeg?.arrival_airport_icao}
                      onFboSelect={(fbo) => {
                        setCurrentPrivateFlightLeg(prev => ({
                          ...prev,
                          arrival_fbo_name: fbo.name,
                          arrival_fbo_id: fbo.id,
                          arrival_airport_icao: fbo.airport_icao,
                          arrival_fbo_address: fbo.address || prev.arrival_fbo_address || '',
                          arrival_fbo_phone: fbo.phone || prev.arrival_fbo_phone || ''
                        }));
                      }}
                      initialFboName={currentPrivateFlightLeg?.arrival_fbo_name || ''}
                      className="h-12 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid"
                      placeholder="Select arrival FBO..."
                    />
                  </div>
                </div>

                {/* Catering Section */}
                <div>
                  <Label className="text-sm font-bold text-ui-ink-2 mb-2 block flex items-center gap-2">
                    <UtensilsCrossed className="w-4 h-4 text-ui-violet-fg" />
                    Catering for this Leg
                  </Label>
                  <Textarea
                    value={currentPrivateFlightLeg?.catering || ''}
                    onChange={(e) => setCurrentPrivateFlightLeg(prev => ({...prev, catering: e.target.value}))}
                    placeholder="Enter catering order for this flight leg"
                    className="h-20 text-base border-ui-line focus:border-ui-violet-solid focus:ring-ui-violet-solid resize-none"
                  />
                </div>

                {/* Passengers for this Leg */}
                <div className="p-4 bg-ui-indigo-bg rounded-xl border border-ui-indigo-line">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-sm font-bold text-ui-ink-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-ui-indigo-fg" />
                      Passengers on this Leg
                    </Label>
                    <div className="bg-ui-indigo-strong text-ui-on-primary text-xs font-medium px-2 py-1 rounded-full min-w-5 text-center">
                      {currentPrivateFlightLeg?.leg_passengers?.length || 0}
                    </div>
                  </div>

                  {/* Passenger Search */}
                  <div className="mb-4">
                    <div className="relative">
                      <Input
                        placeholder="Search executives and contacts..."
                        value={passengerSearchQuery}
                        onChange={(e) => {
                          setPassengerSearchQuery(e.target.value);
                          setShowPassengerSuggestions(e.target.value.length > 0);
                        }}
                        onFocus={() => setShowPassengerSuggestions(passengerSearchQuery.length > 0)}
                        className="h-10 text-sm border-ui-line focus:border-ui-indigo-solid focus:ring-ui-indigo-solid pr-10"
                      />
                      <Search className="absolute right-3 top-2.5 w-4 h-4 text-ui-muted" />

                      {showPassengerSuggestions && (
                        <div className="absolute z-50 w-full mt-1 bg-ui-surface border border-ui-line rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {/* Search Executives */}
                          {filteredExecutives.length > 0 && (
                            <>
                              <div className="px-3 py-1.5 bg-ui-violet-bg border-b border-ui-violet-line">
                                <span className="text-xs font-semibold text-ui-violet-fg uppercase tracking-wide">Executives</span>
                              </div>
                              {filteredExecutives.slice(0, 5).map((exec) => (
                                <div
                                  key={`exec-${exec.id}`}
                                  className="p-2 hover:bg-ui-violet-bg cursor-pointer border-b border-ui-divider transition-colors"
                                  onClick={() => handleAddPassengerFromSearch(exec)}
                                >
                                  <div className="flex items-center gap-2">
                                    <Crown className="w-4 h-4 text-ui-violet-fg" />
                                    <span className="text-sm font-medium text-ui-ink">{exec.name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim()}</span>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}

                          {/* Search Contacts */}
                          {filteredContacts.length > 0 && (
                            <>
                              <div className="px-3 py-1.5 bg-ui-sunk border-b border-ui-divider">
                                <span className="text-xs font-semibold text-ui-ink-2 uppercase tracking-wide">Contacts</span>
                              </div>
                              {filteredContacts.slice(0, 5).map((contact) => (
                                <div
                                  key={`contact-${contact.id}`}
                                  className="p-2 hover:bg-ui-hover cursor-pointer border-b border-ui-divider transition-colors"
                                  onClick={() => handleAddPassengerFromSearch(contact)}
                                >
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-ui-muted" />
                                    <span className="text-sm font-medium text-ui-ink">{contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()}</span>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}

                          {/* No Results */}
                          {filteredExecutives.length === 0 && filteredContacts.length === 0 && passengerSearchQuery.trim() && (
                            <div className="p-2 text-center text-ui-muted text-sm">
                              No results found
                            </div>
                          )}

                          {/* Add New Passenger Option */}
                          <div className="border-t border-ui-line">
                            <div
                              className="p-2 hover:bg-ui-green-bg cursor-pointer transition-colors"
                              onClick={handleOpenNewPassengerModal}
                            >
                              <div className="flex items-center gap-2">
                                <Plus className="w-4 h-4 text-ui-green-fg" />
                                <span className="text-sm font-medium text-ui-green-fg">
                                  {passengerSearchQuery.trim() ? `Add "${passengerSearchQuery.trim()}"` : 'Add New Passenger'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Current Passengers List */}
                  {currentPrivateFlightLeg?.leg_passengers?.length > 0 ? (
                    <div className="space-y-2">
                      {currentPrivateFlightLeg.leg_passengers.map((passenger) => (
                        <div
                          key={passenger.tempId || passenger.name}
                          className="flex items-center justify-between p-2 bg-ui-surface rounded-lg border border-ui-indigo-line"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              passenger.traveler_type === 'executive'
                                ? 'bg-ui-violet-bg'
                                : passenger.traveler_type === 'new_contact'
                                  ? 'bg-ui-green-bg'
                                  : 'bg-ui-hover'
                            }`}>
                              {passenger.traveler_type === 'executive' ? (
                                <Crown className="w-4 h-4 text-ui-violet-fg" />
                              ) : passenger.traveler_type === 'new_contact' ? (
                                <Plus className="w-4 h-4 text-ui-green-fg" />
                              ) : (
                                <User className="w-4 h-4 text-ui-muted" />
                              )}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-ui-ink">{passenger.name}</div>
                              {passenger.email && (
                                <div className="text-xs text-ui-muted">{passenger.email}</div>
                              )}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-ui-red-fg hover:text-ui-red-fg hover:bg-ui-red-bg"
                            onClick={() => handleRemovePassenger(passenger.tempId)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-ui-muted text-sm bg-ui-surface rounded-lg border border-dashed border-ui-control">
                      No passengers added to this leg yet
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPrivateFlightLegForm(false);
                    setCurrentPrivateFlightLeg(null);
                    setEditingLegIndex(-1);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-ui-violet-strong hover:bg-ui-violet-strong/90"
                  onClick={() => {
                    if (currentPrivateFlightLeg) {
                      
                      if (editingLegIndex >= 0) {
                        // Edit existing leg
                        setPrivateFlightLegs(prev => prev.map((leg, i) => 
                          i === editingLegIndex ? currentPrivateFlightLeg : leg
                        ));
                      } else {
                        // Add new leg
                        setPrivateFlightLegs(prev => [...prev, currentPrivateFlightLeg]);
                      }
                      
                      setShowPrivateFlightLegForm(false);
                      setCurrentPrivateFlightLeg(null);
                      setEditingLegIndex(-1);
                    }
                  }}
                >
                  {editingLegIndex >= 0 ? 'Update' : 'Add'} Leg
                </Button>
              </DialogFooter>
              </PortalContainerProvider>
            </DialogContent>
          </Dialog>
        )}

        {/* File Management Section */}
        <div className="space-y-6">
          {renderFileManagement("Flight Documents", "Upload Document", "Upload flight plans, contracts, etc.")}
        </div>
      </div>
    );
  };

  // Memo Render Function
  const renderMemoFields = () => {
    return (
      <div className="space-y-6">
        {/* Primary Executive Selection (Optional) — hidden in speaker_bureau mode */}
        {!isSpeakerBureau && (
        <div className="p-4 bg-ui-blue-bg rounded-xl border border-ui-blue-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Users className="w-5 h-5 text-ui-link" />
            Executives (Optional)
          </Label>

          <div>
            <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
              Select the primary executive for this memo
            </Label>
            <select
              value={formData.component_data?.primary_executive_id || ''}
              onChange={(e) => handleDetailsChange('primary_executive_id', e.target.value)}
              className="w-full h-12 px-4 py-3 border border-ui-line rounded-lg focus:border-ui-primary focus:ring-ui-primary focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
            >
              <option value="">Select Primary Executive (Optional)</option>
              {tripExecutives.map(executive => (
                <option key={executive.id} value={executive.id}>
                  {executive.first_name} {executive.last_name}
                  {executive.title && ` - ${executive.title}`}
                </option>
              ))}
            </select>
            {(!tripExecutives || tripExecutives.length === 0) && (
              <p className="text-xs text-ui-orange-fg mt-1">
                No executives available. Please add executives to the trip first.
              </p>
            )}
          </div>
        </div>
        )}

        {/* Additional Attendees with Contact Search */}
        <div className="p-4 bg-ui-sunk rounded-xl border border-ui-line">
          <div className="flex items-center justify-between mb-4">
            <Label className="text-lg font-semibold text-ui-ink flex items-center gap-2">
              <Users className="w-5 h-5 text-ui-ink-3" />
              Additional Attendees (Optional)
            </Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowAttendeeForm(true)}
                className="bg-ui-surface hover:bg-ui-hover border-ui-control"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Contact
              </Button>
            </div>
          </div>

          {/* Contact Search */}
          <div className="mb-4">
            <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
              Add from Directory
            </Label>

            {/* Search Input and Filter */}
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={attendeeSearchQuery}
                  onChange={(e) => setAttendeeSearchQuery(e.target.value)}
                  className="w-full h-12 px-4 pr-10 border border-ui-line rounded-lg focus:border-ui-control focus:ring-ui-control focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
                  placeholder="Search contacts by name..."
                  autoComplete="off"
                />
                <div className="absolute right-3 top-3">
                  <Users className="w-6 h-6 text-ui-muted" />
                </div>
              </div>

              {/* Contact Type Filter */}
              <select
                value={contactFilter}
                onChange={(e) => setContactFilter(e.target.value)}
                className="h-12 px-3 border border-ui-line rounded-lg focus:border-ui-control focus:ring-ui-control bg-ui-surface text-ui-ink"
              >
                <option value="all">All</option>
                <option value="executives">Executives</option>
                <option value="external">External</option>
              </select>
            </div>

            {/* Search Results */}
            {attendeeSearchQuery.length >= 2 && (
              <div className="mt-2 max-h-40 overflow-y-auto border border-ui-line rounded-lg bg-ui-surface">
                {(() => {
                  let combinedContacts = [...allExecutivesList];

                  if (contactFilter === 'executives') {
                    combinedContacts = combinedContacts.filter(c => c.source === 'executive');
                  } else if (contactFilter === 'external') {
                    combinedContacts = combinedContacts.filter(c => c.source === 'external');
                  }

                  const filteredContacts = combinedContacts.filter(contact =>
                    contact.name.toLowerCase().includes(attendeeSearchQuery.toLowerCase()) ||
                    (contact.email && contact.email.toLowerCase().includes(attendeeSearchQuery.toLowerCase()))
                  );

                  if (filteredContacts.length === 0) {
                    return (
                      <div className="p-3 text-ui-muted text-sm text-center">
                        No matching contacts found
                      </div>
                    );
                  }

                  return filteredContacts.map(contact => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => {
                        const newAttendee = {
                          tempId: `contact-${Date.now()}`,
                          name: contact.name,
                          email: contact.email,
                          title: contact.title
                        };
                        setAttendees(prev => [...prev, newAttendee]);
                        setAttendeeSearchQuery('');
                      }}
                      className="w-full text-left p-3 hover:bg-ui-hover border-b border-ui-divider last:border-b-0"
                    >
                      <div>
                        <div className="font-medium text-ui-ink">{contact.name}</div>
                        <div className="text-sm text-ui-muted">{contact.title}</div>
                        {contact.email && (
                          <div className="text-xs text-ui-muted">{contact.email}</div>
                        )}
                      </div>
                    </button>
                  ));
                })()}
              </div>
            )}

            <p className="text-xs text-ui-muted mt-2">
              Search and select from existing executives and contacts, or click "Add New Contact" to create a new attendee
            </p>
          </div>

          {/* Current Attendees */}
          <div className="space-y-3">
            {attendees.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-ui-line rounded-lg">
                <Users className="w-8 h-8 text-ui-faint mx-auto mb-2" />
                <div className="text-ui-muted text-sm">No attendees added yet</div>
                <div className="text-ui-muted text-xs">Search for existing contacts or add new attendees</div>
              </div>
            ) : (
              attendees.map((attendee, index) => (
                <div key={attendee.tempId || index} className="flex flex-col gap-2 p-3 bg-ui-surface border border-ui-line rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-ui-ink">{attendee.name}</div>
                      {attendee.email && <div className="text-sm text-ui-muted">{attendee.email}</div>}
                      {attendee.title && <div className="text-xs text-ui-muted">{attendee.title}</div>}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditAttendee(attendee)}
                        className="text-ui-link hover:text-ui-link text-sm"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAttendee(attendee.tempId)}
                        className="text-ui-red-fg hover:text-ui-red-fg text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  {/* speaker_bureau: optional link to a saved Speaker */}
                  {isSpeakerBureau && (
                    <SpeakerLinkField
                      speakerId={attendee.speaker_id || null}
                      speakerName={attendee.speaker_name || attendee.name || ''}
                      onPick={(sp) => handleLinkAttendeeSpeaker(attendee.tempId, sp)}
                    />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Show attendee form modal */}
          {showAttendeeForm && (
            <AttendeeFormModal
              attendee={editingAttendee}
              onSave={handleAddAttendee}
              onCancel={() => {
                setShowAttendeeForm(false);
                setEditingAttendee(null);
              }}
              contacts={allContacts}
            />
          )}
        </div>

        {/* File Management */}
        {renderFileManagement("Memo Attachments", "Upload Document", "Upload any related documents or files.")}
      </div>
    );
  };

  // Airport VIP Service Render Function
  const renderAirportVipServiceFields = () => {
    const cd = formData.component_data || {};
    const segments = tripFlightSegments || [];
    const noSegments = !tripFlightSegmentsLoading && segments.length === 0;

    return (
      <div className="space-y-6">
        {/* Linked Flight Segment */}
        <div className="p-4 bg-ui-amber-bg rounded-xl border border-ui-amber-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Plane className="w-5 h-5 text-ui-amber-fg" />
            Linked Flight Segment
          </Label>

          {tripFlightSegmentsLoading ? (
            <div className="text-sm text-ui-muted">Loading flight segments…</div>
          ) : noSegments ? (
            <div className="text-sm text-ui-orange-fg bg-ui-orange-bg border border-ui-orange-line rounded-md p-3">
              No commercial or private flight segments found on this trip yet. Add a flight component first, then attach a VIP service to it.
            </div>
          ) : (
            <>
              <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
                Flight Segment <span className="text-ui-red-fg">*</span>
              </Label>
              <select
                value={cd.linked_segment_id || ''}
                onChange={(e) => handleDetailsChange('linked_segment_id', e.target.value)}
                className="w-full h-12 px-4 py-3 border border-ui-line rounded-lg focus:border-ui-amber-solid focus:ring-ui-amber-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
              >
                <option value="">Select the flight this VIP service is for…</option>
                {segments.map((s) => (
                  <option key={s.segment_id} value={s.segment_id}>
                    {s.component_type === 'private_flight' ? 'Private · ' : 'Commercial · '}
                    {s.label}
                  </option>
                ))}
              </select>

              <div className="mt-4">
                <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
                  Service Direction <span className="text-ui-red-fg">*</span>
                </Label>
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                    cd.service_direction === 'arrival'
                      ? 'border-ui-amber-solid bg-ui-amber-bg text-ui-amber-fg'
                      : 'border-ui-line bg-ui-surface hover:bg-ui-hover'
                  }`}>
                    <input
                      type="radio"
                      name="vip_service_direction"
                      value="arrival"
                      checked={cd.service_direction === 'arrival'}
                      onChange={() => handleDetailsChange('service_direction', 'arrival')}
                    />
                    <PlaneLanding className="w-4 h-4" />
                    <span>Arrival (meet on landing)</span>
                  </label>
                  <label className={`flex-1 flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                    cd.service_direction === 'departure'
                      ? 'border-ui-amber-solid bg-ui-amber-bg text-ui-amber-fg'
                      : 'border-ui-line bg-ui-surface hover:bg-ui-hover'
                  }`}>
                    <input
                      type="radio"
                      name="vip_service_direction"
                      value="departure"
                      checked={cd.service_direction === 'departure'}
                      onChange={() => handleDetailsChange('service_direction', 'departure')}
                    />
                    <PlaneTakeoff className="w-4 h-4" />
                    <span>Departure (meet at airport)</span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Service Provider */}
        <div className="bg-ui-surface p-6 rounded-2xl border border-ui-line shadow-sm">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-ui-amber-fg" />
            VIP Service Provider
          </Label>

          <div className="space-y-4">
            <VipServiceProviderAutocomplete
              value={cd.provider_name || ''}
              onChange={(v) => handleDetailsChange('provider_name', v)}
              selectedProvider={vipSelectedProvider}
              onProviderSelect={(provider) => {
                setVipSelectedProvider(provider);
                if (provider) {
                  // Auto-fill the snapshot fields from the saved profile
                  setFormData(prev => ({
                    ...prev,
                    component_data: {
                      ...prev.component_data,
                      provider_id: provider.id,
                      provider_name: provider.name || '',
                      provider_website: provider.website || '',
                      provider_phone: provider.phone || '',
                      provider_email: provider.email || '',
                    },
                  }));
                } else {
                  // Cleared link — drop provider_id but keep whatever the user typed
                  setFormData(prev => ({
                    ...prev,
                    component_data: { ...prev.component_data, provider_id: null },
                  }));
                }
              }}
              onEditClick={(provider) => setVipEditingProvider(provider)}
              required
            />
            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                id="vip_provider_website"
                label="Provider Website"
                value={cd.provider_website || ''}
                onChange={(v) => handleDetailsChange('provider_website', v)}
                placeholder="e.g. laufer-vip.com"
              />
              <FormField
                id="vip_provider_phone"
                label="Provider 24/7 Dispatch Phone"
                value={cd.provider_phone || ''}
                onChange={(v) => handleDetailsChange('provider_phone', v)}
                placeholder="Backup contact if greeter is unreachable"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                id="vip_provider_email"
                label="Provider Email"
                type="email"
                value={cd.provider_email || ''}
                onChange={(v) => handleDetailsChange('provider_email', v)}
                placeholder="ops@example.com"
              />
              <FormField
                id="vip_confirmation_number"
                label="Booking / Confirmation Number"
                value={cd.confirmation_number || formData.confirmation_number || ''}
                onChange={(v) => {
                  handleDetailsChange('confirmation_number', v);
                  handleInputChange('confirmation_number', v);
                }}
                placeholder="Provider booking reference"
              />
            </div>
            {!vipSelectedProvider && (cd.provider_name || '').trim() && (
              <div className="text-xs text-ui-muted flex items-center gap-2">
                <span>Don&apos;t want to wait for submit? </span>
                <button
                  type="button"
                  onClick={() => setVipEditingProvider({
                    name: cd.provider_name,
                    website: cd.provider_website,
                    phone: cd.provider_phone,
                    email: cd.provider_email,
                  })}
                  className="underline text-ui-amber-fg hover:text-ui-amber-fg"
                >
                  Save &quot;{cd.provider_name}&quot; as a provider profile now
                </button>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">Service Tier</Label>
                <select
                  value={cd.service_tier || ''}
                  onChange={(e) => handleDetailsChange('service_tier', e.target.value)}
                  className="w-full h-12 px-4 py-3 border border-ui-line rounded-lg bg-ui-surface text-ui-ink"
                >
                  <option value="">— Select tier (optional) —</option>
                  <option value="standard">Standard / CIP</option>
                  <option value="vip">VIP</option>
                  <option value="vvip">VVIP</option>
                </select>
              </div>
              <FormField
                id="vip_passenger_count"
                label="Number of Passengers"
                type="number"
                min="1"
                value={cd.passenger_count || ''}
                onChange={(v) => handleDetailsChange('passenger_count', v)}
                placeholder="How many people the greeter will assist"
              />
            </div>
          </div>
        </div>

        {/* Greeter */}
        <div className="bg-ui-surface p-6 rounded-2xl border border-ui-line shadow-sm">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <User className="w-5 h-5 text-ui-amber-fg" />
            Greeter
          </Label>

          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                id="vip_greeter_name"
                label="Greeter Name"
                value={cd.greeter_name || ''}
                onChange={(v) => handleDetailsChange('greeter_name', v)}
                placeholder="Name of the assigned greeter"
              />
              <FormField
                id="vip_greeter_phone"
                label="Greeter Phone"
                value={cd.greeter_phone || ''}
                onChange={(v) => handleDetailsChange('greeter_phone', v)}
                placeholder="Direct mobile of the greeter"
              />
            </div>
            <FormField
              id="vip_greeter_languages"
              label="Greeter Languages (optional)"
              value={cd.greeter_languages || ''}
              onChange={(v) => handleDetailsChange('greeter_languages', v)}
              placeholder="e.g. English, Hebrew, French"
            />
          </div>
        </div>

        {/* Meeting Point */}
        <div className="bg-ui-surface p-6 rounded-2xl border border-ui-line shadow-sm">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <MapPin className="w-5 h-5 text-ui-amber-fg" />
            Meeting Point
          </Label>

          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                id="vip_airport_code"
                label="Airport"
                value={cd.airport_code || ''}
                onChange={(v) => handleDetailsChange('airport_code', String(v).toUpperCase())}
                placeholder="Auto-filled from segment (e.g. JFK)"
              />
              <FormField
                id="vip_terminal"
                label="Terminal (optional)"
                value={cd.terminal || ''}
                onChange={(v) => handleDetailsChange('terminal', v)}
                placeholder="e.g. Terminal 4"
              />
            </div>
            <FormField
              id="vip_greet_location"
              label="Greet Location"
              value={cd.greet_location || ''}
              onChange={(v) => handleDetailsChange('greet_location', v)}
              placeholder="e.g. Jet bridge, immigration kiosk, departures curb at door 3"
            />
            <FormField
              id="vip_name_board"
              label="Name Board Sign"
              value={cd.name_board_sign || ''}
              onChange={(v) => handleDetailsChange('name_board_sign', v)}
              placeholder="Text printed on the greeter's sign (e.g. MR. SMITH)"
            />
          </div>
        </div>

        {/* Included Services */}
        <div className="bg-ui-surface p-6 rounded-2xl border border-ui-line shadow-sm">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Settings className="w-5 h-5 text-ui-amber-fg" />
            Included Services (optional)
          </Label>

          <div className="grid md:grid-cols-2 gap-3">
            {[
              { key: 'fast_track_immigration', label: 'Fast-track immigration / passport control' },
              { key: 'fast_track_security', label: 'Fast-track security' },
              { key: 'fast_track_customs', label: 'Customs assistance' },
              { key: 'porter_service', label: 'Porter / baggage handling' },
              { key: 'lounge_access', label: 'VIP lounge access' },
              { key: 'buggy_transfer', label: 'Buggy / cart transfer in terminal' },
              { key: 'ground_transport_coordinated', label: 'Coordinated with ground transport' },
              { key: 'escort_to_aircraft', label: 'Escort to aircraft / from aircraft' },
            ].map(opt => (
              <label key={opt.key} className="flex items-center gap-2 px-3 py-2 border border-ui-line rounded-lg bg-ui-surface hover:bg-ui-hover cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!cd[opt.key]}
                  onChange={(e) => handleDetailsChange(opt.key, e.target.checked)}
                />
                <span className="text-sm text-ui-ink-2">{opt.label}</span>
              </label>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <FormField
              id="vip_lounge_name"
              label="Lounge Name (if applicable)"
              value={cd.lounge_name || ''}
              onChange={(v) => handleDetailsChange('lounge_name', v)}
              placeholder="e.g. The Wing"
            />
            <FormField
              id="vip_baggage_count"
              label="Bags handled (optional)"
              type="number"
              min="0"
              value={cd.baggage_count || ''}
              onChange={(v) => handleDetailsChange('baggage_count', v)}
              placeholder="Number of bags"
            />
          </div>
        </div>

        {/* Special Instructions */}
        <div className="bg-ui-surface p-6 rounded-2xl border border-ui-line shadow-sm">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Info className="w-5 h-5 text-ui-amber-fg" />
            Special Instructions
          </Label>
          <Textarea
            value={cd.special_instructions || ''}
            onChange={(e) => handleDetailsChange('special_instructions', e.target.value)}
            placeholder="e.g. Discretion required, wheelchair assistance, paparazzi avoidance, traveling with infant…"
            rows={3}
          />
        </div>

        {/* File Management */}
        {renderFileManagement("VIP Service Documents", "Upload Document", "Upload provider confirmation, voucher, or related documents.")}
      </div>
    );
  };

  // Event Render Function
  const renderEventFields = () => {
    return (
      <div className="space-y-6">
        {/* Event Details */}
        <div className="p-4 bg-ui-violet-bg rounded-xl border border-ui-violet-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-ui-violet-fg" />
            Event Details
          </Label>

          <div className="space-y-4">
            {/* Event Title */}
            <div>
              <Label htmlFor="event_title" className="text-sm font-medium text-ui-ink-2 mb-2 block">
                Event Title *
              </Label>
              <input
                id="event_title"
                type="text"
                value={formData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full h-12 px-4 border border-ui-line rounded-lg focus:border-ui-violet-solid focus:ring-ui-violet-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
                placeholder="Enter event title (e.g., Annual Gala, Conference, Workshop)"
                required
              />
            </div>

            {/* Event Type */}
            <div>
              <Label htmlFor="event_type" className="text-sm font-medium text-ui-ink-2 mb-2 block">
                Event Type (Optional)
              </Label>
              <select
                id="event_type"
                value={formData.component_data?.event_type || ''}
                onChange={(e) => handleDetailsChange('event_type', e.target.value)}
                className="w-full h-12 px-4 border border-ui-line rounded-lg focus:border-ui-violet-solid focus:ring-ui-violet-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
              >
                <option value="">Select event type</option>
                <option value="conference">Conference</option>
                <option value="gala">Gala</option>
                <option value="workshop">Workshop</option>
                <option value="seminar">Seminar</option>
                <option value="networking">Networking Event</option>
                <option value="celebration">Celebration</option>
                <option value="fundraiser">Fundraiser</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Primary Executive Selection — hidden in speaker_bureau mode */}
        {!isSpeakerBureau && (
        <div className="p-4 bg-ui-blue-bg rounded-xl border border-ui-blue-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <Users className="w-5 h-5 text-ui-link" />
            Primary Executive (Optional)
          </Label>

          <div>
            <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
              Select the main executive for this event
            </Label>
            <select
              value={formData.component_data?.primary_executive_id || ''}
              onChange={(e) => handleDetailsChange('primary_executive_id', e.target.value)}
              className="w-full h-12 px-4 py-3 border border-ui-line rounded-lg focus:border-ui-primary focus:ring-ui-primary focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
            >
              <option value="">Select Primary Executive</option>
              {tripExecutives.map(executive => (
                <option key={executive.id} value={executive.id}>
                  {executive.first_name} {executive.last_name}
                  {executive.title && ` - ${executive.title}`}
                </option>
              ))}
            </select>
            {(!tripExecutives || tripExecutives.length === 0) && (
              <p className="text-xs text-ui-orange-fg mt-1">
                ⚠️ No executives available. Please add executives to the trip first.
              </p>
            )}
          </div>
        </div>
        )}

        {/* Additional Attendees with Contact Search */}
        <div className="p-4 bg-ui-sunk rounded-xl border border-ui-line">
          <div className="flex items-center justify-between mb-4">
            <Label className="text-lg font-semibold text-ui-ink flex items-center gap-2">
              <Users className="w-5 h-5 text-ui-ink-3" />
              Additional Attendees (Optional)
            </Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowAttendeeForm(true)}
                className="bg-ui-surface hover:bg-ui-hover border-ui-control"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Contact
              </Button>
            </div>
          </div>
          
          {/* Contact Search */}
          <div className="mb-4">
            <Label className="text-sm font-medium text-ui-ink-2 mb-2 block">
              Add from Directory
            </Label>
            
            {/* Search Input and Filter */}
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={attendeeSearchQuery}
                  onChange={(e) => setAttendeeSearchQuery(e.target.value)}
                  className="w-full h-12 px-4 pr-10 border border-ui-line rounded-lg focus:border-ui-control focus:ring-ui-control focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
                  placeholder="Search contacts by name..."
                  autoComplete="off"
                />
                <div className="absolute right-3 top-3">
                  <Users className="w-6 h-6 text-ui-muted" />
                </div>
              </div>
              
              {/* Contact Type Filter */}
              <select
                value={contactFilter}
                onChange={(e) => setContactFilter(e.target.value)}
                className="h-12 px-3 border border-ui-line rounded-lg focus:border-ui-control focus:ring-ui-control bg-ui-surface text-ui-ink"
              >
                <option value="all">All</option>
                <option value="executives">Executives</option>
                <option value="external">External</option>
              </select>
            </div>
            
            {/* Search Results */}
            {attendeeSearchQuery.length >= 2 && (
              <div className="mt-2 max-h-40 overflow-y-auto border border-ui-line rounded-lg bg-ui-surface">
                {(() => {
                  // Use the unified contact list (executives + external contacts)
                  let combinedContacts = [...allExecutivesList];  // allExecutivesList now contains combined list
                  
                  // Apply contact filter
                  if (contactFilter === 'executives') {
                    combinedContacts = combinedContacts.filter(c => c.source === 'executive');
                  } else if (contactFilter === 'external') {
                    combinedContacts = combinedContacts.filter(c => c.source === 'external');
                  }
                  
                  const filteredContacts = combinedContacts.filter(contact =>
                    contact.name.toLowerCase().includes(attendeeSearchQuery.toLowerCase()) ||
                    (contact.email && contact.email.toLowerCase().includes(attendeeSearchQuery.toLowerCase()))
                  );
                  
                  
                  if (filteredContacts.length === 0) {
                    return (
                      <div className="p-3 text-ui-muted text-sm text-center">
                        No matching contacts found
                      </div>
                    );
                  }
                  
                  return filteredContacts.map(contact => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => {
                        // Add contact as attendee
                        const newAttendee = {
                          tempId: `contact-${Date.now()}`,
                          name: contact.name,
                          email: contact.email,
                          title: contact.title
                        };
                        setAttendees(prev => [...prev, newAttendee]);
                        setAttendeeSearchQuery('');
                      }}
                      className="w-full text-left p-3 hover:bg-ui-hover border-b border-ui-divider last:border-b-0"
                    >
                      <div>
                        <div className="font-medium text-ui-ink">{contact.name}</div>
                        <div className="text-sm text-ui-muted">{contact.title}</div>
                        {contact.email && (
                          <div className="text-xs text-ui-muted">{contact.email}</div>
                        )}
                      </div>
                    </button>
                  ));
                })()}
              </div>
            )}
            
            <p className="text-xs text-ui-muted mt-2">
              Search and select from existing executives and contacts, or click "Add New Contact" to create a new attendee
            </p>
          </div>
          
          {/* Current Attendees */}
          <div className="space-y-3">
            {attendees.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-ui-line rounded-lg">
                <Users className="w-8 h-8 text-ui-faint mx-auto mb-2" />
                <div className="text-ui-muted text-sm">No attendees added yet</div>
                <div className="text-ui-muted text-xs">Search for existing contacts or add new attendees</div>
              </div>
            ) : (
              attendees.map((attendee, index) => (
                <div key={attendee.tempId || index} className="flex flex-col gap-2 p-3 bg-ui-surface border border-ui-line rounded-lg">
                  <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-ui-ink">{attendee.name}</div>
                    {attendee.email && <div className="text-sm text-ui-muted">{attendee.email}</div>}
                    {attendee.title && <div className="text-xs text-ui-muted">{attendee.title}</div>}
                    {attendee.type && (
                      <div className="text-xs bg-ui-hover text-ui-ink-3 px-2 py-1 rounded capitalize inline-block mt-1">
                        {attendee.type}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleEditAttendee(attendee)}
                      className="text-ui-link hover:text-ui-link text-sm"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteAttendee(attendee.tempId)}
                      className="text-ui-red-fg hover:text-ui-red-fg text-sm"
                    >
                      Remove
                    </button>
                  </div>
                  </div>
                  {/* speaker_bureau: optional link to a saved Speaker */}
                  {isSpeakerBureau && (
                    <SpeakerLinkField
                      speakerId={attendee.speaker_id || null}
                      speakerName={attendee.speaker_name || attendee.name || ''}
                      onPick={(sp) => handleLinkAttendeeSpeaker(attendee.tempId, sp)}
                    />
                  )}
                </div>
              ))
            )}
          </div>

        {/* Show attendee form modal */}
        {showAttendeeForm && (
          <AttendeeFormModal
            attendee={editingAttendee}
            onSave={handleAddAttendee}
            onCancel={() => {
              setShowAttendeeForm(false);
              setEditingAttendee(null);
            }}
            contacts={allContacts}
          />
        )}
        </div>

        {/* Event Date & Time */}
        <div className="p-4 bg-ui-teal-bg rounded-xl border border-ui-teal-line">
          <Label className="text-lg font-semibold text-ui-ink mb-4 block flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-ui-teal-fg" />
            Event Date & Time
          </Label>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Event Date */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="event_date" className="text-sm font-medium text-ui-ink-2">
                  Event Date
                </Label>
                <Button
                  type="button"
                  variant={isDateTBD ? "default" : "outline"}
                  size="sm"
                  onClick={handleDateTBDToggle}
                  className={`text-xs h-7 px-2 ${isDateTBD ? 'bg-ui-amber-strong hover:bg-ui-amber-strong/90 text-ui-on-primary' : 'text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-bg'}`}
                >
                  {isDateTBD ? '✓ TBD' : 'TBD'}
                </Button>
              </div>
              
              {isDateTBD ? (
                <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                  <span className="text-ui-amber-fg font-medium">📅 Date To Be Determined</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    id="event_date"
                    type="date"
                    value={formData.start_datetime ? (() => {
                      try {
                        if (formData.start_datetime === 'TBD') return '';
                        return format(new Date(formData.start_datetime), 'yyyy-MM-dd');
                      } catch (error) {
                        return '';
                      }
                    })() : ''}
                    onChange={(e) => handleEventDateChange(e.target.value)}
                    className="w-full h-12 px-4 border border-ui-line rounded-lg focus:border-ui-teal-solid focus:ring-ui-teal-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink"
                  />
                  <p className="text-xs text-ui-muted">
                    Select event date (format will match your profile preferences)
                  </p>
                </div>
              )}
            </div>

            {/* Event Time */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="event_time" className="text-sm font-medium text-ui-ink-2">
                  Event Time
                </Label>
                <Button
                  type="button"
                  variant={isStartTimeTBD ? "default" : "outline"}
                  size="sm"
                  onClick={handleStartTimeTBDToggle}
                  className={`text-xs h-7 px-2 ${isStartTimeTBD ? 'bg-ui-amber-strong hover:bg-ui-amber-strong/90 text-ui-on-primary' : 'text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-bg'}`}
                >
                  {isStartTimeTBD ? '✓ TBD' : 'TBD'}
                </Button>
              </div>

              {isStartTimeTBD ? (
                <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                  <span className="text-ui-amber-fg font-medium">🕐 Time To Be Determined</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <select
                    id="event_start_time"
                    value={formData.start_datetime ? (() => {
                      try {
                        if (formData.start_datetime === 'TBD') return '';
                        const date = new Date(formData.start_datetime);
                        return format(date, 'HH:mm');
                      } catch (error) {
                        return '';
                      }
                    })() : ''}
                    onChange={(e) => handleEventStartTimeChange(e.target.value)}
                    onFocus={handleEventStartTimeFocus}
                    disabled={!formData.start_datetime || isDateTBD}
                    className="w-full h-12 px-4 border border-ui-line rounded-lg focus:border-ui-teal-solid focus:ring-ui-teal-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink disabled:bg-ui-hover disabled:cursor-not-allowed"
                  >
                    <option value="">Select start time</option>
                    {eventTimeOptions.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-ui-muted">
                    Select event start time in 15-minute increments
                  </p>
                </div>
              )}
            </div>

            {/* Event End Time */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="event_end_time" className="text-sm font-medium text-ui-ink-2">
                  Event End Time
                </Label>
              </div>

              {isStartTimeTBD ? (
                <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                  <span className="text-ui-amber-fg font-medium">🕐 Time To Be Determined</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <select
                    id="event_end_time"
                    value={formData.end_datetime ? (() => {
                      try {
                        if (formData.end_datetime === 'TBD') return '';
                        const date = new Date(formData.end_datetime);
                        return format(date, 'HH:mm');
                      } catch (error) {
                        return '';
                      }
                    })() : ''}
                    onChange={(e) => handleEventEndTimeChange(e.target.value)}
                    onFocus={handleEventEndTimeFocus}
                    disabled={!formData.start_datetime || isDateTBD}
                    className="w-full h-12 px-4 border border-ui-line rounded-lg focus:border-ui-teal-solid focus:ring-ui-teal-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink disabled:bg-ui-hover disabled:cursor-not-allowed"
                  >
                    <option value="">Select end time</option>
                    {eventTimeOptions.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-ui-muted">
                    Select event end time in 15-minute increments
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Location with Google Places Autocomplete */}
          <div className="mt-4">
            <Label htmlFor="event_location_places" className="text-sm font-medium text-ui-ink-2 mb-2 block flex items-center gap-2">
              <MapPin className="w-4 h-4 text-ui-teal-fg" />
              Event Location *
            </Label>
            <VenueAutocomplete
              placeholder="Search for event venue, address, or point of interest..."
              value={formData.location || ''}
              onChange={handleLocationChange}
              onPlaceSelect={handleEventLocationSelect}
              className="w-full max-w-full h-12 text-base border border-ui-line rounded-lg focus:border-ui-teal-solid focus:ring-ui-teal-solid focus:ring-2 focus:ring-opacity-20 transition-all bg-ui-surface text-ui-ink px-4"
            />
            <p className="text-xs text-ui-muted mt-1">
              Search by venue name, address, or point of interest
            </p>
          </div>
        </div>

        {/* File Management */}
        {renderFileManagement("Event Documents", "Upload Document", "Upload event agenda, schedules, brochures, etc.")}
      </div>
    );
  };

  const renderComponentSpecificFields = () => {
    // Use the actual component type from formData in edit mode, or componentType prop in add mode
    const actualComponentType = component ? (formData.component_type || component.component_type) : componentType;
    
    
    switch (actualComponentType) {
      case 'hotel':
        return renderHotelFields();
        
      case 'meeting':
        return renderMeetingFields();

      case 'speaker_meeting':
        return renderSpeakerMeetingFields();

      case 'restaurant':
        return renderRestaurantFields();

      case 'rental_car':
        return renderRentalCarFields();

      case 'chauffeur_service':
        return renderChauffeurServiceFields();

      case 'commercial_flight':
      case 'flight':
        return renderCommercialFlightFields();

      case 'private_flight':
        return renderPrivateFlightFields();

      case 'event':
        return renderEventFields();

      case 'memo':
        return renderMemoFields();

      case 'airport_vip_service':
        return renderAirportVipServiceFields();

      default:
        return (
          <div className="p-4 text-center text-ui-muted bg-ui-sunk rounded-lg">
            <p>Please select a component type to continue.</p>
            <p className="text-sm mt-1">Detected type: "{actualComponentType}"</p>
          </div>
        );
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Form Header */}
      <div className={`p-6 mb-6 rounded-2xl border-2 ${currentConfig.borderColor} bg-gradient-to-r ${currentConfig.gradient} relative overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-8 gap-4 h-full">
            {Array.from({ length: 32 }).map((_, i) => (
              <div key={i} className={`${currentConfig.color} rounded-full w-2 h-2`} />
            ))}
          </div>
        </div>
        
        <div className="relative z-10 flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl ${currentConfig.color} flex items-center justify-center shadow-lg`}>
            <Icon className="w-6 h-6 text-ui-on-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-ui-ink">
              Add {currentConfig.title}
            </h2>
            <p className="text-ui-ink-3 mt-1">
              {currentConfig.description}
            </p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-ui-red-bg border border-ui-red-line rounded-xl">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-ui-red-fg" />
            <p className="text-sm text-ui-red-fg">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {componentType === 'commercial_flight' && component?.id && tripId && (
          <div className="p-3 border rounded-lg bg-ui-blue-bg">
            <p className="text-sm">Was this updated itinerary added as a separate booking? Reconcile the saved itinerary with the existing booking. Unsaved form edits are not included.</p>
            <Button type="button" variant="outline" onClick={async () => {
              try {
                await fileUnfilledComponent(component.id, { tripId, reconcileComponentId: component.id });
                window.location.reload();
              } catch (error) {
                if (error.message !== 'Assignment cancelled') window.alert(error.response?.data?.detail || error.message);
              }
            }}>Reconcile with existing booking</Button>
          </div>
        )}
        {!!component?.booking_history?.length && <details className="p-3 border rounded-lg">
          <summary className="cursor-pointer font-medium">Booking revision history ({component.booking_history.length})</summary>
          <p className="text-sm">Previous itineraries are retained below. Original and updated source documents remain in Attachments.</p>
          {component.booking_history.map((revision, i) => <div key={i} className="mt-3 border-t pt-2 text-sm">
            <strong>Revision {revision.booking_revision || i + 1}</strong>
            <p>Replaced: {revision.superseded_at || 'Date unavailable'}</p>
            {(revision.component_data?.flight_legs || revision.component_data?.legs || []).map((leg, n) =>
              <p key={n}>{leg.departure_airport_code || leg.departure_airport} → {leg.arrival_airport_code || leg.arrival_airport}: {leg.departure_datetime || leg.departure_date}</p>)}
          </div>)}
        </details>}
        {/* Edit Mode Indicator */}
        {component?.id && (
          <div className="p-3 bg-ui-blue-bg border-l-4 border-ui-primary rounded-r-lg">
            <div className="flex items-center gap-2">
              {(() => {
                const actualComponentType = formData.component_type;
                switch (actualComponentType) {
                  case 'rental_car':
                    return <Car className="w-4 h-4 text-ui-link" />;
                  case 'chauffeur_service':
                    return <Car className="w-4 h-4 text-ui-link" />;
                  case 'hotel':
                    return <Building className="w-4 h-4 text-ui-link" />;
                  case 'meeting':
                    return <Handshake className="w-4 h-4 text-ui-link" />;
                  case 'restaurant':
                    return <UtensilsCrossed className="w-4 h-4 text-ui-link" />;
                  case 'commercial_flight':
                  case 'flight':
                  case 'private_flight':
                  default:
                    return <Plane className="w-4 h-4 text-ui-link" />;
                }
              })()}
              <span className="text-sm font-medium text-ui-blue-fg">
                {(() => {
                  const actualComponentType = formData.component_type;
                  switch (actualComponentType) {
                    case 'rental_car':
                      return 'Edit Rental Car';
                    case 'chauffeur_service':
                      return 'Edit Chauffeur Service';
                    case 'hotel':
                      return 'Edit Hotel';
                    case 'meeting':
                      return 'Edit Meeting';
                    case 'restaurant':
                      return 'Edit Restaurant';
                    case 'commercial_flight':
                    case 'flight':
                      return 'Edit Commercial Flight';
                    case 'private_flight':
                      return 'Edit Private Flight';
                    default:
                      return 'Edit Component';
                  }
                })()}
              </span>
            </div>
          </div>
        )}

        {/* Success Message */}
        {updateStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 bg-ui-green-bg border border-ui-green-line rounded-lg"
          >
            <div className="flex items-center gap-2 text-ui-green-fg">
              <CheckCircle className="w-4 h-4" />
              <span className="font-medium">{currentConfig.title} updated successfully!</span>
            </div>
          </motion.div>
        )}

        {/* Validation Error Display */}
        {validationError && (
          <div className="text-sm text-ui-red-fg bg-ui-red-bg p-3 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Update Error</div>
              <div>{validationError}</div>
            </div>
          </div>
        )}
        {/* Basic Information */}
        <div className="bg-ui-surface p-6 rounded-2xl border border-ui-line shadow-sm">
          <h3 className="text-lg font-semibold text-ui-ink mb-4">Basic Information</h3>
          <div className="space-y-4">
            {/* Show basic fields for all component types except flights, meetings, restaurants, rental cars, hotels, memos, and chauffeur services */}
            {/* Hide Title, Start/End DateTime, Location for flight components, meetings, restaurants, rental cars, hotels, memos, and chauffeur services (they have their own sections) */}
            {(() => {
              const actualComponentType = component ? formData.component_type : componentType;
              return (actualComponentType !== 'commercial_flight' && actualComponentType !== 'flight' && actualComponentType !== 'private_flight' && actualComponentType !== 'meeting' && actualComponentType !== 'speaker_meeting' && actualComponentType !== 'restaurant' && actualComponentType !== 'rental_car' && actualComponentType !== 'hotel' && actualComponentType !== 'event' && actualComponentType !== 'chauffeur_service' && actualComponentType !== 'memo' && actualComponentType !== 'airport_vip_service');
            })() && (
              <>
                <FormField
                  id="title"
                  label="Title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder={`Enter ${currentConfig.title.toLowerCase()} title...`}
                  required
                />
                
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    id="start_datetime"
                    label="Start Date & Time"
                    type="datetime-local"
                    value={formData.start_datetime}
                    onChange={handleStartDateTimeChange}
                    required
                  />
                  <FormField
                    id="end_datetime"
                    label="End Date & Time"
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={handleEndDateTimeChange}
                  />
                </div>

                {/* Hide location field for private flights */}
                {componentType !== 'private_flight' && (
                  <FormField
                    id="location"
                    label="Location"
                    value={formData.location}
                    onChange={handleLocationChange}
                    placeholder={`Enter ${currentConfig.title.toLowerCase()} location...`}
                  />
                )}
              </>
            )}

            {/* Show only title for meetings - schedule and location are handled in dedicated sections */}
            {componentType === 'meeting' && (
              <FormField
                id="title"
                label="Meeting Title"
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="Enter meeting title..."
                required
              />
            )}

            {/* Show only title and date/time for memos */}
            {componentType === 'memo' && (
              <>
                <FormField
                  id="title"
                  label="Memo Title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder="Enter memo title (e.g., Save time for call with Bob)..."
                  required
                />

                {/* Memo Date/Time Fields */}
                <div className="space-y-4 mt-4">
                  {/* Start Date & Start Time Row */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Start Date Field */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="memo_start_date" className="text-sm font-bold text-ui-ink-2 flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 text-ui-amber-fg" />
                          Start Date
                        </Label>
                        <Button
                          type="button"
                          variant={isDateTBD ? "default" : "outline"}
                          size="sm"
                          onClick={handleDateTBDToggle}
                          className={`text-xs h-7 px-2 ${isDateTBD ? 'bg-ui-amber-strong hover:bg-ui-amber-strong/90 text-ui-on-primary' : 'text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-bg'}`}
                        >
                          {isDateTBD ? '✓ TBD' : 'TBD'}
                        </Button>
                      </div>

                      {isDateTBD ? (
                        <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                          <span className="text-ui-amber-fg font-medium">Date To Be Determined</span>
                        </div>
                      ) : (
                        <DatePickerButton
                          label={(() => {
                            if (!formData.start_datetime) return 'Select start date';
                            try {
                              const base = formData.start_datetime.includes('-TBD-TIME')
                                ? formData.start_datetime.split('-TBD-TIME')[0]
                                : formData.start_datetime.split('T')[0];
                              return format(parseLocalDate(base), 'EEE, MMM d, yyyy');
                            } catch { return 'Select start date'; }
                          })()}
                          selectedDate={(() => {
                            if (!formData.start_datetime) return undefined;
                            try {
                              const base = formData.start_datetime.includes('-TBD-TIME')
                                ? formData.start_datetime.split('-TBD-TIME')[0]
                                : formData.start_datetime.split('T')[0];
                              return parseLocalDate(base);
                            } catch { return undefined; }
                          })()}
                          onPick={(date) => {
                            const iso = date ? format(date, 'yyyy-MM-dd') : '';
                            if (iso) {
                              handleMeetingDateChange(iso);
                              if (!formData.end_datetime) {
                                const endDateTime = `${iso}T12:00`;
                                handleInputChange('end_datetime', endDateTime);
                              }
                            }
                          }}
                        />
                      )}

                      <p className="text-xs text-ui-muted mt-1">
                        {isDateTBD ? 'Start date will be determined later' : 'Select start date or set to TBD'}
                      </p>
                    </div>

                    {/* Start Time Field */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="memo_start_time" className="text-sm font-bold text-ui-ink-2 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-ui-amber-fg" />
                          Start Time
                        </Label>
                        <Button
                          type="button"
                          variant={isStartTimeTBD ? "default" : "outline"}
                          size="sm"
                          onClick={handleStartTimeTBDToggle}
                          className={`text-xs h-7 px-2 ${isStartTimeTBD ? 'bg-ui-amber-strong hover:bg-ui-amber-strong/90 text-ui-on-primary' : 'text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-bg'}`}
                        >
                          {isStartTimeTBD ? '✓ TBD' : 'TBD'}
                        </Button>
                      </div>

                      {isStartTimeTBD ? (
                        <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                          <span className="text-ui-amber-fg font-medium">Time To Be Determined</span>
                        </div>
                      ) : (
                        <select
                          id="memo_start_time"
                          value={formData.start_datetime ? (() => {
                            try {
                              if (formData.start_datetime.includes('TBD')) return '';
                              return format(new Date(formData.start_datetime), 'HH:mm');
                            } catch (error) {
                              return '';
                            }
                          })() : ''}
                          onChange={(e) => handleMeetingTimeChange(e.target.value)}
                          className="h-12 w-full text-base border border-ui-line rounded-md px-3 focus:border-ui-amber-solid focus:ring-ui-amber-solid focus:ring-1 focus:outline-none bg-ui-surface text-ui-ink"
                        >
                          <option value="">Select time</option>
                          {timeOptions.map(({ value, display }) => (
                            <option key={value} value={value}>
                              {display}
                            </option>
                          ))}
                        </select>
                      )}

                      <p className="text-xs text-ui-muted mt-1">
                        {isStartTimeTBD ? 'Start time will be determined later' : '15-minute increments'}
                      </p>
                    </div>
                  </div>

                  {/* End Date & End Time Row */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* End Date Field */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="memo_end_date" className="text-sm font-bold text-ui-ink-2 flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 text-ui-amber-fg" />
                          End Date
                        </Label>
                        <Button
                          type="button"
                          variant={formData.component_data?.end_date_is_tbd ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            const currentTBD = formData.component_data?.end_date_is_tbd || false;
                            handleDetailsChange('end_date_is_tbd', !currentTBD);
                            if (!currentTBD) {
                              handleInputChange('end_datetime', '');
                            }
                          }}
                          className={`text-xs h-7 px-2 ${formData.component_data?.end_date_is_tbd ? 'bg-ui-amber-strong hover:bg-ui-amber-strong/90 text-ui-on-primary' : 'text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-bg'}`}
                        >
                          {formData.component_data?.end_date_is_tbd ? '✓ TBD' : 'TBD'}
                        </Button>
                      </div>

                      {formData.component_data?.end_date_is_tbd ? (
                        <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                          <span className="text-ui-amber-fg font-medium">End Date To Be Determined</span>
                        </div>
                      ) : (
                        <DatePickerButton
                          label={(() => {
                            if (!formData.end_datetime) {
                              if (formData.start_datetime && !formData.start_datetime.includes('TBD')) {
                                try {
                                  const base = formData.start_datetime.split('T')[0];
                                  return format(parseLocalDate(base), 'EEE, MMM d, yyyy');
                                } catch { return 'Select end date'; }
                              }
                              return 'Select end date';
                            }
                            try {
                              return format(new Date(formData.end_datetime), 'EEE, MMM d, yyyy');
                            } catch { return 'Select end date'; }
                          })()}
                          selectedDate={(() => {
                            if (formData.end_datetime) {
                              try {
                                return new Date(formData.end_datetime);
                              } catch { return undefined; }
                            }
                            if (formData.start_datetime && !formData.start_datetime.includes('TBD')) {
                              try {
                                const base = formData.start_datetime.split('T')[0];
                                return parseLocalDate(base);
                              } catch { return undefined; }
                            }
                            return undefined;
                          })()}
                          onPick={(date) => {
                            if (date) {
                              const iso = format(date, 'yyyy-MM-dd');
                              let endTime = '12:00';
                              if (formData.end_datetime) {
                                try {
                                  endTime = format(new Date(formData.end_datetime), 'HH:mm');
                                } catch {}
                              } else if (formData.start_datetime && !formData.start_datetime.includes('TBD')) {
                                try {
                                  endTime = format(new Date(formData.start_datetime), 'HH:mm');
                                } catch {}
                              }
                              handleInputChange('end_datetime', `${iso}T${endTime}`);
                            }
                          }}
                        />
                      )}

                      <p className="text-xs text-ui-muted mt-1">
                        {formData.component_data?.end_date_is_tbd ? 'End date will be determined later' : 'Defaults to start date'}
                      </p>
                    </div>

                    {/* End Time Field */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="memo_end_time" className="text-sm font-bold text-ui-ink-2 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-ui-amber-fg" />
                          End Time
                        </Label>
                        <Button
                          type="button"
                          variant={isEndTimeTBD ? "default" : "outline"}
                          size="sm"
                          onClick={handleEndTimeTBDToggle}
                          className={`text-xs h-7 px-2 ${isEndTimeTBD ? 'bg-ui-amber-strong hover:bg-ui-amber-strong/90 text-ui-on-primary' : 'text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-bg'}`}
                        >
                          {isEndTimeTBD ? '✓ TBD' : 'TBD'}
                        </Button>
                      </div>

                      {isEndTimeTBD ? (
                        <div className="h-12 flex items-center justify-center bg-ui-amber-bg border border-ui-amber-line rounded-md">
                          <span className="text-ui-amber-fg font-medium">End Time To Be Determined</span>
                        </div>
                      ) : (
                        <select
                          id="memo_end_time"
                          value={formData.end_datetime ? (() => {
                            try {
                              if (formData.end_datetime === 'TBD') return '';
                              return format(new Date(formData.end_datetime), 'HH:mm');
                            } catch (error) {
                              return '';
                            }
                          })() : ''}
                          onChange={(e) => {
                            if (e.target.value) {
                              let endDateStr = '';
                              if (formData.end_datetime) {
                                try {
                                  endDateStr = format(new Date(formData.end_datetime), 'yyyy-MM-dd');
                                } catch {}
                              }
                              if (!endDateStr && formData.start_datetime && !formData.start_datetime.includes('TBD')) {
                                try {
                                  endDateStr = format(new Date(formData.start_datetime), 'yyyy-MM-dd');
                                } catch {}
                              }
                              if (endDateStr) {
                                handleInputChange('end_datetime', `${endDateStr}T${e.target.value}`);
                              }
                            } else {
                              handleInputChange('end_datetime', '');
                            }
                          }}
                          className="h-12 w-full text-base border border-ui-line rounded-md px-3 focus:border-ui-amber-solid focus:ring-ui-amber-solid focus:ring-1 focus:outline-none bg-ui-surface text-ui-ink"
                        >
                          <option value="">No end time</option>
                          {endTimeOptions.map(({ value, display }) => (
                            <option key={value} value={value}>
                              {display}
                            </option>
                          ))}
                        </select>
                      )}

                      <p className="text-xs text-ui-muted mt-1">
                        {isEndTimeTBD ? 'End time will be determined later' : 'Options start from start time'}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Hide Description field for private flights and memos */}
            {(() => {
              const actualComponentType = component ? formData.component_type : componentType;
              return actualComponentType !== 'private_flight' && actualComponentType !== 'memo';
            })() && (
              <FormField
                id="description"
                label="Description"
                type="textarea"
                value={formData.description}
                onChange={handleDescriptionChange}
                placeholder="Enter description..."
              />
            )}

            <FormField
              id={componentType === 'private_flight' ? 'flight_department_request' : 'notes'}
              label={componentType === 'private_flight' ? 'Request to Flight Department' : 'Additional Notes'}
              type="textarea"
              value={componentType === 'private_flight' ? (formData.component_data?.flight_department_request || '') : formData.notes}
              onChange={componentType === 'private_flight' ?
                (value) => handlePrivateFlightDetailsChange('flight_department_request', value) :
                handleNotesChange
              }
              placeholder={componentType === 'private_flight' ? 'Enter your request details for the flight department...' : 'Add any additional notes...'}
              required={false}
            />

            {/* Component Confidentiality Section */}
            <div className="p-4 bg-ui-amber-bg rounded-xl border border-ui-amber-line mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-semibold text-ui-ink flex items-center gap-2">
                    <svg className="w-4 h-4 text-ui-amber-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Confidential Component
                  </label>
                  <p className="text-xs text-ui-muted mt-0.5">
                    Hide this component from team members
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsConfidential(!isConfidential)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ui-amber-solid focus:ring-offset-2 ${
                    isConfidential ? 'bg-ui-amber-strong' : 'bg-ui-line'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-ui-surface shadow ring-0 transition duration-200 ease-in-out ${
                      isConfidential ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {isConfidential && (
                <div className="mt-4 space-y-3">
                  <div className="text-xs text-ui-amber-fg bg-ui-amber-bg rounded px-2 py-1">
                    Only you, users in the access list below, supervisors, and org admins can see this component.
                  </div>

                  {/* Access List Management */}
                  <div>
                    <label className="text-sm font-medium text-ui-ink-2 mb-2 block">
                      Users with Access
                    </label>

                    {/* Search and Add User */}
                    <div className="relative mb-2">
                      <input
                        type="text"
                        value={accessUserSearch}
                        onChange={(e) => {
                          setAccessUserSearch(e.target.value);
                          setShowAccessUserDropdown(true);
                        }}
                        onFocus={() => setShowAccessUserDropdown(true)}
                        placeholder="Search users to add..."
                        className="w-full border border-ui-line rounded-md px-3 py-2 text-sm focus:border-ui-amber-solid focus:ring-ui-amber-solid"
                      />
                      {showAccessUserDropdown && accessUserSearch.trim() && (
                        <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {orgUsers
                            .filter(u => {
                              const searchLower = accessUserSearch.toLowerCase();
                              const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
                              const email = (u.email || '').toLowerCase();
                              const matchesSearch = fullName.includes(searchLower) || email.includes(searchLower);
                              const notAlreadyAdded = !accessList.some(a => a.user_id === u.id);
                              return matchesSearch && notAlreadyAdded;
                            })
                            .slice(0, 10)
                            .map(u => (
                              <button
                                key={u.id}
                                type="button"
                                onClick={() => {
                                  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                                  setAccessList([...accessList, {
                                    user_id: u.id,
                                    user_name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                                    user_email: u.email,
                                    added_by_id: currentUser.id,
                                    added_by_name: `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
                                  }]);
                                  setAccessUserSearch('');
                                  setShowAccessUserDropdown(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-ui-amber-bg text-sm"
                              >
                                <div className="font-medium">{u.first_name} {u.last_name}</div>
                                <div className="text-xs text-ui-muted">{u.email}</div>
                              </button>
                            ))}
                          {orgUsers.filter(u => {
                            const searchLower = accessUserSearch.toLowerCase();
                            const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
                            return fullName.includes(searchLower) || (u.email || '').toLowerCase().includes(searchLower);
                          }).length === 0 && (
                            <div className="px-3 py-2 text-sm text-ui-muted">No users found</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Current Access List */}
                    <div className="space-y-1">
                      {accessList.length === 0 ? (
                        <div className="text-xs text-ui-muted italic py-2">
                          No additional users added. Only you can see this component.
                        </div>
                      ) : (
                        accessList.map((entry, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-ui-surface rounded-md px-3 py-2 border border-ui-line">
                            <div>
                              <div className="text-sm font-medium">{entry.user_name}</div>
                              <div className="text-xs text-ui-muted">{entry.user_email}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setAccessList(accessList.filter((_, i) => i !== idx))}
                              className="text-ui-red-fg hover:text-ui-red-fg p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Component-Specific Fields */}
        {renderComponentSpecificFields()}

        {/* Form Footer */}
        <div className="sticky bottom-0 bg-ui-surface/95 backdrop-blur-lg py-6 -mx-6 px-6 border-t border-ui-line shadow-xl rounded-t-2xl">
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCancel();
              }}
              disabled={loading}
              className="px-8 py-3 h-auto text-base border border-ui-control rounded-lg hover:bg-ui-hover transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                // Flights must have at least one leg
                ((componentType === 'commercial_flight' || componentType === 'flight') && flightLegs.length === 0) ||
                // Private flight must have at least one leg
                (componentType === 'private_flight' && privateFlightLegs.length === 0) ||
                // Meetings: require a title only (allow TBD/no start_datetime)
                (componentType === 'meeting' && (!formData.title || !formData.title.trim())) ||
                // Restaurants: require title and start_datetime only if not TBD
                (componentType === 'restaurant' && (
                  (!formData.title || !formData.title.trim()) ||
                  (!formData.date_is_tbd && !formData.time_is_tbd && !formData.start_datetime)
                )) ||
                // Events: require title and location, allow TBD dates/times
                (componentType === 'event' && (
                  (!formData.title || !formData.title.trim()) ||
                  (!formData.location || !formData.location.trim()) ||
                  (!isDateTBD && !isStartTimeTBD && !formData.start_datetime)
                )) ||
                // Memos: require only a title (allow TBD dates/times)
                (componentType === 'memo' && (!formData.title || !formData.title.trim())) ||
                // Speaker meetings: require a title or program topic (allow TBD dates/times)
                (componentType === 'speaker_meeting' && (
                  (!formData.title || !formData.title.trim()) &&
                  (!formData.component_data?.program_topic || !formData.component_data.program_topic.trim())
                )) ||
                // Airport VIP Service: require linked segment, direction, and provider
                (componentType === 'airport_vip_service' && (
                  !formData.component_data?.linked_segment_id ||
                  !formData.component_data?.service_direction ||
                  !formData.component_data?.provider_name?.trim?.()
                )) ||
                // Other component types (excluding rental cars, restaurants, events, memos, and airport VIP services which handle own validation): require title and start_datetime
                (componentType !== 'commercial_flight' && componentType !== 'flight' && componentType !== 'private_flight' && componentType !== 'meeting' && componentType !== 'speaker_meeting' && componentType !== 'rental_car' && componentType !== 'restaurant' && componentType !== 'event' && componentType !== 'memo' && componentType !== 'airport_vip_service' && (!formData.title?.trim?.() || !formData.start_datetime))
              }
              className="bg-gradient-to-r from-ui-violet-strong to-ui-violet-strong hover:from-ui-violet-strong hover:to-ui-violet-strong min-w-40 px-8 py-3 h-auto text-base shadow-lg hover:shadow-xl text-ui-on-primary rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {component?.id ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                <>
                  {component?.id ? (
                    <>
                      <Edit className="w-4 h-4" />
                      Update {currentConfig.title}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add {currentConfig.title}
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Document Viewer Modal - Production Ready */}
      {viewingDocument && (
        <div className="fixed inset-0 bg-ui-overlay bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-ui-surface rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-ui-ink">
                {viewingDocument.name}
              </h3>
              <button
                onClick={() => {
                  setViewingDocument(null);
                  setDocumentContent(null);
                }}
                className="text-ui-muted hover:text-ui-ink-3 hover:bg-ui-hover rounded-full p-2"
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 p-4 min-h-0 relative">
              {/* PDF Controls Overlay - Show for PDF files */}
              {documentContent && documentContent.type === 'pdf' && (
                <div className="absolute top-6 right-6 bg-ui-primary text-ui-on-primary p-3 rounded-lg shadow-xl z-10 max-w-xs">
                  <div className="text-sm font-medium mb-2">
                    📄 PDF Controls
                  </div>
                  <div className="text-xs text-ui-on-primary mb-3">
                    {viewingDocument?.name || 'PDF File'}
                  </div>
                  
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        // Try to navigate current window to PDF
                        window.location.href = documentContent.blobUrl;
                      }}
                      className="block w-full bg-ui-violet-strong hover:bg-ui-violet-strong/90 text-ui-on-primary px-3 py-2 rounded text-xs text-center transition-colors"
                    >
                      📖 View Full PDF
                    </button>
                    
                    <div className="text-xs text-ui-on-primary mt-2 pt-2 border-t border-ui-primary">
                      PDF loaded: {Math.round((viewingDocument?.size || 0) / 1024)}KB
                    </div>
                  </div>
                </div>
              )}
              
              <div className="h-full border border-ui-line rounded-lg bg-ui-sunk">{documentContent?.type === 'email' ? (
                  <iframe title="Original email" sandbox="" referrerPolicy="no-referrer" srcDoc={documentContent.content} className="w-full h-full rounded-lg" />
                ) : documentContent?.type === 'pdf' || documentContent?.type === 'blob' ? (
                  <div className="w-full h-full">
                    {documentContent?.type === 'pdf' ? (
                      // PDF viewer with navigation controls
                      <iframe
                        src={`${documentContent.blobUrl}#toolbar=1&navpanes=1&scrollbar=1&page=1&zoom=auto`}
                        className="w-full h-full rounded-lg border-0"
                        title={`PDF Document: ${viewingDocument.name}`}
                        type="application/pdf"
                      />
                    ) : (
                      // Generic document viewer for images and other file types
                      <iframe
                        src={documentContent.blobUrl}
                        className="w-full h-full rounded-lg"
                        title={`Document: ${viewingDocument.name}`}
                        type={documentContent.contentType || 'application/octet-stream'}
                      />
                    )}
                  </div>
                ) : documentContent?.type === 'text' ? (
                  <div className="h-full p-6 overflow-auto bg-ui-surface">
                    <pre className="whitespace-pre-wrap text-sm font-mono text-ui-ink">
                      {documentContent.content}
                    </pre>
                  </div>
                ) : documentContent?.type === 'error' ? (
                  <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                    <div className="mb-4">
                      <svg className="w-16 h-16 text-ui-red-fg mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-ui-red-fg mb-2">Unable to Load Document</h3>
                    <p className="text-ui-red-fg mb-4">{documentContent.message}</p>
                    <p className="text-xs text-ui-muted mt-2 px-4 py-2 bg-ui-hover rounded border">
                      💡 Check the browser console (F12) for detailed error information
                    </p>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ui-primary mx-auto mb-4"></div>
                      <p className="text-ui-muted">Loading document...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t bg-ui-sunk">
              <div className="text-sm text-ui-ink-3">
                {Number.isFinite(documentContent?.fileInfo?.size) && (
                  <>
                    {Math.round(documentContent.fileInfo.size / 1024)}KB
                    {documentContent.contentType && ` • ${documentContent.contentType}`}
                  </>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    // Download via authenticated fetch
                    if (documentContent?.downloadUrl) {
                      fetch(documentContent.downloadUrl, {
                        headers: getAuthHeaders(),
                        credentials: 'include'
                      })
                      .then(response => response.blob())
                      .then(blob => {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = viewingDocument.name;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      })
                      .catch(err => console.error('Download failed:', err));
                    }
                  }}
                  className="px-4 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover transition-colors"
                >
                  Download
                </button>
                <button
                  onClick={handleCloseDocumentViewer}
                  className="px-4 py-2 bg-ui-ink text-ui-surface rounded-md hover:bg-ui-ink transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Flight Leg Form Modal */}
      <Dialog open={showFlightLegForm} onOpenChange={setShowFlightLegForm}>
        <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()}>
          <PortalContainerProvider>
            <DialogHeader>
              <DialogTitle>
                {currentFlightLeg?.index !== undefined ? 'Edit' : 'Add'} Flight Leg
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Airline Selection */}
              <div>
                <Label className="text-sm font-semibold text-ui-ink-2 mb-2 block">Airline</Label>
                <AirlineAutocomplete
                value={currentFlightLeg?.airline_name || currentFlightLeg?.airline_code || ''}
                onSelect={(airline) => {
                  // airline: { code, name }
                  handleFlightLegChange('airline_code', airline.code);
                  handleFlightLegChange('airline_name', airline.name);
                  // Set formatted airline display for backend storage (e.g., "American Airlines (AA)")
                  handleFlightLegChange('airline', airline.code ? `${airline.name} (${airline.code})` : airline.name);
                }}
                onChangeText={(text) => {
                  // Keep typing in state
                  handleFlightLegChange('airline_name', text);
                  // If user types a 2-3 letter code (like "AA", "UAL"), also set it as airline_code
                  // This allows the verify button to work even without selecting from dropdown
                  const trimmed = (text || '').trim().toUpperCase();
                  if (/^[A-Z]{2,3}$/.test(trimmed)) {
                    handleFlightLegChange('airline_code', trimmed);
                  } else if (text.includes(' — ')) {
                    // Handle case where display format is "AA — American Airlines"
                    const code = text.split(' — ')[0].trim().toUpperCase();
                    if (/^[A-Z]{2,3}$/.test(code)) {
                      handleFlightLegChange('airline_code', code);
                    }
                  }
                }}
                placeholder="Type airline name or 2-letter code"
                className="w-full"
              />
            </div>
            
            {/* Flight Number */}
            <div>
              <Label className="text-sm font-semibold text-ui-ink-2 mb-2 block">Flight Number</Label>
              <Input 
                value={currentFlightLeg?.flight_number || ''}
                onChange={(e) => handleFlightLegChange('flight_number', e.target.value)}
                placeholder="e.g. 123"
                className="w-full"
              />
            </div>
            
            {/* Departure Airport */}
            <div>
              <Label className="text-sm font-semibold text-ui-ink-2 mb-2 block">
                Departure Airport <span className="text-ui-red-fg">*</span>
              </Label>
              <AirportAutocomplete
                placeholder="Departure airport (ICAO/IATA/name)"
                value={currentFlightLeg?.departure_airport || ''}
                onAirportSelect={(ap) => {
                  setCurrentFlightLeg(prev => ({
                    ...prev,
                    departure_airport: ap.iata_code || ap.icao_code || ap.name,
                    departure_airport_icao: ap.icao_code || '',
                  }));
                }}
                onValueChange={(text) => setCurrentFlightLeg(prev => ({...prev, departure_airport: text}))}
                className="w-full"
              />
            </div>
            
            {/* Arrival Airport */}
            <div>
              <Label className="text-sm font-semibold text-ui-ink-2 mb-2 block">
                Arrival Airport <span className="text-ui-red-fg">*</span>
              </Label>
              <AirportAutocomplete
                placeholder="Arrival airport (ICAO/IATA/name)"
                value={currentFlightLeg?.arrival_airport || ''}
                onAirportSelect={(ap) => {
                  setCurrentFlightLeg(prev => ({
                    ...prev,
                    arrival_airport: ap.iata_code || ap.icao_code || ap.name,
                    arrival_airport_icao: ap.icao_code || '',
                  }));
                }}
                onValueChange={(text) => setCurrentFlightLeg(prev => ({...prev, arrival_airport: text}))}
                className="w-full"
              />
            </div>
            
            {/* Departure Date & Time (separated) */}
            <div>
              <Label className="text-sm font-semibold text-ui-ink-2 mb-2 block">Departure Date & Time</Label>
              <div className="grid md:grid-cols-2 gap-2">
                {/* Date picker */}
                <DatePickerButton
                  label={(() => {
                    const v = currentFlightLeg?.departure_time || '';
                    if (!v) return 'Select date';
                    const datePart = v.includes('T') ? v.split('T')[0] : v.split(' ')[0];
                    try { return format(new Date(datePart), 'EEE, MMM d, yyyy'); } catch { return 'Select date'; }
                  })()}
                  selectedDate={(() => {
                    const v = currentFlightLeg?.departure_time || '';
                    if (!v) return undefined;
                    const datePart = v.includes('T') ? v.split('T')[0] : v.split(' ')[0];
                    try { return new Date(datePart); } catch { return undefined; }
                  })()}
                  onPick={(date) => {
                    const iso = date ? format(date, 'yyyy-MM-dd') : '';
                    const v = currentFlightLeg?.departure_time || '';
                    const timePart = (v && v.includes('T')) ? v.split('T')[1].substring(0,5) : (depTimeRaw || '09:00');
                    if (iso) {
                      const dt = `${iso}T${timePart}`;
                      handleFlightLegChange('departure_time', dt);
                      // Mirror arrival to departure by default
                      handleFlightLegChange('arrival_time', dt);
                      // Set raw time values if not already set
                      if (!depTimeRaw) setDepTimeRaw(timePart);
                      if (!arrTimeRaw) setArrTimeRaw(timePart);
                    }
                  }}
                />
                {/* Time input with 24H/AM/PM pulldown */}
                <div className="flex gap-2">
                  <Input
                    placeholder="HH:MM"
                    value={depTimeRaw}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setDepTimeRaw(raw);
                      // Try to parse and update the datetime if valid
                      const fmt = depTimeFormat || '24H';
                      const parsed = parseTimeInput(fmt === '24H' ? raw : `${raw} ${fmt}`, { time_view_format: fmt });
                      if (parsed) {
                        const v = currentFlightLeg?.departure_time || '';
                        const datePart = v && v.includes('T') ? v.split('T')[0] : (new Date().toISOString().split('T')[0]);
                        handleFlightLegChange('departure_time', `${datePart}T${parsed}`);
                        // Also update arrival if not yet set
                        if (!currentFlightLeg?.arrival_time) {
                          handleFlightLegChange('arrival_time', `${datePart}T${parsed}`);
                          setArrTimeRaw(parsed);
                        }
                      }
                    }}
                    onBlur={() => {
                      // On blur, try to parse and normalize the time
                      const fmt = depTimeFormat || '24H';
                      const parsed = parseTimeInput(fmt === '24H' ? depTimeRaw : `${depTimeRaw} ${fmt}`, { time_view_format: fmt });
                      if (parsed) {
                        setDepTimeRaw(parsed);
                      }
                    }}
                    className="w-full"
                  />
                  <Select value={depTimeFormat} onValueChange={(val) => setDepTimeFormat(val)}>
                    <SelectTrigger className="w-28">
                      <SelectValue placeholder="24H" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24H">24H</SelectItem>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Verify via FlightAware */}
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                className={`px-4 py-2 rounded-md text-ui-on-primary flex items-center gap-2 ${!flightVerificationLocked && currentFlightLeg?.airline_code && currentFlightLeg?.flight_number && (currentFlightLeg?.departure_time || currentFlightLeg?.arrival_time) ? 'bg-ui-primary hover:bg-ui-primary-hover' : 'bg-ui-control cursor-not-allowed'}`}
                disabled={flightVerificationLocked || !(currentFlightLeg?.airline_code && currentFlightLeg?.flight_number && (currentFlightLeg?.departure_time || currentFlightLeg?.arrival_time))}
                onClick={async () => {
                  if (!(currentFlightLeg?.airline_code && currentFlightLeg?.flight_number && (currentFlightLeg?.departure_time || currentFlightLeg?.arrival_time))) return;
                  try {
                    setLoading(true);
                    // Support verification by either departure or arrival date
                    const depDate = currentFlightLeg.departure_time ? currentFlightLeg.departure_time.split('T')[0] : null;
                    const arrDate = currentFlightLeg.arrival_time ? currentFlightLeg.arrival_time.split('T')[0] : null;
                    const requestBody = {
                      airline_code: String(currentFlightLeg.airline_code || '').trim().toUpperCase(),
                      flight_number: String(currentFlightLeg.flight_number || '').trim(),
                      origin: (currentFlightLeg?.departure_airport_icao || currentFlightLeg?.departure_airport || '').toString().toUpperCase() || undefined,
                      destination: (currentFlightLeg?.arrival_airport_icao || currentFlightLeg?.arrival_airport || '').toString().toUpperCase() || undefined
                    };
                    // Send departure_date if available, otherwise send arrival_date
                    if (depDate) {
                      requestBody.departure_date = depDate;
                    } else if (arrDate) {
                      requestBody.arrival_date = arrDate;
                    }
                    const res = await fetch(`${getApiBase()}/flightaware/verify`, {
                      method: 'POST',
                      headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json'
                      },
                      credentials: 'include',
                      body: JSON.stringify(requestBody)
                    });
                    const data = await res.json();
                    if (!res.ok || !data?.success || (data?.total_legs || 0) === 0) {
                      throw new Error(data?.message || 'Verification failed');
                    }
                    const legs = data.flight_legs || [];
                    // Try to pick best leg using any user-specified airports
                    const userDep = (currentFlightLeg?.departure_airport || currentFlightLeg?.departure_airport_icao || '').toUpperCase();
                    const userArr = (currentFlightLeg?.arrival_airport || currentFlightLeg?.arrival_airport_icao || '').toUpperCase();
                    const scoreLeg = (leg) => {
                      let s = 0;
                      const o = leg.origin || {}; const d = leg.destination || {};
                      const oc = (o.code_iata || o.code_icao || '').toUpperCase();
                      const dc = (d.code_iata || d.code_icao || '').toUpperCase();
                      if (userDep && (oc.includes(userDep))) s += 5;
                      if (userArr && (dc.includes(userArr))) s += 5;
                      // Prefer scheduled match on same date (already filtered server-side)
                      if (leg?.times?.scheduled_departure) s += 1;
                      return s;
                    };
                    legs.sort((a,b)=>scoreLeg(b)-scoreLeg(a));
                    let picked = legs[0];
                    if (!picked) throw new Error('No matching leg found');
                    // If multiple and no clear origin/destination match, prompt user to select
                    if (legs.length > 1 && !(userDep && userArr)) {
                      // Open a modal picker for user to select a leg
                      setFlightAwareOptions(legs);
                      setShowFlightAwarePicker(true);
                      return; // wait for user selection
                    }
                    // Fill all fields with correct info
                    const o = picked.origin || {}; const d = picked.destination || {}; const t = picked.times || {};
                    const depTimeValue = t.scheduled_departure || t.estimated_departure || t.actual_departure || '';
                    const arrTimeValue = t.scheduled_arrival || t.estimated_arrival || t.actual_arrival || '';
                    setCurrentFlightLeg(prev => ({
                      ...prev,
                      airline_code: (currentFlightLeg?.airline_code || picked.ident?.replace(/\d+.*/, '') || prev?.airline_code || '').toUpperCase(),
                      airline_name: prev?.airline_name || '',
                      departure_airport: o.code_iata || o.code_icao || prev?.departure_airport || '',
                      departure_airport_icao: o.code_icao || '',
                      arrival_airport: d.code_iata || d.code_icao || prev?.arrival_airport || '',
                      arrival_airport_icao: d.code_icao || '',
                      departure_time: depTimeValue || prev?.departure_time || '',
                      arrival_time: arrTimeValue || prev?.arrival_time || '',
                      departure_terminal: o.terminal || prev?.departure_terminal || '',
                      arrival_terminal: d.terminal || prev?.arrival_terminal || '',
                      status: picked.status || prev?.status || '',
                      aircraft_type: picked.aircraft_type || prev?.aircraft_type || ''
                    }));
                    // Update raw time inputs for display
                    if (depTimeValue && depTimeValue.includes('T')) {
                      setDepTimeRaw(depTimeValue.split('T')[1].substring(0, 5));
                    }
                    if (arrTimeValue && arrTimeValue.includes('T')) {
                      setArrTimeRaw(arrTimeValue.split('T')[1].substring(0, 5));
                    }
                  } catch (e) {
                    alert(`Verification failed: ${e?.message || e}`);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Verify
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}

              </button>
              {flightVerificationLocked ? (
                <span className="text-xs text-ui-amber-fg">
                  Flight verification not included in {flightVerificationPackageName || 'current'} package
                </span>
              ) : (
                <span className="text-xs text-ui-muted">Requires Airline, Flight Number, and Departure or Arrival Date</span>
              )}
            </div>

            {/* Arrival Date & Time (separated) */}
            <div>
              <Label className="text-sm font-semibold text-ui-ink-2 mb-2 block">Arrival Date & Time</Label>
              <div className="grid md:grid-cols-2 gap-2">
                {/* Date picker */}
                <DatePickerButton
                  label={(() => {
                    const v = currentFlightLeg?.arrival_time || '';
                    if (!v) return 'Select date';
                    const datePart = v.includes('T') ? v.split('T')[0] : v.split(' ')[0];
                    try { return format(new Date(datePart), 'EEE, MMM d, yyyy'); } catch { return 'Select date'; }
                  })()}
                  selectedDate={(() => {
                    const v = currentFlightLeg?.arrival_time || '';
                    if (!v) return undefined;
                    const datePart = v.includes('T') ? v.split('T')[0] : v.split(' ')[0];
                    try { return new Date(datePart); } catch { return undefined; }
                  })()}
                  onPick={(date) => {
                    const iso = date ? format(date, 'yyyy-MM-dd') : '';
                    const v = currentFlightLeg?.arrival_time || '';
                    const timePart = (v && v.includes('T')) ? v.split('T')[1].substring(0,5) : (arrTimeRaw || '11:00');
                    if (iso) {
                      handleFlightLegChange('arrival_time', `${iso}T${timePart}`);
                      if (!arrTimeRaw) setArrTimeRaw(timePart);
                    }
                  }}
                />
                {/* Time input with 24H/AM/PM pulldown */}
                <div className="flex gap-2">
                  <Input
                    placeholder="HH:MM"
                    value={arrTimeRaw}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setArrTimeRaw(raw);
                      // Try to parse and update the datetime if valid
                      const fmt = arrTimeFormat || '24H';
                      const parsed = parseTimeInput(fmt === '24H' ? raw : `${raw} ${fmt}`, { time_view_format: fmt });
                      if (parsed) {
                        const v = currentFlightLeg?.arrival_time || '';
                        const datePart = v && v.includes('T') ? v.split('T')[0] : (new Date().toISOString().split('T')[0]);
                        handleFlightLegChange('arrival_time', `${datePart}T${parsed}`);
                      }
                    }}
                    onBlur={() => {
                      // On blur, try to parse and normalize the time
                      const fmt = arrTimeFormat || '24H';
                      const parsed = parseTimeInput(fmt === '24H' ? arrTimeRaw : `${arrTimeRaw} ${fmt}`, { time_view_format: fmt });
                      if (parsed) {
                        setArrTimeRaw(parsed);
                      }
                    }}
                    className="w-full"
                  />
                  <Select value={arrTimeFormat} onValueChange={(val) => setArrTimeFormat(val)}>
                    <SelectTrigger className="w-28">
                      <SelectValue placeholder="24H" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24H">24H</SelectItem>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Validation Error in Modal */}
            {validationError && (
              <div className="p-3 bg-ui-red-bg border border-ui-red-line rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-ui-red-fg" />
                  <p className="text-ui-red-fg text-sm">{validationError}</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelFlightLeg}>
              Cancel
            </Button>
            <Button onClick={handleAddOrUpdateFlightLeg} className="bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary">
              {currentFlightLeg?.index !== undefined ? 'Update Leg' : 'Add Leg'}
            </Button>
          </DialogFooter>
          </PortalContainerProvider>
        </DialogContent>
      </Dialog>

      {/* Attendee Form Modal */}
      {showAttendeeForm && (
        <AttendeeFormModal
          attendee={editingAttendee}
          onSubmit={handleAddAttendee}
          onCancel={handleCancelAttendeeForm}
        />
      )}

      {/* New/Edit Contact Modal - Available for all component types (hotel room guests, etc.) */}
      {showNewPassengerModal && (
        <Dialog open={showNewPassengerModal} onOpenChange={handleCloseNewPassengerModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingContact ? (
                  <>
                    <Edit className="w-5 h-5 text-ui-link" />
                    Edit Contact
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 text-ui-green-fg" />
                    Add New Contact
                  </>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-ui-ink-2 mb-1 block">First Name *</Label>
                  <Input
                    value={newPassengerForm.first_name}
                    onChange={(e) => setNewPassengerForm(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="First name"
                    className="h-10"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-ui-ink-2 mb-1 block">Last Name</Label>
                  <Input
                    value={newPassengerForm.last_name}
                    onChange={(e) => setNewPassengerForm(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Last name"
                    className="h-10"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <Label className="text-sm font-medium text-ui-ink-2 mb-1 block">Email</Label>
                <Input
                  type="email"
                  value={newPassengerForm.email}
                  onChange={(e) => setNewPassengerForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="h-10"
                />
              </div>

              {/* Phone */}
              <div>
                <Label className="text-sm font-medium text-ui-ink-2 mb-1 block">Phone</Label>
                <Input
                  value={newPassengerForm.mobile_phone}
                  onChange={(e) => setNewPassengerForm(prev => ({ ...prev, mobile_phone: e.target.value }))}
                  placeholder="Phone number"
                  className="h-10"
                />
              </div>

              {/* Company */}
              <div>
                <Label className="text-sm font-medium text-ui-ink-2 mb-1 block">Company</Label>
                <Input
                  value={newPassengerForm.company}
                  onChange={(e) => setNewPassengerForm(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="Company name"
                  className="h-10"
                />
              </div>

              {/* Save to Contacts Checkbox */}
              {!editingContact && (
                <div className="flex items-center gap-3 pt-2 border-t border-ui-line">
                  <input
                    type="checkbox"
                    id="saveToContactsGlobal"
                    checked={newPassengerForm.saveToContacts}
                    onChange={(e) => setNewPassengerForm(prev => ({ ...prev, saveToContacts: e.target.checked }))}
                    className="w-4 h-4 text-ui-green-fg border-ui-control rounded focus:ring-ui-green-solid"
                  />
                  <label htmlFor="saveToContactsGlobal" className="text-sm text-ui-ink-2 cursor-pointer">
                    <span className="font-medium">Save to Contacts</span>
                    <span className="text-ui-muted block text-xs">Add this person to your contacts list</span>
                  </label>
                </div>
              )}
            </div>

            <DialogFooter className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseNewPassengerModal}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddNewPassenger}
                className="flex-1 bg-ui-green-strong hover:bg-ui-green-strong/90 text-ui-on-primary"
              >
                {editingContact ? 'Update Contact' : 'Add Contact'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* VIP Service Provider editor (inline modal launched from the autocomplete pencil) */}
      {vipEditingProvider && (
        <EditVipProviderModal
          provider={vipEditingProvider}
          onClose={() => setVipEditingProvider(null)}
          onSaved={(provider) => {
            setVipSelectedProvider(provider);
            setFormData(prev => ({
              ...prev,
              component_data: {
                ...prev.component_data,
                provider_id: provider.id,
                provider_name: provider.name || '',
                provider_website: provider.website || '',
                provider_phone: provider.phone || '',
                provider_email: provider.email || '',
              },
            }));
          }}
          onDeleted={(deletedId) => {
            if (vipSelectedProvider?.id === deletedId) {
              setVipSelectedProvider(null);
              setFormData(prev => ({
                ...prev,
                component_data: { ...prev.component_data, provider_id: null },
              }));
            }
          }}
        />
      )}
    </div>
  );
};

export default TripComponentForm;